import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  runTransaction,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from "firebase/firestore";
import {
  onAuthStateChanged,
  signInAnonymously,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { getFunctions, httpsCallable } from "firebase/functions";
import {
  getDownloadURL,
  ref as storageRef,
  uploadBytes,
} from "firebase/storage";
import {
  AlertTriangle,
  ArrowUpRight,
  BarChart3,
  Banknote,
  Bot,
  BriefcaseBusiness,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  Check,
  CheckCheck,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  ClipboardCheck,
  ClipboardList,
  Clock,
  Crown,
  Database,
  Download,
  Eye,
  FileCheck2,
  FileSpreadsheet,
  FileText,
  Gauge,
  Home,
  IndianRupee,
  Layers3,
  Landmark,
  Lock,
  LogOut,
  Mail,
  MapPin,
  MessageSquareText,
  Pencil,
  RefreshCw,
  ReceiptIndianRupee,
  Search,
  Save,
  Settings,
  ShieldCheck,
  ShieldAlert,
  Sparkles,
  Target,
  TrendingUp,
  Trash2,
  UserCheck,
  UserRoundSearch,
  Users,
  Video,
  Wallet,
  X,
  Zap,
} from "lucide-react";
import { auth, db, storage } from "../../firebaseConfig";
import InterviewSchedulerModal from "../../components/interview/InterviewSchedulerModal";
import VideoInterviewRoom from "../../components/interview/VideoInterviewRoom";

const interviewFunctions = getFunctions(undefined, "asia-south1");

const COLLECTIONS = {
  users: "hr_users",
  candidateUsers: "candidate_users",
  requisitions: "hr_job_requisitions",
  jobs: "hr_job_postings",
  applications: "job_applications",
  interviews: "hr_interviews",
  employees: "employees",
  onboarding: "hr_onboarding",
  attendance: "attendance_logs",
  employeeRequests: "hr_employee_requests",
  leaves: "hr_leave_requests",
  payroll: "payroll_records",
  statutory: "hr_statutory_obligations",
  holidays: "hr_holidays",
  settings: "hrms_settings",
  payrollBatches: "payroll_batches",
  automationRuns: "automation_runs",
  reimbursements: "hr_reimbursements",
  documents: "hr_documents",
  approvals: "approval_requests",
  paymentRefs: "payroll_payment_references",
  mail: "mail_queue",
  audit: "audit_logs",
};

const OFFICIAL_EMAILS = Object.freeze({
  CEO: "ceo@shineagri.com",
  CEO_LEGACY: "ceo@shineagri.org",
  HR: "hr@shineagri.org",
  ACCOUNT: "account@shineagri.org",
});

// PRODUCTION LOGIN:
// Keep this `false`. CEO and HR must sign in through Firebase Email/Password.
// This switch affects only the HRMS test bypass; it does not change mail_queue,
// SMTP secrets, Cloud Functions, candidate login or employee ESS login.
const HRMS_TEST_MODE = false;
const DEPARTMENT_OPTIONS = [
  ["admin-hr", "Administration & Human Resources"],
  ["finance-accounts", "Finance & Accounts"],
  ["procurement-ops", "Procurement & Operations"],
  ["member-services", "Member Services & Cooperative Affairs"],
  ["projects-planning", "Projects & Planning"],
  ["it-digital", "IT & Digital Systems"],
  ["legal-compliance", "Legal & Compliance"],
  ["support-services", "Support Services"],
];
const ROLE = Object.freeze({
  CEO_MASTER: "CEO_MASTER",
  HR_ADMIN: "HR_ADMIN",
  ACCOUNTS_ADMIN: "ACCOUNTS_ADMIN",
});

// Existing Cloud Functions/Firestore rules may still read the old `role`
// values. `dashboardRole` is the new two-login model; the legacy role is kept
// only until the backend/rules migration step is completed.
const LOGIN_DIRECTORY = Object.freeze({
  [OFFICIAL_EMAILS.CEO]: {
    dashboardRole: ROLE.CEO_MASTER,
    legacyRole: "CEO",
    name: "Chief Executive Officer",
  },
  [OFFICIAL_EMAILS.CEO_LEGACY]: {
    dashboardRole: ROLE.CEO_MASTER,
    legacyRole: "CEO",
    name: "Chief Executive Officer",
  },
  [OFFICIAL_EMAILS.HR]: {
    dashboardRole: ROLE.HR_ADMIN,
    legacyRole: "HR",
    name: "HR Administrator",
  },
  [OFFICIAL_EMAILS.ACCOUNT]: {
    dashboardRole: ROLE.ACCOUNTS_ADMIN,
    legacyRole: "ACCOUNTS",
    name: "Accounts Administrator",
  },
});

const CEO_LOGIN_EMAILS = new Set([
  OFFICIAL_EMAILS.CEO,
  OFFICIAL_EMAILS.CEO_LEGACY,
]);

const ACCOUNTS_MODULES = new Set([
  "dashboard",
  "payroll",
  "statutory",
]);

const CEO_APPROVAL_TYPES = new Set([
  "JOB_REQUISITION",
  "FINAL_CANDIDATE_SELECTION",
  "PAYROLL_APPROVAL",
  "LEAVE_EXCEPTION",
  "REIMBURSEMENT_APPROVAL",
]);

// CEO deletion is intentionally implemented as an audited, recoverable
// removal. Government/financial HRMS records must not disappear without an
// evidence trail, so the original document is retained with deletion metadata
// and hidden from all operational views.
const CEO_DELETABLE_COLLECTIONS = new Set([
  COLLECTIONS.requisitions,
  COLLECTIONS.applications,
  COLLECTIONS.interviews,
  COLLECTIONS.onboarding,
  COLLECTIONS.employees,
  COLLECTIONS.attendance,
  COLLECTIONS.employeeRequests,
  COLLECTIONS.leaves,
  COLLECTIONS.holidays,
  COLLECTIONS.payroll,
  COLLECTIONS.reimbursements,
  COLLECTIONS.documents,
  COLLECTIONS.approvals,
  COLLECTIONS.mail,
]);

// Claims above this configurable control limit require CEO approval.
const REIMBURSEMENT_CEO_THRESHOLD = 10000;
const DEFAULT_OFFER_LEAVE_POLICY = Object.freeze({
  CL: 12,
  EL: 18,
  OL: 6,
  COMP_OFF: 0,
});

const DEFAULT_PAYROLL_CALENDAR = Object.freeze({
  weeklyOffDays: [0],
  saturdayPolicy: "NONE",
  timeZone: "Asia/Kolkata",
});

// Gujarat GAD notification GS/22/2025/JSR/2025/509/GH. The Bakri Eid
// date includes the later GAD correction from 27 May to 28 May 2026.
const GUJARAT_2026_GENERAL_HOLIDAYS = Object.freeze([
  ["2026-01-14", "Makar Sankranti"],
  ["2026-01-26", "Republic Day"],
  ["2026-03-04", "Holi 2nd Day – Dhuleti"],
  ["2026-03-19", "Chetichand"],
  ["2026-03-21", "Ramjan Eid (Eid-Ul-Fitra)"],
  ["2026-03-26", "Shree Ram Navmi"],
  ["2026-03-31", "Mahavir Janma Kalyanak"],
  ["2026-04-03", "Good Friday"],
  ["2026-04-14", "Dr. Babasaheb Ambedkar Jayanti"],
  ["2026-05-28", "Eid-Ul-Adha (Bakri Eid)"],
  ["2026-06-26", "Muharram (Ashoora)"],
  ["2026-08-15", "Independence Day / Parsi New Year"],
  ["2026-08-26", "Eid-e-Meeladunnabi"],
  ["2026-08-28", "Raksha Bandhan"],
  ["2026-09-04", "Janmashtami"],
  ["2026-09-15", "Samvatsari"],
  ["2026-10-02", "Mahatma Gandhi Jayanti"],
  ["2026-10-20", "Dusshera (Vijaya Dashmi)"],
  ["2026-10-31", "Sardar Vallabhbhai Patel Jayanti"],
  ["2026-11-10", "Vikram Samvant New Year"],
  ["2026-11-11", "Bhai Bij"],
  ["2026-11-24", "Guru Nanak Jayanti"],
  ["2026-12-25", "Christmas"],
]);

const NAV = [
  ["requisitions", "Job Requisition", BriefcaseBusiness],
  ["recruitment", "Recruitment ATS", ClipboardList],
  ["interviews", "Interview Process", CalendarCheck],
  ["onboarding", "Onboarding", UserCheck],
  ["employees", "Employee Master", Users],
  ["attendance", "Attendance", MapPin],
  ["employeeRequests", "Employee Requests", MessageSquareText],
  ["leave", "Leave Management", Clock],
  ["holidays", "Holiday Calendar", CalendarDays],
  ["payroll", "Payroll", Wallet],
  ["statutory", "Statutory Compliance", Landmark],
  ["reimbursements", "Reimbursements", CircleDollarSign],
  ["documents", "Documents", FileText],
  ["approvals", "Approval Center", Crown],
  ["mail", "Communication", Mail],
  ["reports", "Reports & Audit", Database],
  ["settings", "Settings", Settings],
];

const MODULE_DESCRIPTIONS = {
  requisitions:
    "Create approved vacancies and control publication to the Career Portal.",
  recruitment:
    "Screen applications, shortlist talent and move candidates through ATS.",
  interviews:
    "Schedule secure panel interviews, evaluate candidates and record decisions.",
  onboarding:
    "Complete documents, employee ID, role, hierarchy and attendance activation.",
  employees:
    "Maintain authoritative employee, bank, hierarchy and workplace records.",
  attendance:
    "Review photo, device, IP and location-backed attendance evidence.",
  employeeRequests:
    "Process employee corrections, profile changes and service requests.",
  leave:
    "Manage leave balance, approvals, exceptions and LOP impact.",
  holidays:
    "Control weekly offs, official holidays and festival leave used by attendance, leave and payroll.",
  payroll:
    "Calculate, approve and send salary disbursement instructions to Accounts.",
  statutory:
    "Track person-wise Section 192/194 TDS and PF deductions, due dates, challans and payment evidence.",
  reimbursements:
    "Control expense claims, CEO thresholds and payment references.",
  documents:
    "Verify employee documents, expiry dates and secure file access.",
  approvals:
    "Central executive governance for risk, hiring and financial decisions.",
  mail:
    "Track official email delivery, failures, retries and linked workflow records.",
  reports:
    "Export management reports and review the immutable HR activity audit trail.",
  settings:
    "Review role access, production controls and HRMS operating configuration.",
};

// Compact enterprise navigation. Every detailed module remains available from
// Portal Home; the sticky header only shows clear business workspaces.
const NAV_SECTIONS = [
  {
    id: "talent",
    label: "Talent",
    icon: BriefcaseBusiness,
    target: "requisitions",
    modules: ["requisitions", "recruitment", "interviews"],
  },
  {
    id: "people",
    label: "People",
    icon: Users,
    target: "employees",
    modules: ["onboarding", "employees", "documents"],
  },
  {
    id: "time",
    label: "Time & Service",
    icon: CalendarDays,
    target: "attendance",
    modules: ["attendance", "employeeRequests", "leave", "holidays"],
  },
  {
    id: "finance",
    label: "Finance",
    icon: Wallet,
    target: "payroll",
    modules: ["payroll", "statutory", "reimbursements"],
  },
  {
    id: "governance",
    label: "Governance",
    icon: ShieldCheck,
    target: "approvals",
    modules: ["approvals", "reports"],
  },
  {
    id: "communication",
    label: "Communication",
    icon: Mail,
    target: "mail",
    modules: ["mail", "settings"],
  },
];

const badgeMap = {
  CEO_MASTER: "bg-violet-100 text-violet-700",
  HR_ADMIN: "bg-blue-100 text-blue-700",
  ACCOUNTS_ADMIN: "bg-emerald-100 text-emerald-700",
  ACTIVE: "bg-emerald-100 text-emerald-700",
  APPROVED: "bg-emerald-100 text-emerald-700",
  APPROVED_BY_CEO: "bg-emerald-100 text-emerald-700",
  READY_FOR_ACCOUNTS: "bg-cyan-100 text-cyan-700",
  SENT_TO_ACCOUNTS: "bg-indigo-100 text-indigo-700",
  MAIL_QUEUED: "bg-indigo-100 text-indigo-700",
  PENDING_ACCOUNT_PAYMENT: "bg-orange-100 text-orange-700",
  NOT_REQUESTED: "bg-slate-100 text-slate-700",
  PAYMENT_CONFIRMED: "bg-emerald-100 text-emerald-700",
  PAID: "bg-emerald-100 text-emerald-700",
  PENDING_PAYMENT: "bg-amber-100 text-amber-800",
  DUE_SOON: "bg-orange-100 text-orange-800",
  OVERDUE: "bg-red-100 text-red-700",
  PARTIALLY_PAID: "bg-violet-100 text-violet-700",
  COMPLETED: "bg-emerald-100 text-emerald-700",
  PRESENT: "bg-emerald-100 text-emerald-700",
  LIVE: "bg-emerald-100 text-emerald-700",
  WAITING: "bg-violet-100 text-violet-700",
  SHORTLISTED: "bg-cyan-100 text-cyan-700",
  INTERVIEW_SCHEDULED: "bg-violet-100 text-violet-700",
  OFFER_RELEASED: "bg-indigo-100 text-indigo-700",
  AFTER_SELECTION: "bg-violet-100 text-violet-700",
  SALARY_PENDING: "bg-amber-100 text-amber-700",
  PENDING: "bg-amber-100 text-amber-700",
  OPEN: "bg-amber-100 text-amber-700",
  PENDING_HR_REVIEW: "bg-orange-100 text-orange-700",
  PENDING_REVIEW: "bg-orange-100 text-orange-700",
  PENDING_HR_VERIFICATION: "bg-orange-100 text-orange-700",
  IN_PROGRESS: "bg-blue-100 text-blue-700",
  RESOLVED: "bg-emerald-100 text-emerald-700",
  VERIFIED: "bg-emerald-100 text-emerald-700",
  CANCELLED_BY_EMPLOYEE: "bg-slate-200 text-slate-700",
  ARCHIVED: "bg-slate-200 text-slate-700",
  PENDING_HR_APPROVAL: "bg-orange-100 text-orange-700",
  PENDING_CEO_APPROVAL: "bg-orange-100 text-orange-700",
  PAYMENT_PENDING: "bg-orange-100 text-orange-700",
  REJECTED: "bg-red-100 text-red-700",
  CANCELLED: "bg-red-100 text-red-700",
  EXPIRED: "bg-slate-200 text-slate-700",
  NO_SHOW: "bg-red-100 text-red-700",
  ABSENT: "bg-red-100 text-red-700",
  LOP: "bg-red-100 text-red-700",
};

const EMPLOYMENT_TYPE_OPTIONS = [
  {
    value: "FULL_TIME",
    label: "Full Time / Permanent",
    details:
      "Regular full-time employment. Appointment confirmation probation completion, performance review and organization policy ke subject rahega.",
  },
  {
    value: "CONTRACTUAL",
    label: "Contractual Basis",
    details:
      "Fixed contractual engagement. Contract duration, renewal, notice period, deliverables aur statutory terms appointment letter me clearly mention honge.",
  },
  {
    value: "CONTRACT",
    label: "Fixed-Term Contract",
    details:
      "Defined start aur end date wala employment. Extension sirf written management approval aur performance/requirement ke basis par hoga.",
  },
  {
    value: "PART_TIME",
    label: "Part Time",
    details:
      "Limited working hours/days wala engagement. Work schedule, monthly payout aur benefit eligibility appointment terms ke mutabik hogi.",
  },
  {
    value: "INTERNSHIP",
    label: "Internship / Trainee",
    details:
      "Learning-based fixed tenure engagement. Stipend, mentor, training objectives aur completion certificate conditions offer letter me mention hongi.",
  },
  {
    value: "CONSULTANT",
    label: "Consultant / Professional Retainer",
    details:
      "Professional service arrangement; employee benefits automatically applicable nahi honge. Retainer, scope, milestones, tax/TDS aur termination terms agreement me rahenge.",
  },
];

const defaultEmploymentDetails = (employmentType) =>
  EMPLOYMENT_TYPE_OPTIONS.find((item) => item.value === employmentType)
    ?.details || "";

const emptyReq = {
  title: "",
  department: "",
  vacancies: 1,
  employmentType: "FULL_TIME",
  employmentTypeDetails: defaultEmploymentDetails("FULL_TIME"),
  experience: "",
  reason: "",
  jobOverview: "",
  location: "",
  areasCovered: "",
  responsibilities: "",
  qualifications: "",
  skills: "",
  preferredExperience: "",
};
const civilPmcTemplate = {
  title: "Civil & PMC Engineer",
  department: "Projects & Planning",
  vacancies: 1,
  employmentType: "CONTRACTUAL",
  employmentTypeDetails: defaultEmploymentDetails("CONTRACTUAL"),
  experience: "Minimum 3 Years",
  reason: "Field-based PMC project monitoring requirement across North Gujarat.",
  jobOverview:
    "We are looking for a dynamic and experienced Civil & PMC Engineer to oversee project execution, monitor progress, prepare technical reports, and ensure quality and timely completion of infrastructure/construction projects. The candidate should possess sound knowledge of project management, estimation, billing, and construction drawings.",
  location: "North Gujarat (Field-Based)",
  areasCovered: "Banaskantha\nVav\nTharad\nSabarkantha\nMehsana",
  responsibilities:
    "Monitor the progress of ongoing projects and ensure timely execution.\nConduct regular site visits and prepare detailed project monitoring reports.\nCoordinate with contractors, consultants, and stakeholders for smooth project implementation.\nPrepare and verify Bills of Quantities (BOQ) and project estimates.\nReview and interpret Good for Construction (GFC) drawings and technical designs.\nVerify and certify Running Account (RA) Bills submitted by contractors.\nEnsure work execution is as per approved drawings, specifications, and quality standards.\nTrack project timelines, identify delays, and recommend corrective actions.\nMaintain project documentation and submit periodic progress reports.\nSupport Project Management Consultancy (PMC) activities and ensure compliance with contractual requirements.\nEnsure adherence to safety, quality, and statutory guidelines at project sites.",
  qualifications:
    "Bachelor's Degree/Diploma in Civil Engineering or an equivalent discipline.\nMinimum 3 years of relevant experience in Project Monitoring, PMC, or Infrastructure/Construction Projects.",
  skills:
    "Experience in Project Monitoring and Progress Reporting.\nStrong knowledge of BOQ preparation and quantity estimation.\nExperience in preparing and verifying RA Bills.\nAbility to read and understand GFC drawings and engineering designs.\nKnowledge of construction methodologies and quality standards.\nGood communication and coordination skills.\nProficiency in MS Excel, MS Office, and AutoCAD (preferred).\nStrong analytical, reporting, and documentation skills.\nWillingness to travel extensively across North Gujarat project locations.",
  preferredExperience:
    "Candidates having experience in Government, Semi-Government, Infrastructure, Rural Development, Water Supply, Irrigation, or Project Management Consultancy (PMC) projects will be preferred.",
};

function buildSmartJdTemplate(current) {
  const title = clean(current.title) || "Executive";
  const department = clean(current.department) || "Corporate Services";
  const key = `${title} ${department}`.toLowerCase();
  const shared = {
    ...current,
    title,
    department,
    vacancies: number(current.vacancies) || 1,
    location: current.location || "As per organizational requirement",
    experience: current.experience || "Minimum 2–3 Years",
    jobOverview: `We are looking for a dynamic, responsible and result-oriented ${title} for the ${department} department. The selected candidate will be responsible for efficient execution of assigned functions, coordination with internal and external stakeholders, timely reporting, documentation, compliance and achievement of organizational objectives.`,
    responsibilities:
      "Execute assigned departmental responsibilities accurately and within approved timelines.\nCoordinate with internal teams, management, vendors and relevant stakeholders.\nPrepare, maintain and submit periodic MIS, progress reports and supporting documentation.\nMonitor pending activities, identify operational issues and recommend corrective actions.\nEnsure compliance with organizational policies, approved procedures and applicable regulations.\nMaintain complete digital and physical records for audit and management review.\nSupport department leadership in planning, implementation and performance monitoring.\nProtect confidential organizational information and maintain professional conduct.",
    qualifications:
      `Bachelor's Degree/Diploma or equivalent qualification relevant to the ${title} position.\nMinimum relevant experience as specified for the role.\nProfessional certification, where applicable, will be preferred.`,
    skills:
      "Strong communication, coordination and documentation skills.\nProficiency in MS Office, email, spreadsheets and digital reporting systems.\nGood analytical, problem-solving and time-management ability.\nAbility to work independently and as part of a multidisciplinary team.\nAttention to detail, accountability and willingness to learn.",
    preferredExperience:
      "Candidates with relevant sector experience, strong reporting exposure and experience of working with Government, Semi-Government, Cooperative or professionally managed organizations will be preferred.",
  };

  if (/(civil|pmc|project engineer|site engineer)/.test(key)) {
    return {
      ...shared,
      ...civilPmcTemplate,
      title,
      department,
      vacancies: number(current.vacancies) || 1,
      location: current.location || civilPmcTemplate.location,
      experience: current.experience || civilPmcTemplate.experience,
    };
  }

  if (/(account|finance|audit|tax|gst|tds)/.test(key)) {
    return {
      ...shared,
      employmentType: current.employmentType || "FULL_TIME",
      jobOverview: `We are looking for a detail-oriented and experienced ${title} for the ${department} department. The candidate will manage day-to-day accounting, statutory records, reconciliations, taxation support, payment documentation and accurate financial reporting in accordance with organizational policies and applicable laws.`,
      responsibilities:
        "Maintain books of accounts, ledgers, vouchers and supporting financial records.\nProcess and verify purchase, expense, contractor and vendor bills.\nPrepare bank, cash, vendor and customer reconciliations.\nSupport GST, TDS, professional tax and other statutory compliance activities.\nPrepare monthly MIS, expense statements and management reports.\nCoordinate with auditors, banks, vendors and internal departments.\nVerify payment documentation and maintain approval trails.\nAssist in payroll accounting, salary reconciliation and statutory deductions.\nEnsure timely filing, accurate documentation and audit-ready records.\nMaintain confidentiality and internal financial controls.",
      qualifications:
        "B.Com, M.Com, CA Inter, CMA Inter or equivalent finance/accounting qualification.\nRelevant experience in accounting, taxation, reconciliation or audit support.\nKnowledge of Indian statutory and accounting requirements.",
      skills:
        "Strong knowledge of accounting principles, GST and TDS.\nExperience with Tally, accounting ERP or similar software.\nAdvanced working knowledge of MS Excel and financial MIS.\nBank reconciliation, ledger scrutiny and voucher verification.\nStrong numerical accuracy, documentation and analytical skills.\nGood communication and coordination ability.",
      preferredExperience:
        "Experience in Cooperative, Government, Semi-Government, Infrastructure, Agriculture, Construction, PMC or multi-location organizations will be preferred.",
    };
  }

  if (/(human resource|hr |recruit|payroll|admin)/.test(`${key} `)) {
    return {
      ...shared,
      jobOverview: `We are looking for a people-focused ${title} to support the complete employee lifecycle for the ${department} department, including recruitment, onboarding, attendance, leave, payroll coordination, employee documentation, policy implementation and management reporting.`,
      responsibilities:
        "Manage recruitment requisitions, job postings, screening and interview coordination.\nComplete employee onboarding, document verification and employee master creation.\nMonitor attendance, leave, late coming, LOP and employee records.\nCoordinate payroll inputs, statutory deductions and salary documentation.\nIssue HR letters, notices, warnings and employee communications.\nMaintain personnel files, document expiry alerts and HR MIS.\nSupport policy implementation, performance reviews and employee engagement.\nEnsure confidentiality and compliance with applicable labour requirements.",
      qualifications:
        "MBA/PGDM in HR, Human Resource Management or equivalent qualification.\nRelevant experience in HR operations, recruitment, payroll or administration.",
      skills:
        "Recruitment, onboarding and employee lifecycle management.\nAttendance, leave and payroll coordination.\nHR documentation, policy drafting and labour compliance awareness.\nStrong communication, confidentiality and conflict-management skills.\nProficiency in HRMS, MS Excel and management reporting.",
    };
  }

  if (/(procurement|purchase|vendor|tender|supply)/.test(key)) {
    return {
      ...shared,
      jobOverview: `We are looking for a commercially aware ${title} for the ${department} department to manage procurement planning, vendor coordination, quotations, tender documentation, purchase controls, delivery monitoring and cost-effective sourcing.`,
      responsibilities:
        "Prepare purchase requirements, enquiries, comparative statements and approvals.\nIdentify, evaluate and coordinate with qualified vendors.\nSupport tender, quotation, negotiation and purchase-order processes.\nTrack delivery schedules, material quality and pending supplies.\nMaintain vendor records, contracts and complete procurement documentation.\nCoordinate with projects, stores, accounts and management.\nMonitor budgets, commercial terms and cost-saving opportunities.\nEnsure transparent procurement controls and audit compliance.",
      qualifications:
        "Graduate/Postgraduate in Commerce, Management, Supply Chain, Engineering, Agriculture or equivalent discipline.\nRelevant experience in procurement, vendor management, tendering or operations.",
      skills:
        "Vendor development, quotation comparison and commercial negotiation.\nTender and purchase documentation.\nMS Excel, MIS and purchase tracking.\nUnderstanding of contracts, delivery terms and quality requirements.\nStrong coordination, integrity and analytical skills.",
    };
  }

  if (/(software|developer|technology| it |digital|system|data)/.test(` ${key} `)) {
    return {
      ...shared,
      jobOverview: `We are looking for a technically capable ${title} for the ${department} department to develop, operate and improve secure digital systems, support users, maintain technical documentation and deliver reliable technology solutions aligned with business requirements.`,
      responsibilities:
        "Develop, configure or maintain assigned applications and digital systems.\nAnalyse business requirements and translate them into technical solutions.\nResolve incidents, bugs and user-support requests within timelines.\nMaintain data security, access controls, backups and technical documentation.\nCoordinate testing, deployment, monitoring and system improvements.\nPrepare progress reports and maintain change/audit records.\nCoordinate with users, vendors and management for successful delivery.",
      qualifications:
        "Degree/Diploma in Computer Science, IT, Engineering or equivalent discipline.\nRelevant technical experience for the selected technology and role.",
      skills:
        "Application development, system support or relevant technical stack.\nDatabase, API, cloud and security fundamentals.\nProblem solving, testing and documentation.\nStrong communication and user-coordination skills.\nAbility to learn and implement new technologies.",
    };
  }

  return shared;
}
const emptyEmployee = {
  name: "",
  email: "",
  mobile: "",
  department: "",
  designation: "",
  managerName: "",
  joiningDate: "",
  basic: "",
  hra: "",
  da: "",
  allowances: "",
  pf: "",
  employerPf: "",
  esi: "",
  professionalTax: "",
  tdsMode: "SALARY_DEDUCTION",
  tds: "",
  otherDeduction: "",
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  bankBranch: "",
  leaveCL: "12",
  leaveEL: "18",
  leaveOL: "6",
  leaveCompOff: "0",
};
const emptyLeave = {
  employeeId: "",
  candidateUserId: "",
  employeeName: "",
  type: "CL",
  fromDate: "",
  toDate: "",
  days: 1,
  reason: "",
};
const emptyPayroll = {
  employeeId: "",
  candidateUserId: "",
  employeeName: "",
  employeeEmail: "",
  employeeCode: "",
  department: "",
  designation: "",
  month: "",
  joiningDate: "",
  exitDate: "",
  basic: "",
  hra: "",
  da: "",
  allowances: "",
  pf: "",
  employerPf: "",
  esi: "",
  professionalTax: "",
  tdsMode: "SALARY_DEDUCTION",
  tds: "",
  otherDeduction: "",
  accountHolderName: "",
  bankName: "",
  accountNumber: "",
  ifscCode: "",
  bankBranch: "",
};
const emptyHoliday = {
  date: "",
  name: "",
  type: "FESTIVAL",
  paid: true,
  notes: "",
};
const emptyReimbursement = {
  employeeId: "",
  candidateUserId: "",
  employeeName: "",
  employeeEmail: "",
  employeeCode: "",
  claimDate: "",
  category: "TRAVEL",
  amount: "",
  description: "",
  receiptUrl: "",
};
const emptyStatutoryEntry = {
  section: "194C",
  nature: "Contractor / work payment",
  payeeName: "",
  payeePan: "",
  invoiceReference: "",
  paymentDate: "",
  period: "",
  grossAmount: "",
  tdsRate: "",
  tdsAmount: "",
  remarks: "",
};
const emptyOfferPackage = {
  joiningDate: "",
  basic: "",
  hra: "",
  da: "",
  allowances: "",
  pfApplicable: "YES",
  pf: "",
  employerPf: "",
  esi: "",
  professionalTax: "",
  tdsMode: "SALARY_DEDUCTION",
  tds: "",
  otherDeduction: "",
};

const money = (value) =>
  `₹${Number(value || 0).toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  })}`;
const number = (value) => Number(value || 0);
const clean = (value) => String(value || "").trim();
const TDS_MODE = Object.freeze({
  SALARY_DEDUCTION: "SALARY_DEDUCTION",
  COMPANY_BORNE: "COMPANY_BORNE",
  NOT_APPLICABLE: "NOT_APPLICABLE",
});
const normalizeTdsMode = (value) => {
  const normalized = clean(value).toUpperCase().replace(/[\s-]+/g, "_");
  if (["COMPANY_BORNE", "COMPANY_PAID", "PAID_BY_COMPANY"].includes(normalized)) {
    return TDS_MODE.COMPANY_BORNE;
  }
  if (["NOT_APPLICABLE", "NA", "NONE", "NO_TDS"].includes(normalized)) {
    return TDS_MODE.NOT_APPLICABLE;
  }
  return TDS_MODE.SALARY_DEDUCTION;
};
const tdsModeLabel = (value) => {
  const mode = normalizeTdsMode(value);
  if (mode === TDS_MODE.COMPANY_BORNE) return "Company pays separately";
  if (mode === TDS_MODE.NOT_APPLICABLE) return "Not applicable";
  return "Deduct from salary";
};
const salaryDeductedTds = (record = {}) =>
  normalizeTdsMode(record.tdsMode) === TDS_MODE.SALARY_DEDUCTION
    ? number(record.tdsSalaryDeduction ?? record.tds)
    : 0;
const companyPaidTds = (record = {}) =>
  normalizeTdsMode(record.tdsMode) === TDS_MODE.COMPANY_BORNE
    ? number(record.companyBorneTds ?? record.tds)
    : 0;
const statutoryDueDate = (section, period) => {
  const match = /^(\d{4})-(0[1-9]|1[0-2])$/.exec(clean(period));
  if (!match) return "";
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (clean(section).toUpperCase() === "EPF") {
    const due = new Date(Date.UTC(year, month, 15));
    return due.toISOString().slice(0, 10);
  }
  if (month === 3) return `${year}-04-30`;
  const due = new Date(Date.UTC(year, month, 7));
  return due.toISOString().slice(0, 10);
};
const effectiveStatutoryStatus = (item = {}) => {
  const stored = clean(item.status || "PENDING_PAYMENT").toUpperCase();
  if (item.isClosed || ["PAID", "CANCELLED", "NOT_APPLICABLE"].includes(stored)) {
    return stored;
  }
  const dueDate = clean(item.dueDate);
  if (!dueDate) return stored;
  if (dueDate < today()) return "OVERDUE";
  const sevenDays = new Date(`${today()}T00:00:00.000Z`);
  sevenDays.setUTCDate(sevenDays.getUTCDate() + 7);
  return dueDate <= sevenDays.toISOString().slice(0, 10)
    ? "DUE_SOON"
    : stored;
};
const applicationCvRecord = (application = {}, candidate = {}) => {
  const resumeObjects = [
    application.resume,
    application.cv,
    application.resumeFile,
    application.cvFile,
    application.documents?.resume,
    application.documents?.cv,
    candidate.resume,
    candidate.cv,
    candidate.resumeFile,
    candidate.cvFile,
    candidate.documents?.resume,
    candidate.documents?.cv,
  ].filter((value) => value && typeof value === "object");
  const path = firstProfileValue(
    application.resumeStoragePath,
    application.cvStoragePath,
    application.resumePath,
    application.cvPath,
    application.resumeUrl,
    application.resumeURL,
    application.resumeFileUrl,
    application.resumeFileURL,
    application.cvUrl,
    application.cvURL,
    application.cvFileUrl,
    application.cvFileURL,
    application.resumeDownloadUrl,
    application.cvDownloadUrl,
    candidate.resumeStoragePath,
    candidate.cvStoragePath,
    candidate.resumePath,
    candidate.cvPath,
    candidate.resumeUrl,
    candidate.resumeURL,
    candidate.resumeFileUrl,
    candidate.resumeFileURL,
    candidate.cvUrl,
    candidate.cvURL,
    candidate.cvFileUrl,
    candidate.cvFileURL,
    ...resumeObjects.flatMap((value) => [
      value.storagePath,
      value.path,
      value.url,
      value.downloadUrl,
    ])
  );
  const fileName = firstProfileValue(
    application.resumeFileName,
    application.cvFileName,
    candidate.resumeFileName,
    candidate.cvFileName,
    ...resumeObjects.map((value) => value.fileName || value.name)
  );
  return { path, fileName: fileName || "Candidate-CV.pdf" };
};
const normalizeWorkflowToken = (value) =>
  clean(value).toUpperCase().replace(/[\s-]+/g, "_");
const APPROVAL_TYPE_ALIASES = Object.freeze({
  JOB_APPROVAL: "JOB_REQUISITION",
  REQUISITION_APPROVAL: "JOB_REQUISITION",
  FINAL_SELECTION: "FINAL_CANDIDATE_SELECTION",
  CANDIDATE_SELECTION: "FINAL_CANDIDATE_SELECTION",
  PAYROLL: "PAYROLL_APPROVAL",
  LEAVE_APPROVAL: "LEAVE_EXCEPTION",
  REIMBURSEMENT: "REIMBURSEMENT_APPROVAL",
});
const normalizeApprovalType = (value) => {
  const normalized = normalizeWorkflowToken(value);
  return APPROVAL_TYPE_ALIASES[normalized] || normalized;
};
const PENDING_CEO_APPROVAL_STATUSES = new Set([
  "PENDING_CEO_APPROVAL",
  "PENDING_CEO",
  "PENDING_APPROVAL",
  "PENDING",
]);
const isPendingCEOApproval = (value) =>
  PENDING_CEO_APPROVAL_STATUSES.has(normalizeWorkflowToken(value));
const roundMoney = (value) => Math.round((number(value) + Number.EPSILON) * 100) / 100;
const applicationBlockReasons = new Set([
  "APPLICATION_REJECTED",
  "FINAL_SELECTION_REJECTED",
  "INTERVIEW_REJECTED",
]);
const isLegacyApplicationBlock = (candidate, applicationId = "") => {
  if (clean(candidate?.status).toUpperCase() !== "BLOCKED") return false;
  const reason = clean(candidate?.blockedReason).toUpperCase();
  const blockedApplicationId = clean(
    candidate?.blockedApplicationId || candidate?.applicationId
  );
  return (
    applicationBlockReasons.has(reason) &&
    (!blockedApplicationId || !applicationId || blockedApplicationId === applicationId)
  );
};
const getOfferLeavePolicy = (employee) => {
  const candidates = [
    employee?.offer?.leavePolicy,
    employee?.offerDetails?.leavePolicy,
    employee?.offerLeavePolicy,
    employee?.leavePolicy,
  ];
  return (
    candidates.find(
      (value) => value && typeof value === "object" && !Array.isArray(value)
    ) || {}
  );
};
const leaveBalancesFromEmployee = (employee) => {
  const defaults = { CL: 12, EL: 18, OL: 6, COMP_OFF: 0 };
  const policy = getOfferLeavePolicy(employee);
  const entitlements =
    policy.entitlements ||
    policy.balances ||
    policy.annualEntitlements ||
    policy;
  const policyBalances = Object.fromEntries(
    Object.keys(defaults).map((key) => {
      const raw =
        entitlements?.[key] ??
        entitlements?.[key.toLowerCase()] ??
        defaults[key];
      return [key, Math.max(0, number(raw))];
    })
  );
  return {
    ...policyBalances,
    ...(employee?.leaveBalances || {}),
  };
};
const firstProfileValue = (...values) => {
  for (const value of values) {
    if (clean(value)) return clean(value);
  }
  return "";
};
const employeeProfileFromApplication = (application = {}, candidate = {}) => ({
  name: firstProfileValue(
    application.candidateName,
    application.fullName,
    application.name,
    candidate.fullName,
    candidate.name
  ),
  mobile: firstProfileValue(
    application.mobile,
    application.mobileNumber,
    application.phone,
    candidate.mobile,
    candidate.mobileNumber,
    candidate.phone
  ),
  department: firstProfileValue(
    application.deptName,
    application.department,
    application.jobDepartment,
    candidate.department
  ),
  designation: firstProfileValue(
    application.jobTitle,
    application.position,
    application.designation,
    candidate.designation
  ),
  managerName: firstProfileValue(
    application.managerName,
    application.reportingManager,
    candidate.managerName,
    candidate.reportingManager
  ),
  joiningDate: firstProfileValue(
    application.joiningDate,
    application.expectedJoiningDate,
    application.offeredJoiningDate,
    candidate.joiningDate
  ),
  dateOfBirth: firstProfileValue(
    application.dateOfBirth,
    application.dob,
    candidate.dateOfBirth,
    candidate.dob
  ),
  gender: firstProfileValue(application.gender, candidate.gender),
  bloodGroup: firstProfileValue(application.bloodGroup, candidate.bloodGroup),
  currentAddress: firstProfileValue(
    application.currentAddress,
    application.address,
    candidate.currentAddress,
    candidate.address
  ),
  permanentAddress: firstProfileValue(
    application.permanentAddress,
    candidate.permanentAddress
  ),
  city: firstProfileValue(
    application.city,
    application.village,
    candidate.city,
    candidate.village
  ),
  taluka: firstProfileValue(application.taluka, candidate.taluka),
  district: firstProfileValue(application.district, candidate.district),
  state: firstProfileValue(application.state, candidate.state),
  pincode: firstProfileValue(
    application.pincode,
    application.pinCode,
    candidate.pincode,
    candidate.pinCode
  ),
  emergencyContactName: firstProfileValue(
    application.emergencyContactName,
    candidate.emergencyContactName
  ),
  emergencyContactMobile: firstProfileValue(
    application.emergencyContactMobile,
    application.emergencyPhone,
    candidate.emergencyContactMobile,
    candidate.emergencyPhone
  ),
  highestQualification: firstProfileValue(
    application.highestQualification,
    application.qualification,
    candidate.highestQualification,
    candidate.qualification
  ),
  totalExperience: firstProfileValue(
    application.totalExperience,
    application.experience,
    candidate.totalExperience,
    candidate.experience
  ),
});
const employeeEditFormFromRecord = (employee = {}) => {
  const source = employee || {};
  const balances = leaveBalancesFromEmployee(source);
  const policy = getOfferLeavePolicy(source);
  const entitlements =
    policy.entitlements || policy.annualEntitlements || policy.balances || policy;
  return {
    name: clean(source.name),
    email: clean(source.email),
    mobile: clean(source.mobile),
    department: clean(source.department),
    designation: clean(source.designation),
    managerName: clean(source.managerName),
    joiningDate: clean(source.joiningDate),
    dateOfBirth: clean(source.dateOfBirth || source.dob),
    gender: clean(source.gender),
    bloodGroup: clean(source.bloodGroup),
    currentAddress: clean(source.currentAddress || source.address),
    permanentAddress: clean(source.permanentAddress),
    city: clean(source.city || source.village),
    taluka: clean(source.taluka),
    district: clean(source.district),
    state: clean(source.state),
    pincode: clean(source.pincode || source.pinCode),
    emergencyContactName: clean(source.emergencyContactName),
    emergencyContactMobile: clean(source.emergencyContactMobile),
    highestQualification: clean(source.highestQualification),
    totalExperience: clean(source.totalExperience),
    status: clean(source.status || "ACTIVE").toUpperCase(),
    leaveCL: String(entitlements?.CL ?? entitlements?.cl ?? balances.CL ?? 12),
    leaveEL: String(entitlements?.EL ?? entitlements?.el ?? balances.EL ?? 18),
    leaveOL: String(entitlements?.OL ?? entitlements?.ol ?? balances.OL ?? 6),
    leaveCompOff: String(
      entitlements?.COMP_OFF ??
        entitlements?.compOff ??
        balances.COMP_OFF ??
        0
    ),
  };
};
const maskAccount = (value) => {
  const account = clean(value);
  if (!account) return "-";
  return `${"*".repeat(Math.max(0, account.length - 4))}${account.slice(-4)}`;
};
const stableHashToken = (input) => {
  const value = clean(input).toLowerCase();
  let hash = 2166136261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36).toUpperCase().padStart(7, "0").slice(0, 7);
};
const employeeDocumentId = (email) =>
  `emp_${encodeURIComponent(clean(email).toLowerCase())}`;
const employeeCodeFromEmail = (email) => {
  return `SAAM-${new Date().getFullYear()}-${stableHashToken(email)}`;
};
const enumerateDateRange = (fromDate, toDate, maxDays = 370) => {
  const toUtcDate = (value) => {
    const [year, month, day] = String(value || "")
      .split("-")
      .map(Number);
    return new Date(Date.UTC(year, month - 1, day));
  };
  const start = toUtcDate(fromDate);
  const end = toUtcDate(toDate || fromDate);
  if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    end < start
  ) {
    return [];
  }
  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end && dates.length < maxDays) {
    dates.push(cursor.toISOString().slice(0, 10));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return dates;
};
const today = () =>
  new Intl.DateTimeFormat("en-CA", {
    timeZone: "Asia/Kolkata",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(new Date());
const isMonthKey = (value) => /^\d{4}-(0[1-9]|1[0-2])$/.test(clean(value));
const previousClosedMonthKey = (dateKey = today()) => {
  const [year, month] = clean(dateKey).slice(0, 7).split("-").map(Number);
  return new Date(Date.UTC(year, month - 2, 1)).toISOString().slice(0, 7);
};
const monthDateKeys = (month) => {
  if (!isMonthKey(month)) return [];
  const [year, monthNumber] = month.split("-").map(Number);
  const calendarDays = new Date(
    Date.UTC(year, monthNumber, 0)
  ).getUTCDate();
  return Array.from({ length: calendarDays }, (_, index) =>
    month + "-" + String(index + 1).padStart(2, "0")
  );
};
const payrollCalendarSettings = (settingsRows = []) => {
  const row =
    settingsRows.find((item) => item.id === "payroll") ||
    settingsRows.find((item) => item.settingType === "PAYROLL") ||
    {};
  const source = row.calendar || row;
  const weeklyOffDays = [
    ...new Set(
      (Array.isArray(source.weeklyOffDays)
        ? source.weeklyOffDays
        : DEFAULT_PAYROLL_CALENDAR.weeklyOffDays
      )
        .map(Number)
        .filter((value) => Number.isInteger(value) && value >= 0 && value <= 6)
    ),
  ];
  const saturdayPolicy = ["NONE", "ALL", "SECOND_FOURTH"].includes(
    clean(source.saturdayPolicy).toUpperCase()
  )
    ? clean(source.saturdayPolicy).toUpperCase()
    : DEFAULT_PAYROLL_CALENDAR.saturdayPolicy;
  return {
    weeklyOffDays: weeklyOffDays.length ? weeklyOffDays : [0],
    saturdayPolicy,
    timeZone: "Asia/Kolkata",
  };
};
const isConfiguredWeeklyOff = (dateKey, settings) => {
  const date = new Date(dateKey + "T00:00:00.000Z");
  if (Number.isNaN(date.getTime())) return false;
  const weekday = date.getUTCDay();
  if (weekday === 6 && settings.saturdayPolicy === "ALL") return true;
  if (weekday === 6 && settings.saturdayPolicy === "SECOND_FOURTH") {
    const weekOfMonth = Math.ceil(date.getUTCDate() / 7);
    return weekOfMonth === 2 || weekOfMonth === 4;
  }
  return new Set(settings.weeklyOffDays).has(weekday);
};
const activeHolidayRows = (holidayRows = []) =>
  holidayRows.filter(
    (item) =>
      !item?.isDeleted &&
      clean(item?.status).toUpperCase() !== "CANCELLED" &&
      isValidIsoDate(item?.date)
  );
const buildMonthCalendar = (
  month,
  holidayRows = [],
  settingsRows = [],
  employment = {}
) => {
  const allDates = monthDateKeys(month);
  const settings = payrollCalendarSettings(settingsRows);
  const applicableHolidays = activeHolidayRows(holidayRows).filter((item) =>
    clean(item.date).startsWith(month)
  );
  const nonWorkingHolidayDates = new Set(
    applicableHolidays
      .filter((item) => item.isWorkingDay !== true)
      .map((item) => clean(item.date))
  );
  const workingDayOverrides = new Set(
    applicableHolidays
      .filter((item) => item.isWorkingDay === true)
      .map((item) => clean(item.date))
  );
  const joiningDate = clean(employment.joiningDate);
  const exitDate = clean(employment.exitDate || employment.lastWorkingDate);
  const weeklyOffDates = allDates.filter(
    (dateKey) =>
      isConfiguredWeeklyOff(dateKey, settings) &&
      !workingDayOverrides.has(dateKey)
  );
  const expectedDates = allDates.filter(
    (dateKey) =>
      (!isConfiguredWeeklyOff(dateKey, settings) ||
        workingDayOverrides.has(dateKey)) &&
      (!nonWorkingHolidayDates.has(dateKey) ||
        workingDayOverrides.has(dateKey)) &&
      (!joiningDate || dateKey >= joiningDate) &&
      (!exitDate || dateKey <= exitDate)
  );
  const holidayDates = [...nonWorkingHolidayDates].sort();
  return {
    month,
    calendarDays: allDates.length,
    workingDays: expectedDates.length,
    weeklyOffDays: weeklyOffDates.length,
    holidayDays: holidayDates.filter(
      (dateKey) => !weeklyOffDates.includes(dateKey)
    ).length,
    holidayCount: holidayDates.length,
    allDates,
    expectedDates,
    weeklyOffDates,
    holidayDates,
    holidays: applicableHolidays,
    settings,
  };
};
const chargeableLeaveDates = (
  fromDate,
  toDate,
  holidayRows = [],
  settingsRows = []
) => {
  const settings = payrollCalendarSettings(settingsRows);
  const activeHolidays = activeHolidayRows(holidayRows);
  const nonWorking = new Set(
    activeHolidays
      .filter((item) => item.isWorkingDay !== true)
      .map((item) => clean(item.date))
  );
  const workingOverrides = new Set(
    activeHolidays
      .filter((item) => item.isWorkingDay === true)
      .map((item) => clean(item.date))
  );
  return enumerateDateRange(fromDate, toDate).filter(
    (dateKey) =>
      workingOverrides.has(dateKey) ||
      (!isConfiguredWeeklyOff(dateKey, settings) && !nonWorking.has(dateKey))
  );
};
const isValidIsoDate = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(clean(value));
  if (!match) return false;
  const [, year, month, day] = match.map(Number);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.toISOString().slice(0, 10) === clean(value);
};
const parseIstDateTime = (value) => {
  const input = clean(value);
  if (!input) return new Date(Number.NaN);
  if (/Z$|[+-]\d{2}:?\d{2}$/.test(input)) return new Date(input);
  return new Date(`${input.length === 16 ? `${input}:00` : input}+05:30`);
};
const formatDate = (value) => {
  if (!value) return "-";
  if (value?.toDate) return value.toDate().toLocaleString("en-IN");
  return String(value);
};

const normalizedDashboardRole = (profile) => {
  const value = clean(profile?.dashboardRole || profile?.role).toUpperCase();
  if (["CEO_MASTER", "CEO", "SUPER_ADMIN"].includes(value)) {
    return ROLE.CEO_MASTER;
  }
  if (["HR_ADMIN", "HR"].includes(value)) {
    return ROLE.HR_ADMIN;
  }
  if (["ACCOUNTS_ADMIN", "ACCOUNT_ADMIN", "ACCOUNTS", "ACCOUNT"].includes(value)) {
    return ROLE.ACCOUNTS_ADMIN;
  }
  return "";
};

function calculatePayrollSnapshot(
  form,
  attendanceRows = [],
  leaveRows = [],
  holidayRows = [],
  settingsRows = []
) {
  const month = clean(form.month);
  const calendar = buildMonthCalendar(
    month,
    holidayRows,
    settingsRows,
    form
  );
  const expectedDateSet = new Set(calendar.expectedDates);
  const attendanceAccepted = (record) => {
    const statuses = [
      record.hrReviewStatus,
      record.status,
      record.attendanceStatus,
      record.attendanceFlag,
    ].map((value) => clean(value).toUpperCase());
    if (
      statuses.some((status) =>
        ["ABSENT", "INVALID", "REJECTED", "CANCELLED"].includes(status)
      )
    ) {
      return false;
    }
    if (
      statuses.some((status) =>
        ["PRESENT", "APPROVED", "AUTO_VALID", "VALID"].includes(status)
      )
    ) {
      return true;
    }
    return Boolean(record.checkInAt || record.checkInTime);
  };
  const presentDates = new Set(
    attendanceRows
      .filter(
        (record) =>
          record.employeeId === form.employeeId &&
          expectedDateSet.has(clean(record.date).slice(0, 10)) &&
          attendanceAccepted(record)
      )
      .map((record) => clean(record.date).slice(0, 10))
      .filter(Boolean)
  );
  const approvedPaidLeaveDates = new Set();
  const approvedLopDates = new Set();
  leaveRows
    .filter(
      (record) =>
        record.employeeId === form.employeeId &&
        ["APPROVED", "APPROVED_BY_CEO"].includes(
          clean(record.status).toUpperCase()
        )
    )
    .forEach((record) => {
      const leaveDates = chargeableLeaveDates(
        record.fromDate,
        record.toDate,
        holidayRows,
        settingsRows
      ).filter((dateKey) => expectedDateSet.has(dateKey));
      const explicitLopDates = new Set(
        (Array.isArray(record.lopDates) ? record.lopDates : [])
          .map((value) => clean(value).slice(0, 10))
          .filter(Boolean)
      );
      const fallbackLopCount = Math.max(
        0,
        Math.ceil(number(record.lopDays))
      );
      leaveDates.forEach((dateKey, index) => {
        if (
          clean(record.type).toUpperCase() === "LOP" ||
          explicitLopDates.has(dateKey) ||
          (!explicitLopDates.size && index < fallbackLopCount)
        ) {
          approvedLopDates.add(dateKey);
        } else {
          approvedPaidLeaveDates.add(dateKey);
        }
      });
    });
  const attendanceCutoff =
    month === today().slice(0, 7)
      ? today()
      : calendar.expectedDates.at(-1) || month + "-31";
  const unpaidDates = calendar.expectedDates.filter(
    (dateKey) =>
      dateKey <= attendanceCutoff &&
      (approvedLopDates.has(dateKey) ||
        (!presentDates.has(dateKey) && !approvedPaidLeaveDates.has(dateKey)))
  );
  const unpaidDays = Math.min(calendar.workingDays, unpaidDates.length);
  const paidDays = Math.max(0, calendar.workingDays - unpaidDays);
  const gross = roundMoney(
    number(form.basic) +
      number(form.hra) +
      number(form.da) +
      number(form.allowances)
  );
  const attendanceDeduction =
    calendar.workingDays > 0
      ? roundMoney((unpaidDays * gross) / calendar.workingDays)
      : 0;
  const tdsMode = normalizeTdsMode(form.tdsMode);
  const tds =
    tdsMode === TDS_MODE.NOT_APPLICABLE ? 0 : roundMoney(number(form.tds));
  const tdsSalaryDeduction =
    tdsMode === TDS_MODE.SALARY_DEDUCTION ? tds : 0;
  const companyBorneTds = tdsMode === TDS_MODE.COMPANY_BORNE ? tds : 0;
  const employerPf = roundMoney(number(form.employerPf));
  const statutoryDeduction = roundMoney(
    number(form.pf) +
      number(form.esi) +
      number(form.professionalTax) +
      tdsSalaryDeduction +
      number(form.otherDeduction)
  );
  const totalDeduction = roundMoney(
    attendanceDeduction + statutoryDeduction
  );
  return {
    gross,
    calendarDays: calendar.calendarDays,
    workingDays: calendar.workingDays,
    weeklyOffDays: calendar.weeklyOffDays,
    holidayDays: calendar.holidayDays,
    holidayCount: calendar.holidayCount,
    holidayDates: calendar.holidayDates,
    paidDays,
    attendancePresentDays: presentDates.size,
    approvedPaidLeaveDays: approvedPaidLeaveDates.size,
    attendanceAbsentDays: unpaidDates.filter(
      (dateKey) => !approvedLopDates.has(dateKey)
    ).length,
    approvedLopDays: approvedLopDates.size,
    unpaidDays,
    unpaidDates,
    attendanceDeduction,
    tdsMode,
    tds,
    tdsSalaryDeduction,
    companyBorneTds,
    employerPf,
    companyTotalOutflow: roundMoney(gross + companyBorneTds + employerPf),
    statutoryDeduction,
    totalDeduction,
    net: roundMoney(Math.max(0, gross - totalDeduction)),
  };
}

async function fetchCollection(name, constraints = []) {
  const source = constraints.length
    ? query(collection(db, name), ...constraints)
    : collection(db, name);
  const snapshot = await getDocs(source);
  return snapshot.docs.map((item) => ({ id: item.id, ...item.data() }));
}

function Badge({ value }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border border-current/10 px-2.5 py-1 text-[10px] font-black uppercase tracking-[.08em] shadow-sm ${
        badgeMap[value] || "bg-slate-100 text-slate-700"
      }`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current opacity-70" />
      {String(value || "PENDING").replaceAll("_", " ")}
    </span>
  );
}

function Field({ label, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[10px] font-black uppercase tracking-[.1em] text-slate-500">
        {label}
      </span>
      <input
        {...props}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-800 shadow-sm outline-none transition placeholder:font-medium placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50 disabled:text-slate-500"
      />
    </label>
  );
}

function Select({ label, children, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[10px] font-black uppercase tracking-[.1em] text-slate-500">
        {label}
      </span>
      <select
        {...props}
        className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-800 shadow-sm outline-none transition hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50"
      >
        {children}
      </select>
    </label>
  );
}

function TextArea({ label, className = "", ...props }) {
  return (
    <label className={`block ${className}`}>
      <span className="mb-2 block text-[10px] font-black uppercase tracking-[.1em] text-slate-500">
        {label}
      </span>
      <textarea
        {...props}
        className="w-full rounded-xl border border-slate-200 bg-white px-3.5 py-3 text-sm font-semibold leading-6 text-slate-800 shadow-sm outline-none transition placeholder:font-medium placeholder:text-slate-400 hover:border-slate-300 focus:border-blue-500 focus:ring-4 focus:ring-blue-100 disabled:bg-slate-50"
      />
    </label>
  );
}

function Button({ children, tone = "blue", className = "", ...props }) {
  const tones = {
    blue: "border border-blue-600 bg-gradient-to-r from-blue-700 to-blue-600 text-white shadow-blue-900/15 hover:from-blue-800 hover:to-blue-700",
    green: "border border-emerald-600 bg-gradient-to-r from-emerald-700 to-emerald-600 text-white shadow-emerald-900/15 hover:from-emerald-800 hover:to-emerald-700",
    red: "border border-red-200 bg-red-50 text-red-700 hover:border-red-300 hover:bg-red-100",
    dark: "border border-slate-800 bg-gradient-to-r from-slate-950 to-slate-800 text-white shadow-slate-950/20 hover:from-slate-900 hover:to-slate-700",
    light: "border border-slate-200 bg-white text-slate-700 hover:border-blue-200 hover:bg-blue-50 hover:text-blue-700",
    amber: "border border-amber-500 bg-gradient-to-r from-amber-600 to-orange-500 text-white shadow-amber-900/15 hover:from-amber-700 hover:to-orange-600",
  };
  return (
    <button
      type="button"
      {...props}
      className={`inline-flex min-h-10 items-center justify-center gap-2 rounded-xl px-4 py-2 text-sm font-black shadow-lg transition duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-blue-100 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 ${tones[tone] || tones.blue} ${className}`}
    >
      {children}
    </button>
  );
}

function SecureStorageLink({ path, label = "Open File", className = "" }) {
  const [url, setUrl] = useState("");
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let active = true;
    setUrl("");
    setFailed(false);
    if (!path) return () => {};
    if (/^https?:\/\//i.test(path)) {
      setUrl(path);
      return () => {};
    }
    getDownloadURL(storageRef(storage, path))
      .then((downloadUrl) => {
        if (active) setUrl(downloadUrl);
      })
      .catch(() => {
        if (active) setFailed(true);
      });
    return () => {
      active = false;
    };
  }, [path]);

  if (!path) return <span className="text-slate-400">-</span>;
  if (failed) {
    return (
      <span className="text-xs font-semibold text-red-600">
        File access unavailable
      </span>
    );
  }
  if (!url) {
    return (
      <span className="text-xs font-semibold text-slate-400">Loading…</span>
    );
  }
  return (
    <a
      className={`font-semibold text-blue-600 hover:text-blue-800 ${className}`}
      href={url}
      target="_blank"
      rel="noreferrer"
    >
      {label}
    </a>
  );
}

function Card({ title, icon: Icon, action, children, className = "" }) {
  const flushContent = className.includes("!p-0");
  return (
    <section
      className={`overflow-hidden rounded-2xl border border-slate-200/90 bg-white shadow-[0_12px_40px_rgba(15,23,42,.06)] ${className}`}
    >
      {(title || action) && (
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 bg-gradient-to-r from-white via-slate-50/80 to-blue-50/40 px-4 py-4 sm:px-5">
          <h2 className="flex items-center gap-3 text-[15px] font-black text-slate-900">
            {Icon && (
              <span className="flex h-9 w-9 items-center justify-center rounded-xl border border-blue-100 bg-blue-50 text-blue-700 shadow-sm">
                <Icon size={17} />
              </span>
            )}
            {title}
          </h2>
          {action}
        </div>
      )}
      <div className={flushContent ? "" : "p-4 sm:p-5"}>{children}</div>
    </section>
  );
}

const CEODeleteContext = React.createContext({
  canDelete: false,
  requestDelete: () => {},
});

function Table({
  columns,
  rows,
  renderRow,
  empty = "No record found",
  collectionName = "",
  recordLabel = "record",
}) {
  const deleteControl = React.useContext(CEODeleteContext);
  const visibleRows = rows.filter((item) => !item?.isDeleted);
  const showDelete =
    deleteControl.canDelete &&
    CEO_DELETABLE_COLLECTIONS.has(collectionName);

  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-inner shadow-slate-100/70">
      <table className="w-full min-w-[850px] border-collapse text-sm">
        <thead className="sticky top-0 z-10 border-b border-slate-200 bg-slate-50/95 text-slate-600 backdrop-blur">
          <tr>
            {columns.map((column) => (
              <th
                key={column}
                className="border-r border-slate-200 px-4 py-3 text-left text-[10px] font-black uppercase tracking-[.12em] last:border-r-0"
              >
                {column}
              </th>
            ))}
            {showDelete && (
              <th className="w-[132px] px-3 py-2.5 text-left text-[10px] font-bold uppercase tracking-[.08em]">
                CEO Control
              </th>
            )}
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {visibleRows.map((item) => {
            const rendered = renderRow(item);
            if (!showDelete || !React.isValidElement(rendered)) {
              return rendered;
            }
            return React.cloneElement(
              rendered,
              {
                className: `${rendered.props.className || ""} transition hover:bg-[#f7fafc]`,
              },
              ...React.Children.toArray(rendered.props.children),
              <TD key={`ceo-delete-${item.id}`} className="whitespace-nowrap">
                <Button
                  tone="red"
                  className="min-h-8 px-2.5 py-1 text-xs"
                  title={`Delete ${recordLabel}`}
                  aria-label={`Delete ${recordLabel} ${item.id}`}
                  onClick={() =>
                    deleteControl.requestDelete(
                      collectionName,
                      item,
                      recordLabel
                    )
                  }
                >
                  <Trash2 size={14} /> Delete
                </Button>
              </TD>
            );
          })}
          {!visibleRows.length && (
            <tr>
              <td
                colSpan={columns.length + (showDelete ? 1 : 0)}
                className="px-4 py-12 text-center text-sm font-semibold text-slate-400"
              >
                {empty}
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

const TD = ({ children, className = "" }) => (
  <td
    className={`border-r border-slate-100 px-4 py-3.5 align-top text-[13px] font-medium leading-5 text-slate-700 last:border-r-0 ${className}`}
  >
    {children}
  </td>
);

function CEODeleteDialog({
  request,
  confirmation,
  setConfirmation,
  reason,
  setReason,
  busy,
  onConfirm,
  onClose,
}) {
  if (!request) return null;
  const item = request.item || {};
  const displayName =
    item.title ||
    item.candidateName ||
    item.employeeName ||
    item.name ||
    item.subject ||
    item.id;

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      aria-labelledby="ceo-delete-title"
    >
      <div className="w-full max-w-lg overflow-hidden rounded-lg border border-[#aebdcb] bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 border-b border-red-200 bg-red-50 px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-red-200 bg-white text-red-700">
              <Trash2 size={19} />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[.12em] text-red-700">
                CEO Restricted Control
              </p>
              <h2
                id="ceo-delete-title"
                className="mt-1 text-lg font-semibold text-slate-950"
              >
                Delete {request.recordLabel}
              </h2>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-2 text-slate-500 hover:bg-white hover:text-slate-900"
            aria-label="Close delete dialog"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-4 p-5">
          <div className="rounded-md border border-[#d2dce5] bg-[#f7f9fb] p-4">
            <p className="text-xs font-semibold text-slate-500">Selected record</p>
            <p className="mt-1 font-semibold text-[#17365d]">{displayName}</p>
            <p className="mt-1 break-all font-mono text-[11px] text-slate-500">
              {item.id}
            </p>
          </div>
          <div className="flex gap-3 rounded-md border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle
              size={19}
              className="mt-0.5 shrink-0 text-amber-700"
            />
            <p className="text-xs font-medium leading-5 text-amber-900">
              Record operational tables se remove ho jayega. Compliance ke
              liye original record aur CEO audit evidence securely retained
              rahenge.
            </p>
          </div>
          <TextArea
            label="Deletion reason (mandatory)"
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            placeholder="Official reason for removing this record"
          />
          <Field
            label='Confirmation — type "DELETE"'
            value={confirmation}
            onChange={(event) => setConfirmation(event.target.value)}
            autoComplete="off"
          />
        </div>
        <div className="flex justify-end gap-2 border-t border-[#d7e0e8] bg-[#fbfcfd] px-5 py-4">
          <Button tone="light" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button
            tone="red"
            onClick={onConfirm}
            disabled={
              busy ||
              confirmation.trim().toUpperCase() !== "DELETE" ||
              !reason.trim()
            }
          >
            <Trash2 size={15} />
            {busy ? "Deleting..." : "Confirm CEO Delete"}
          </Button>
        </div>
      </div>
    </div>
  );
}

export default function HrDashboard() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [login, setLogin] = useState({ email: "", password: "" });
  const [active, setActive] = useState("dashboard");
  const [dashboardView, setDashboardView] = useState("portal");
  const [loading, setLoading] = useState(false);
  const [authChecking, setAuthChecking] = useState(!HRMS_TEST_MODE);
  const [message, setMessage] = useState("");
  const [loadWarnings, setLoadWarnings] = useState([]);
  const [search, setSearch] = useState("");
  const [data, setData] = useState({
    requisitions: [],
    jobs: [],
    applications: [],
    interviews: [],
    employees: [],
    onboarding: [],
    attendance: [],
    employeeRequests: [],
    leaves: [],
    holidays: [],
    settings: [],
    payroll: [],
    statutory: [],
    payrollBatches: [],
    automationRuns: [],
    reimbursements: [],
    documents: [],
    approvals: [],
    mail: [],
    audit: [],
    candidateUsers: [],
  });
  const [reqForm, setReqForm] = useState(emptyReq);
  const [employeeForm, setEmployeeForm] = useState(emptyEmployee);
  const [leaveForm, setLeaveForm] = useState(emptyLeave);
  const [holidayForm, setHolidayForm] = useState(emptyHoliday);
  const [payForm, setPayForm] = useState({
    ...emptyPayroll,
    month: previousClosedMonthKey(),
  });
  const [reimbursementForm, setReimbursementForm] = useState(
    emptyReimbursement
  );
  const [statutoryForm, setStatutoryForm] = useState(emptyStatutoryEntry);
  const [testBooting, setTestBooting] = useState(HRMS_TEST_MODE);
  const [scheduleCandidate, setScheduleCandidate] = useState(null);
  const [liveInterview, setLiveInterview] = useState(null);
  const [offerApproval, setOfferApproval] = useState(null);
  const [offerLeaveForm, setOfferLeaveForm] = useState({
    ...DEFAULT_OFFER_LEAVE_POLICY,
  });
  const [offerPreparation, setOfferPreparation] = useState(null);
  const [offerPackageForm, setOfferPackageForm] = useState({
    ...emptyOfferPackage,
  });
  const [offerLetterFile, setOfferLetterFile] = useState(null);
  const [offerUploading, setOfferUploading] = useState(false);
  const [deleteRequest, setDeleteRequest] = useState(null);
  const [deleteConfirmation, setDeleteConfirmation] = useState("");
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteBusy, setDeleteBusy] = useState(false);
  const waitingSeenRef = useRef(new Set());
  const manualLoginRef = useRef(false);

  const role = normalizedDashboardRole(profile);
  const canCEO = role === ROLE.CEO_MASTER;
  const isHRAdmin = role === ROLE.HR_ADMIN;
  const canAccounts = role === ROLE.ACCOUNTS_ADMIN;
  const canHR = [ROLE.HR_ADMIN, ROLE.CEO_MASTER].includes(role);
  const canManageMail = canHR;
  const canSendToAccounts = isHRAdmin || canCEO;
  const canRecordAccountsPayment = canAccounts || isHRAdmin || canCEO;
  const canCEODelete =
    canCEO &&
    Boolean(user) &&
    !user?.isAnonymous &&
    CEO_LOGIN_EMAILS.has(clean(profile?.email).toLowerCase());
  const visibleData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(data).map(([key, rows]) => [
          key,
          Array.isArray(rows)
            ? rows.filter((item) => !item?.isDeleted)
            : rows,
        ])
      ),
    [data]
  );

  useEffect(() => {
    if (canAccounts && !ACCOUNTS_MODULES.has(active)) {
      setActive("dashboard");
      setDashboardView("portal");
    }
  }, [active, canAccounts]);

  const notify = (text) => {
    setMessage(text);
    window.setTimeout(() => setMessage(""), 3500);
  };

  // Restore an already authenticated CEO/HR session after a browser refresh.
  // Anonymous users created by the public website are intentionally ignored.
  useEffect(() => {
    if (HRMS_TEST_MODE) {
      setAuthChecking(false);
      return undefined;
    }
    let activeListener = true;
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      try {
        if (manualLoginRef.current) return;
        if (!currentUser || currentUser.isAnonymous) {
          if (activeListener) {
            setUser(null);
            setProfile(null);
          }
          return;
        }

        const authenticatedEmail = clean(currentUser.email).toLowerCase();
        const expectedLogin = LOGIN_DIRECTORY[authenticatedEmail];
        if (!expectedLogin) {
          await signOut(auth);
          return;
        }

        const profileSnapshot = await getDoc(
          doc(db, COLLECTIONS.users, currentUser.uid)
        );
        let storedProfile = profileSnapshot.exists()
          ? {
              id: profileSnapshot.id,
              ...profileSnapshot.data(),
            }
          : null;
        if (!storedProfile) {
          const legacySnapshot = await getDocs(
            query(
              collection(db, COLLECTIONS.users),
              where("email", "==", authenticatedEmail),
              limit(1)
            )
          );
          if (!legacySnapshot.empty) {
            const legacyProfile = legacySnapshot.docs[0];
            storedProfile = {
              id: legacyProfile.id,
              ...legacyProfile.data(),
            };
          }
        }
        if (!storedProfile) {
          await signOut(auth);
          return;
        }
        const allowed =
          clean(storedProfile.email).toLowerCase() === authenticatedEmail &&
          String(storedProfile.status || "").toUpperCase() === "ACTIVE" &&
          storedProfile.isActive !== false &&
          normalizedDashboardRole(storedProfile) ===
            expectedLogin.dashboardRole;

        if (!allowed) {
          await signOut(auth);
          return;
        }

        if (activeListener) {
          setUser(currentUser);
          setProfile({
            ...storedProfile,
            uid: currentUser.uid,
            email: authenticatedEmail,
            role: expectedLogin.legacyRole,
            dashboardRole: expectedLogin.dashboardRole,
          });
        }
      } catch (error) {
        console.warn("HRMS session restore failed", error);
        if (activeListener) {
          setUser(null);
          setProfile(null);
        }
      } finally {
        if (activeListener) setAuthChecking(false);
      }
    });
    return () => {
      activeListener = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!HRMS_TEST_MODE || user) return;
    let active = true;
    const openTestDashboard = async () => {
      setTestBooting(true);
      try {
        // Candidate/public portal login must never be reused as an HRMS test
        // identity. Cloud Functions authorise the real Firebase UID, not the
        // role label shown on screen.
        if (auth.currentUser && !auth.currentUser.isAnonymous) {
          await signOut(auth);
        }
        const credential = auth.currentUser?.isAnonymous
          ? { user: auth.currentUser }
          : await signInAnonymously(auth);
        const testProfile = {
          uid: credential.user.uid,
          name: LOGIN_DIRECTORY[OFFICIAL_EMAILS.HR].name,
          email: OFFICIAL_EMAILS.HR,
          mobile: "",
          role: LOGIN_DIRECTORY[OFFICIAL_EMAILS.HR].legacyRole,
          dashboardRole: ROLE.HR_ADMIN,
          department: "Administration & Human Resources",
          status: "ACTIVE",
          isTestUser: true,
          updatedAt: serverTimestamp(),
        };
        await setDoc(
          doc(db, COLLECTIONS.users, credential.user.uid),
          {
            ...testProfile,
            createdAt: serverTimestamp(),
          },
          { merge: true }
        );
        if (!active) return;
        setUser(credential.user);
        setProfile(testProfile);
        setActive("dashboard");
      } catch (error) {
        console.error("HRMS test login failed", error);
        if (active) {
          notify(
            error?.code === "auth/operation-not-allowed"
              ? "Firebase Authentication me Anonymous provider Enable karein."
              : `Test Mode Error: ${error.message}`
          );
        }
      } finally {
        if (active) setTestBooting(false);
      }
    };
    openTestDashboard();
    return () => {
      active = false;
    };
  }, [user]);

  const switchTestRole = async (nextRole) => {
    if (!HRMS_TEST_MODE || !user) return;
    const isCEO = nextRole === ROLE.CEO_MASTER;
    const email = isCEO ? OFFICIAL_EMAILS.CEO : OFFICIAL_EMAILS.HR;
    const directory = LOGIN_DIRECTORY[email];
    const nextProfile = {
      ...profile,
      uid: user.uid,
      name: directory.name,
      email,
      role: directory.legacyRole,
      dashboardRole: directory.dashboardRole,
      department: isCEO
        ? "Executive Management"
        : "Administration & Human Resources",
      status: "ACTIVE",
      isTestUser: true,
      updatedAt: serverTimestamp(),
    };
    await setDoc(doc(db, COLLECTIONS.users, user.uid), nextProfile, {
      merge: true,
    });
    setProfile(nextProfile);
    setActive("dashboard");
    setDashboardView("portal");
    notify(`${isCEO ? "CEO Master" : "HR Admin"} test view active`);
  };

  const audit = async (action, targetType, targetId = "", metadata = {}) => {
    await addDoc(collection(db, COLLECTIONS.audit), {
      action,
      targetType,
      targetId,
      byUid: user?.uid || user?.id || "",
      byName: profile?.name || user?.email || "System",
      byRole: role,
      byEmail: profile?.email || user?.email || "",
      metadata,
      createdAt: serverTimestamp(),
    });
  };

  const queueMail = async (
    type,
    to,
    subject,
    body,
    relatedId = "",
    options = {}
  ) => {
    const recipient = clean(to).toLowerCase();
    const requestedBcc = Array.isArray(options.bcc) ? options.bcc : [];
    const bcc = [...new Set([...requestedBcc, OFFICIAL_EMAILS.CEO])].filter(
      (email) => clean(email).toLowerCase() !== recipient
    );
    const mailRef = await addDoc(collection(db, COLLECTIONS.mail), {
      type,
      from: options.from || OFFICIAL_EMAILS.HR,
      to,
      cc: options.cc || [],
      bcc,
      subject,
      body,
      relatedId,
      correlationId: options.correlationId || relatedId,
      payload: options.payload || {},
      status: "PENDING_SEND",
      createdByUid: user?.uid || "",
      createdByName: profile?.name || "",
      createdByEmail: profile?.email || "",
      createdAt: serverTimestamp(),
    });
    return mailRef;
  };

  const downloadApplicantCv = async (application, disposition = "attachment") => {
    if (!canHR) return notify("HR/CEO access required");
    const previewTab = window.open("", "_blank", "noopener,noreferrer");
    try {
      const getCv = httpsCallable(
        interviewFunctions,
        "getApplicantCvDownloadUrl"
      );
      const response = await getCv({ applicationId: application.id, disposition });
      const url = response.data?.url;
      if (!url) throw new Error("Candidate CV file nahi mila");
      if (previewTab) previewTab.location.href = url;
      else window.location.assign(url);
      await audit(
        disposition === "inline"
          ? "Candidate CV previewed by HR"
          : "Candidate CV downloaded by HR",
        "APPLICATION",
        application.id,
        {
        candidateName: application.candidateName || application.name || "",
        fileName: response.data?.fileName || "Candidate-CV.pdf",
        }
      );
    } catch (error) {
      previewTab?.close();
      notify(error.message || "Candidate CV download nahi hua");
    }
  };

  const sendMailSystemTest = async () => {
    if (!canManageMail) return notify("HR/CEO access required");
    setLoading(true);
    try {
      const testMail = httpsCallable(interviewFunctions, "testHrmsMail");
      const response = await testMail();
      notify(
        `Test email ${response.data?.recipient || profile?.email || "official inbox"} par send ho gaya`
      );
      await loadAll();
    } catch (error) {
      notify(error.message || "SMTP test fail hua; Communication Queue error dekhein");
    } finally {
      setLoading(false);
    }
  };

  const createStatutoryEntry = async () => {
    if (!canRecordAccountsPayment) {
      return notify("HR/CEO/Accounts access required");
    }
    const section = clean(statutoryForm.section).toUpperCase();
    const payeeName = clean(statutoryForm.payeeName);
    const payeePan = clean(statutoryForm.payeePan).toUpperCase();
    const paymentDate = clean(statutoryForm.paymentDate);
    const period = clean(
      statutoryForm.period || paymentDate.slice(0, 7)
    );
    const grossAmount = roundMoney(statutoryForm.grossAmount);
    const tdsRate = roundMoney(statutoryForm.tdsRate);
    const tdsAmount = roundMoney(
      statutoryForm.tdsAmount || (grossAmount * tdsRate) / 100
    );
    if (!["194C", "194H", "194I", "194J", "194Q", "194A", "194OTHER"].includes(section)) {
      return notify("Valid Section 194 category select karein");
    }
    if (!payeeName || !paymentDate || !isValidIsoDate(paymentDate)) {
      return notify("Payee name aur valid payment/deduction date required hai");
    }
    if (!/^[A-Z]{5}[0-9]{4}[A-Z]$/.test(payeePan)) {
      return notify("Valid 10-character PAN required hai");
    }
    if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) {
      return notify("Valid deduction month required hai");
    }
    if (grossAmount <= 0 || tdsRate < 0 || tdsRate > 100 || tdsAmount <= 0) {
      return notify("Gross amount, rate aur TDS amount valid hona chahiye");
    }
    const dueDate = statutoryDueDate(section, period);
    const statutoryRef = doc(collection(db, COLLECTIONS.statutory));
    const mailRef = doc(
      db,
      COLLECTIONS.mail,
      `statutory_194_${statutoryRef.id}`
    );
    const batch = writeBatch(db);
    batch.set(statutoryRef, {
      complianceType: "TDS",
      section,
      sectionLabel: `Section ${section.replace("OTHER", " (Other)")}`,
      nature: clean(statutoryForm.nature),
      payeeName,
      payeePan,
      invoiceReference: clean(statutoryForm.invoiceReference),
      paymentDate,
      deductionDate: paymentDate,
      period,
      grossAmount,
      tdsRate,
      amount: tdsAmount,
      tdsAmount,
      dueDate,
      remarks: clean(statutoryForm.remarks),
      sourceType: "MANUAL_194_PAYMENT",
      status: "PENDING_PAYMENT",
      isClosed: false,
      createdByUid: user.uid,
      createdByName: profile.name,
      createdByEmail: profile.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.set(mailRef, {
      type: "STATUTORY_TDS_194_CREATED",
      from: OFFICIAL_EMAILS.HR,
      to: OFFICIAL_EMAILS.ACCOUNT,
      cc: [OFFICIAL_EMAILS.HR, OFFICIAL_EMAILS.CEO],
      subject: `TDS ${section} payable | ${payeeName} | Due ${dueDate}`,
      body: [
        "Dear Accounts Team,",
        "",
        `A ${section} TDS liability has been recorded in HRMS.`,
        `Payee: ${payeeName}`,
        `PAN: ${payeePan}`,
        `Payment / deduction date: ${paymentDate}`,
        `Gross amount: ${money(grossAmount)}`,
        `TDS rate: ${tdsRate}%`,
        `TDS payable: ${money(tdsAmount)}`,
        `Statutory due date: ${dueDate}`,
        `Invoice / reference: ${clean(statutoryForm.invoiceReference) || "-"}`,
        "",
        "Please deposit the liability and record challan/CIN in Statutory Compliance.",
      ].join("\n"),
      relatedId: statutoryRef.id,
      correlationId: statutoryRef.id,
      status: "PENDING_SEND",
      retryCount: 0,
      createdByUid: user.uid,
      createdByName: profile.name,
      createdByEmail: profile.email,
      createdAt: serverTimestamp(),
    });
    await batch.commit();
    await audit("Section 194 TDS liability created", "STATUTORY", statutoryRef.id, {
      section,
      payeeName,
      tdsAmount,
      dueDate,
    });
    setStatutoryForm(emptyStatutoryEntry);
    notify(`${section} TDS liability aur Accounts mail queue ho gayi`);
    await loadAll();
  };

  const recordStatutoryPayment = async (item, values) => {
    if (!canRecordAccountsPayment) {
      return notify("HR/CEO/Accounts access required");
    }
    const paymentDate = clean(values.paymentDate);
    const challanReference = clean(values.challanReference).toUpperCase();
    const bsrCode = clean(values.bsrCode);
    const paymentAmount = roundMoney(values.paidAmount);
    const paidAmount = roundMoney(number(item.paidAmount) + paymentAmount);
    if (!isValidIsoDate(paymentDate) || !challanReference || paymentAmount <= 0) {
      notify("Valid payment date, challan/CIN aur paid amount required hai");
      return false;
    }
    const statutoryRef = doc(db, COLLECTIONS.statutory, item.id);
    const mailRef = doc(collection(db, COLLECTIONS.mail));
    const batch = writeBatch(db);
    batch.update(statutoryRef, {
      status: paidAmount >= number(item.amount) ? "PAID" : "PARTIALLY_PAID",
      isClosed: paidAmount >= number(item.amount),
      paidAmount,
      lastPaymentAmount: paymentAmount,
      paymentDate,
      challanReference,
      bsrCode,
      bankReference: clean(values.bankReference),
      challanProofUrl: clean(values.challanProofUrl),
      paidByUid: user.uid,
      paidByName: profile.name,
      paidByEmail: profile.email,
      paidAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    batch.set(mailRef, {
      type: "STATUTORY_PAYMENT_RECORDED",
      from: OFFICIAL_EMAILS.ACCOUNT,
      to: OFFICIAL_EMAILS.HR,
      cc: [OFFICIAL_EMAILS.ACCOUNT, OFFICIAL_EMAILS.CEO],
      subject: `${item.section || item.complianceType} statutory payment recorded | ${item.period}`,
      body: [
        "Statutory payment has been recorded in Shine Agri HRMS.",
        `Section / Type: ${item.section || item.complianceType}`,
        `Payee / Employee: ${item.payeeName || item.employeeName || "Consolidated"}`,
        `Period: ${item.period || "-"}`,
        `Liability: ${money(item.amount)}`,
        `Paid in this entry: ${money(paymentAmount)}`,
        `Cumulative paid: ${money(paidAmount)}`,
        `Payment date: ${paymentDate}`,
        `Challan / CIN: ${challanReference}`,
        `BSR: ${bsrCode || "-"}`,
      ].join("\n"),
      relatedId: item.id,
      correlationId: challanReference,
      status: "PENDING_SEND",
      retryCount: 0,
      createdByUid: user.uid,
      createdByName: profile.name,
      createdByEmail: profile.email,
      createdAt: serverTimestamp(),
    });
    await batch.commit();
    await audit("Statutory payment recorded", "STATUTORY", item.id, {
      section: item.section || item.complianceType,
      paidAmount,
      paymentDate,
      challanReference,
    });
    notify("Statutory payment/challan record save aur confirmation mail queue ho gayi");
    await loadAll();
    return true;
  };

  const retryFailedMail = async (item) => {
    if (!canManageMail) return notify("HR/CEO access required");
    if (!["FAILED", "ERROR"].includes(clean(item.status).toUpperCase())) {
      return notify("Sirf failed mail requeue ho sakti hai");
    }
    try {
      let retryMailId = "";
      await runTransaction(db, async (transaction) => {
        const originalRef = doc(db, COLLECTIONS.mail, item.id);
        const originalSnapshot = await transaction.get(originalRef);
        if (!originalSnapshot.exists()) {
          throw new Error("Original mail queue record nahi mila");
        }
        const original = originalSnapshot.data();
        if (
          !["FAILED", "ERROR"].includes(
            clean(original.status).toUpperCase()
          )
        ) {
          throw new Error("Mail pehle hi retry/process ho chuki hai");
        }
        const retryCount = number(original.retryCount) + 1;
        const retryRef = doc(
          db,
          COLLECTIONS.mail,
          `retry_${item.id}_${retryCount}`
        );
        const retrySnapshot = await transaction.get(retryRef);
        if (retrySnapshot.exists()) {
          throw new Error("Is mail ka retry pehle hi queue ho chuka hai");
        }
        let payrollRef = null;
        let payrollSnapshot = null;
        let reimbursementRef = null;
        let reimbursementSnapshot = null;
        const isAccountsPaymentMail =
          original.type === "SALARY_NEFT_REQUEST" && original.relatedId;
        const isPayslipMail =
          ["PAYSLIP", "PAYSLIP_GENERATED", "PAYSLIP_PDF"].includes(original.type) &&
          original.relatedId;
        if (isAccountsPaymentMail || isPayslipMail) {
          payrollRef = doc(
            db,
            COLLECTIONS.payroll,
            original.relatedId
          );
          payrollSnapshot = await transaction.get(payrollRef);
          if (!payrollSnapshot.exists()) {
            throw new Error("Linked payroll record nahi mila");
          }
          const linkedMailId = isAccountsPaymentMail
            ? payrollSnapshot.data().accountsMailId
            : payrollSnapshot.data().payslipMailId;
          if (linkedMailId !== item.id) {
            throw new Error("Payroll kisi newer mail retry se linked hai");
          }
        }
        const isReimbursementAccountsMail =
          original.type === "REIMBURSEMENT_PAYMENT_REQUEST" &&
          original.relatedId;
        if (isReimbursementAccountsMail) {
          reimbursementRef = doc(
            db,
            COLLECTIONS.reimbursements,
            original.relatedId
          );
          reimbursementSnapshot = await transaction.get(reimbursementRef);
          if (!reimbursementSnapshot.exists()) {
            throw new Error("Linked reimbursement claim nahi mila");
          }
          if (reimbursementSnapshot.data().accountsMailId !== item.id) {
            throw new Error(
              "Reimbursement kisi newer mail retry se linked hai"
            );
          }
        }
        retryMailId = retryRef.id;
        transaction.set(retryRef, {
          type: original.type || "",
          from: original.from || OFFICIAL_EMAILS.HR,
          to: original.to || "",
          cc: Array.isArray(original.cc) ? original.cc : [],
          subject: original.subject || "",
          body: original.body || "",
          html: original.html || "",
          attachments: Array.isArray(original.attachments)
            ? original.attachments
            : [],
          replyTo: original.replyTo || "",
          relatedId: original.relatedId || "",
          correlationId:
            original.correlationId || original.relatedId || item.id,
          payload: original.payload || {},
          originalMailId: item.id,
          parentMailId: original.parentMailId || item.id,
          retryCount,
          status: "PENDING_SEND",
          createdByUid: user.uid || "",
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
        transaction.update(originalRef, {
          status: "RETRIED",
          retryMailId: retryRef.id,
          retriedByUid: user.uid || "",
          retriedByName: profile.name,
          retriedByEmail: profile.email,
          retriedAt: serverTimestamp(),
        });
        if (payrollRef && payrollSnapshot) {
          transaction.update(
            payrollRef,
            isAccountsPaymentMail
              ? {
                  accountsMailId: retryRef.id,
                  accountsMailStatus: "MAIL_QUEUED",
                  accountsMailRetriedAt: serverTimestamp(),
                }
              : {
                  payslipMailId: retryRef.id,
                  payslipMailStatus: "PENDING_SEND",
                  payslipMailRetriedAt: serverTimestamp(),
                }
          );
        }
        if (reimbursementRef && reimbursementSnapshot) {
          transaction.update(reimbursementRef, {
            accountsMailId: retryRef.id,
            accountsMailStatus: "MAIL_QUEUED",
            accountsMailRetriedAt: serverTimestamp(),
          });
        }
      });
      await audit("Failed mail requeued", "MAIL_QUEUE", retryMailId, {
        originalMailId: item.id,
        type: item.type || "",
        to: item.to || "",
      });
      notify("Failed mail dobara queue ho gayi");
      loadAll();
    } catch (error) {
      notify(error.message || "Mail requeue nahi ho saki");
    }
  };

  const closeRelatedApproval = async (
    type,
    relatedId,
    status,
    extra = {}
  ) => {
    if (!relatedId) return;
    const relatedSnapshot = await getDocs(
      query(
        collection(db, COLLECTIONS.approvals),
        where("relatedId", "==", relatedId)
      )
    );
    await Promise.all(
      relatedSnapshot.docs
        .filter(
          (item) =>
            normalizeApprovalType(item.data().type) ===
              normalizeApprovalType(type) &&
            isPendingCEOApproval(item.data().status)
        )
        .map((item) =>
          updateDoc(item.ref, {
            status,
            actionByUid: user?.uid || "",
            actionBy: profile?.name || "",
            actionByEmail: profile?.email || "",
            actionAt: serverTimestamp(),
            ...extra,
          })
        )
    );
  };

  const loadAll = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const keys = Object.keys(data);
      const requestedKeys = canAccounts
        ? [
            "payroll",
            "statutory",
            "payrollBatches",
            "automationRuns",
          ]
        : keys;
      const names = requestedKeys.map((key) => COLLECTIONS[key]);
      const results = await Promise.all(
        names.map(async (name) => {
          try {
            return {
              rows: await fetchCollection(name, [
                orderBy("createdAt", "desc"),
              ]),
              error: "",
            };
          } catch {
            try {
              return { rows: await fetchCollection(name), error: "" };
            } catch (error) {
              console.warn(`HRMS collection unavailable: ${name}`, error);
              return {
                rows: [],
                error: `${name}: ${error.code || error.message || "unavailable"}`,
              };
            }
          }
        })
      );
      setLoadWarnings(
        results.map((result) => result.error).filter(Boolean)
      );
      setData(
        Object.fromEntries(
          keys.map((key) => {
            const requestedIndex = requestedKeys.indexOf(key);
            return [
              key,
              requestedIndex >= 0 ? results[requestedIndex].rows : [],
            ];
          })
        )
      );
    } catch (error) {
      console.error(error);
      notify(`Data load error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user && profile) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile]);

  useEffect(() => {
    if (!user || !profile || canAccounts) return undefined;
    const interviewQuery = query(
      collection(db, COLLECTIONS.interviews),
      orderBy("createdAt", "desc")
    );
    const unsubscribe = onSnapshot(
      interviewQuery,
      (snapshot) => {
        const rows = snapshot.docs.map((item) => ({
          id: item.id,
          ...item.data(),
        }));
        const newlyWaiting = rows.find(
          (item) =>
            (item.status === "WAITING" || item.roomStatus === "WAITING") &&
            !waitingSeenRef.current.has(item.id)
        );
        rows
          .filter(
            (item) =>
              item.status === "WAITING" || item.roomStatus === "WAITING"
          )
          .forEach((item) => waitingSeenRef.current.add(item.id));
        setData((current) => ({ ...current, interviews: rows }));
        if (newlyWaiting) {
          notify(
            `${newlyWaiting.candidateName || "Candidate"} interview waiting room me join ho gaya hai.`
          );
        }
      },
      (error) => console.warn("Interview live listener unavailable", error)
    );
    return unsubscribe;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, profile]);

  const handleLogin = async (event) => {
    event?.preventDefault();
    manualLoginRef.current = true;
    setLoading(true);
    try {
      if (!clean(login.email) || !login.password) {
        throw new Error("Email aur password required hai.");
      }
      let credential;
      try {
        credential = await signInWithEmailAndPassword(
          auth,
          clean(login.email).toLowerCase(),
          login.password
        );
      } catch (authError) {
        if (authError?.code === "auth/too-many-requests") {
          throw new Error(
            "Firebase ne repeated login ke karan temporary block lagaya hai. 15–30 minute baad dobara try karein."
          );
        }
        if (
          ["auth/invalid-credential", "auth/wrong-password"].includes(
            authError?.code
          )
        ) {
          throw new Error(
            "Email ya password sahi nahi hai. Firebase Authentication me password reset karke dobara try karein."
          );
        }
        if (authError?.code === "auth/user-not-found") {
          throw new Error(
            "Firebase Authentication me is HR email ka user nahi bana hai. Authentication > Users me same email/password ka user add karein."
          );
        }
        throw authError;
      }

      const authenticatedEmail = clean(
        credential.user.email || login.email
      ).toLowerCase();
      const expectedLogin = LOGIN_DIRECTORY[authenticatedEmail];
      if (!expectedLogin) {
        await signOut(auth);
        throw new Error(
          `HRMS access sirf ${OFFICIAL_EMAILS.CEO}, ${OFFICIAL_EMAILS.HR} aur ${OFFICIAL_EMAILS.ACCOUNT} ke liye enabled hai.`
        );
      }

      // The server creates/repairs the exact UID-based role profile only after
      // Firebase Authentication has verified one of the official accounts.
      // This removes the old generated-document-ID mismatch without opening
      // client-side profile creation in Firestore Rules.
      try {
        const bootstrapProfile = httpsCallable(
          interviewFunctions,
          "bootstrapHrmsProfile"
        );
        await bootstrapProfile();
      } catch (bootstrapError) {
        console.warn("Official HRMS profile bootstrap skipped", bootstrapError);
      }

      const uidProfile = await getDoc(
        doc(db, COLLECTIONS.users, credential.user.uid)
      );
      let found = uidProfile.exists()
        ? { id: uidProfile.id, ...uidProfile.data() }
        : null;

      // One-time compatibility for old hr_users documents whose document ID
      // was not the Firebase Authentication UID. This lookup happens only
      // after successful Firebase Authentication and never checks/stores a
      // plaintext password.
      if (!found) {
        const legacyQuery = query(
          collection(db, COLLECTIONS.users),
          where("email", "==", authenticatedEmail),
          limit(1)
        );
        const legacySnapshot = await getDocs(legacyQuery);
        if (!legacySnapshot.empty) {
          const legacyDoc = legacySnapshot.docs[0];
          found = { id: legacyDoc.id, ...legacyDoc.data() };
        }
      }

      if (
        !found ||
        String(found.status || "").toUpperCase() !== "ACTIVE" ||
        found.isActive === false
      ) {
        await signOut(auth);
        throw new Error("HR profile nahi mila ya user inactive hai.");
      }
      const foundEmail = clean(found.email).toLowerCase();
      const foundRole = normalizedDashboardRole(found);
      if (
        foundEmail !== authenticatedEmail ||
        foundRole !== expectedLogin.dashboardRole
      ) {
        await signOut(auth);
        throw new Error(
          "Official email aur assigned CEO/HR/Accounts role match nahi kar raha hai."
        );
      }
      const secureProfile = {
        uid: credential.user.uid,
        name:
          found.name ||
          credential.user.displayName ||
          expectedLogin.name,
        email: authenticatedEmail,
        mobile: found.mobile || "",
        role: expectedLogin.legacyRole,
        dashboardRole: expectedLogin.dashboardRole,
        department: found.department || "",
        status: found.status || "ACTIVE",
        migratedToFirebaseAuth: true,
        authMigratedAt: serverTimestamp(),
      };
      // Profile migration is best-effort. A valid official login must not fail
      // merely because production rules intentionally allow profile reads but
      // restrict client-side profile writes.
      try {
        await setDoc(
          doc(db, COLLECTIONS.users, credential.user.uid),
          secureProfile,
          { merge: true }
        );
        if (found.id !== credential.user.uid) {
          await updateDoc(doc(db, COLLECTIONS.users, found.id), {
            uid: credential.user.uid,
            migratedToFirebaseAuth: true,
            authMigratedAt: serverTimestamp(),
          });
        }
      } catch (profileSyncError) {
        console.warn(
          "HRMS profile migration skipped by security rules",
          profileSyncError
        );
      }
      setUser(credential.user);
      setProfile({ ...secureProfile, id: credential.user.uid });
      setActive("dashboard");
      setDashboardView("portal");
      setLogin({ email: "", password: "" });
      try {
        await addDoc(collection(db, COLLECTIONS.audit), {
          action: "HRMS Login",
          targetType: "LOGIN",
          targetId: credential.user.uid,
          byUid: credential.user.uid,
          byName: secureProfile.name,
          byRole: secureProfile.dashboardRole,
          email: secureProfile.email,
          createdAt: serverTimestamp(),
        });
      } catch (auditError) {
        console.warn("HRMS login audit could not be written", auditError);
      }
    } catch (error) {
      setLogin((current) => ({ ...current, password: "" }));
      notify(error.message || "Login failed");
    } finally {
      manualLoginRef.current = false;
      setLoading(false);
    }
  };

  const logout = async () => {
    await signOut(auth);
    setUser(null);
    setProfile(null);
    setLogin({ email: "", password: "" });
    setDashboardView("portal");
  };

  const closeCEODeleteDialog = (force = false) => {
    if (deleteBusy && !force) return;
    setDeleteRequest(null);
    setDeleteConfirmation("");
    setDeleteReason("");
  };

  const requestCEODelete = (collectionName, item, recordLabel = "record") => {
    if (!canCEODelete) {
      return notify(
        `Only ${OFFICIAL_EMAILS.CEO} can use the audited delete control`
      );
    }
    if (
      !CEO_DELETABLE_COLLECTIONS.has(collectionName) ||
      !clean(item?.id)
    ) {
      return notify("This record cannot be deleted from HRMS");
    }
    setDeleteConfirmation("");
    setDeleteReason("");
    setDeleteRequest({ collectionName, item, recordLabel });
  };

  const confirmCEODelete = async () => {
    if (!deleteRequest || deleteBusy) return;
    if (!canCEODelete) {
      return notify("CEO authorization required");
    }
    if (deleteConfirmation.trim().toUpperCase() !== "DELETE") {
      return notify('Confirmation box me "DELETE" type karein');
    }
    if (!clean(deleteReason)) {
      return notify("Official deletion reason required hai");
    }

    const { collectionName, item, recordLabel } = deleteRequest;
    if (
      !CEO_DELETABLE_COLLECTIONS.has(collectionName) ||
      !clean(item?.id)
    ) {
      return notify("Invalid deletion request");
    }

    setDeleteBusy(true);
    try {
      const targetRef = doc(db, collectionName, item.id);
      const currentSnapshot = await getDoc(targetRef);
      if (!currentSnapshot.exists()) {
        throw new Error("Record pehle hi remove ho chuka hai");
      }
      const currentRecord = currentSnapshot.data();
      if (currentRecord.isDeleted) {
        throw new Error("Record pehle hi CEO delete archive me hai");
      }

      const batch = writeBatch(db);
      const deletionFields = {
        isDeleted: true,
        deletedAt: serverTimestamp(),
        deletedByUid: user.uid,
        deletedByName: profile.name,
        deletedByEmail: profile.email,
        deletedByRole: ROLE.CEO_MASTER,
        deletedFromStatus: clean(currentRecord.status),
        deletionReason: clean(deleteReason),
        updatedAt: serverTimestamp(),
      };
      batch.set(targetRef, deletionFields, { merge: true });

      // A published requisition and its public vacancy share the same ID.
      // Hide both together so the Career page cannot keep showing an orphan.
      if (collectionName === COLLECTIONS.requisitions) {
        const linkedJobRef = doc(db, COLLECTIONS.jobs, item.id);
        const linkedJobSnapshot = await getDoc(linkedJobRef);
        if (linkedJobSnapshot.exists()) {
          batch.set(
            linkedJobRef,
            {
              ...deletionFields,
              status: "UNPUBLISHED_BY_CEO",
              postingStatus: "UNPUBLISHED_BY_CEO",
              isPublished: false,
              active: false,
            },
            { merge: true }
          );
        }
      }

      const auditRef = doc(collection(db, COLLECTIONS.audit));
      const displayName =
        currentRecord.title ||
        currentRecord.candidateName ||
        currentRecord.employeeName ||
        currentRecord.name ||
        currentRecord.subject ||
        item.id;
      batch.set(auditRef, {
        action: "CEO_RECORD_DELETED",
        targetType: collectionName,
        targetId: item.id,
        byUid: user.uid,
        byName: profile.name,
        byRole: ROLE.CEO_MASTER,
        byEmail: profile.email,
        metadata: {
          recordLabel,
          displayName: clean(displayName).slice(0, 180),
          previousStatus: clean(currentRecord.status).slice(0, 80),
          reason: clean(deleteReason).slice(0, 500),
          deletionMode: "AUDITED_SOFT_DELETE",
        },
        createdAt: serverTimestamp(),
      });

      await batch.commit();
      closeCEODeleteDialog(true);
      notify(`${recordLabel} CEO audit ke saath delete ho gaya`);
      await loadAll();
    } catch (error) {
      console.error("CEO delete failed", error);
      notify(
        error?.code === "permission-denied"
          ? "Firestore rules ne delete archive block kiya. CEO update permission verify karein."
          : error.message || "Record delete nahi ho saka"
      );
    } finally {
      setDeleteBusy(false);
    }
  };

  const createRequisition = async () => {
    if (!canHR) return notify("HR/CEO access required");
    if (!clean(reqForm.title) || !clean(reqForm.department))
      return notify("Job title aur department required hai");
    const requisitionKey = [
      today(),
      reqForm.title,
      reqForm.department,
      reqForm.vacancies,
      reqForm.employmentType,
    ].join("|");
    const requisitionRef = doc(
      db,
      COLLECTIONS.requisitions,
      `req_${stableHashToken(requisitionKey)}`
    );
    const approvalRef = canCEO
      ? null
      : doc(
          db,
          COLLECTIONS.approvals,
          `job_requisition_${requisitionRef.id}`
        );
    try {
      await runTransaction(db, async (transaction) => {
        const reads = [transaction.get(requisitionRef)];
        if (approvalRef) reads.push(transaction.get(approvalRef));
        const [requisitionSnapshot, approvalSnapshot] = await Promise.all(
          reads
        );
        if (requisitionSnapshot.exists() || approvalSnapshot?.exists()) {
          throw new Error(
            "Yahi requisition aaj pehle hi submit ho chuki hai"
          );
        }
        transaction.set(requisitionRef, {
          title: clean(reqForm.title),
          department: clean(reqForm.department),
          vacancies: number(reqForm.vacancies),
          employmentType: reqForm.employmentType || "FULL_TIME",
          employmentTypeDetails:
            clean(reqForm.employmentTypeDetails) ||
            defaultEmploymentDetails(reqForm.employmentType),
          experience: clean(reqForm.experience),
          reason: clean(reqForm.reason),
          jobOverview: clean(reqForm.jobOverview),
          location: clean(reqForm.location),
          areasCovered: clean(reqForm.areasCovered),
          responsibilities: clean(reqForm.responsibilities),
          qualifications: clean(reqForm.qualifications),
          skills: clean(reqForm.skills),
          preferredExperience: clean(reqForm.preferredExperience),
          compensationStatus: "TO_BE_DEFINED_AFTER_SELECTION",
          salaryConfigured: false,
          status: canCEO ? "APPROVED" : "PENDING_CEO_APPROVAL",
          approvalRequestId: approvalRef?.id || "",
          requestKey: requisitionKey,
          requestedBy: profile.name,
          requestedByUid: user.uid || user.id,
          requestedByEmail: profile.email,
          requestedByRole: role,
          ...(canCEO
            ? {
                ceoActionBy: profile.name,
                ceoActionByUid: user.uid,
                ceoActionByEmail: profile.email,
                ceoActionAt: serverTimestamp(),
              }
            : {}),
          createdAt: serverTimestamp(),
        });
        if (approvalRef) {
          transaction.set(approvalRef, {
            type: "JOB_REQUISITION",
            relatedId: requisitionRef.id,
            title: reqForm.title,
            department: reqForm.department,
            requestedBy: profile.name,
            requestedByUid: user.uid,
            requestedByEmail: profile.email,
            status: "PENDING_CEO_APPROVAL",
            createdAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Job requisition save nahi hui");
      await loadAll();
      return;
    }
    await audit(
      canCEO
        ? "Job requisition created and approved by CEO"
        : "Job requisition submitted for CEO approval",
      "JOB_REQUISITION",
      requisitionRef.id,
      { title: reqForm.title, department: reqForm.department }
    );
    setReqForm(emptyReq);
    notify("Job requisition save ho gayi");
    loadAll();
  };

  const approveRequisition = async (item, decision) => {
    if (!canCEO) return notify("CEO approval required");
    const requisitionRef = doc(db, COLLECTIONS.requisitions, item.id);
    let linkedApprovalId = "";
    try {
      await runTransaction(db, async (transaction) => {
        const requisitionSnapshot = await transaction.get(requisitionRef);
        if (!requisitionSnapshot.exists()) {
          throw new Error("Requisition record nahi mila");
        }
        const currentRequisition = requisitionSnapshot.data();
        if (!isPendingCEOApproval(currentRequisition.status)) {
          throw new Error("Requisition pehle hi process ho chuki hai");
        }
        linkedApprovalId = currentRequisition.approvalRequestId || "";
        let approvalRef = null;
        if (linkedApprovalId) {
          approvalRef = doc(
            db,
            COLLECTIONS.approvals,
            linkedApprovalId
          );
          const approvalSnapshot = await transaction.get(approvalRef);
          if (
            !approvalSnapshot.exists() ||
            !isPendingCEOApproval(approvalSnapshot.data().status)
          ) {
            throw new Error("Linked CEO approval pending nahi hai");
          }
        }
        transaction.update(requisitionRef, {
          status: decision,
          ceoActionBy: profile.name,
          ceoActionByUid: user.uid,
          ceoActionByEmail: profile.email,
          ceoActionAt: serverTimestamp(),
        });
        if (approvalRef) {
          transaction.update(approvalRef, {
            status:
              decision === "APPROVED" ? "APPROVED_BY_CEO" : "REJECTED",
            actionByUid: user.uid || "",
            actionBy: profile.name,
            actionByEmail: profile.email,
            actionAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Requisition decision save nahi hua");
      await loadAll();
      return;
    }
    if (!linkedApprovalId) {
      await closeRelatedApproval(
        "JOB_REQUISITION",
        item.id,
        decision === "APPROVED" ? "APPROVED_BY_CEO" : "REJECTED"
      );
    }
    await audit(`Job requisition ${decision}`, "JOB_REQUISITION", item.id);
    notify(`Requisition ${decision}`);
    loadAll();
  };

  const publishJob = async (item) => {
    if (!canHR || item.status !== "APPROVED") return notify("Approved requisition required");
    const requisitionRef = doc(db, COLLECTIONS.requisitions, item.id);
    const postingRef = doc(db, COLLECTIONS.jobs, item.id);
    try {
      await runTransaction(db, async (transaction) => {
        const [requisitionSnapshot, postingSnapshot] = await Promise.all([
          transaction.get(requisitionRef),
          transaction.get(postingRef),
        ]);
        if (!requisitionSnapshot.exists()) {
          throw new Error("Requisition record nahi mila");
        }
        const requisition = requisitionSnapshot.data();
        if (requisition.status !== "APPROVED") {
          throw new Error("CEO-approved requisition required hai");
        }
        if (
          postingSnapshot.exists() ||
          requisition.postingStatus === "PUBLISHED"
        ) {
          throw new Error("Job pehle hi publish ho chuki hai");
        }
        transaction.set(postingRef, {
          requisitionId: item.id,
          title: requisition.title,
          department: requisition.department,
          departmentId:
            DEPARTMENT_OPTIONS.find(
              ([, name]) => name === requisition.department
            )?.[0] || "",
          vacancies: requisition.vacancies,
          employmentType: requisition.employmentType,
          employmentTypeDetails: requisition.employmentTypeDetails || "",
          experience: requisition.experience,
          jobOverview: requisition.jobOverview || "",
          location: requisition.location || "",
          areasCovered: requisition.areasCovered || "",
          responsibilities: requisition.responsibilities || "",
          qualifications: requisition.qualifications || "",
          skills: requisition.skills || "",
          preferredExperience: requisition.preferredExperience || "",
          status: "PUBLISHED",
          publishDate: today(),
          publishedBy: profile.name,
          publishedByUid: user.uid,
          publishedByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
        transaction.update(requisitionRef, {
          postingId: postingRef.id,
          postingStatus: "PUBLISHED",
          publishedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Job publish nahi hui");
      await loadAll();
      return;
    }
    await audit("Job published", "JOB_POSTING", postingRef.id);
    notify("Job publish ho gayi");
    loadAll();
  };

  const updateCandidateStage = async (item, status) => {
    if (!canHR) return notify("HR access required");
    const rejectionReason =
      status === "REJECTED"
        ? window.prompt("Application rejection reason", "")
        : "";
    if (status === "REJECTED" && rejectionReason === null) return;
    await updateDoc(doc(db, COLLECTIONS.applications, item.id), {
      status,
      updatedBy: profile.name,
      updatedByUid: user.uid,
      updatedByEmail: profile.email,
      updatedAt: serverTimestamp(),
      ...(status === "REJECTED"
        ? {
            rejectionReason: clean(rejectionReason),
            rejectedBy: profile.name,
            rejectedByUid: user.uid,
            rejectedByEmail: profile.email,
            rejectedAt: serverTimestamp(),
          }
        : {}),
    });
    if (status === "REJECTED") {
      await queueMail(
        "CANDIDATE_REJECTION",
        item.email,
        "Application Status",
        `Dear ${item.candidateName}, thank you for applying. We are unable to proceed with your application.`,
        item.id
      );
    }
    await audit(`Candidate moved to ${status}`, "APPLICATION", item.id);
    loadAll();
  };

  const resolveCandidateApplication = (source) => {
    if (!source) return null;
    const directId = clean(
      source.applicationId ||
        source.jobApplicationId ||
        source.relatedApplicationId
    );
    if (directId) {
      return data.applications.find((item) => item.id === directId) || null;
    }
    const directApplication = data.applications.find(
      (item) => item.id === source.id
    );
    if (directApplication) return directApplication;
    const candidateUserId = clean(source.candidateUserId);
    const email = clean(source.candidateEmail || source.email).toLowerCase();
    const jobTitle = clean(source.jobTitle || source.position).toLowerCase();
    const matches = data.applications.filter((item) => {
      const identityMatches = candidateUserId
        ? item.candidateUserId === candidateUserId
        : email &&
          clean(item.email || item.candidateEmail).toLowerCase() === email;
      const jobMatches =
        !jobTitle ||
        clean(item.jobTitle || item.position).toLowerCase() === jobTitle;
      return identityMatches && jobMatches;
    });
    return matches.length === 1 ? matches[0] : null;
  };

  const archiveCandidateApplication = async (source) => {
    if (!canHR) return notify("HR access required");
    const application = resolveCandidateApplication(source);
    if (!application) {
      return notify("Linked candidate application uniquely identify nahi hui");
    }
    if (application.status === "ARCHIVED") {
      return notify("Application pehle hi archived hai");
    }
    const reason = window.prompt("Archive reason (required)", "");
    if (reason === null) return;
    if (!clean(reason)) return notify("Archive reason required hai");
    await updateDoc(doc(db, COLLECTIONS.applications, application.id), {
      status: "ARCHIVED",
      archivedFromStatus: application.status || "UNKNOWN",
      archivedReason: clean(reason),
      archivedAt: serverTimestamp(),
      archivedBy: profile.name,
      archivedByUid: user.uid,
      archivedByEmail: profile.email,
      updatedAt: serverTimestamp(),
    });
    await audit("Candidate application archived", "APPLICATION", application.id, {
      archivedFromStatus: application.status || "UNKNOWN",
      reason: clean(reason),
    });
    notify("Application safely archived; record aur history preserved hai");
    loadAll();
  };

  const reopenCandidateApplication = async (source) => {
    if (!canHR) return notify("HR access required");
    const application = resolveCandidateApplication(source);
    if (!application) {
      return notify("Linked candidate application uniquely identify nahi hui");
    }
    if (!["ARCHIVED", "REJECTED"].includes(application.status)) {
      return notify("Sirf archived/rejected application reopen ho sakti hai");
    }
    const applicationRef = doc(
      db,
      COLLECTIONS.applications,
      application.id
    );
    const candidateRef = application.candidateUserId
      ? doc(db, COLLECTIONS.candidateUsers, application.candidateUserId)
      : null;
    let candidateAccessRestored = false;
    try {
      await runTransaction(db, async (transaction) => {
        const [applicationSnapshot, candidateSnapshot] = await Promise.all([
          transaction.get(applicationRef),
          candidateRef ? transaction.get(candidateRef) : Promise.resolve(null),
        ]);
        if (!applicationSnapshot.exists()) {
          throw new Error("Candidate application record nahi mila");
        }
        const currentApplication = applicationSnapshot.data();
        if (!["ARCHIVED", "REJECTED"].includes(currentApplication.status)) {
          throw new Error("Application state badal chuki hai; refresh karein");
        }
        transaction.update(applicationRef, {
          status: "SHORTLISTED",
          reopenedFromStatus: currentApplication.status,
          reopenedAt: serverTimestamp(),
          reopenedBy: profile.name,
          reopenedByUid: user.uid,
          reopenedByEmail: profile.email,
          updatedAt: serverTimestamp(),
        });
        if (
          candidateRef &&
          candidateSnapshot?.exists() &&
          isLegacyApplicationBlock(candidateSnapshot.data(), application.id)
        ) {
          candidateAccessRestored = true;
          transaction.update(candidateRef, {
            status: "ACTIVE",
            blockedReason: "",
            blockedAt: null,
            accessRestoredForApplicationId: application.id,
            accessRestoredAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Application reopen nahi hui");
      return;
    }
    await audit("Candidate application reopened", "APPLICATION", application.id, {
      fromStatus: application.status,
      candidateAccessRestored,
    });
    notify(
      candidateAccessRestored
        ? "Application shortlisted aur legacy candidate block restore ho gaya"
        : "Application dobara shortlisted ho gayi"
    );
    loadAll();
  };

  const reInterviewCandidate = async (source) => {
    if (!canHR) return notify("HR access required");
    const application = resolveCandidateApplication(source);
    if (!application) {
      return notify("Linked candidate application uniquely identify nahi hui");
    }
    if (
      !["REJECTED", "COMPLETED", "INTERVIEW_COMPLETED"].includes(
        application.status
      )
    ) {
      return notify(
        "Re-interview sirf rejected/completed application ke liye available hai"
      );
    }
    if (!application.candidateUserId) {
      return notify("Secure re-interview ke liye candidate portal identity required hai");
    }
    const applicationRef = doc(
      db,
      COLLECTIONS.applications,
      application.id
    );
    const candidateRef = doc(
      db,
      COLLECTIONS.candidateUsers,
      application.candidateUserId
    );
    try {
      await runTransaction(db, async (transaction) => {
        const [applicationSnapshot, candidateSnapshot] = await Promise.all([
          transaction.get(applicationRef),
          transaction.get(candidateRef),
        ]);
        if (!applicationSnapshot.exists()) {
          throw new Error("Candidate application record nahi mila");
        }
        const currentApplication = applicationSnapshot.data();
        if (
          !["REJECTED", "COMPLETED", "INTERVIEW_COMPLETED"].includes(
            currentApplication.status
          )
        ) {
          throw new Error("Application state badal chuki hai; refresh karein");
        }
        transaction.update(applicationRef, {
          status: "INTERVIEW_NEXT_ROUND",
          reInterviewFromStatus: currentApplication.status,
          reInterviewCount: number(currentApplication.reInterviewCount) + 1,
          reInterviewRequestedAt: serverTimestamp(),
          reInterviewRequestedBy: profile.name,
          reInterviewRequestedByUid: user.uid,
          reInterviewRequestedByEmail: profile.email,
          updatedAt: serverTimestamp(),
        });
        if (
          candidateSnapshot.exists() &&
          isLegacyApplicationBlock(candidateSnapshot.data(), application.id)
        ) {
          transaction.update(candidateRef, {
            status: "ACTIVE",
            blockedReason: "",
            blockedAt: null,
            accessRestoredForApplicationId: application.id,
            accessRestoredAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Re-interview workflow start nahi hua");
      return;
    }
    await audit("Candidate re-interview requested", "APPLICATION", application.id, {
      previousStatus: application.status,
      preservedInterviewHistory: true,
    });
    setScheduleCandidate({
      ...application,
      status: "INTERVIEW_NEXT_ROUND",
      reInterviewCount: number(application.reInterviewCount) + 1,
    });
  };

  const scheduleInterview = (item) => {
    if (!canHR) return notify("HR access required");
    if (!item.candidateUserId) {
      return notify(
        "Secure video interview ke liye candidate ka Career Portal account required hai."
      );
    }
    setScheduleCandidate(item);
  };

  const interviewScheduled = async (result) => {
    setScheduleCandidate(null);
    const panelMailText = result?.panelInviteCount
      ? ` ${result.panelEmailSent || 0}/${result.panelInviteCount} panel/expert email sent.`
      : "";
    notify(
      result?.emailSent
        ? `Secure interview room create hua aur candidate email/calendar invite send ho gaya.${panelMailText}`
        : `Interview create hua. Email provider pending hai; Communication Queue check karein.${panelMailText}`
    );
    await loadAll();
  };

  const resendInterviewInvite = async (item) => {
    if (!canHR) return notify("HR access required");
    setLoading(true);
    try {
      const resend = httpsCallable(
        interviewFunctions,
        "resendInterviewInvite"
      );
      const response = await resend({ interviewId: item.id });
      notify(
        response.data?.emailStatus === "SENT"
          ? `Naya secure link candidate aur ${response.data?.panelEmailSent || 0} panel/expert member(s) ko email ho gaya.`
          : "Invite Communication Queue me add hua; SMTP configuration check karein."
      );
      await loadAll();
    } catch (error) {
      notify(error.message || "Interview invite resend nahi hua.");
    } finally {
      setLoading(false);
    }
  };

  const rescheduleInterview = async (item) => {
    if (!canHR) return notify("HR access required");
    const value = window.prompt(
      "New interview date/time (example: 2026-07-30T11:00)",
      ""
    );
    if (!value) return;
    const parsed = parseIstDateTime(value);
    if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now()) {
      return notify("Valid future date/time required hai.");
    }
    setLoading(true);
    try {
      const reschedule = httpsCallable(
        interviewFunctions,
        "rescheduleVideoInterview"
      );
      const response = await reschedule({
        interviewId: item.id,
        scheduledStart: value,
        durationMinutes: 60,
        timeZone: "Asia/Kolkata",
      });
      notify(
        response.data?.emailStatus === "SENT"
          ? `Interview reschedule hua; candidate aur ${response.data?.panelEmailSent || 0} panel/expert member(s) ko updated link email ho gaya.`
          : "Interview reschedule hua; mail queue check karein."
      );
      await loadAll();
    } catch (error) {
      notify(error.message || "Interview reschedule nahi hua.");
    } finally {
      setLoading(false);
    }
  };

  const cancelInterview = async (item) => {
    if (!canHR) return notify("HR access required");
    const reason = window.prompt("Interview cancellation reason", "");
    if (reason === null) return;
    if (!window.confirm("Interview cancel karke secure link revoke karna hai?"))
      return;
    setLoading(true);
    try {
      const cancel = httpsCallable(
        interviewFunctions,
        "cancelVideoInterview"
      );
      await cancel({ interviewId: item.id, reason });
      notify("Interview cancelled; link revoke aur candidate email process complete.");
      await loadAll();
    } catch (error) {
      notify(error.message || "Interview cancel nahi hua.");
    } finally {
      setLoading(false);
    }
  };

  const interviewResult = async (item, result) => {
    const linkedApplication = resolveCandidateApplication(item);
    const interviewRound = clean(item.round || "HR").toUpperCase();
    const isCeoRound = interviewRound.includes("CEO");
    if (isCeoRound && !canCEO) {
      return notify("CEO round ka scorecard CEO View se submit hoga");
    }
    if (!isCeoRound && !isHRAdmin) {
      return notify(
        "Ye interview scorecard HR View se submit hoga. CEO final decision Approval Center me karega."
      );
    }
    const score = window.prompt("Score out of 100", "75");
    if (score === null) return;
    const parsedScore = Number(score);
    if (!Number.isFinite(parsedScore) || parsedScore < 0 || parsedScore > 100) {
      return notify("Score 0 se 100 ke beech numeric hona chahiye.");
    }
    const normalizedScore = Math.round(parsedScore);
    const rating = Math.max(1, Math.min(5, Math.round(normalizedScore / 20)));
    const remarks = window.prompt("Interview remarks", "");
    setLoading(true);
    try {
      const submitScorecard = httpsCallable(
        interviewFunctions,
        "submitInterviewScorecard"
      );
      await submitScorecard({
        interviewId: item.id,
        ratings: {
          technical: rating,
          communication: rating,
          experience: rating,
          problemSolving: rating,
          cultureFit: rating,
        },
        recommendation: result === "SELECTED" ? "SELECT" : "REJECT",
        comments: remarks || `Overall panel score: ${normalizedScore}/100`,
      });
      let applicationUpdateWarning = "";
      if (result === "REJECTED" && linkedApplication) {
        const applicationRef = doc(
          db,
          COLLECTIONS.applications,
          linkedApplication.id
        );
        const candidateRef = linkedApplication.candidateUserId
          ? doc(
              db,
              COLLECTIONS.candidateUsers,
              linkedApplication.candidateUserId
            )
          : null;
        try {
          await runTransaction(db, async (transaction) => {
            const [applicationSnapshot, candidateSnapshot] = await Promise.all([
              transaction.get(applicationRef),
              candidateRef
                ? transaction.get(candidateRef)
                : Promise.resolve(null),
            ]);
            if (!applicationSnapshot.exists()) {
              throw new Error("Linked application record nahi mila");
            }
            transaction.update(applicationRef, {
              status: "REJECTED",
              rejectedFromInterviewId: item.id,
              interviewRejectionReason:
                clean(remarks) ||
                `Interview score ${normalizedScore}/100`,
              rejectedAt: serverTimestamp(),
              rejectedBy: profile.name,
              rejectedByUid: user.uid,
              rejectedByEmail: profile.email,
              updatedAt: serverTimestamp(),
            });
            if (
              candidateRef &&
              candidateSnapshot?.exists() &&
              isLegacyApplicationBlock(
                candidateSnapshot.data(),
                linkedApplication.id
              )
            ) {
              transaction.update(candidateRef, {
                status: "ACTIVE",
                blockedReason: "",
                blockedAt: null,
                accessRestoredForApplicationId: linkedApplication.id,
                accessRestoredAt: serverTimestamp(),
              });
            }
          });
          await audit(
            "Interview rejection applied to application only",
            "APPLICATION",
            linkedApplication.id,
            { interviewId: item.id, score: normalizedScore }
          );
        } catch (applicationError) {
          console.warn(
            "Interview scorecard saved but application lifecycle sync failed",
            applicationError
          );
          applicationUpdateWarning =
            " Scorecard saved, lekin application status sync retry required hai.";
        }
      }
      notify(
        result === "SELECTED"
          ? "Scorecard saved; candidate CEO approval queue me gaya."
          : `Scorecard saved; sirf linked application reject hui, candidate login active rahega.${applicationUpdateWarning}`
      );
      await loadAll();
    } catch (error) {
      notify(
        String(error?.code || "").includes("permission-denied")
          ? "Assigned HR/panel identity required hai. HR VIEW select karke scorecard submit karein."
          : error.message || "Interview evaluation submit nahi hua."
      );
    } finally {
      setLoading(false);
    }
  };

  const openOfferPreparation = (application) => {
    if (!isHRAdmin) return notify("Final offer package sirf HR prepare karega");
    if (clean(application.status).toUpperCase() !== "PENDING_CEO_APPROVAL") {
      return notify("Interview selection ke baad pending CEO application required hai");
    }
    const approval = data.approvals.find(
      (item) =>
        item.relatedId === application.id &&
        normalizeApprovalType(item.type) === "FINAL_CANDIDATE_SELECTION" &&
        isPendingCEOApproval(item.status)
    );
    if (!approval) return notify("Linked CEO approval request nahi mila");
    const offerPackage = application.offerPackage || {};
    const salary = offerPackage.salaryStructure || {};
    const pfCompliance = offerPackage.pfCompliance || {};
    const tdsCompliance = offerPackage.tdsCompliance || {};
    setOfferPackageForm({
      joiningDate:
        offerPackage.joiningDate || application.joiningDate || "",
      basic: String(salary.basic ?? ""),
      hra: String(salary.hra ?? ""),
      da: String(salary.da ?? ""),
      allowances: String(salary.allowances ?? ""),
      pfApplicable: pfCompliance.applicable === false ? "NO" : "YES",
      pf: String(salary.pf ?? pfCompliance.employeeContribution ?? ""),
      employerPf: String(
        salary.employerPf ?? pfCompliance.employerContribution ?? ""
      ),
      esi: String(salary.esi ?? ""),
      professionalTax: String(salary.professionalTax ?? ""),
      tdsMode: normalizeTdsMode(
        salary.tdsMode || tdsCompliance.treatment
      ),
      tds: String(salary.tds ?? tdsCompliance.monthlyAmount ?? ""),
      otherDeduction: String(salary.otherDeduction ?? ""),
    });
    setOfferLetterFile(null);
    setOfferPreparation({ application, approval });
  };

  const submitOfferPreparation = async () => {
    if (!isHRAdmin || !offerPreparation) {
      return notify("HR offer preparation access required");
    }
    const { application, approval } = offerPreparation;
    const joiningDate = clean(offerPackageForm.joiningDate);
    const numericFields = [
      "basic",
      "hra",
      "da",
      "allowances",
      "pf",
      "employerPf",
      "esi",
      "professionalTax",
      "tds",
      "otherDeduction",
    ];
    if (
      numericFields.some(
        (key) =>
          !Number.isFinite(Number(offerPackageForm[key] || 0)) ||
          Number(offerPackageForm[key] || 0) < 0
      )
    ) {
      return notify("Salary, TDS aur PF values valid non-negative honi chahiye");
    }
    if (!isValidIsoDate(joiningDate)) {
      return notify("Valid proposed joining date required hai");
    }
    const gross = roundMoney(
      number(offerPackageForm.basic) +
        number(offerPackageForm.hra) +
        number(offerPackageForm.da) +
        number(offerPackageForm.allowances)
    );
    if (gross <= 0) return notify("Gross monthly salary zero nahi ho sakti");
    const pfApplicable = offerPackageForm.pfApplicable === "YES";
    const employeePf = pfApplicable ? roundMoney(offerPackageForm.pf) : 0;
    const employerPf = pfApplicable
      ? roundMoney(offerPackageForm.employerPf)
      : 0;
    if (pfApplicable && employeePf + employerPf <= 0) {
      return notify("PF applicable hai to employee/employer PF amount enter karein");
    }
    const tdsMode = normalizeTdsMode(offerPackageForm.tdsMode);
    const tds =
      tdsMode === TDS_MODE.NOT_APPLICABLE
        ? 0
        : roundMoney(offerPackageForm.tds);
    const existingPackage = application.offerPackage || {};
    if (!offerLetterFile && !clean(existingPackage.offerLetterStoragePath)) {
      return notify("HR ka final signed offer/appointment letter PDF required hai");
    }
    if (offerLetterFile) {
      const isPdf =
        offerLetterFile.type === "application/pdf" ||
        /\.pdf$/i.test(offerLetterFile.name);
      if (!isPdf) return notify("Offer/appointment letter sirf PDF me upload karein");
      if (offerLetterFile.size > 10 * 1024 * 1024) {
        return notify("Offer letter PDF maximum 10 MB ho sakti hai");
      }
    }

    setOfferUploading(true);
    try {
      let offerLetterStoragePath = clean(
        existingPackage.offerLetterStoragePath
      );
      let offerLetterFileName = clean(existingPackage.offerLetterFileName);
      let offerLetterSize = number(existingPackage.offerLetterSize);
      if (offerLetterFile) {
        const safeName = offerLetterFile.name.replace(/[^A-Za-z0-9._-]/g, "_");
        offerLetterStoragePath = `hr_offer_letters/${
          application.candidateUserId || "candidate"
        }/${application.id}/approved-draft-${safeName}`;
        await uploadBytes(
          storageRef(storage, offerLetterStoragePath),
          offerLetterFile,
          {
            contentType: "application/pdf",
            customMetadata: {
              applicationId: application.id,
              candidateUserId: application.candidateUserId || "",
              uploadedByUid: user.uid,
            },
          }
        );
        offerLetterFileName = safeName;
        offerLetterSize = offerLetterFile.size;
      }
      const salaryStructure = {
        basic: roundMoney(offerPackageForm.basic),
        hra: roundMoney(offerPackageForm.hra),
        da: roundMoney(offerPackageForm.da),
        allowances: roundMoney(offerPackageForm.allowances),
        pf: employeePf,
        employerPf,
        esi: roundMoney(offerPackageForm.esi),
        professionalTax: roundMoney(offerPackageForm.professionalTax),
        tdsMode,
        tds,
        tdsSalaryDeduction:
          tdsMode === TDS_MODE.SALARY_DEDUCTION ? tds : 0,
        companyBorneTds: tdsMode === TDS_MODE.COMPANY_BORNE ? tds : 0,
        otherDeduction: roundMoney(offerPackageForm.otherDeduction),
        gross,
        effectiveFrom: joiningDate,
      };
      const offerPackage = {
        status: "READY_FOR_CEO",
        joiningDate,
        salaryStructure,
        tdsCompliance: {
          section: "192",
          treatment: tdsMode,
          monthlyAmount: tds,
        },
        pfCompliance: {
          section: "EPF",
          applicable: pfApplicable,
          employeeContribution: employeePf,
          employerContribution: employerPf,
        },
        offerLetterStoragePath,
        offerLetterFileName,
        offerLetterContentType: "application/pdf",
        offerLetterSize,
        preparedByUid: user.uid,
        preparedByName: profile.name,
        preparedByEmail: profile.email,
        preparedAt: serverTimestamp(),
      };
      const applicationRef = doc(
        db,
        COLLECTIONS.applications,
        application.id
      );
      const approvalRef = doc(db, COLLECTIONS.approvals, approval.id);
      const ceoMailRef = doc(
        db,
        COLLECTIONS.mail,
        `offer_package_ceo_review_${approval.id}`
      );
      const batch = writeBatch(db);
      batch.update(applicationRef, {
        offerPackage,
        offerPackageStatus: "READY_FOR_CEO",
        proposedJoiningDate: joiningDate,
        updatedAt: serverTimestamp(),
      });
      batch.update(approvalRef, {
        offerPackageStatus: "READY_FOR_CEO",
        offerLetterStoragePath,
        offerLetterFileName,
        offerGrossMonthly: gross,
        offerTdsMode: tdsMode,
        offerMonthlyTds: tds,
        offerPfApplicable: pfApplicable,
        offerEmployeePf: employeePf,
        offerEmployerPf: employerPf,
        packagePreparedByUid: user.uid,
        packagePreparedByName: profile.name,
        packagePreparedByEmail: profile.email,
        packagePreparedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      batch.set(
        ceoMailRef,
        {
          type: "OFFER_PACKAGE_CEO_APPROVAL_REQUEST",
          from: OFFICIAL_EMAILS.HR,
          to: OFFICIAL_EMAILS.CEO,
          cc: [OFFICIAL_EMAILS.HR],
          bcc: [],
          subject: `Offer approval required | ${
            application.candidateName || application.name || "Candidate"
          } | ${application.jobTitle || application.position || "Position"}`,
          body: [
            "Dear CEO Sir,",
            "",
            "HR has prepared the final offer and payroll compliance package.",
            `Candidate: ${application.candidateName || application.name || "-"}`,
            `Position: ${application.jobTitle || application.position || "-"}`,
            `Proposed joining: ${joiningDate}`,
            `Gross monthly salary: ${money(gross)}`,
            `TDS Section 192 treatment: ${tdsModeLabel(tdsMode)}`,
            `Monthly TDS: ${money(tds)}`,
            `PF applicable: ${pfApplicable ? "Yes" : "No"}`,
            `Employee PF: ${money(employeePf)}`,
            `Employer PF: ${money(employerPf)}`,
            "",
            "The HR-uploaded offer PDF is attached. Please approve or reject it from HRMS Approval Center.",
          ].join("\n"),
          attachments: [
            {
              storagePath: offerLetterStoragePath,
              filename: offerLetterFileName || "Offer-Letter.pdf",
              contentType: "application/pdf",
            },
          ],
          relatedId: application.id,
          correlationId: approval.id,
          status: "PENDING_SEND",
          retryCount: 0,
          createdByUid: user.uid,
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        },
        { merge: true }
      );
      await batch.commit();
      await audit("HR offer and payroll compliance package submitted", "APPLICATION", application.id, {
        approvalId: approval.id,
        gross,
        tdsMode,
        tds,
        pfApplicable,
        employeePf,
        employerPf,
        offerLetterFileName,
      });
      setOfferPreparation(null);
      setOfferPackageForm({ ...emptyOfferPackage });
      setOfferLetterFile(null);
      notify("Offer PDF, TDS aur PF package CEO approval ke liye submit ho gaya");
      await loadAll();
    } catch (error) {
      notify(error.message || "Offer package submit nahi hua");
    } finally {
      setOfferUploading(false);
    }
  };

  const finalCandidateDecision = async (
    approval,
    decision,
    approvedLeavePolicy = null
  ) => {
    if (!canCEO) return notify("CEO access required");
    const approved = decision === "APPROVED_BY_CEO";
    const offerLeavePolicy = approved
      ? {
          entitlements: Object.fromEntries(
            Object.keys(DEFAULT_OFFER_LEAVE_POLICY).map((key) => [
              key,
              Math.max(
                0,
                number(
                  approvedLeavePolicy?.[key] ??
                    DEFAULT_OFFER_LEAVE_POLICY[key]
                )
              ),
            ])
          ),
          yearBasis: "ANNUAL",
          excessLeaveTreatment: "LOP",
          perDayDeductionBasis: "GROSS_DIVIDED_BY_WORKING_DAYS",
          approvedByRole: "CEO_MASTER",
        }
      : null;
    const approvalRef = doc(db, COLLECTIONS.approvals, approval.id);
    const decisionMailRef = doc(
      db,
      COLLECTIONS.mail,
      `candidate_decision_${approval.id}`
    );
    let committedApplication = null;
    let approvedOfferPackage = null;
    try {
      await runTransaction(db, async (transaction) => {
        const [approvalSnapshot, decisionMailSnapshot] = await Promise.all([
          transaction.get(approvalRef),
          transaction.get(decisionMailRef),
        ]);
        if (!approvalSnapshot.exists()) {
          throw new Error("Approval record nahi mila");
        }
        if (decisionMailSnapshot.exists()) {
          throw new Error("Candidate decision mail pehle hi queue ho chuka hai");
        }
        const currentApproval = approvalSnapshot.data();
        if (!isPendingCEOApproval(currentApproval.status)) {
          throw new Error("Final candidate decision pehle hi process ho gaya");
        }
        let applicationRef = null;
        let candidateUserRef = null;
        let restoreLegacyCandidateAccess = false;
        if (currentApproval.relatedId) {
          applicationRef = doc(
            db,
            COLLECTIONS.applications,
            currentApproval.relatedId
          );
          const applicationSnapshot = await transaction.get(applicationRef);
          if (!applicationSnapshot.exists()) {
            throw new Error("Candidate application record nahi mila");
          }
          committedApplication = {
            id: applicationSnapshot.id,
            ...applicationSnapshot.data(),
          };
          if (approved) {
            const preparedPackage = committedApplication.offerPackage || {};
            const preparedSalary = preparedPackage.salaryStructure || {};
            const preparedGross = roundMoney(
              number(preparedSalary.basic) +
                number(preparedSalary.hra) +
                number(preparedSalary.da) +
                number(preparedSalary.allowances)
            );
            if (
              preparedPackage.status !== "READY_FOR_CEO" ||
              !clean(preparedPackage.offerLetterStoragePath) ||
              preparedGross <= 0 ||
              !preparedPackage.tdsCompliance ||
              !preparedPackage.pfCompliance
            ) {
              throw new Error(
                "HR ko pehle signed offer PDF, salary, Section 192 TDS aur PF compliance submit karna hoga"
              );
            }
            approvedOfferPackage = {
              ...preparedPackage,
              status: "APPROVED_BY_CEO",
              salaryStructure: {
                ...preparedSalary,
                gross: preparedGross,
              },
              approvedByUid: user.uid,
              approvedByName: profile.name,
              approvedByEmail: profile.email,
              approvedAt: serverTimestamp(),
            };
          }
          if (
            decision !== "APPROVED_BY_CEO" &&
            committedApplication.candidateUserId
          ) {
            candidateUserRef = doc(
              db,
              COLLECTIONS.candidateUsers,
              committedApplication.candidateUserId
            );
            const candidateSnapshot = await transaction.get(
              candidateUserRef
            );
            if (
              candidateSnapshot.exists() &&
              isLegacyApplicationBlock(
                candidateSnapshot.data(),
                committedApplication.id
              )
            ) {
              restoreLegacyCandidateAccess = true;
            }
          }
        }
        transaction.update(approvalRef, {
          status: decision,
          ...(approved
            ? {
                offerLeavePolicy,
                offerPackageStatus: "APPROVED_BY_CEO",
                offerPackageApprovedAt: serverTimestamp(),
              }
            : {}),
          actionByUid: user.uid,
          actionBy: profile.name,
          actionByEmail: profile.email,
          actionAt: serverTimestamp(),
        });
        if (applicationRef) {
          transaction.update(applicationRef, {
            status:
              approved ? "OFFER_RELEASED" : "REJECTED",
            ...(approved
              ? {
                  offerLeavePolicy,
                  offerPackage: approvedOfferPackage,
                  offerPackageStatus: "APPROVED_BY_CEO",
                  salaryApprovedForPayroll: true,
                }
              : {}),
            ceoDecisionBy: profile.name,
            ceoDecisionByUid: user.uid,
            ceoDecisionByEmail: profile.email,
            ceoDecisionAt: serverTimestamp(),
          });
        }
        if (candidateUserRef && restoreLegacyCandidateAccess) {
          transaction.update(candidateUserRef, {
            status: "ACTIVE",
            blockedReason: "",
            blockedAt: null,
            accessRestoredForApplicationId: committedApplication.id,
            accessRestoredAt: serverTimestamp(),
          });
        }
        if (committedApplication) {
          const entitlements = offerLeavePolicy?.entitlements || {};
          if (approved) {
            const salary = approvedOfferPackage.salaryStructure;
            const pfCompliance = approvedOfferPackage.pfCompliance;
            const attachment = {
              storagePath: approvedOfferPackage.offerLetterStoragePath,
              filename:
                approvedOfferPackage.offerLetterFileName || "Offer-Letter.pdf",
              contentType: "application/pdf",
            };
            const internalBody = [
              "The final candidate offer has been approved by the CEO.",
              `Candidate: ${committedApplication.candidateName || committedApplication.name || "-"}`,
              `Position: ${committedApplication.jobTitle || committedApplication.position || "-"}`,
              `Department: ${committedApplication.deptName || committedApplication.department || "-"}`,
              `Joining date: ${approvedOfferPackage.joiningDate || "-"}`,
              `Gross monthly salary: ${money(salary.gross)}`,
              `Section 192 TDS: ${tdsModeLabel(salary.tdsMode)} | ${money(salary.tds)}`,
              `PF applicable: ${pfCompliance.applicable ? "Yes" : "No"}`,
              `Employee PF: ${money(pfCompliance.employeeContribution)}`,
              `Employer PF: ${money(pfCompliance.employerContribution)}`,
              "",
              "The CEO-approved offer/appointment letter is attached. After candidate acceptance, HR can activate the employee and the approved salary/TDS/PF package will automatically feed payroll.",
            ].join("\n");
            const candidateBody = [
              `Dear ${committedApplication.candidateName || "Candidate"},`,
              "",
              `Your selection for ${committedApplication.jobTitle || "the notified position"} has been approved by the competent authority of Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.`,
              "The final offer/appointment letter is attached with this email.",
              "",
              `Proposed joining date: ${approvedOfferPackage.joiningDate || "As stated in the attached letter"}`,
              "Please sign in to the candidate portal and record your acceptance before employee activation.",
              "",
              "Approved annual leave entitlement:",
              `• Casual Leave (CL): ${entitlements.CL || 0} day(s)`,
              `• Earned Leave (EL): ${entitlements.EL || 0} day(s)`,
              `• Other Leave (OL): ${entitlements.OL || 0} day(s)`,
              `• Compensatory Off: ${entitlements.COMP_OFF || 0} day(s)`,
              "",
              "Yours faithfully,",
              "Human Resources Department",
              "Shine Agri Allied & Marketing Multistate Cooperative Society Ltd.",
            ].join("\n");
            const recipients = [
              {
                key: "employee",
                ref: decisionMailRef,
                to: committedApplication.email,
                body: candidateBody,
              },
              {
                key: "hr",
                ref: doc(db, COLLECTIONS.mail, `candidate_offer_${approval.id}_hr`),
                to: OFFICIAL_EMAILS.HR,
                body: internalBody,
              },
              {
                key: "ceo",
                ref: doc(db, COLLECTIONS.mail, `candidate_offer_${approval.id}_ceo`),
                to: OFFICIAL_EMAILS.CEO,
                body: internalBody,
              },
              {
                key: "accounts",
                ref: doc(db, COLLECTIONS.mail, `candidate_offer_${approval.id}_accounts`),
                to: OFFICIAL_EMAILS.ACCOUNT,
                body: internalBody,
              },
            ];
            recipients.forEach((recipient) => {
              transaction.set(recipient.ref, {
                type: "CEO_APPROVED_OFFER_LETTER",
                from: OFFICIAL_EMAILS.HR,
                to: recipient.to,
                cc: [],
                bcc:
                  clean(recipient.to).toLowerCase() === OFFICIAL_EMAILS.CEO
                    ? []
                    : [OFFICIAL_EMAILS.CEO],
                subject: `CEO Approved Offer | ${
                  committedApplication.candidateName || "Candidate"
                } | ${committedApplication.jobTitle || "Shine Agri"}`,
                body: recipient.body,
                attachments: [attachment],
                relatedId: committedApplication.id,
                correlationId: approval.id,
                payload: {
                  approvalId: approval.id,
                  applicationId: committedApplication.id,
                  recipientType: recipient.key,
                  offerLeavePolicy,
                  offerPackageStatus: "APPROVED_BY_CEO",
                },
                status: "PENDING_SEND",
                retryCount: 0,
                createdByUid: user.uid || "",
                createdByName: profile.name,
                createdByEmail: profile.email,
                createdAt: serverTimestamp(),
              });
            });
          } else {
            transaction.set(decisionMailRef, {
              type: "CANDIDATE_REJECTION",
              from: OFFICIAL_EMAILS.HR,
              to: committedApplication.email,
              cc: [],
              bcc: [OFFICIAL_EMAILS.CEO],
              subject: "Application Status",
              body: `Dear ${committedApplication.candidateName}, thank you for your time. We are unable to proceed further.`,
              relatedId: committedApplication.id,
              correlationId: approval.id,
              payload: {
                approvalId: approval.id,
                applicationId: committedApplication.id,
                decision,
              },
              status: "PENDING_SEND",
              retryCount: 0,
              createdByUid: user.uid || "",
              createdByName: profile.name,
              createdByEmail: profile.email,
              createdAt: serverTimestamp(),
            });
          }
        }
      });
    } catch (error) {
      notify(error.message || "Final candidate decision save nahi hua");
      await loadAll();
      return false;
    }
    await audit(`CEO candidate decision ${decision}`, "APPROVAL", approval.id);
    notify("Final decision save ho gaya");
    loadAll();
    return true;
  };

  const createEmployee = async () => {
    if (!canCEO) {
      return notify(
        "HR new employee ko Recruitment → Offer Package → CEO Approval → Candidate Acceptance se activate karega"
      );
    }
    if (!clean(employeeForm.name) || !clean(employeeForm.email))
      return notify("Employee name aur email required hai");
    const employeeEmail = clean(employeeForm.email).toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeEmail)) {
      return notify("Valid employee email required hai");
    }
    const salaryFields = [
      "basic",
      "hra",
      "da",
      "allowances",
      "pf",
      "employerPf",
      "esi",
      "professionalTax",
      "tds",
      "otherDeduction",
    ];
    if (
      salaryFields.some(
        (key) =>
          !Number.isFinite(Number(employeeForm[key] || 0)) ||
          Number(employeeForm[key] || 0) < 0
      )
    ) {
      return notify("Salary aur deduction values valid non-negative honi chahiye");
    }
    if (
      roundMoney(
        number(employeeForm.basic) +
          number(employeeForm.hra) +
          number(employeeForm.da) +
          number(employeeForm.allowances)
      ) <= 0
    ) {
      return notify("Employee gross salary zero nahi ho sakti");
    }
    const tdsMode = normalizeTdsMode(employeeForm.tdsMode);
    const tds =
      tdsMode === TDS_MODE.NOT_APPLICABLE ? 0 : number(employeeForm.tds);
    const tdsSalaryDeduction =
      tdsMode === TDS_MODE.SALARY_DEDUCTION ? tds : 0;
    const companyBorneTds = tdsMode === TDS_MODE.COMPANY_BORNE ? tds : 0;
    const employeeCode = employeeCodeFromEmail(employeeEmail);
    const employeeRef = doc(
      db,
      COLLECTIONS.employees,
      employeeDocumentId(employeeEmail)
    );
    const onboardingRef = doc(
      db,
      COLLECTIONS.onboarding,
      employeeRef.id
    );
    const welcomeMailRef = doc(
      db,
      COLLECTIONS.mail,
      `employee_welcome_${employeeRef.id}`
    );
    try {
      await runTransaction(db, async (transaction) => {
        const [
          employeeSnapshot,
          onboardingSnapshot,
          mailSnapshot,
        ] = await Promise.all([
          transaction.get(employeeRef),
          transaction.get(onboardingRef),
          transaction.get(welcomeMailRef),
        ]);
        if (
          employeeSnapshot.exists() ||
          onboardingSnapshot.exists() ||
          mailSnapshot.exists()
        ) {
          throw new Error("Is email ka employee record pehle se bana hua hai");
        }
        transaction.set(employeeRef, {
          ...employeeForm,
          email: employeeEmail,
          employeeCode,
          profileSource: "DIRECT_HR_ENTRY",
          profileRevision: 1,
          basic: number(employeeForm.basic),
          hra: number(employeeForm.hra),
          da: number(employeeForm.da),
          allowances: number(employeeForm.allowances),
          pf: number(employeeForm.pf),
          employerPf: number(employeeForm.employerPf),
          esi: number(employeeForm.esi),
          professionalTax: number(employeeForm.professionalTax),
          tdsMode,
          tds,
          tdsSalaryDeduction,
          companyBorneTds,
          otherDeduction: number(employeeForm.otherDeduction),
          salaryStructure: {
            basic: number(employeeForm.basic),
            hra: number(employeeForm.hra),
            da: number(employeeForm.da),
            allowances: number(employeeForm.allowances),
            pf: number(employeeForm.pf),
            employerPf: number(employeeForm.employerPf),
            esi: number(employeeForm.esi),
            professionalTax: number(employeeForm.professionalTax),
            tdsMode,
            tds,
            tdsSalaryDeduction,
            companyBorneTds,
            otherDeduction: number(employeeForm.otherDeduction),
            effectiveFrom: employeeForm.joiningDate || today(),
            updatedBy: profile.email,
          },
          offerLeavePolicy: {
            entitlements: {
              CL: Math.max(0, number(employeeForm.leaveCL)),
              EL: Math.max(0, number(employeeForm.leaveEL)),
              OL: Math.max(0, number(employeeForm.leaveOL)),
              COMP_OFF: Math.max(0, number(employeeForm.leaveCompOff)),
            },
            yearBasis: "ANNUAL",
            excessLeaveTreatment: "LOP",
            perDayDeductionBasis: "GROSS_DIVIDED_BY_WORKING_DAYS",
            source: "HR_EMPLOYEE_MASTER",
          },
          leaveBalances: {
            CL: Math.max(0, number(employeeForm.leaveCL)),
            EL: Math.max(0, number(employeeForm.leaveEL)),
            OL: Math.max(0, number(employeeForm.leaveOL)),
            COMP_OFF: Math.max(0, number(employeeForm.leaveCompOff)),
          },
          status: "ACTIVE",
          salaryStatus: "CONFIGURED",
          salaryConfigured: true,
          attendanceActive: false,
          documentStatus: "PENDING",
          createdAt: serverTimestamp(),
        });
        transaction.set(onboardingRef, {
          employeeId: employeeRef.id,
          employeeCode,
          employeeName: employeeForm.name,
          documentCollection: false,
          idCreated: true,
          salaryConfigured: true,
          roleMapped: Boolean(
            employeeForm.department && employeeForm.designation
          ),
          hierarchySetup: Boolean(employeeForm.managerName),
          attendanceActivated: false,
          status: "IN_PROGRESS",
          createdAt: serverTimestamp(),
        });
        transaction.set(welcomeMailRef, {
          type: "WELCOME_JOINING",
          from: OFFICIAL_EMAILS.HR,
          to: employeeEmail,
          cc: [],
          subject: "Welcome to Shine Agri",
          body: `Dear ${employeeForm.name}, your Employee ID is ${employeeCode}. Your onboarding process has started.`,
          relatedId: employeeRef.id,
          correlationId: employeeRef.id,
          payload: { employeeCode },
          status: "PENDING_SEND",
          createdByUid: user.uid || "",
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Employee master create nahi hua");
      await loadAll();
      return;
    }
    await audit("Employee master created", "EMPLOYEE", employeeRef.id);
    setEmployeeForm(emptyEmployee);
    notify(`Employee created: ${employeeCode}`);
    loadAll();
  };

  const updateEmployeeDetails = async (item, values) => {
    if (!canHR) {
      notify("HR access required");
      return false;
    }
    const employeeName = clean(values.name);
    const mobile = clean(values.mobile);
    const joiningDate = clean(values.joiningDate);
    const dateOfBirth = clean(values.dateOfBirth);
    const emergencyContactMobile = clean(values.emergencyContactMobile);
    const pincode = clean(values.pincode);
    if (!employeeName) {
      notify("Employee name required hai");
      return false;
    }
    if (mobile && !/^[0-9+\-\s]{7,18}$/.test(mobile)) {
      notify("Valid employee mobile number required hai");
      return false;
    }
    if (
      emergencyContactMobile &&
      !/^[0-9+\-\s]{7,18}$/.test(emergencyContactMobile)
    ) {
      notify("Valid emergency contact mobile required hai");
      return false;
    }
    if (pincode && !/^\d{6}$/.test(pincode)) {
      notify("Pincode exactly 6 digits ka hona chahiye");
      return false;
    }
    if (joiningDate && !isValidIsoDate(joiningDate)) {
      notify("Valid joining date required hai");
      return false;
    }
    if (
      dateOfBirth &&
      (!isValidIsoDate(dateOfBirth) || dateOfBirth > today())
    ) {
      notify("Valid date of birth required hai");
      return false;
    }
    const status = clean(values.status).toUpperCase();
    if (!["ACTIVE", "PROBATION", "ON_LEAVE", "INACTIVE"].includes(status)) {
      notify("Valid employee status select karein");
      return false;
    }

    const leaveInputs = {
      CL: values.leaveCL,
      EL: values.leaveEL,
      OL: values.leaveOL,
      COMP_OFF: values.leaveCompOff,
    };
    if (
      Object.values(leaveInputs).some(
        (value) => !Number.isFinite(Number(value)) || Number(value) < 0
      )
    ) {
      notify("Leave entitlement valid non-negative number hona chahiye");
      return false;
    }
    const nextEntitlements = Object.fromEntries(
      Object.entries(leaveInputs).map(([key, value]) => [key, number(value)])
    );
    const previousPolicy = getOfferLeavePolicy(item);
    const previousEntitlements =
      previousPolicy.entitlements ||
      previousPolicy.annualEntitlements ||
      previousPolicy.balances ||
      previousPolicy;
    const previousBalances = leaveBalancesFromEmployee(item);
    const defaultEntitlements = { CL: 12, EL: 18, OL: 6, COMP_OFF: 0 };
    const nextBalances = Object.fromEntries(
      Object.keys(nextEntitlements).map((key) => {
        if (key === "COMP_OFF") return [key, nextEntitlements[key]];
        const previousEntitlement = number(
          previousEntitlements?.[key] ??
            previousEntitlements?.[key.toLowerCase()] ??
            defaultEntitlements[key]
        );
        const consumed = Math.max(
          0,
          previousEntitlement - number(previousBalances[key])
        );
        return [key, Math.max(0, nextEntitlements[key] - consumed)];
      })
    );

    const profileUpdate = {
      name: employeeName,
      mobile,
      department: clean(values.department),
      designation: clean(values.designation),
      managerName: clean(values.managerName),
      joiningDate,
      dateOfBirth,
      gender: clean(values.gender),
      bloodGroup: clean(values.bloodGroup),
      currentAddress: clean(values.currentAddress),
      permanentAddress: clean(values.permanentAddress),
      address: clean(values.currentAddress),
      city: clean(values.city),
      taluka: clean(values.taluka),
      district: clean(values.district),
      state: clean(values.state),
      pincode,
      emergencyContactName: clean(values.emergencyContactName),
      emergencyContactMobile,
      highestQualification: clean(values.highestQualification),
      totalExperience: clean(values.totalExperience),
      status,
      offerLeavePolicy: {
        ...previousPolicy,
        entitlements: nextEntitlements,
        yearBasis: previousPolicy.yearBasis || "ANNUAL",
        excessLeaveTreatment:
          previousPolicy.excessLeaveTreatment || "LOP",
        perDayDeductionBasis:
          previousPolicy.perDayDeductionBasis ||
          "GROSS_DIVIDED_BY_WORKING_DAYS",
        source: "HR_EMPLOYEE_MASTER_UPDATE",
      },
      leaveBalances: nextBalances,
      profileRevision: number(item.profileRevision) + 1,
      profileUpdatedByUid: user.uid,
      profileUpdatedByName: profile.name,
      profileUpdatedByEmail: profile.email,
      profileUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const changedFields = [
      "name",
      "mobile",
      "department",
      "designation",
      "managerName",
      "joiningDate",
      "dateOfBirth",
      "gender",
      "bloodGroup",
      "currentAddress",
      "permanentAddress",
      "city",
      "taluka",
      "district",
      "state",
      "pincode",
      "emergencyContactName",
      "emergencyContactMobile",
      "highestQualification",
      "totalExperience",
      "status",
    ].filter((key) => clean(item[key]) !== clean(profileUpdate[key]));
    if (
      Object.keys(nextEntitlements).some(
        (key) =>
          number(previousEntitlements?.[key] ?? defaultEntitlements[key]) !==
          number(nextEntitlements[key])
      )
    ) {
      changedFields.push("leaveEntitlements");
    }
    if (Array.isArray(item._autoFilledFields)) {
      changedFields.push(...item._autoFilledFields.map((key) => `auto:${key}`));
    }

    try {
      const employeeRef = doc(db, COLLECTIONS.employees, item.id);
      const onboardingRef = doc(db, COLLECTIONS.onboarding, item.id);
      const onboardingSnapshot = await getDoc(onboardingRef);
      const onboardingData = onboardingSnapshot.exists()
        ? onboardingSnapshot.data()
        : {};
      const nextOnboarding = {
        ...onboardingData,
        employeeName,
        employeeCode: item.employeeCode || onboardingData.employeeCode || "",
        employeeId: item.id,
        roleMapped: Boolean(profileUpdate.department && profileUpdate.designation),
        hierarchySetup: Boolean(profileUpdate.managerName),
      };
      const onboardingDone = [
        "documentCollection",
        "idCreated",
        "salaryConfigured",
        "roleMapped",
        "hierarchySetup",
        "attendanceActivated",
      ].every((key) => Boolean(nextOnboarding[key]));
      const batch = writeBatch(db);
      batch.update(employeeRef, profileUpdate);
      batch.set(
        onboardingRef,
        {
          employeeId: item.id,
          employeeCode: item.employeeCode || "",
          employeeName,
          roleMapped: nextOnboarding.roleMapped,
          hierarchySetup: nextOnboarding.hierarchySetup,
          status: onboardingDone ? "COMPLETED" : "IN_PROGRESS",
          profileSyncedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      if (item.candidateUserId) {
        batch.set(
          doc(db, COLLECTIONS.candidateUsers, item.candidateUserId),
          {
            name: employeeName,
            mobile,
            department: profileUpdate.department,
            designation: profileUpdate.designation,
            managerName: profileUpdate.managerName,
            employeeId: item.id,
            employeeCode: item.employeeCode || "",
            employeeProfileSyncedAt: serverTimestamp(),
          },
          { merge: true }
        );
      }
      await batch.commit();
      await audit("Employee profile updated by HR", "EMPLOYEE", item.id, {
        employeeCode: item.employeeCode || "",
        changedFields,
        profileRevision: profileUpdate.profileRevision,
      });
      notify("Employee details update ho gayi aur connected records sync ho gaye");
      await loadAll();
      return true;
    } catch (error) {
      notify(error.message || "Employee details update nahi hui");
      return false;
    }
  };

  const convertApplicantToEmployee = async (application) => {
    if (!canHR) return notify("HR access required");
    if (application.status !== "CANDIDATE_ACCEPTED") {
      return notify("Candidate offer accept karne ke baad hi employee activate hoga");
    }
    const employeeEmail = clean(application.email).toLowerCase();
    if (!employeeEmail || !application.candidateUserId) {
      return notify("Verified candidate portal identity required hai");
    }
    const employeeCode = employeeCodeFromEmail(employeeEmail);
    const employeeRef = doc(
      db,
      COLLECTIONS.employees,
      employeeDocumentId(employeeEmail)
    );
    const onboardingRef = doc(
      db,
      COLLECTIONS.onboarding,
      employeeRef.id
    );
    const applicationRef = doc(
      db,
      COLLECTIONS.applications,
      application.id
    );
    const candidateRef = doc(
      db,
      COLLECTIONS.candidateUsers,
      application.candidateUserId
    );
    const activationMailRef = doc(
      db,
      COLLECTIONS.mail,
      `employee_activation_${employeeRef.id}`
    );
    try {
      await runTransaction(db, async (transaction) => {
        const [
          employeeSnapshot,
          onboardingSnapshot,
          applicationSnapshot,
          candidateSnapshot,
          mailSnapshot,
        ] = await Promise.all([
          transaction.get(employeeRef),
          transaction.get(onboardingRef),
          transaction.get(applicationRef),
          transaction.get(candidateRef),
          transaction.get(activationMailRef),
        ]);
        if (
          employeeSnapshot.exists() ||
          onboardingSnapshot.exists() ||
          mailSnapshot.exists()
        ) {
          throw new Error("Candidate employee me pehle hi convert ho chuka hai");
        }
        if (!applicationSnapshot.exists() || !candidateSnapshot.exists()) {
          throw new Error("Candidate application/login record nahi mila");
        }
        const currentApplication = applicationSnapshot.data();
        if (currentApplication.status !== "CANDIDATE_ACCEPTED") {
          throw new Error("Candidate acceptance pending hai");
        }
        const offerPackage = currentApplication.offerPackage || {};
        const approvedSalary = offerPackage.salaryStructure || {};
        const approvedGross = roundMoney(
          number(approvedSalary.basic) +
            number(approvedSalary.hra) +
            number(approvedSalary.da) +
            number(approvedSalary.allowances)
        );
        if (
          offerPackage.status !== "APPROVED_BY_CEO" ||
          !currentApplication.salaryApprovedForPayroll ||
          approvedGross <= 0 ||
          !offerPackage.tdsCompliance ||
          !offerPackage.pfCompliance
        ) {
          throw new Error("CEO-approved salary, TDS aur PF package required hai");
        }
        const candidateProfile = candidateSnapshot.data();
        if (candidateProfile.status === "BLOCKED") {
          throw new Error("Blocked candidate ko employee nahi bana sakte");
        }
        const autoProfile = employeeProfileFromApplication(
          currentApplication,
          candidateProfile
        );
        const employeeName = autoProfile.name;
        if (!employeeName) {
          throw new Error("Candidate ka verified name employee master ke liye required hai");
        }
        const offerLeavePolicy = getOfferLeavePolicy(currentApplication);
        const hasOfferLeavePolicy = Object.keys(offerLeavePolicy).length > 0;
        transaction.set(employeeRef, {
          ...autoProfile,
          email: employeeEmail,
          employeeCode,
          sourceApplicationId: application.id,
          candidateUserId: application.candidateUserId,
          profileSource: "SELECTED_CANDIDATE",
          profileRevision: 1,
          profileAutoFilledAt: serverTimestamp(),
          ...(hasOfferLeavePolicy
            ? {
                offerLeavePolicy,
                leaveBalances: leaveBalancesFromEmployee({
                  offerLeavePolicy,
                }),
              }
            : {}),
          joiningDate: offerPackage.joiningDate || autoProfile.joiningDate || "",
          basic: number(approvedSalary.basic),
          hra: number(approvedSalary.hra),
          da: number(approvedSalary.da),
          allowances: number(approvedSalary.allowances),
          pf: number(approvedSalary.pf),
          employerPf: number(approvedSalary.employerPf),
          esi: number(approvedSalary.esi),
          professionalTax: number(approvedSalary.professionalTax),
          tdsMode: normalizeTdsMode(approvedSalary.tdsMode),
          tds: number(approvedSalary.tds),
          tdsSalaryDeduction: number(approvedSalary.tdsSalaryDeduction),
          companyBorneTds: number(approvedSalary.companyBorneTds),
          otherDeduction: number(approvedSalary.otherDeduction),
          salaryStructure: {
            ...approvedSalary,
            gross: approvedGross,
            effectiveFrom:
              offerPackage.joiningDate || approvedSalary.effectiveFrom || today(),
            source: "CEO_APPROVED_OFFER_PACKAGE",
            sourceApplicationId: application.id,
          },
          pfCompliance: offerPackage.pfCompliance,
          tdsCompliance: offerPackage.tdsCompliance,
          offerLetterStoragePath: offerPackage.offerLetterStoragePath,
          offerLetterFileName: offerPackage.offerLetterFileName,
          status: "ACTIVE",
          salaryStatus: "CEO_APPROVED_CONFIGURED",
          salaryConfigured: true,
          attendanceActive: false,
          documentStatus: "PENDING",
          createdAt: serverTimestamp(),
        });
        transaction.set(onboardingRef, {
          employeeId: employeeRef.id,
          employeeCode,
          employeeName,
          candidateUserId: application.candidateUserId,
          documentCollection: false,
          idCreated: true,
          roleMapped: Boolean(
            autoProfile.department && autoProfile.designation
          ),
          hierarchySetup: Boolean(autoProfile.managerName),
          attendanceActivated: false,
          salaryConfigured: true,
          status: "IN_PROGRESS",
          createdAt: serverTimestamp(),
        });
        transaction.update(applicationRef, {
          status: "EMPLOYEE_CREATED",
          employeeId: employeeRef.id,
          employeeCode,
          convertedBy: profile.name,
          convertedByUid: user.uid,
          convertedByEmail: profile.email,
          convertedAt: serverTimestamp(),
        });
        transaction.update(candidateRef, {
          status: "ACTIVE",
          portalMode: "EMPLOYEE",
          employeeId: employeeRef.id,
          employeeCode,
          convertedAt: serverTimestamp(),
        });
        transaction.set(activationMailRef, {
          type: "EMPLOYEE_ACCOUNT_ACTIVATED",
          from: OFFICIAL_EMAILS.HR,
          to: employeeEmail,
          cc: [OFFICIAL_EMAILS.HR, OFFICIAL_EMAILS.ACCOUNT],
          bcc: [OFFICIAL_EMAILS.CEO],
          subject: "Employee Portal Activated",
          body: `Congratulations. Your accepted offer has been converted to an active Employee Portal account. Employee ID: ${employeeCode}. The CEO-approved salary, Section 192 TDS and PF setup is now connected to payroll.`,
          relatedId: employeeRef.id,
          correlationId: application.id,
          payload: { employeeCode, applicationId: application.id },
          status: "PENDING_SEND",
          createdByUid: user.uid || "",
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Candidate employee me convert nahi hua");
      await loadAll();
      return;
    }
    await audit("Candidate converted to employee", "EMPLOYEE", employeeRef.id);
    notify(`Employee created: ${employeeCode}`);
    loadAll();
  };

  const updateEmployeeBank = async (item) => {
    if (!canHR) return notify("HR access required");
    const accountHolderName = window.prompt(
      "Bank account holder name",
      item.accountHolderName || item.name || ""
    );
    if (accountHolderName === null) return;
    const bankName = window.prompt("Bank name", item.bankName || "");
    if (bankName === null) return;
    if (!clean(accountHolderName) || !clean(bankName)) {
      return notify("Account holder aur bank name required hai");
    }
    const accountNumber = clean(
      window.prompt("Bank account number (9–18 digits)", item.accountNumber || "")
    );
    if (!/^[0-9]{9,18}$/.test(accountNumber)) {
      return notify("Valid 9–18 digit bank account number required hai");
    }
    const ifscCode = clean(
      window.prompt("IFSC code", item.ifscCode || "")
    ).toUpperCase();
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      return notify("Valid IFSC code required hai, example SBIN0001234");
    }
    const bankBranch = window.prompt("Bank branch", item.bankBranch || "");
    if (bankBranch === null) return;
    await updateDoc(doc(db, COLLECTIONS.employees, item.id), {
      accountHolderName: clean(accountHolderName),
      bankName: clean(bankName),
      accountNumber,
      ifscCode,
      bankBranch: clean(bankBranch),
      bankUpdatedBy: profile.name,
      bankUpdatedByUid: user.uid,
      bankUpdatedByEmail: profile.email,
      bankUpdatedAt: serverTimestamp(),
    });
    await audit("Employee bank details updated", "EMPLOYEE", item.id, {
      employeeCode: item.employeeCode || "",
      bankAccountLast4: accountNumber.slice(-4),
      ifscCode,
    });
    notify("Employee bank details update ho gayi");
    loadAll();
  };

  const updateAttendanceLocation = async (item) => {
    if (!canHR) return notify("HR access required");
    const existing =
      item.attendanceLocation || item.officeLocation || {};
    const latitudeInput = window.prompt(
      "Office latitude (example 23.0225)",
      String(existing.latitude ?? item.officeLatitude ?? "")
    );
    if (latitudeInput === null) return;
    const longitudeInput = window.prompt(
      "Office longitude (example 72.5714)",
      String(existing.longitude ?? item.officeLongitude ?? "")
    );
    if (longitudeInput === null) return;
    const radiusInput = window.prompt(
      "Allowed attendance radius in metres (20–5000)",
      String(existing.radiusMeters ?? item.attendanceRadiusMeters ?? 150)
    );
    if (radiusInput === null) return;
    const labelInput = window.prompt(
      "Office / site name",
      String(existing.label || item.officeLocationName || "Assigned Office")
    );
    if (labelInput === null) return;

    const latitude = Number(latitudeInput);
    const longitude = Number(longitudeInput);
    const radiusMeters = Math.round(Number(radiusInput));
    if (!Number.isFinite(latitude) || latitude < -90 || latitude > 90) {
      return notify("Latitude -90 se 90 ke beech honi chahiye");
    }
    if (!Number.isFinite(longitude) || longitude < -180 || longitude > 180) {
      return notify("Longitude -180 se 180 ke beech honi chahiye");
    }
    if (
      !Number.isFinite(radiusMeters) ||
      radiusMeters < 20 ||
      radiusMeters > 5000
    ) {
      return notify("Attendance radius 20 se 5000 metres ke beech rakhein");
    }

    const attendanceLocation = {
      latitude,
      longitude,
      radiusMeters,
      label: clean(labelInput) || "Assigned Office",
    };
    await updateDoc(doc(db, COLLECTIONS.employees, item.id), {
      attendanceLocation,
      officeLatitude: latitude,
      officeLongitude: longitude,
      attendanceRadiusMeters: radiusMeters,
      officeLocationName: attendanceLocation.label,
      attendanceLocationUpdatedByUid: user.uid,
      attendanceLocationUpdatedByEmail: profile.email,
      attendanceLocationUpdatedAt: serverTimestamp(),
    });
    await audit("Employee attendance geofence updated", "EMPLOYEE", item.id, {
      employeeCode: item.employeeCode || "",
      officeLabel: attendanceLocation.label,
      radiusMeters,
    });
    notify("Attendance office location aur geofence save ho gaya");
    loadAll();
  };

  const resetAttendanceDevice = async (item) => {
    if (!canHR) return notify("HR access required");
    if (
      !window.confirm(
        `${item.name || "Employee"} ka registered attendance device reset karein? Agla check-in HR review me aayega.`
      )
    ) {
      return;
    }
    await updateDoc(doc(db, COLLECTIONS.employees, item.id), {
      attendanceDeviceFingerprintHash: "",
      allowedAttendanceDeviceHash: "",
      attendanceDeviceResetByUid: user.uid,
      attendanceDeviceResetByEmail: profile.email,
      attendanceDeviceResetAt: serverTimestamp(),
    });
    await audit("Employee attendance device reset", "EMPLOYEE", item.id, {
      employeeCode: item.employeeCode || "",
    });
    notify("Attendance device reset ho gaya; next device HR approval se bind hoga");
    loadAll();
  };

  const updateEmployeeSalary = async (item) => {
    if (!canHR) return notify("HR access required");
    const fields = [
      ["basic", "Basic Salary"],
      ["hra", "HRA"],
      ["da", "DA"],
      ["allowances", "Allowances"],
      ["pf", "Employee PF Deduction"],
      ["employerPf", "Employer PF Contribution"],
      ["esi", "Employee ESI Deduction"],
      ["professionalTax", "Professional Tax"],
      ["tds", "Monthly TDS Amount"],
      ["otherDeduction", "Other Monthly Deduction"],
    ];
    const values = {};
    for (const [key, label] of fields) {
      const input = window.prompt(label, String(item[key] ?? item.salaryStructure?.[key] ?? 0));
      if (input === null) return;
      if (!Number.isFinite(Number(input)) || Number(input) < 0) {
        return notify(`${label} valid non-negative amount hona chahiye`);
      }
      values[key] = roundMoney(input);
    }
    const currentTdsMode = normalizeTdsMode(
      item.tdsMode || item.salaryStructure?.tdsMode
    );
    const tdsModeInput = window.prompt(
      "TDS treatment: SALARY_DEDUCTION / COMPANY_BORNE / NOT_APPLICABLE",
      currentTdsMode
    );
    if (tdsModeInput === null) return;
    const normalizedTdsInput = clean(tdsModeInput)
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
    const allowedTdsInputs = [
      "SALARY_DEDUCTION",
      "COMPANY_BORNE",
      "COMPANY_PAID",
      "PAID_BY_COMPANY",
      "NOT_APPLICABLE",
      "NA",
      "NONE",
      "NO_TDS",
    ];
    if (!allowedTdsInputs.includes(normalizedTdsInput)) {
      return notify(
        "TDS treatment SALARY_DEDUCTION, COMPANY_BORNE ya NOT_APPLICABLE hona chahiye"
      );
    }
    const tdsMode = normalizeTdsMode(normalizedTdsInput);
    if (tdsMode === TDS_MODE.NOT_APPLICABLE) values.tds = 0;
    const tdsSalaryDeduction =
      tdsMode === TDS_MODE.SALARY_DEDUCTION ? values.tds : 0;
    const companyBorneTds =
      tdsMode === TDS_MODE.COMPANY_BORNE ? values.tds : 0;
    const effectiveFrom = clean(
      window.prompt("Salary effective date (YYYY-MM-DD)", today())
    );
    if (!isValidIsoDate(effectiveFrom)) {
      return notify("Valid salary effective date required hai");
    }
    const gross = roundMoney(
      values.basic + values.hra + values.da + values.allowances
    );
    if (gross <= 0) return notify("Gross salary zero nahi ho sakti");
    if (
      !window.confirm(
        `New gross salary ${money(gross)} save karein?\nTDS: ${tdsModeLabel(
          tdsMode
        )} (${money(values.tds)})`
      )
    )
      return;
    const employeeRef = doc(db, COLLECTIONS.employees, item.id);
    const onboardingRef = doc(db, COLLECTIONS.onboarding, item.id);
    const onboardingSnapshot = await getDoc(onboardingRef);
    const onboardingData = onboardingSnapshot.exists()
      ? onboardingSnapshot.data()
      : {};
    const onboardingCompleteAfterSalary = [
      "documentCollection",
      "idCreated",
      "roleMapped",
      "hierarchySetup",
      "attendanceActivated",
    ].every((key) => Boolean(onboardingData[key]));
    const salaryHistoryRef = doc(collection(db, "salary_history"));
    const salaryBatch = writeBatch(db);
    salaryBatch.set(salaryHistoryRef, {
      employeeId: item.id,
      employeeCode: item.employeeCode || "",
      employeeName: item.name || "",
      previousStructure: item.salaryStructure || {
        basic: number(item.basic),
        hra: number(item.hra),
        da: number(item.da),
        allowances: number(item.allowances),
        pf: number(item.pf),
        employerPf: number(item.employerPf),
        esi: number(item.esi),
        professionalTax: number(item.professionalTax),
        tdsMode: normalizeTdsMode(
          item.tdsMode || item.salaryStructure?.tdsMode
        ),
        tds: number(item.tds),
        otherDeduction: number(item.otherDeduction),
      },
      newStructure: {
        ...values,
        gross,
        tdsMode,
        tdsSalaryDeduction,
        companyBorneTds,
      },
      effectiveFrom,
      changedByUid: user.uid,
      changedByEmail: profile.email,
      createdAt: serverTimestamp(),
    });
    salaryBatch.update(employeeRef, {
      ...values,
      tdsMode,
      tdsSalaryDeduction,
      companyBorneTds,
      salaryStructure: {
        ...values,
        gross,
        tdsMode,
        tdsSalaryDeduction,
        companyBorneTds,
        effectiveFrom,
        updatedBy: profile.email,
        updatedByUid: user.uid,
        updatedAt: serverTimestamp(),
      },
      salaryEffectiveFrom: effectiveFrom,
      salaryStatus: "CONFIGURED",
      salaryConfigured: true,
      salaryUpdatedByUid: user.uid,
      salaryUpdatedByEmail: profile.email,
      salaryUpdatedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    salaryBatch.set(
      onboardingRef,
      {
        salaryConfigured: true,
        salaryConfiguredByUid: user.uid,
        salaryConfiguredByEmail: profile.email,
        salaryConfiguredAt: serverTimestamp(),
        status: onboardingCompleteAfterSalary ? "COMPLETED" : "IN_PROGRESS",
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    await salaryBatch.commit();
    await audit("Employee salary structure updated", "EMPLOYEE", item.id, {
      employeeCode: item.employeeCode || "",
      effectiveFrom,
      gross,
      tdsMode,
      tds: values.tds,
    });
    notify("Employee salary structure update ho gaya");
    loadAll();
  };

  const setAttendancePin = async (item) => {
    if (!canHR) return notify("HR access required");
    const pin = clean(
      window.prompt(
        `${item.name || "Employee"} ke liye new 6-digit Attendance PIN`,
        ""
      )
    );
    if (!/^\d{6}$/.test(pin)) {
      return notify("Exactly 6-digit Attendance PIN required hai");
    }
    const confirmation = clean(
      window.prompt("Security confirmation ke liye same PIN dobara likhein", "")
    );
    if (pin !== confirmation) return notify("Dono PIN match nahi kar rahe");
    setLoading(true);
    try {
      const setPin = httpsCallable(
        interviewFunctions,
        "setEmployeeAttendancePin"
      );
      await setPin({ employeeId: item.id, pin });
      notify("Attendance PIN securely set ho gaya; PIN employee ko privately dein");
      await loadAll();
    } catch (error) {
      notify(error.message || "Attendance PIN set nahi hua");
    } finally {
      setLoading(false);
    }
  };

  const toggleOnboarding = async (item, field) => {
    if (!canHR) return notify("HR access required");
    const next = !item[field];
    const updated = { ...item, [field]: next };
    const done = [
      "documentCollection",
      "idCreated",
      "salaryConfigured",
      "roleMapped",
      "hierarchySetup",
      "attendanceActivated",
    ].every((key) => Boolean(updated[key]));
    await updateDoc(doc(db, COLLECTIONS.onboarding, item.id), {
      [field]: next,
      status: done ? "COMPLETED" : "IN_PROGRESS",
      updatedAt: serverTimestamp(),
    });
    if (field === "attendanceActivated" && item.employeeId) {
      await updateDoc(doc(db, COLLECTIONS.employees, item.employeeId), {
        attendanceActive: next,
      });
    }
    await audit(`Onboarding ${field} ${next ? "completed" : "reopened"}`, "ONBOARDING", item.id);
    loadAll();
  };

  const saveHoliday = async () => {
    if (!canHR) return notify("HR/CEO access required");
    const date = clean(holidayForm.date);
    const name = clean(holidayForm.name);
    if (!isValidIsoDate(date) || !name) {
      return notify("Valid holiday date aur holiday name required hai");
    }
    const existing = data.holidays.find(
      (item) => !item.isDeleted && clean(item.date) === date
    );
    const holidayId =
      clean(holidayForm.id) ||
      existing?.id ||
      "holiday_" + date.replaceAll("-", "_");
    await setDoc(
      doc(db, COLLECTIONS.holidays, holidayId),
      {
        date,
        name,
        type: clean(holidayForm.type).toUpperCase() || "FESTIVAL",
        paid: holidayForm.paid !== false,
        isPaidHoliday: holidayForm.paid !== false,
        isWorkingDay: false,
        notes: clean(holidayForm.notes),
        source: clean(holidayForm.source) || "COMPANY_CALENDAR",
        status: "ACTIVE",
        updatedByUid: user.uid,
        updatedByName: profile.name,
        updatedByEmail: profile.email,
        updatedAt: serverTimestamp(),
        ...(existing
          ? {}
          : {
              createdByUid: user.uid,
              createdByName: profile.name,
              createdByEmail: profile.email,
              createdAt: serverTimestamp(),
            }),
      },
      { merge: true }
    );
    await audit(
      existing ? "Holiday calendar updated" : "Holiday calendar created",
      "HOLIDAY",
      holidayId,
      { date, name }
    );
    setHolidayForm(emptyHoliday);
    notify(existing ? "Holiday update ho gayi" : "Holiday calendar me add ho gayi");
    await loadAll();
  };

  const importGujarat2026Holidays = async () => {
    if (!canHR) return notify("HR/CEO access required");
    if (
      !window.confirm(
        "Gujarat GAD 2026 ki 23 general holidays company calendar me import karein? Existing same-date entry safely update hogi."
      )
    ) {
      return;
    }
    const batch = writeBatch(db);
    GUJARAT_2026_GENERAL_HOLIDAYS.forEach(([date, name]) => {
      const existing = data.holidays.find(
        (item) => !item.isDeleted && clean(item.date) === date
      );
      const holidayId =
        existing?.id || "holiday_" + date.replaceAll("-", "_");
      batch.set(
        doc(db, COLLECTIONS.holidays, holidayId),
        {
          date,
          name,
          type: "PUBLIC_HOLIDAY",
          paid: true,
          isPaidHoliday: true,
          isWorkingDay: false,
          status: "ACTIVE",
          source: "GUJARAT_GAD_2026",
          sourceNotification: "GS/22/2025/JSR/2025/509/GH",
          sourceUrl: "https://gad.gujarat.gov.in/personnel/holidays.htm",
          updatedByUid: user.uid,
          updatedByName: profile.name,
          updatedByEmail: profile.email,
          updatedAt: serverTimestamp(),
          ...(!existing
            ? {
                createdByUid: user.uid,
                createdByName: profile.name,
                createdByEmail: profile.email,
                createdAt: serverTimestamp(),
              }
            : {}),
        },
        { merge: true }
      );
    });
    await batch.commit();
    await audit(
      "Gujarat GAD 2026 holiday template imported",
      "HOLIDAY_CALENDAR",
      "GUJARAT_2026",
      { importedDates: GUJARAT_2026_GENERAL_HOLIDAYS.length }
    );
    notify("Gujarat 2026 official holiday calendar import ho gaya");
    await loadAll();
  };

  const savePayrollCalendarSettings = async ({
    weeklyOffDays,
    saturdayPolicy,
  }) => {
    if (!canHR) return notify("HR/CEO access required");
    const normalisedDays = [
      ...new Set(
        (Array.isArray(weeklyOffDays) ? weeklyOffDays : [])
          .map(Number)
          .filter(
            (value) =>
              Number.isInteger(value) && value >= 0 && value <= 6
          )
      ),
    ];
    if (!normalisedDays.length && saturdayPolicy === "NONE") {
      return notify("Kam se kam ek weekly off ya Saturday policy select karein");
    }
    await setDoc(
      doc(db, COLLECTIONS.settings, "payroll"),
      {
        settingType: "PAYROLL",
        weeklyOffDays: normalisedDays,
        saturdayPolicy: ["NONE", "ALL", "SECOND_FOURTH"].includes(
          saturdayPolicy
        )
          ? saturdayPolicy
          : "NONE",
        timeZone: "Asia/Kolkata",
        calendarCalculation: "ACTUAL_MONTH_DAYS_MINUS_WEEKLY_OFF_AND_HOLIDAYS",
        updatedByUid: user.uid,
        updatedByName: profile.name,
        updatedByEmail: profile.email,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
    await audit(
      "Payroll calendar settings updated",
      "HRMS_SETTING",
      "payroll",
      { weeklyOffDays: normalisedDays, saturdayPolicy }
    );
    notify("Weekly off aur payroll calendar setting save ho gayi");
    await loadAll();
  };

  const submitLeave = async () => {
    if (!clean(leaveForm.employeeName) || !leaveForm.fromDate || !leaveForm.toDate)
      return notify("Employee aur leave dates required hain");
    const calendarDates = enumerateDateRange(
      leaveForm.fromDate,
      leaveForm.toDate
    );
    const leaveDates = chargeableLeaveDates(
      leaveForm.fromDate,
      leaveForm.toDate,
      data.holidays,
      data.settings
    );
    if (!leaveDates.length || leaveDates.length > 365) {
      return notify(
        "Selected range me koi working day nahi hai; weekly off/holiday par leave deduct nahi hogi"
      );
    }
    const requestedDays = leaveDates.length;
    const leaveRef = doc(collection(db, COLLECTIONS.leaves));
    const mailRef = doc(collection(db, COLLECTIONS.mail));
    const batch = writeBatch(db);
    batch.set(leaveRef, {
      ...leaveForm,
      days: requestedDays,
      chargeableDates: leaveDates,
      calendarSpanDays: calendarDates.length,
      excludedWeeklyOffOrHolidayDays:
        calendarDates.length - leaveDates.length,
      status: "PENDING_HR_APPROVAL",
      lopDays: 0,
      createdAt: serverTimestamp(),
    });
    batch.set(mailRef, {
      type: "LEAVE_APPROVAL_REQUEST",
      from: OFFICIAL_EMAILS.HR,
      to: OFFICIAL_EMAILS.HR,
      cc: [],
      subject: `Leave approval: ${leaveForm.employeeName}`,
      body: `${leaveForm.employeeName} requested ${requestedDays} day(s) ${leaveForm.type} leave.`,
      relatedId: leaveRef.id,
      correlationId: leaveRef.id,
      payload: {},
      status: "PENDING_SEND",
      createdByUid: user.uid || "",
      createdByName: profile.name,
      createdByEmail: profile.email,
      createdAt: serverTimestamp(),
    });
    await batch.commit();
    await audit("Leave request submitted", "LEAVE", leaveRef.id);
    setLeaveForm(emptyLeave);
    notify("Leave request submit ho gayi");
    loadAll();
  };

  const leaveDecision = async (item, stage, decision) => {
    if (stage === "HR" && !isHRAdmin) return notify("HR access required");
    if (stage === "CEO" && !canCEO) return notify("CEO approval required");
    if (stage === "CEO_OVERRIDE" && !canCEO) {
      return notify("CEO Master access required");
    }
    const leaveRef = doc(db, COLLECTIONS.leaves, item.id);
    const forwardApprovalRef = doc(
      db,
      COLLECTIONS.approvals,
      `leave_exception_${item.id}`
    );
    const forwardMailRef = doc(
      db,
      COLLECTIONS.mail,
      `leave_exception_${item.id}`
    );
    let finalStatus = "";
    let finalApprovalRequestId = "";
    let finalLeave = item;
    try {
      await runTransaction(db, async (transaction) => {
        const leaveSnapshot = await transaction.get(leaveRef);
        if (!leaveSnapshot.exists()) {
          throw new Error("Leave record nahi mila");
        }
        const currentLeave = {
          id: leaveSnapshot.id,
          ...leaveSnapshot.data(),
        };
        finalLeave = currentLeave;
        const expectedStatus =
          stage === "CEO"
            ? "PENDING_CEO_APPROVAL"
            : "PENDING_HR_APPROVAL";
        if (
          stage === "CEO"
            ? !isPendingCEOApproval(currentLeave.status)
            : currentLeave.status !== expectedStatus
        ) {
          throw new Error("Leave decision pehle hi process ho chuka hai");
        }
        const leaveDates = chargeableLeaveDates(
          currentLeave.fromDate,
          currentLeave.toDate,
          data.holidays,
          data.settings
        );
        if (!leaveDates.length || leaveDates.length > 365) {
          throw new Error(
            "Leave range me working day nahi mila; weekly off/holiday automatically excluded hai"
          );
        }
        const requestedDays = leaveDates.length;
        let employeeRef = null;
        let employeeRecord = null;
        let balances = null;
        let paidLeaveDays = 0;
        let lopDays = 0;
        let lopDates = [];
        let leavePolicySource = "NOT_APPLICABLE";
        if (decision !== "REJECTED" && currentLeave.type === "LOP") {
          lopDays = requestedDays;
          lopDates = leaveDates.slice(0, Math.ceil(lopDays));
          leavePolicySource = "EXPLICIT_LOP";
        } else if (
          decision !== "REJECTED" &&
          currentLeave.employeeId &&
          currentLeave.type !== "LOP"
        ) {
          employeeRef = doc(
            db,
            COLLECTIONS.employees,
            currentLeave.employeeId
          );
          const employeeSnapshot = await transaction.get(employeeRef);
          if (!employeeSnapshot.exists()) {
            throw new Error("Employee leave-balance record nahi mila");
          }
          employeeRecord = employeeSnapshot.data();
          balances = leaveBalancesFromEmployee(employeeRecord);
          const available = Math.max(
            0,
            number(balances[currentLeave.type])
          );
          paidLeaveDays = Math.min(available, requestedDays);
          lopDays = Math.max(0, requestedDays - paidLeaveDays);
          lopDates = leaveDates.slice(
            paidLeaveDays,
            paidLeaveDays + Math.ceil(lopDays)
          );
          leavePolicySource = employeeRecord.leaveBalances
            ? "CURRENT_BALANCE"
            : Object.keys(getOfferLeavePolicy(employeeRecord)).length
            ? "OFFER_LEAVE_POLICY"
            : "SYSTEM_DEFAULT";
        }
        const requiresCEO =
          decision !== "REJECTED" &&
          (currentLeave.type === "LOP" ||
            requestedDays > 3 ||
            lopDays > 0 ||
            currentLeave.requiresCEOApproval === true);
        const forwardToCEO =
          stage === "HR" &&
          decision !== "REJECTED" &&
          requiresCEO;
        finalStatus =
          decision === "REJECTED"
            ? "REJECTED"
            : forwardToCEO
            ? "PENDING_CEO_APPROVAL"
            : "APPROVED";
        let approvalRef = null;
        let approvalSnapshot = null;
        if (stage === "CEO" && currentLeave.approvalRequestId) {
          approvalRef = doc(
            db,
            COLLECTIONS.approvals,
            currentLeave.approvalRequestId
          );
          approvalSnapshot = await transaction.get(approvalRef);
          if (
            !approvalSnapshot.exists() ||
            !isPendingCEOApproval(approvalSnapshot.data().status)
          ) {
            throw new Error("Linked CEO leave approval pending nahi hai");
          }
        }
        if (forwardToCEO) {
          const [existingApproval, existingMail] = await Promise.all([
            transaction.get(forwardApprovalRef),
            transaction.get(forwardMailRef),
          ]);
          if (existingApproval.exists() || existingMail.exists()) {
            throw new Error("Leave exception pehle hi CEO ko forward hui hai");
          }
          approvalRef = forwardApprovalRef;
        }
        let balanceUpdate = null;
        if (
          finalStatus === "APPROVED" &&
          employeeRef &&
          balances &&
          currentLeave.type !== "LOP"
        ) {
          const available = Math.max(
            0,
            number(balances[currentLeave.type])
          );
          balances[currentLeave.type] = Math.max(
            0,
            available - paidLeaveDays
          );
          balanceUpdate = {
            leaveBalances: balances,
            leaveBalanceUpdatedAt: serverTimestamp(),
          };
        }
        finalApprovalRequestId = forwardToCEO
          ? forwardApprovalRef.id
          : currentLeave.approvalRequestId || "";
        const actionKey =
          stage === "CEO_OVERRIDE" ? "ceoOverride" : stage.toLowerCase();
        transaction.update(leaveRef, {
          status: finalStatus,
          days: requestedDays,
          chargeableDates: leaveDates,
          lopDays,
          lopDates,
          paidLeaveDays,
          leavePolicySource,
          approvalRequestId: finalApprovalRequestId,
          requiresCEOApproval: requiresCEO,
          [`${actionKey}ActionBy`]: profile.name,
          [`${actionKey}ActionByUid`]: user.uid,
          [`${actionKey}ActionByEmail`]: profile.email,
          [`${actionKey}ActionAt`]: serverTimestamp(),
        });
        if (employeeRef && balanceUpdate) {
          transaction.update(employeeRef, balanceUpdate);
        }
        if (forwardToCEO) {
          transaction.set(forwardApprovalRef, {
            type: "LEAVE_EXCEPTION",
            relatedId: currentLeave.id,
            employeeName: currentLeave.employeeName,
            title: `${currentLeave.type} • ${requestedDays} day(s) • LOP ${lopDays}`,
            lopDays,
            requestedBy: profile.name,
            requestedByUid: user.uid,
            requestedByEmail: profile.email,
            status: "PENDING_CEO_APPROVAL",
            createdAt: serverTimestamp(),
          });
          transaction.set(forwardMailRef, {
            type: "CEO_LEAVE_EXCEPTION_APPROVAL",
            from: OFFICIAL_EMAILS.HR,
            to: OFFICIAL_EMAILS.CEO,
            cc: [OFFICIAL_EMAILS.HR],
            subject: `CEO Leave Approval - ${currentLeave.employeeName}`,
            body: `${currentLeave.employeeName} ki ${
              currentLeave.type
            } leave (${requestedDays} days) HR ne review karke CEO approval ke liye forward ki hai. Derived LOP: ${lopDays} day(s). Leave source: ${leavePolicySource}.`,
            relatedId: currentLeave.id,
            correlationId: forwardApprovalRef.id,
            payload: {},
            status: "PENDING_SEND",
            createdByUid: user.uid || "",
            createdByName: profile.name,
            createdByEmail: profile.email,
            createdAt: serverTimestamp(),
          });
        } else if (stage === "CEO" && approvalRef && approvalSnapshot) {
          transaction.update(approvalRef, {
            status:
              finalStatus === "APPROVED"
                ? "APPROVED_BY_CEO"
                : "REJECTED",
            actionByUid: user.uid || "",
            actionBy: profile.name,
            actionByEmail: profile.email,
            actionAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Leave decision save nahi hua");
      await loadAll();
      return;
    }
    if (stage === "CEO" && !finalApprovalRequestId) {
      await closeRelatedApproval(
        "LEAVE_EXCEPTION",
        item.id,
        finalStatus === "APPROVED" ? "APPROVED_BY_CEO" : "REJECTED"
      );
    }
    await audit(
      `${stage} leave decision ${finalStatus}`,
      "LEAVE",
      item.id,
      {
        employeeName: finalLeave.employeeName || "",
        leaveType: finalLeave.type || "",
      }
    );
    notify(`Leave ${finalStatus}`);
    loadAll();
  };

  const attendanceDecision = async (item, decision) => {
    if (!canHR) return notify("HR/CEO access required");
    const normalizedDecision = clean(decision).toUpperCase();
    if (!["APPROVED", "REJECTED"].includes(normalizedDecision)) {
      return notify("Attendance decision valid nahi hai");
    }
    const reviewReason =
      normalizedDecision === "REJECTED"
        ? window.prompt("Attendance rejection/invalid reason (required)", "")
        : "";
    if (normalizedDecision === "REJECTED" && reviewReason === null) return;
    if (
      normalizedDecision === "REJECTED" &&
      !clean(reviewReason)
    ) {
      return notify("Attendance reject karne ke liye reason required hai");
    }
    const attendanceRef = doc(db, COLLECTIONS.attendance, item.id);
    try {
      await runTransaction(db, async (transaction) => {
        const attendanceSnapshot = await transaction.get(attendanceRef);
        if (!attendanceSnapshot.exists()) {
          throw new Error("Attendance record nahi mila");
        }
        const currentAttendance = attendanceSnapshot.data();
        const reviewStatus = clean(
          currentAttendance.hrReviewStatus || "PENDING_REVIEW"
        ).toUpperCase();
        if (
          ![
            "PENDING",
            "PENDING_REVIEW",
            "PENDING_HR_REVIEW",
          ].includes(reviewStatus)
        ) {
          throw new Error("Attendance review pehle hi process ho chuka hai");
        }
        let employeeRef = null;
        let employeeSnapshot = null;
        if (currentAttendance.employeeId) {
          employeeRef = doc(
            db,
            COLLECTIONS.employees,
            currentAttendance.employeeId
          );
          employeeSnapshot = await transaction.get(employeeRef);
        }
        const capturedDeviceHash =
          currentAttendance.checkInDeviceFingerprintHash ||
          currentAttendance.deviceFingerprintHash ||
          currentAttendance.checkOutDevice?.fingerprintHash ||
          "";
        if (
          normalizedDecision === "APPROVED" &&
          employeeSnapshot?.exists() &&
          capturedDeviceHash
        ) {
          const registeredDeviceHash =
            employeeSnapshot.data().attendanceDeviceFingerprintHash ||
            employeeSnapshot.data().allowedAttendanceDeviceHash ||
            "";
          if (
            registeredDeviceHash &&
            registeredDeviceHash !== capturedDeviceHash
          ) {
            throw new Error(
              "Captured device registered employee device se match nahi karta"
            );
          }
          if (!registeredDeviceHash) {
            transaction.update(employeeRef, {
              attendanceDeviceFingerprintHash: capturedDeviceHash,
              attendanceDeviceRegisteredByUid: user.uid || "",
              attendanceDeviceRegisteredByEmail: profile.email,
              attendanceDeviceRegisteredAt: serverTimestamp(),
            });
          }
        }
        transaction.update(attendanceRef, {
          hrReviewStatus: normalizedDecision,
          ...(normalizedDecision === "APPROVED"
            ? {
                deviceVerified: Boolean(capturedDeviceHash),
                deviceBindingStatus: capturedDeviceHash
                  ? "REGISTERED_DEVICE"
                  : "NO_DEVICE_EVIDENCE",
              }
            : {}),
          ...(normalizedDecision === "REJECTED"
            ? {
                originalAttendanceStatus:
                  currentAttendance.originalAttendanceStatus ||
                  currentAttendance.attendanceStatus ||
                  currentAttendance.status ||
                  "",
                attendanceStatus: "INVALID",
                invalidReason: clean(reviewReason),
              }
            : {}),
          reviewReason: clean(reviewReason),
          reviewedByUid: user.uid || "",
          reviewedByName: profile.name,
          reviewedByEmail: profile.email,
          reviewedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Attendance review save nahi hui");
      return;
    }
    await audit(
      `Direct attendance ${normalizedDecision.toLowerCase()}`,
      "ATTENDANCE",
      item.id,
      {
        employeeId: item.employeeId || "",
        employeeCode: item.employeeCode || "",
        date: item.date || "",
        reason: clean(reviewReason),
      }
    );
    notify(`Attendance ${normalizedDecision}`);
    loadAll();
  };

  const employeeRequestDecision = async (
    item,
    nextStatus,
    resolutionNote = ""
  ) => {
    if (!canHR) return notify("HR/CEO access required");

    const allowedStatuses = new Set([
      "IN_PROGRESS",
      "APPROVED",
      "REJECTED",
      "RESOLVED",
    ]);
    const currentStatus = clean(item.status || "PENDING_HR_REVIEW").toUpperCase();
    const normalizedStatus = clean(nextStatus).toUpperCase();
    if (!allowedStatuses.has(normalizedStatus)) {
      return notify("Employee request status valid nahi hai");
    }
    if (
      !["OPEN", "PENDING", "PENDING_HR_REVIEW", "IN_PROGRESS"].includes(
        currentStatus
      )
    ) {
      return notify("Employee request pehle hi close ho chuki hai");
    }
    if (normalizedStatus === "REJECTED" && !clean(resolutionNote)) {
      return notify("Reject karne ke liye HR response required hai");
    }

    const requestType = clean(item.requestType || item.type).toUpperCase();
    const payload =
      item.payload && typeof item.payload === "object" ? item.payload : {};
    const requestRef = doc(db, COLLECTIONS.employeeRequests, item.id);
    const batch = writeBatch(db);
    let sideEffectType = "";
    let sideEffectTargetId = "";

    try {
      if (
        normalizedStatus === "APPROVED" &&
        requestType === "ATTENDANCE_CORRECTION"
      ) {
        const requestingEmployee = data.employees.find(
          (record) =>
            record.id === item.employeeId &&
            item.candidateUserId &&
            record.candidateUserId === item.candidateUserId
        );
        if (!requestingEmployee) {
          throw new Error(
            "Requesting employee identity safely verify nahi hui"
          );
        }
        const requestedDate = clean(
          payload.date || item.attendanceDate || item.referenceDate
        );
        const requestedAttendanceId = clean(
          payload.attendanceId || item.attendanceId
        );
        let attendanceRecord = requestedAttendanceId
          ? data.attendance.find(
              (record) => record.id === requestedAttendanceId
            )
          : null;

        if (!attendanceRecord && requestedDate && item.employeeId) {
          const matchingRecords = data.attendance.filter(
            (record) =>
              record.employeeId === item.employeeId &&
              clean(record.date).slice(0, 10) ===
                requestedDate.slice(0, 10)
          );
          if (matchingRecords.length === 1) {
            [attendanceRecord] = matchingRecords;
          }
        }

        if (!attendanceRecord) {
          throw new Error(
            "Attendance correction ke liye exact attendance record nahi mila"
          );
        }
        const attendanceBelongsToEmployee =
          attendanceRecord.employeeId === requestingEmployee.id &&
          (!attendanceRecord.candidateUserId ||
            attendanceRecord.candidateUserId === item.candidateUserId);
        if (!attendanceBelongsToEmployee) {
          throw new Error(
            "Attendance record aur requesting employee match nahi karte"
          );
        }

        const requestedCheckIn = clean(
          payload.requestedCheckIn ||
            payload.requestedCheckInTime ||
            item.requestedCheckIn
        );
        const requestedCheckOut = clean(
          payload.requestedCheckOut ||
            payload.requestedCheckOutTime ||
            item.requestedCheckOut
        );
        if (!requestedCheckIn && !requestedCheckOut) {
          throw new Error(
            "Correction request me requested check-in/check-out missing hai"
          );
        }

        const attendanceUpdate = {
          hrReviewStatus: "APPROVED",
          correctionRequestId: item.id,
          correctedByUid: user.uid || "",
          correctedByName: profile.name,
          correctedByEmail: profile.email,
          correctedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };
        if (requestedCheckIn) {
          attendanceUpdate.checkInTime = requestedCheckIn;
        }
        if (requestedCheckOut) {
          attendanceUpdate.checkOutTime = requestedCheckOut;
        }
        batch.update(
          doc(db, COLLECTIONS.attendance, attendanceRecord.id),
          attendanceUpdate
        );
        sideEffectType = "ATTENDANCE";
        sideEffectTargetId = attendanceRecord.id;
      }

      if (
        normalizedStatus === "APPROVED" &&
        requestType === "PROFILE_CHANGE"
      ) {
        const fieldAliases = {
          phone: "mobile",
          mobile: "mobile",
          mobilenumber: "mobile",
          address: "address",
          currentaddress: "currentAddress",
          permanentaddress: "permanentAddress",
          emergencycontactname: "emergencyContactName",
          emergencyphone: "emergencyContactMobile",
          emergencycontactmobile: "emergencyContactMobile",
        };
        const requestedField = clean(
          payload.field || item.profileField || item.requestedField
        );
        const safeField =
          fieldAliases[requestedField.toLowerCase()] || requestedField;
        const allowedProfileFields = new Set([
          "mobile",
          "address",
          "currentAddress",
          "permanentAddress",
          "emergencyContactName",
          "emergencyContactMobile",
        ]);
        if (!allowedProfileFields.has(safeField)) {
          throw new Error(
            "Is profile field ko automatic update karna allowed nahi hai"
          );
        }
        const rawValue =
          payload.requestedValue ??
          item.requestedValue ??
          item.newValue ??
          "";
        if (!["string", "number"].includes(typeof rawValue)) {
          throw new Error("Requested profile value valid nahi hai");
        }
        const requestedValue = clean(rawValue);
        if (!requestedValue) {
          throw new Error("Requested profile value blank nahi ho sakti");
        }
        if (
          ["mobile", "emergencyContactMobile"].includes(safeField) &&
          !/^[0-9+\-\s]{7,18}$/.test(requestedValue)
        ) {
          throw new Error("Requested mobile number valid nahi hai");
        }

        const employeeRecord = data.employees.find(
          (record) =>
            record.id === item.employeeId &&
            item.candidateUserId &&
            record.candidateUserId === item.candidateUserId
        );
        if (!employeeRecord) {
          throw new Error("Employee profile identity safely verify nahi hui");
        }
        batch.update(doc(db, COLLECTIONS.employees, employeeRecord.id), {
          [safeField]: requestedValue,
          profileRequestId: item.id,
          profileUpdatedByUid: user.uid || "",
          profileUpdatedByName: profile.name,
          profileUpdatedByEmail: profile.email,
          profileUpdatedAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
        sideEffectType = "EMPLOYEE_PROFILE";
        sideEffectTargetId = employeeRecord.id;
      }

      batch.update(requestRef, {
        status: normalizedStatus,
        resolutionNote: clean(resolutionNote),
        actionByUid: user.uid || "",
        actionByName: profile.name,
        actionByEmail: profile.email,
        actionAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        ...(sideEffectType
          ? {
              sideEffectType,
              sideEffectTargetId,
              sideEffectAppliedAt: serverTimestamp(),
            }
          : {}),
      });
      await batch.commit();
    } catch (error) {
      notify(error.message || "Employee request update nahi hui");
      return;
    }

    const followUps = [
      audit(
        `Employee request ${normalizedStatus.toLowerCase()}`,
        "EMPLOYEE_REQUEST",
        item.id,
        {
          requestType,
          sideEffectType,
          sideEffectTargetId,
        }
      ),
    ];
    const employeeEmail = clean(item.employeeEmail || item.email);
    if (
      employeeEmail &&
      ["APPROVED", "REJECTED", "RESOLVED"].includes(normalizedStatus)
    ) {
      followUps.push(
        queueMail(
          "EMPLOYEE_REQUEST_DECISION",
          employeeEmail,
          `HR request update: ${String(
            item.subject || requestType || "Employee request"
          )}`,
          `Dear ${item.employeeName || "Employee"},\n\nYour ${String(
            requestType || "HR"
          ).replaceAll("_", " ")} request is ${normalizedStatus.replaceAll(
            "_",
            " "
          )}.${clean(resolutionNote) ? `\n\nHR response: ${clean(resolutionNote)}` : ""}`,
          item.id,
          { correlationId: item.id }
        )
      );
    }
    await Promise.allSettled(followUps);
    notify(`Employee request ${normalizedStatus.replaceAll("_", " ")}`);
    loadAll();
  };

  const documentDecision = async (
    item,
    nextStatus,
    verificationNote = ""
  ) => {
    if (!canHR) return notify("HR/CEO access required");

    const currentStatus = clean(item.status || "PENDING").toUpperCase();
    const normalizedStatus = clean(nextStatus).toUpperCase();
    if (!["VERIFIED", "REJECTED"].includes(normalizedStatus)) {
      return notify("Document decision valid nahi hai");
    }
    if (
      !["PENDING", "PENDING_HR_VERIFICATION", "PENDING_VERIFICATION"].includes(
        currentStatus
      )
    ) {
      return notify("Document verification pehle hi process ho chuki hai");
    }
    if (normalizedStatus === "REJECTED" && !clean(verificationNote)) {
      return notify("Document reject karne ke liye reason required hai");
    }

    try {
      await updateDoc(doc(db, COLLECTIONS.documents, item.id), {
        status: normalizedStatus,
        verificationNote: clean(verificationNote),
        verifiedByUid: user.uid || "",
        verifiedByName: profile.name,
        verifiedByEmail: profile.email,
        verifiedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    } catch (error) {
      notify(error.message || "Document decision save nahi hui");
      return;
    }

    const employee = data.employees.find(
      (record) =>
        record.id === item.employeeId ||
        (item.candidateUserId &&
          record.candidateUserId === item.candidateUserId)
    );
    const employeeEmail = clean(
      item.employeeEmail || item.email || employee?.email
    );
    const followUps = [
      audit(
        `Employee document ${normalizedStatus.toLowerCase()}`,
        "EMPLOYEE_DOCUMENT",
        item.id,
        {
          employeeId: item.employeeId || "",
          employeeName: item.employeeName || employee?.name || "",
          documentType: item.documentType || "",
        }
      ),
    ];
    if (employeeEmail) {
      followUps.push(
        queueMail(
          "EMPLOYEE_DOCUMENT_DECISION",
          employeeEmail,
          `Document verification: ${
            item.documentType || "Employee document"
          }`,
          `Dear ${
            item.employeeName || employee?.name || "Employee"
          },\n\nYour ${
            item.documentType || "document"
          } verification status is ${normalizedStatus}.${
            clean(verificationNote)
              ? `\n\nHR remarks: ${clean(verificationNote)}`
              : ""
          }`,
          item.id,
          { correlationId: item.id }
        )
      );
    }
    await Promise.allSettled(followUps);
    notify(`Document ${normalizedStatus}`);
    loadAll();
  };

  const payrollCalculation = useMemo(
    () =>
      calculatePayrollSnapshot(
        payForm,
        data.attendance,
        data.leaves,
        data.holidays,
        data.settings
      ),
    [
      payForm,
      data.attendance,
      data.leaves,
      data.holidays,
      data.settings,
    ]
  );

  const runPayrollAutomation = async (requestedMonth) => {
    if (!canHR) return notify("HR/CEO access required");
    const month = clean(
      requestedMonth || payForm.month || previousClosedMonthKey()
    );
    if (!isMonthKey(month) || month > previousClosedMonthKey()) {
      return notify(
        "Payroll sirf completed month ka run hoga; current month abhi close nahi hua"
      );
    }
    if (
      !window.confirm(
        `${month} ka server-side payroll run karein? Existing payroll duplicate nahi hoga.`
      )
    ) {
      return;
    }
    setLoading(true);
    try {
      const runAutomation = httpsCallable(
        interviewFunctions,
        "runMonthlyPayrollAutomation"
      );
      const response = await runAutomation({ month });
      const result = response.data || {};
      notify(
        `${month}: ${result.createdCount || 0} payroll created, ${
          result.exceptionCount || 0
        } exception, total ${money(result.totalNet)}`
      );
      await loadAll();
    } catch (error) {
      notify(error.message || "Payroll automation run nahi hua");
    } finally {
      setLoading(false);
    }
  };

  const downloadPayslip = async (item) => {
    const previewTab = window.open("", "_blank", "noopener,noreferrer");
    try {
      const getPayslip = httpsCallable(
        interviewFunctions,
        "getPayslipDownloadUrl"
      );
      const response = await getPayslip({ payrollId: item.id });
      const url = response.data?.url;
      if (!url) throw new Error("Payslip URL nahi mila");
      if (previewTab) previewTab.location.href = url;
      else window.location.assign(url);
    } catch (error) {
      previewTab?.close();
      notify(error.message || "Payslip open nahi hui");
    }
  };

  const selectPayrollEmployee = (employeeId) => {
    const employee = data.employees.find((item) => item.id === employeeId);
    if (!employee) return;
    setPayForm({
      ...payForm,
      employeeId,
      employeeName: employee.name,
      employeeEmail: employee.email,
      employeeCode: employee.employeeCode || "",
      department: employee.department || "",
      designation: employee.designation || "",
      candidateUserId: employee.candidateUserId || "",
      joiningDate: employee.joiningDate || "",
      exitDate: employee.exitDate || employee.lastWorkingDate || "",
      basic: employee.basic || "",
      hra: employee.hra || "",
      da: employee.da || "",
      allowances: employee.allowances || "",
      pf: employee.pf || employee.salaryStructure?.pf || "",
      employerPf:
        employee.employerPf || employee.salaryStructure?.employerPf || "",
      esi: employee.esi || employee.salaryStructure?.esi || "",
      professionalTax:
        employee.professionalTax || employee.salaryStructure?.professionalTax || "",
      tdsMode: normalizeTdsMode(
        employee.tdsMode || employee.salaryStructure?.tdsMode
      ),
      tds: employee.tds || employee.salaryStructure?.tds || "",
      otherDeduction:
        employee.otherDeduction || employee.salaryStructure?.otherDeduction || "",
      accountHolderName: employee.accountHolderName || employee.name || "",
      bankName: employee.bankName || "",
      accountNumber: employee.accountNumber || "",
      ifscCode: employee.ifscCode || "",
      bankBranch: employee.bankBranch || "",
    });
  };

  const preparePayroll = async () => {
    if (!canHR) return notify("HR access required");
    if (!payForm.employeeId || !payForm.month) {
      return notify("Employee aur payroll month required hai");
    }
    if (
      !isMonthKey(payForm.month) ||
      payForm.month > previousClosedMonthKey()
    ) {
      return notify(
        "Payroll month completed/past month hona chahiye; current month finalise nahi ho sakta"
      );
    }
    const employee = data.employees.find(
      (item) => item.id === payForm.employeeId
    );
    if (!employee || employee.status === "INACTIVE") {
      return notify("Active employee record required hai");
    }
    const missingBankFields = [
      ["Account Holder", payForm.accountHolderName],
      ["Bank Name", payForm.bankName],
      ["Account Number", payForm.accountNumber],
      ["IFSC Code", payForm.ifscCode],
    ]
      .filter(([, value]) => !clean(value))
      .map(([label]) => label);
    if (missingBankFields.length) {
      return notify(
        `Employee bank details incomplete: ${missingBankFields.join(", ")}`
      );
    }
    const accountNumber = clean(payForm.accountNumber);
    const ifscCode = clean(payForm.ifscCode).toUpperCase();
    if (!/^[0-9]{9,18}$/.test(accountNumber)) {
      return notify("Employee ka valid 9–18 digit account number required hai");
    }
    if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode)) {
      return notify("Employee ka valid IFSC code required hai");
    }
    const payrollNumbers = [
      ["Basic", payForm.basic],
      ["HRA", payForm.hra],
      ["DA", payForm.da],
      ["Allowances", payForm.allowances],
      ["PF", payForm.pf],
      ["Employer PF", payForm.employerPf],
      ["ESI", payForm.esi],
      ["Professional Tax", payForm.professionalTax],
      ["TDS", payForm.tds],
      ["Other Deduction", payForm.otherDeduction],
    ];
    if (
      payrollNumbers.some(
        ([, value]) => !Number.isFinite(Number(value)) || Number(value) < 0
      )
    ) {
      return notify("Payroll values valid aur non-negative honi chahiye");
    }
    const duplicate = data.payroll.find(
      (item) =>
        item.employeeId === payForm.employeeId &&
        item.month === payForm.month &&
        item.status !== "REJECTED"
    );
    if (duplicate) {
      return notify("Is employee ka is month ka payroll pehle se bana hua hai");
    }
    const finalCalculation = calculatePayrollSnapshot(
      payForm,
      data.attendance,
      data.leaves,
      data.holidays,
      data.settings
    );
    if (finalCalculation.net <= 0) {
      return notify("Net salary zero hai; salary structure verify karein");
    }
    if (finalCalculation.workingDays <= 0) {
      return notify(
        "Is month me payroll working days zero hain; Holiday Calendar setting verify karein"
      );
    }

    const payrollRef = doc(
      db,
      COLLECTIONS.payroll,
      `${payForm.employeeId}_${payForm.month}`
    );
    const approvalRef = doc(collection(db, COLLECTIONS.approvals));
    try {
      await runTransaction(db, async (transaction) => {
        const existingPayroll = await transaction.get(payrollRef);
        const existingData = existingPayroll.exists()
          ? existingPayroll.data()
          : null;
        if (existingData && existingData.status !== "REJECTED") {
          throw new Error(
            "Is employee ka is month ka payroll pehle se bana hua hai"
          );
        }
        transaction.set(payrollRef, {
        ...payForm,
        ...finalCalculation,
        accountNumber,
        ifscCode,
        basic: number(payForm.basic),
        hra: number(payForm.hra),
        da: number(payForm.da),
        allowances: number(payForm.allowances),
        pf: number(payForm.pf),
        employerPf: number(payForm.employerPf),
        esi: number(payForm.esi),
        professionalTax: number(payForm.professionalTax),
        tdsMode: finalCalculation.tdsMode,
        tds: finalCalculation.tds,
        tdsSalaryDeduction: finalCalculation.tdsSalaryDeduction,
        companyBorneTds: finalCalculation.companyBorneTds,
        companyTotalOutflow: finalCalculation.companyTotalOutflow,
        otherDeduction: number(payForm.otherDeduction),
        status: "PENDING_CEO_APPROVAL",
        paymentStatus: "NOT_REQUESTED",
        accountsMailStatus: "NOT_SENT",
        approvalRequestId: approvalRef.id,
        revision: number(existingData?.revision) + 1,
        previousRejectionReason:
          existingData?.ceoRejectionReason || "",
        ceoActionBy: null,
        ceoActionByUid: null,
        ceoActionByEmail: null,
        ceoActionAt: null,
        ceoRejectionReason: null,
        locked: true,
        preparedBy: profile.name,
        preparedByUid: user.uid,
        preparedByEmail: profile.email,
        preparedByRole: role,
        hrFinalizedAt: serverTimestamp(),
        createdAt: existingData?.createdAt || serverTimestamp(),
        updatedAt: serverTimestamp(),
        }, { merge: true });
        transaction.set(approvalRef, {
        type: "PAYROLL_APPROVAL",
        relatedId: payrollRef.id,
        employeeName: payForm.employeeName,
        employeeCode: payForm.employeeCode,
        title: payForm.month,
        amount: finalCalculation.net,
        requestedBy: profile.name,
        requestedByUid: user.uid,
        requestedByEmail: profile.email,
        status: "PENDING_CEO_APPROVAL",
        createdAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Payroll finalise nahi hua");
      return;
    }
    await audit(
      "Payroll finalised by HR and sent to CEO",
      "PAYROLL",
      payrollRef.id,
      {
        employeeCode: payForm.employeeCode,
        month: payForm.month,
        net: finalCalculation.net,
        bankAccountLast4: clean(payForm.accountNumber).slice(-4),
      }
    );
    setPayForm({
      ...emptyPayroll,
      month: previousClosedMonthKey(),
    });
    notify("Payroll CEO approval ke liye bhej diya");
    loadAll();
  };

  const payrollDecision = async (item, decision) => {
    if (!canCEO) return notify("CEO access required");
    if (!isPendingCEOApproval(item.status)) {
      return notify("Payroll approval pehle hi process ho chuka hai");
    }
    const rejectionReason =
      decision === "REJECTED"
        ? window.prompt("Payroll rejection reason", "")
        : "";
    if (decision === "REJECTED" && !clean(rejectionReason)) return;
    const payrollRef = doc(db, COLLECTIONS.payroll, item.id);
    let linkedApprovalId = "";
    try {
      await runTransaction(db, async (transaction) => {
        const payrollSnapshot = await transaction.get(payrollRef);
        if (!payrollSnapshot.exists()) {
          throw new Error("Payroll record nahi mila");
        }
        const currentPayroll = payrollSnapshot.data();
        if (!isPendingCEOApproval(currentPayroll.status)) {
          throw new Error("Payroll approval pehle hi process ho chuka hai");
        }
        linkedApprovalId = currentPayroll.approvalRequestId || "";
        let approvalRef = null;
        if (linkedApprovalId) {
          approvalRef = doc(
            db,
            COLLECTIONS.approvals,
            linkedApprovalId
          );
          const approvalSnapshot = await transaction.get(approvalRef);
          if (
            !approvalSnapshot.exists() ||
            !isPendingCEOApproval(approvalSnapshot.data().status)
          ) {
            throw new Error("Linked CEO approval pending nahi hai");
          }
        }
        transaction.update(payrollRef, {
          status: decision,
          paymentStatus:
            decision === "APPROVED_BY_CEO"
              ? "READY_FOR_ACCOUNTS"
              : "NOT_REQUESTED",
          ceoActionBy: profile.name,
          ceoActionByUid: user.uid,
          ceoActionByEmail: profile.email,
          ceoActionAt: serverTimestamp(),
          ceoRejectionReason: clean(rejectionReason),
        });
        if (approvalRef) {
          transaction.update(approvalRef, {
            status: decision,
            actionByUid: user.uid || "",
            actionBy: profile.name,
            actionByEmail: profile.email,
            actionAt: serverTimestamp(),
            rejectionReason: clean(rejectionReason),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Payroll CEO decision save nahi hua");
      await loadAll();
      return;
    }
    if (!linkedApprovalId) {
      await closeRelatedApproval(
        "PAYROLL_APPROVAL",
        item.id,
        decision,
        { rejectionReason: clean(rejectionReason) }
      );
    }
    await audit(
      `Payroll ${decision}`,
      "PAYROLL",
      item.id,
      { month: item.month, net: number(item.net) }
    );
    notify(
      decision === "APPROVED_BY_CEO"
        ? "CEO approval complete. Final Accounts NEFT mail automatically queue ho raha hai."
        : "Payroll reject karke HR correction queue me bhej diya"
    );
    loadAll();
  };

  const sendPayrollToAccount = async (item) => {
    if (!canSendToAccounts) {
      return notify("Final Accounts instruction HR Admin ya CEO Master bhej sakta hai");
    }
    if (
      item.status !== "APPROVED_BY_CEO" ||
      item.paymentStatus !== "READY_FOR_ACCOUNTS"
    ) {
      return notify("CEO-approved payroll required hai");
    }
    if (item.accountsMailStatus === "MAIL_QUEUED") {
      return notify("Accounts payment mail pehle hi queue ho chuka hai");
    }
    if (
      !item.ceoActionByUid ||
      !item.ceoActionByEmail ||
      !item.ceoActionAt
    ) {
      return notify("Stored CEO approval evidence incomplete hai");
    }
    const paymentRequestId = `SAAM-PAY-${item.month}-${item.id
      .slice(0, 8)
      .toUpperCase()}`;
    const payrollRef = doc(db, COLLECTIONS.payroll, item.id);
    const mailRef = doc(
      db,
      COLLECTIONS.mail,
      `salary_neft_${item.id}`
    );
    let committedPayroll = item;
    try {
      await runTransaction(db, async (transaction) => {
        const [payrollSnapshot, mailSnapshot] = await Promise.all([
          transaction.get(payrollRef),
          transaction.get(mailRef),
        ]);
        if (!payrollSnapshot.exists()) {
          throw new Error("Payroll record nahi mila");
        }
        const currentPayroll = {
          id: payrollSnapshot.id,
          ...payrollSnapshot.data(),
        };
        if (
          currentPayroll.status !== "APPROVED_BY_CEO" ||
          currentPayroll.paymentStatus !== "READY_FOR_ACCOUNTS"
        ) {
          throw new Error("Payroll Account mail ke liye ready nahi hai");
        }
        if (mailSnapshot.exists()) {
          throw new Error("Accounts payment mail pehle hi queue ho chuka hai");
        }
        if (
          !currentPayroll.ceoActionByUid ||
          !currentPayroll.ceoActionByEmail ||
          !currentPayroll.ceoActionAt
        ) {
          throw new Error("Stored CEO approval evidence incomplete hai");
        }
        committedPayroll = currentPayroll;
        const body = [
          "Dear Accounts Team,",
          "",
          "CEO-approved salary payment instruction is ready for NEFT processing.",
          "",
          `Payment Request ID: ${paymentRequestId}`,
          `Payroll ID: ${currentPayroll.id}`,
          `Approval Request ID: ${
            currentPayroll.approvalRequestId || "-"
          }`,
          `Employee: ${currentPayroll.employeeName} (${
            currentPayroll.employeeCode || "-"
          })`,
          `Department / Designation: ${
            currentPayroll.department || "-"
          } / ${currentPayroll.designation || "-"}`,
          `Payroll Month: ${currentPayroll.month}`,
          `Working / Paid Days: ${currentPayroll.workingDays} / ${
            currentPayroll.paidDays
          }`,
          `Absent Days: ${currentPayroll.attendanceAbsentDays || 0}`,
          `LOP Days: ${currentPayroll.approvedLopDays || 0}`,
          "",
          `Basic: ${money(currentPayroll.basic)}`,
          `HRA: ${money(currentPayroll.hra)}`,
          `DA: ${money(currentPayroll.da)}`,
          `Allowances: ${money(currentPayroll.allowances)}`,
          `Gross Salary: ${money(currentPayroll.gross)}`,
          `Attendance / LOP Deduction: ${money(
            currentPayroll.attendanceDeduction
          )}`,
          `PF: ${money(currentPayroll.pf)}`,
          `ESI: ${money(currentPayroll.esi)}`,
          `Professional Tax: ${money(currentPayroll.professionalTax)}`,
          `TDS Treatment: ${tdsModeLabel(currentPayroll.tdsMode)}`,
          `TDS Amount: ${money(currentPayroll.tds)}`,
          `TDS Deducted From Salary: ${money(
            salaryDeductedTds(currentPayroll)
          )}`,
          `TDS Paid Separately By Company: ${money(
            companyPaidTds(currentPayroll)
          )}`,
          `Other Deduction: ${money(currentPayroll.otherDeduction)}`,
          `Total Deduction: ${money(currentPayroll.totalDeduction)}`,
          `NET PAYABLE: ${money(currentPayroll.net)}`,
          "",
          `Account Holder: ${currentPayroll.accountHolderName}`,
          `Bank: ${currentPayroll.bankName}`,
          `Account Number: ${currentPayroll.accountNumber}`,
          `IFSC: ${currentPayroll.ifscCode}`,
          `Branch: ${currentPayroll.bankBranch || "-"}`,
          "",
          `HR Finalised By: ${currentPayroll.preparedBy || "-"} (${
            currentPayroll.preparedByEmail || OFFICIAL_EMAILS.HR
          })`,
          `HR Finalised At: ${formatDate(currentPayroll.hrFinalizedAt)}`,
          `CEO Approved By: ${currentPayroll.ceoActionBy} (${
            currentPayroll.ceoActionByEmail
          })`,
          `CEO Approved At: ${formatDate(currentPayroll.ceoActionAt)}`,
          "",
          "Please process NEFT and share UTR/reference with HR for payment confirmation.",
        ].join("\n");
        transaction.set(mailRef, {
          type: "SALARY_NEFT_REQUEST",
          from: OFFICIAL_EMAILS.HR,
          to: OFFICIAL_EMAILS.ACCOUNT,
          cc: [OFFICIAL_EMAILS.CEO],
          subject: `CEO Approved Salary | ${currentPayroll.month} | ${
            currentPayroll.employeeCode || currentPayroll.employeeName
          } | ${money(currentPayroll.net)}`,
          body,
          relatedId: currentPayroll.id,
          correlationId: paymentRequestId,
          payload: {
            paymentRequestId,
            payrollId: currentPayroll.id,
            approvalRequestId:
              currentPayroll.approvalRequestId || "",
            employeeId: currentPayroll.employeeId,
            employeeCode: currentPayroll.employeeCode || "",
            employeeName: currentPayroll.employeeName,
            month: currentPayroll.month,
            net: number(currentPayroll.net),
            accountHolderName: currentPayroll.accountHolderName,
            bankName: currentPayroll.bankName,
            accountNumber: currentPayroll.accountNumber,
            ifscCode: currentPayroll.ifscCode,
            bankBranch: currentPayroll.bankBranch || "",
            hrFinalizedAt: currentPayroll.hrFinalizedAt || null,
            ceoActionAt: currentPayroll.ceoActionAt || null,
          },
          status: "PENDING_SEND",
          createdByUid: user.uid || "",
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
        transaction.update(payrollRef, {
          paymentStatus: "PENDING_ACCOUNT_PAYMENT",
          accountsMailStatus: "MAIL_QUEUED",
          accountsMailId: mailRef.id,
          accountsEmail: OFFICIAL_EMAILS.ACCOUNT,
          paymentRequestId,
          accountsMailQueuedBy: profile.name,
          accountsMailQueuedByUid: user.uid,
          accountsMailQueuedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Account payment advice queue nahi hua");
      await loadAll();
      return;
    }
    await audit(
      "Final salary payment advice queued for Accounts",
      "PAYROLL",
      item.id,
      {
        paymentRequestId,
        to: OFFICIAL_EMAILS.ACCOUNT,
        amount: number(committedPayroll.net),
        bankAccountLast4: clean(committedPayroll.accountNumber).slice(-4),
      }
    );
    notify(`Detailed payment mail ${OFFICIAL_EMAILS.ACCOUNT} ke liye queue ho gaya`);
    loadAll();
  };

  const recordAccountsPayment = async (item) => {
    if (!canRecordAccountsPayment) {
      return notify("Accounts UTR ke liye Accounts/HR/CEO access required hai");
    }
    if (item.paymentStatus !== "PENDING_ACCOUNT_PAYMENT") {
      return notify("Accounts payment request pending nahi hai");
    }
    const reference = clean(
      window.prompt("Accounts se received NEFT/UTR reference number", "")
    ).toUpperCase();
    if (!/^[A-Z0-9/-]{6,35}$/.test(reference)) {
      return notify("Valid 6–35 character UTR/reference required hai");
    }
    if (
      data.payroll.some(
        (record) =>
          record.id !== item.id &&
          clean(record.utrNumber).toUpperCase() === reference
      )
    ) {
      return notify("Ye UTR kisi dusre payroll me already use hua hai");
    }
    const confirmedAmount = number(
      window.prompt("Accounts confirmed payment amount", String(item.net))
    );
    if (roundMoney(confirmedAmount) !== roundMoney(item.net)) {
      return notify("Confirmed amount payroll net salary se match nahi karta");
    }
    const paymentDate = clean(
      window.prompt("Bank payment date (YYYY-MM-DD)", today())
    );
    if (!isValidIsoDate(paymentDate) || paymentDate > today()) {
      return notify("Valid payment date required hai");
    }
    const payrollRef = doc(db, COLLECTIONS.payroll, item.id);
    const paymentRef = doc(
      db,
      COLLECTIONS.paymentRefs,
      reference.replaceAll("/", "_")
    );
    const payslipMailRef = doc(
      db,
      COLLECTIONS.mail,
      `payslip_${item.id}`
    );
    let committedPayroll = item;
    try {
      await runTransaction(db, async (transaction) => {
        const [
          payrollSnapshot,
          paymentReferenceSnapshot,
          payslipMailSnapshot,
        ] = await Promise.all([
          transaction.get(payrollRef),
          transaction.get(paymentRef),
          transaction.get(payslipMailRef),
        ]);
        if (!payrollSnapshot.exists()) {
          throw new Error("Payroll record nahi mila");
        }
        const currentPayroll = {
          id: payrollSnapshot.id,
          ...payrollSnapshot.data(),
        };
        if (currentPayroll.paymentStatus !== "PENDING_ACCOUNT_PAYMENT") {
          throw new Error("Accounts payment request pending nahi hai");
        }
        if (
          roundMoney(confirmedAmount) !== roundMoney(currentPayroll.net)
        ) {
          throw new Error(
            "Confirmed amount payroll net salary se match nahi karta"
          );
        }
        if (paymentReferenceSnapshot.exists()) {
          throw new Error("Ye UTR/reference already use hua hai");
        }
        if (payslipMailSnapshot.exists()) {
          throw new Error("Payslip mail pehle hi queue ho chuka hai");
        }
        if (!currentPayroll.accountsMailId) {
          throw new Error("Accounts payment advice link missing hai");
        }
        const accountsMailRef = doc(
          db,
          COLLECTIONS.mail,
          currentPayroll.accountsMailId
        );
        const accountsMailSnapshot = await transaction.get(accountsMailRef);
        const accountMailStatus = clean(
          accountsMailSnapshot.data()?.status
        ).toUpperCase();
        if (
          !accountsMailSnapshot.exists() ||
          !["SENT", "DELIVERED", "SUCCESS"].includes(accountMailStatus)
        ) {
          throw new Error(
            "Account mail abhi SENT nahi hai. Communication queue check karein."
          );
        }
        committedPayroll = currentPayroll;
        const payslipBody = [
          `Dear ${currentPayroll.employeeName},`,
          "",
          `Your salary for ${currentPayroll.month} has been processed.`,
          `Employee ID: ${currentPayroll.employeeCode || "-"}`,
          `Department / Designation: ${
            currentPayroll.department || "-"
          } / ${currentPayroll.designation || "-"}`,
          `Working / Paid Days: ${currentPayroll.workingDays} / ${
            currentPayroll.paidDays
          }`,
          `Gross Salary: ${money(currentPayroll.gross)}`,
          `Attendance / LOP Deduction: ${money(
            currentPayroll.attendanceDeduction
          )}`,
          `PF: ${money(currentPayroll.pf)}`,
          `ESI: ${money(currentPayroll.esi)}`,
          `Professional Tax: ${money(currentPayroll.professionalTax)}`,
          `TDS Treatment: ${tdsModeLabel(currentPayroll.tdsMode)}`,
          `TDS Amount: ${money(currentPayroll.tds)}`,
          `TDS Deducted From Salary: ${money(
            salaryDeductedTds(currentPayroll)
          )}`,
          `TDS Paid Separately By Company: ${money(
            companyPaidTds(currentPayroll)
          )}`,
          `Other Deduction: ${money(currentPayroll.otherDeduction)}`,
          `Total Deduction: ${money(currentPayroll.totalDeduction)}`,
          `NET PAID: ${money(currentPayroll.net)}`,
          `Payment Date: ${paymentDate}`,
          `UTR / Bank Reference: ${reference}`,
          `Paid To: ${maskAccount(currentPayroll.accountNumber)} • ${
            currentPayroll.ifscCode
          }`,
          "",
          "This system-generated payslip advice is subject to payroll records.",
        ].join("\n");
        transaction.set(paymentRef, {
          utrNumber: reference,
          payrollId: currentPayroll.id,
          employeeId: currentPayroll.employeeId,
          amount: confirmedAmount,
          paymentDate,
          recordedByUid: user.uid || "",
          recordedByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
        transaction.update(payrollRef, {
          paymentStatus: "PAID",
          accountsMailStatus: "PAYMENT_CONFIRMED",
          utrNumber: reference,
          paymentDate,
          paymentConfirmedAmount: confirmedAmount,
          paymentRecordedBy: profile.name,
          paymentRecordedByUid: user.uid,
          paymentRecordedByEmail: profile.email,
          payslipMailId: payslipMailRef.id,
          payslipMailStatus: "WAITING_FOR_PDF",
          payslipStatus: "GENERATING",
          paidAt: serverTimestamp(),
        });
        transaction.set(payslipMailRef, {
          type: "PAYSLIP_PENDING",
          from: OFFICIAL_EMAILS.HR,
          to: currentPayroll.employeeEmail,
          cc: [],
          subject: `Payslip - ${currentPayroll.month}`,
          body: payslipBody,
          relatedId: currentPayroll.id,
          correlationId: reference,
          payload: {
            payrollId: currentPayroll.id,
            employeeId: currentPayroll.employeeId,
            employeeCode: currentPayroll.employeeCode || "",
            month: currentPayroll.month,
            gross: number(currentPayroll.gross),
            totalDeduction: number(currentPayroll.totalDeduction),
            net: number(currentPayroll.net),
            paymentDate,
            utrNumber: reference,
          },
          status: "WAITING_FOR_PDF",
          createdByUid: user.uid || "",
          createdByName: profile.name,
          createdByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Payment confirmation save nahi hua");
      await loadAll();
      return;
    }
    await audit(
      "Account UTR recorded and salary marked paid",
      "PAYROLL",
      item.id,
      {
        utr: reference,
        amount: confirmedAmount,
        paymentDate,
        employeeCode: committedPayroll.employeeCode || "",
      }
    );
    notify("UTR save ho gaya; secure PDF payslip automatically generate ho rahi hai");
    loadAll();
  };

  const selectReimbursementEmployee = (employeeId) => {
    const employee = data.employees.find((item) => item.id === employeeId);
    setReimbursementForm((current) => ({
      ...current,
      employeeId: employee?.id || "",
      candidateUserId: employee?.candidateUserId || "",
      employeeName: employee?.name || "",
      employeeEmail: employee?.email || "",
      employeeCode: employee?.employeeCode || "",
    }));
  };

  const submitReimbursement = async () => {
    if (!isHRAdmin) return notify("Reimbursement entry sirf HR Admin kar sakta hai");
    if (
      !reimbursementForm.employeeId ||
      !clean(reimbursementForm.employeeName)
    ) {
      return notify("Reimbursement ke liye employee required hai");
    }
    if (
      !isValidIsoDate(reimbursementForm.claimDate) ||
      reimbursementForm.claimDate > today()
    ) {
      return notify("Valid current/past claim date required hai");
    }
    const amount = roundMoney(reimbursementForm.amount);
    if (!Number.isFinite(amount) || amount <= 0) {
      return notify("Valid reimbursement amount required hai");
    }
    if (clean(reimbursementForm.description).length < 5) {
      return notify("Claim description minimum 5 characters honi chahiye");
    }
    if (
      clean(reimbursementForm.receiptUrl) &&
      !/^https?:\/\//i.test(clean(reimbursementForm.receiptUrl))
    ) {
      return notify("Receipt URL valid http/https link honi chahiye");
    }
    const reimbursementRef = doc(
      collection(db, COLLECTIONS.reimbursements)
    );
    const claimNumber = `SAAM-RMB-${new Date().getFullYear()}-${reimbursementRef.id
      .slice(0, 8)
      .toUpperCase()}`;
    await setDoc(reimbursementRef, {
      ...reimbursementForm,
      amount,
      description: clean(reimbursementForm.description),
      purpose: clean(reimbursementForm.description),
      expenseDate: reimbursementForm.claimDate,
      receiptUrl: clean(reimbursementForm.receiptUrl),
      claimNumber,
      status: "PENDING_HR_REVIEW",
      requiresCEOApproval: amount > REIMBURSEMENT_CEO_THRESHOLD,
      paymentStatus: "NOT_REQUESTED",
      accountsMailStatus: "NOT_SENT",
      source: "HR_ADMIN_ENTRY",
      submittedByUid: user.uid || "",
      submittedByName: profile.name,
      submittedByEmail: profile.email,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    await audit("Reimbursement claim submitted", "REIMBURSEMENT", reimbursementRef.id, {
      claimNumber,
      employeeCode: reimbursementForm.employeeCode || "",
      category: reimbursementForm.category,
      amount,
    });
    setReimbursementForm(emptyReimbursement);
    notify(`${claimNumber} HR review queue me add ho gaya`);
    loadAll();
  };

  const reimbursementDecision = async (item, stage, decision) => {
    if (stage === "HR" && !isHRAdmin) {
      return notify("HR Admin review required hai");
    }
    if (stage === "CEO" && !canCEO) {
      return notify("CEO approval required hai");
    }
    const normalizedDecision = clean(decision).toUpperCase();
    if (!["APPROVED", "REJECTED"].includes(normalizedDecision)) {
      return notify("Reimbursement decision valid nahi hai");
    }
    const rejectionReason =
      normalizedDecision === "REJECTED"
        ? window.prompt("Reimbursement rejection reason (required)", "")
        : "";
    if (normalizedDecision === "REJECTED" && rejectionReason === null) return;
    if (
      normalizedDecision === "REJECTED" &&
      !clean(rejectionReason)
    ) {
      return notify("Rejection reason required hai");
    }

    const reimbursementRef = doc(
      db,
      COLLECTIONS.reimbursements,
      item.id
    );
    const approvalRef = doc(
      db,
      COLLECTIONS.approvals,
      `reimbursement_${item.id}`
    );
    const ceoMailRef = doc(
      db,
      COLLECTIONS.mail,
      `reimbursement_approval_${item.id}`
    );
    const accountsMailRef = doc(
      db,
      COLLECTIONS.mail,
      `reimbursement_accounts_${item.id}`
    );
    let committedClaim = item;
    let finalStatus = "";
    try {
      await runTransaction(db, async (transaction) => {
        const [
          reimbursementSnapshot,
          approvalSnapshot,
          ceoMailSnapshot,
          accountsMailSnapshot,
        ] = await Promise.all([
          transaction.get(reimbursementRef),
          transaction.get(approvalRef),
          transaction.get(ceoMailRef),
          transaction.get(accountsMailRef),
        ]);
        if (!reimbursementSnapshot.exists()) {
          throw new Error("Reimbursement claim record nahi mila");
        }
        const currentClaim = {
          id: reimbursementSnapshot.id,
          ...reimbursementSnapshot.data(),
        };
        committedClaim = currentClaim;
        const expectedStatus =
          stage === "CEO"
            ? "PENDING_CEO_APPROVAL"
            : "PENDING_HR_REVIEW";
        if (
          stage === "HR"
            ? !["PENDING_HR_REVIEW", "PENDING_HR_APPROVAL"].includes(
                currentClaim.status
              )
            : !isPendingCEOApproval(currentClaim.status)
        ) {
          throw new Error("Reimbursement decision pehle hi process ho chuka hai");
        }
        const amount = roundMoney(currentClaim.amount);
        if (!Number.isFinite(amount) || amount <= 0) {
          throw new Error("Claim amount valid nahi hai");
        }
        const requiresCEO = amount > REIMBURSEMENT_CEO_THRESHOLD;
        if (stage === "CEO" && !requiresCEO) {
          throw new Error("Is claim ko CEO approval required nahi hai");
        }
        const forwardToCEO =
          stage === "HR" &&
          normalizedDecision === "APPROVED" &&
          requiresCEO;
        const approvedForAccounts =
          normalizedDecision === "APPROVED" && !forwardToCEO;
        finalStatus =
          normalizedDecision === "REJECTED"
            ? "REJECTED"
            : forwardToCEO
            ? "PENDING_CEO_APPROVAL"
            : stage === "CEO"
            ? "APPROVED_BY_CEO"
            : "APPROVED";

        if (forwardToCEO && (approvalSnapshot.exists() || ceoMailSnapshot.exists())) {
          throw new Error("CEO reimbursement approval pehle hi create ho chuka hai");
        }
        if (
          stage === "CEO" &&
          (!approvalSnapshot.exists() ||
            !isPendingCEOApproval(approvalSnapshot.data().status))
        ) {
          throw new Error("Linked CEO reimbursement approval pending nahi hai");
        }
        if (approvedForAccounts && accountsMailSnapshot.exists()) {
          throw new Error("Accounts reimbursement mail pehle hi queue ho chuka hai");
        }

        const actionKey = stage.toLowerCase();
        transaction.update(reimbursementRef, {
          status: finalStatus,
          amount,
          requiresCEOApproval: requiresCEO,
          approvalRequestId:
            stage === "CEO" || forwardToCEO ? approvalRef.id : "",
          paymentStatus: approvedForAccounts
            ? "PENDING_ACCOUNT_PAYMENT"
            : "NOT_REQUESTED",
          accountsMailStatus: approvedForAccounts
            ? "MAIL_QUEUED"
            : "NOT_SENT",
          ...(approvedForAccounts
            ? {
                accountsMailId: accountsMailRef.id,
                accountsEmail: OFFICIAL_EMAILS.ACCOUNT,
                accountsMailQueuedAt: serverTimestamp(),
                accountsMailQueuedBy: profile.name,
                accountsMailQueuedByUid: user.uid,
              }
            : {}),
          rejectionReason: clean(rejectionReason),
          [`${actionKey}ActionBy`]: profile.name,
          [`${actionKey}ActionByUid`]: user.uid,
          [`${actionKey}ActionByEmail`]: profile.email,
          [`${actionKey}ActionAt`]: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });

        if (forwardToCEO) {
          transaction.set(approvalRef, {
            type: "REIMBURSEMENT_APPROVAL",
            relatedId: currentClaim.id,
            employeeName: currentClaim.employeeName,
            employeeCode: currentClaim.employeeCode || "",
            title: `${currentClaim.category || "Expense"} • ${
              currentClaim.claimNumber || currentClaim.id
            }`,
            amount,
            requestedBy: profile.name,
            requestedByUid: user.uid,
            requestedByEmail: profile.email,
            status: "PENDING_CEO_APPROVAL",
            createdAt: serverTimestamp(),
          });
          transaction.set(ceoMailRef, {
            type: "CEO_REIMBURSEMENT_APPROVAL",
            from: OFFICIAL_EMAILS.HR,
            to: OFFICIAL_EMAILS.CEO,
            cc: [OFFICIAL_EMAILS.HR],
            subject: `CEO Reimbursement Approval | ${
              currentClaim.claimNumber || currentClaim.id
            } | ${money(amount)}`,
            body: `${currentClaim.employeeName} ka ${
              currentClaim.category || "expense"
            } reimbursement ${money(
              amount
            )} HR ne verify karke CEO approval ke liye forward kiya hai.\n\nClaim date: ${
              currentClaim.claimDate || currentClaim.expenseDate || "-"
            }\nDescription: ${
              currentClaim.description || currentClaim.purpose || "-"
            }\nReceipt: ${
              currentClaim.receiptUrl ||
              currentClaim.receiptFileName ||
              currentClaim.receiptStoragePath ||
              "Not attached"
            }`,
            relatedId: currentClaim.id,
            correlationId: approvalRef.id,
            payload: {
              reimbursementId: currentClaim.id,
              claimNumber: currentClaim.claimNumber || "",
              amount,
              receiptStoragePath: currentClaim.receiptStoragePath || "",
              receiptFileName: currentClaim.receiptFileName || "",
            },
            status: "PENDING_SEND",
            createdByUid: user.uid || "",
            createdByName: profile.name,
            createdByEmail: profile.email,
            createdAt: serverTimestamp(),
          });
        } else if (stage === "CEO") {
          transaction.update(approvalRef, {
            status:
              normalizedDecision === "APPROVED"
                ? "APPROVED_BY_CEO"
                : "REJECTED",
            actionByUid: user.uid || "",
            actionBy: profile.name,
            actionByEmail: profile.email,
            actionAt: serverTimestamp(),
            rejectionReason: clean(rejectionReason),
          });
        }

        if (approvedForAccounts) {
          transaction.set(accountsMailRef, {
            type: "REIMBURSEMENT_PAYMENT_REQUEST",
            from: OFFICIAL_EMAILS.HR,
            to: OFFICIAL_EMAILS.ACCOUNT,
            cc: requiresCEO ? [OFFICIAL_EMAILS.CEO] : [],
            subject: `Approved Reimbursement | ${
              currentClaim.claimNumber || currentClaim.id
            } | ${money(amount)}`,
            body: [
              "Dear Accounts Team,",
              "",
              "The following approved reimbursement is ready for payment:",
              `Claim: ${currentClaim.claimNumber || currentClaim.id}`,
              `Employee: ${currentClaim.employeeName} (${
                currentClaim.employeeCode || "-"
              })`,
              `Claim Date: ${
                currentClaim.claimDate || currentClaim.expenseDate || "-"
              }`,
              `Category: ${currentClaim.category || "-"}`,
              `Amount: ${money(amount)}`,
              `Description: ${
                currentClaim.description || currentClaim.purpose || "-"
              }`,
              `Project / Site: ${currentClaim.projectSite || "-"}`,
              `Receipt: ${
                currentClaim.receiptUrl ||
                currentClaim.receiptFileName ||
                currentClaim.receiptStoragePath ||
                "Not attached"
              }`,
              `HR Reviewed By: ${
                stage === "HR"
                  ? profile.name
                  : currentClaim.hrActionBy || "-"
              }`,
              ...(requiresCEO
                ? [`CEO Approved By: ${profile.name} (${profile.email})`]
                : []),
              "",
              "Please process payment and share UTR/payment reference with HR.",
            ].join("\n"),
            relatedId: currentClaim.id,
            correlationId: currentClaim.claimNumber || currentClaim.id,
            payload: {
              reimbursementId: currentClaim.id,
              claimNumber: currentClaim.claimNumber || "",
              employeeId: currentClaim.employeeId || "",
              employeeCode: currentClaim.employeeCode || "",
              amount,
              category: currentClaim.category || "",
              requiresCEOApproval: requiresCEO,
              receiptUrl: currentClaim.receiptUrl || "",
              receiptStoragePath: currentClaim.receiptStoragePath || "",
              receiptFileName: currentClaim.receiptFileName || "",
            },
            status: "PENDING_SEND",
            createdByUid: user.uid || "",
            createdByName: profile.name,
            createdByEmail: profile.email,
            createdAt: serverTimestamp(),
          });
        }
      });
    } catch (error) {
      notify(error.message || "Reimbursement decision save nahi hua");
      await loadAll();
      return;
    }
    await audit(
      `${stage} reimbursement decision ${finalStatus}`,
      "REIMBURSEMENT",
      item.id,
      {
        claimNumber: committedClaim.claimNumber || "",
        employeeCode: committedClaim.employeeCode || "",
        amount: number(committedClaim.amount),
        accountsPending: ["APPROVED", "APPROVED_BY_CEO"].includes(
          finalStatus
        ),
      }
    );
    notify(
      ["APPROVED", "APPROVED_BY_CEO"].includes(finalStatus)
        ? `Claim approved aur ${OFFICIAL_EMAILS.ACCOUNT} ko payment queue bhej di gayi`
        : `Reimbursement ${finalStatus.replaceAll("_", " ")}`
    );
    loadAll();
  };

  const recordReimbursementPayment = async (item) => {
    if (!canRecordAccountsPayment) {
      return notify("Reimbursement payment ke liye Accounts/HR/CEO access required hai");
    }
    if (item.paymentStatus !== "PENDING_ACCOUNT_PAYMENT") {
      return notify("Reimbursement Accounts payment pending nahi hai");
    }
    const reference = clean(
      window.prompt("Accounts UTR/payment reference", "")
    ).toUpperCase();
    if (!/^[A-Z0-9/-]{6,35}$/.test(reference)) {
      return notify("Valid 6–35 character UTR/payment reference required hai");
    }
    if (
      data.reimbursements.some(
        (record) =>
          record.id !== item.id &&
          clean(record.paymentReference || record.utrNumber).toUpperCase() ===
            reference
      ) ||
      data.payroll.some(
        (record) => clean(record.utrNumber).toUpperCase() === reference
      )
    ) {
      return notify("Ye payment reference kisi aur payment me use ho chuka hai");
    }
    const confirmedAmount = roundMoney(
      window.prompt("Accounts confirmed amount", String(item.amount))
    );
    if (confirmedAmount !== roundMoney(item.amount)) {
      return notify("Confirmed amount claim amount se match nahi karta");
    }
    const paymentDate = clean(
      window.prompt("Payment date (YYYY-MM-DD)", today())
    );
    if (!isValidIsoDate(paymentDate) || paymentDate > today()) {
      return notify("Valid payment date required hai");
    }
    const reimbursementRef = doc(
      db,
      COLLECTIONS.reimbursements,
      item.id
    );
    const paymentRef = doc(
      db,
      COLLECTIONS.paymentRefs,
      reference.replaceAll("/", "_")
    );
    let committedClaim = item;
    try {
      await runTransaction(db, async (transaction) => {
        const [reimbursementSnapshot, paymentSnapshot] = await Promise.all([
          transaction.get(reimbursementRef),
          transaction.get(paymentRef),
        ]);
        if (!reimbursementSnapshot.exists()) {
          throw new Error("Reimbursement claim record nahi mila");
        }
        const currentClaim = {
          id: reimbursementSnapshot.id,
          ...reimbursementSnapshot.data(),
        };
        committedClaim = currentClaim;
        if (currentClaim.paymentStatus !== "PENDING_ACCOUNT_PAYMENT") {
          throw new Error("Accounts payment pending nahi hai");
        }
        if (roundMoney(currentClaim.amount) !== confirmedAmount) {
          throw new Error("Confirmed amount latest claim amount se match nahi karta");
        }
        if (paymentSnapshot.exists()) {
          throw new Error("Ye UTR/payment reference pehle hi use ho chuka hai");
        }
        if (!currentClaim.accountsMailId) {
          throw new Error("Linked Accounts payment mail missing hai");
        }
        const accountsMailRef = doc(
          db,
          COLLECTIONS.mail,
          currentClaim.accountsMailId
        );
        const accountsMailSnapshot = await transaction.get(accountsMailRef);
        if (!accountsMailSnapshot.exists()) {
          throw new Error("Linked Accounts mail record nahi mila");
        }
        const mailStatus = clean(
          accountsMailSnapshot.data().status
        ).toUpperCase();
        if (!["SENT", "DELIVERED", "SUCCESS"].includes(mailStatus)) {
          throw new Error(
            "Accounts mail abhi SENT nahi hai. Communication queue check karein."
          );
        }
        transaction.set(paymentRef, {
          recordType: "REIMBURSEMENT",
          reimbursementId: currentClaim.id,
          employeeId: currentClaim.employeeId || "",
          employeeCode: currentClaim.employeeCode || "",
          amount: confirmedAmount,
          paymentDate,
          utrNumber: reference,
          recordedByUid: user.uid || "",
          recordedByEmail: profile.email,
          createdAt: serverTimestamp(),
        });
        transaction.update(reimbursementRef, {
          paymentStatus: "PAID",
          accountsMailStatus: "PAYMENT_CONFIRMED",
          paymentReference: reference,
          utrNumber: reference,
          paymentDate,
          paymentConfirmedAmount: confirmedAmount,
          paymentRecordedBy: profile.name,
          paymentRecordedByUid: user.uid,
          paymentRecordedByEmail: profile.email,
          paidAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        });
      });
    } catch (error) {
      notify(error.message || "Reimbursement payment confirmation save nahi hui");
      await loadAll();
      return;
    }
    const followUps = [
      audit(
        "Reimbursement Accounts payment marked paid",
        "REIMBURSEMENT",
        item.id,
        {
          claimNumber: committedClaim.claimNumber || "",
          employeeCode: committedClaim.employeeCode || "",
          amount: confirmedAmount,
          paymentDate,
          paymentReference: reference,
        }
      ),
    ];
    if (clean(committedClaim.employeeEmail)) {
      followUps.push(
        queueMail(
          "REIMBURSEMENT_PAID",
          committedClaim.employeeEmail,
          `Reimbursement paid - ${
            committedClaim.claimNumber || committedClaim.id
          }`,
          `Dear ${
            committedClaim.employeeName || "Employee"
          },\n\nYour reimbursement of ${money(
            confirmedAmount
          )} was paid on ${paymentDate}.\nPayment reference: ${reference}.`,
          committedClaim.id,
          { correlationId: reference }
        )
      );
    }
    await Promise.allSettled(followUps);
    notify("Reimbursement paid mark ho gaya aur UTR/reference audit me save hai");
    loadAll();
  };

  const handleApprovalDecision = async (approval, decision) => {
    if (!canCEO) return notify("CEO Master approval required");
    if (!isPendingCEOApproval(approval.status)) {
      return notify("Approval pehle hi process ho chuka hai");
    }
    const approvalType = normalizeApprovalType(approval.type);
    const normalizedApproval = {
      ...approval,
      type: approvalType,
      status: "PENDING_CEO_APPROVAL",
    };
    if (approvalType === "JOB_REQUISITION") {
      const requisition = data.requisitions.find(
        (item) => item.id === approval.relatedId
      );
      if (!requisition) return notify("Related requisition record nahi mila");
      return approveRequisition(
        requisition,
        decision === "APPROVED_BY_CEO" ? "APPROVED" : "REJECTED"
      );
    }
    if (approvalType === "FINAL_CANDIDATE_SELECTION") {
      if (decision === "APPROVED_BY_CEO") {
        const application = data.applications.find(
          (item) => item.id === approval.relatedId
        );
        if (!application) return notify("Related candidate application nahi mili");
        if (
          application.offerPackageStatus !== "READY_FOR_CEO" ||
          application.offerPackage?.status !== "READY_FOR_CEO" ||
          !clean(application.offerPackage?.offerLetterStoragePath)
        ) {
          return notify(
            "HR ka offer PDF, salary, Section 192 TDS aur PF package abhi pending hai"
          );
        }
        const existing = getOfferLeavePolicy(application);
        const entitlements =
          existing.entitlements ||
          existing.balances ||
          existing.annualEntitlements ||
          existing;
        setOfferLeaveForm(
          Object.fromEntries(
            Object.keys(DEFAULT_OFFER_LEAVE_POLICY).map((key) => [
              key,
              String(
                entitlements?.[key] ??
                  entitlements?.[key.toLowerCase()] ??
                  DEFAULT_OFFER_LEAVE_POLICY[key]
              ),
            ])
          )
        );
        setOfferApproval({
          ...normalizedApproval,
          application,
        });
        return;
      }
      return finalCandidateDecision(normalizedApproval, decision);
    }
    if (approvalType === "PAYROLL_APPROVAL") {
      const payroll = data.payroll.find(
        (item) => item.id === approval.relatedId
      );
      if (!payroll) return notify("Related payroll record nahi mila");
      return payrollDecision(payroll, decision);
    }
    if (approvalType === "LEAVE_EXCEPTION") {
      const leave = data.leaves.find(
        (item) => item.id === approval.relatedId
      );
      if (!leave) return notify("Related leave record nahi mila");
      return leaveDecision(
        leave,
        "CEO",
        decision === "APPROVED_BY_CEO" ? "APPROVED" : "REJECTED"
      );
    }
    if (approvalType === "REIMBURSEMENT_APPROVAL") {
      const reimbursement = data.reimbursements.find(
        (item) => item.id === approval.relatedId
      );
      if (!reimbursement) {
        return notify("Related reimbursement claim record nahi mila");
      }
      return reimbursementDecision(
        reimbursement,
        "CEO",
        decision === "APPROVED_BY_CEO" ? "APPROVED" : "REJECTED"
      );
    }
    notify("Is approval ka action module me available hai");
  };

  const confirmCandidateOffer = async () => {
    if (!offerApproval) return;
    const policy = Object.fromEntries(
      Object.keys(DEFAULT_OFFER_LEAVE_POLICY).map((key) => [
        key,
        Number(offerLeaveForm[key]),
      ])
    );
    if (
      Object.values(policy).some(
        (value) => !Number.isFinite(value) || value < 0 || value > 365
      )
    ) {
      return notify("Har leave entitlement 0 se 365 days ke beech rakhein");
    }
    setLoading(true);
    const completed = await finalCandidateDecision(
      offerApproval,
      "APPROVED_BY_CEO",
      policy
    );
    setLoading(false);
    if (completed) {
      setOfferApproval(null);
      setOfferLeaveForm({ ...DEFAULT_OFFER_LEAVE_POLICY });
    }
  };

  const stats = useMemo(
    () => ({
      applications: visibleData.applications.length,
      employees: visibleData.employees.filter((x) => x.status !== "INACTIVE").length,
      present: visibleData.attendance.filter(
        (x) => x.date === today() && ["PRESENT", "CHECK_IN"].includes(x.status || x.actionType)
      ).length,
      leavePending: visibleData.leaves.filter(
        (x) => x.status === "PENDING_HR_APPROVAL"
      ).length,
      employeeRequestPending: visibleData.employeeRequests.filter((x) =>
        ["OPEN", "PENDING", "PENDING_HR_REVIEW", "IN_PROGRESS"].includes(
          clean(x.status).toUpperCase()
        )
      ).length,
      approvalPending: visibleData.approvals.filter(
        (x) =>
          isPendingCEOApproval(x.status) &&
          CEO_APPROVAL_TYPES.has(normalizeApprovalType(x.type))
      ).length,
      payrollPending: visibleData.payroll.filter((x) =>
        isPendingCEOApproval(x.status)
      ).length,
      salaryPending: visibleData.payroll.filter((x) =>
        ["READY_FOR_ACCOUNTS", "PENDING_ACCOUNT_PAYMENT"].includes(
          x.paymentStatus
        )
      ).length,
      payrollAttention: visibleData.payroll.filter(
        (x) =>
          isPendingCEOApproval(x.status) ||
          ["READY_FOR_ACCOUNTS", "PENDING_ACCOUNT_PAYMENT"].includes(
            x.paymentStatus
          )
      ).length,
      reimbursementPending: visibleData.reimbursements.filter((x) =>
        [
          "PENDING_HR_REVIEW",
          "PENDING_HR_APPROVAL",
          "PENDING_CEO_APPROVAL",
        ].includes(x.status)
      ).length,
      reimbursementPaymentPending: visibleData.reimbursements.filter(
        (x) => x.paymentStatus === "PENDING_ACCOUNT_PAYMENT"
      ).length,
      mailPending: visibleData.mail.filter((x) => x.status === "PENDING_SEND").length,
    }),
    [visibleData]
  );
  const activeModuleDefinition = NAV.find(([id]) => id === active);
  const ActiveModuleIcon = activeModuleDefinition?.[2] || Layers3;

  if (!user || !profile) {
    if (authChecking) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-[#eef3f8] p-4">
          <div className="rounded-lg border border-slate-200 bg-white px-8 py-7 text-center shadow-lg">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-xl bg-[#12345b] text-white">
              <RefreshCw size={21} className="animate-spin" />
            </div>
            <p className="mt-4 text-sm font-semibold text-slate-800">
              Verifying secure session
            </p>
            <p className="mt-1 text-xs text-slate-500">
              Please wait while HRMS validates your official account.
            </p>
          </div>
        </div>
      );
    }
    if (HRMS_TEST_MODE) {
      return (
        <div className="flex min-h-screen items-center justify-center bg-gradient-to-br from-slate-950 via-blue-950 to-cyan-900 p-4">
          {message && (
            <div className="fixed right-5 top-5 z-50 max-w-sm rounded-lg bg-red-600 px-5 py-4 font-bold text-white shadow-2xl">
              {message}
            </div>
          )}
          <div className="w-full max-w-md rounded-[2rem] border border-white/15 bg-white p-8 text-center shadow-2xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-lg bg-blue-600 text-white">
              <RefreshCw size={28} className={testBooting ? "animate-spin" : ""} />
            </div>
            <h1 className="mt-5 text-2xl font-semibold">HRMS Test Mode</h1>
            <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
              {testBooting
                ? "Dashboard automatically open ho raha hai..."
                : "Anonymous Authentication enable karke page refresh karein."}
            </p>
            {!testBooting && (
              <Button className="mt-5 w-full" onClick={() => window.location.reload()}>
                Retry Test Dashboard
              </Button>
            )}
          </div>
        </div>
      );
    }
    return (
      <div className="min-h-screen bg-[#eef3f8] px-4 py-8 text-slate-900 sm:px-6 lg:py-12">
        {message && (
          <div
            role="alert"
            className="fixed right-5 top-5 z-50 max-w-md rounded-xl border border-red-200 bg-white px-5 py-4 text-sm font-semibold text-red-700 shadow-xl"
          >
            {message}
          </div>
        )}

        <div className="mx-auto grid min-h-[680px] w-full max-w-6xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_28px_80px_rgba(15,23,42,.12)] lg:grid-cols-[1.08fr_.92fr]">
          <section className="relative hidden overflow-hidden bg-[#0b2340] p-12 text-white lg:flex lg:flex-col">
            <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full border-[70px] border-white/[.035]" />
            <div className="absolute -bottom-40 -left-32 h-96 w-96 rounded-full bg-blue-500/10 blur-3xl" />

            <div className="relative flex items-center gap-4">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/15 bg-white/10">
                <Building2 size={24} />
              </div>
              <div>
                <p className="text-lg font-semibold">Shine Agri HRMS</p>
                <p className="text-xs text-blue-100/70">
                  Human Resource Management System
                </p>
              </div>
            </div>

            <div className="relative my-auto max-w-xl py-12">
              <p className="text-xs font-semibold uppercase tracking-[.24em] text-cyan-300">
                Official Administration Portal
              </p>
              <h1 className="mt-5 text-4xl font-semibold leading-tight tracking-tight xl:text-5xl">
                One secure workspace for people operations.
              </h1>
              <p className="mt-5 max-w-lg text-sm leading-7 text-slate-300">
                Recruitment, onboarding, attendance, employee service, leave,
                payroll, compliance and executive approvals—connected through
                one auditable workflow.
              </p>

              <div className="mt-9 grid gap-3 sm:grid-cols-2">
                {[
                  [ShieldCheck, "Role-based access", "CEO and HR authorization"],
                  [CheckCheck, "Controlled approvals", "Every decision recorded"],
                  [Mail, "Official communication", "Queue-based mail delivery"],
                  [Database, "Audit-ready records", "Live Firestore workflow"],
                ].map(([Icon, title, detail]) => (
                  <div
                    key={title}
                    className="rounded-xl border border-white/10 bg-white/[.055] p-4"
                  >
                    <Icon size={18} className="text-cyan-300" />
                    <p className="mt-3 text-sm font-semibold">{title}</p>
                    <p className="mt-1 text-xs text-slate-400">{detail}</p>
                  </div>
                ))}
              </div>
            </div>

            <p className="relative text-xs leading-5 text-slate-400">
              Authorized access only. Login activity and administrative actions
              may be recorded for security and audit purposes.
            </p>
          </section>

          <section className="flex items-center px-5 py-10 sm:px-10 lg:px-12">
            <form onSubmit={handleLogin} className="mx-auto w-full max-w-md">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700 lg:hidden">
                <Building2 size={23} />
              </div>
              <p className="mt-6 text-xs font-semibold uppercase tracking-[.2em] text-blue-700">
                Secure sign in
              </p>
              <h2 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
                Welcome to HRMS
              </h2>
              <p className="mt-3 text-sm leading-6 text-slate-500">
                Continue with your registered official CEO, HR or Accounts account.
              </p>

              <div className="mt-8">
                <Field
                  label="Official Email Address"
                  type="email"
                  autoComplete="username"
                  placeholder="name@shineagri.org"
                  value={login.email}
                  onChange={(e) =>
                    setLogin({ ...login, email: e.target.value })
                  }
                />
                <Field
                  label="Password"
                  type="password"
                  autoComplete="current-password"
                  className="mt-5"
                  value={login.password}
                  onChange={(e) =>
                    setLogin({ ...login, password: e.target.value })
                  }
                />
              </div>

              <Button
                type="submit"
                className="mt-7 min-h-12 w-full"
                disabled={loading}
              >
                <Lock size={17} />
                {loading ? "Verifying account..." : "Sign in securely"}
              </Button>

              <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="flex items-start gap-3">
                  <ShieldCheck
                    size={18}
                    className="mt-0.5 shrink-0 text-emerald-600"
                  />
                  <p className="text-xs font-medium leading-5 text-slate-600">
                    Firebase Authentication verifies the password. Access is
                    granted only when the matching HRMS profile is ACTIVE and
                    assigned to the correct official role.
                  </p>
                </div>
              </div>

              <p className="mt-7 text-center text-xs text-slate-400">
                CEO: {OFFICIAL_EMAILS.CEO} &nbsp;•&nbsp; HR:{" "}
                {OFFICIAL_EMAILS.HR} &nbsp;•&nbsp; Accounts:{" "}
                {OFFICIAL_EMAILS.ACCOUNT}
              </p>
            </form>
          </section>
        </div>
      </div>
    );
  }

  return (
    <CEODeleteContext.Provider
      value={{
        canDelete: canCEODelete,
        requestDelete: requestCEODelete,
      }}
    >
    <div className="min-h-screen bg-[radial-gradient(circle_at_top_left,rgba(37,99,235,.08),transparent_28%),linear-gradient(180deg,#f8fafc_0%,#eef3f8_100%)] font-sans text-slate-900">
      {scheduleCandidate && (
        <InterviewSchedulerModal
          candidate={scheduleCandidate}
          currentUser={profile}
          firebaseFunctions={interviewFunctions}
          onClose={() => setScheduleCandidate(null)}
          onScheduled={interviewScheduled}
        />
      )}
      {offerApproval && (
        <OfferLeavePolicyModal
          approval={offerApproval}
          form={offerLeaveForm}
          setForm={setOfferLeaveForm}
          busy={loading}
          onConfirm={confirmCandidateOffer}
          onClose={() => setOfferApproval(null)}
        />
      )}
      {offerPreparation && (
        <OfferPreparationModal
          application={offerPreparation.application}
          form={offerPackageForm}
          setForm={setOfferPackageForm}
          file={offerLetterFile}
          setFile={setOfferLetterFile}
          busy={offerUploading}
          onSubmit={submitOfferPreparation}
          onClose={() => {
            if (offerUploading) return;
            setOfferPreparation(null);
            setOfferLetterFile(null);
          }}
        />
      )}
      <CEODeleteDialog
        request={deleteRequest}
        confirmation={deleteConfirmation}
        setConfirmation={setDeleteConfirmation}
        reason={deleteReason}
        setReason={setDeleteReason}
        busy={deleteBusy}
        onConfirm={confirmCEODelete}
        onClose={closeCEODeleteDialog}
      />
      {liveInterview && (
        <VideoInterviewRoom
          firebaseFunctions={interviewFunctions}
          interviewId={liveInterview.id}
          inviteId={
            liveInterview.inviteId ||
            liveInterview.interviewInviteId ||
            ""
          }
          viewerRole="STAFF"
          viewerName={profile.name}
          currentUser={user}
          candidate={liveInterview}
          onClose={() => {
            setLiveInterview(null);
            loadAll();
          }}
        />
      )}
      {message && (
        <div
          role="status"
          className="fixed right-5 top-24 z-[60] max-w-sm rounded-2xl border border-white/10 bg-slate-950/95 px-5 py-4 text-sm font-bold text-white shadow-[0_24px_70px_rgba(2,6,23,.35)] backdrop-blur-xl"
        >
          {message}
        </div>
      )}
      <header className="sticky top-0 z-40 border-b border-slate-800 bg-slate-950/95 text-white shadow-[0_12px_40px_rgba(2,6,23,.18)] backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1560px] items-center justify-between gap-3 px-4 py-3.5 sm:px-6">
          <button
            type="button"
            onClick={() => {
              setActive("dashboard");
              setDashboardView("portal");
            }}
            className="flex min-w-0 items-center gap-3 text-left"
          >
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-blue-600 via-cyan-500 to-emerald-500 text-white shadow-lg shadow-blue-950/40 ring-1 ring-white/20">
              <Building2 size={21} />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <p className="truncate text-base font-black tracking-tight text-white">
                  Shine Agri HRMS
                </p>
                <span className="hidden items-center gap-1.5 rounded-full border border-emerald-300/20 bg-emerald-400/10 px-2.5 py-1 text-[9px] font-black uppercase tracking-wide text-emerald-300 sm:inline-flex">
                  <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-400" /> Live
                </span>
              </div>
              <p className="truncate text-[11px] font-semibold text-slate-400">
                Enterprise Human Capital Command Center
              </p>
            </div>
          </button>
          <div className="flex items-center gap-2">
            {HRMS_TEST_MODE && (
              <div className="hidden items-center rounded-xl border border-white/10 bg-white/5 p-1 lg:flex">
                <button
                  type="button"
                  onClick={() => switchTestRole(ROLE.HR_ADMIN)}
                  className={`rounded-md px-3 py-2 text-[10px] font-semibold ${
                    role === ROLE.HR_ADMIN
                      ? "bg-blue-600 text-white"
                      : "text-slate-300 hover:bg-white/10"
                  }`}
                >
                  HR VIEW
                </button>
                <button
                  type="button"
                  onClick={() => switchTestRole(ROLE.CEO_MASTER)}
                  className={`rounded-md px-3 py-2 text-[10px] font-semibold ${
                    role === ROLE.CEO_MASTER
                      ? "bg-slate-950 text-white"
                      : "text-slate-300 hover:bg-white/10"
                  }`}
                >
                  CEO VIEW
                </button>
              </div>
            )}
            <Button tone="light" onClick={loadAll} disabled={loading}>
              <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
              <span className="hidden sm:inline">Refresh</span>
            </Button>
            <div className="hidden border-l border-white/10 pl-4 sm:block">
              <p className="text-[10px] font-black uppercase tracking-[.12em] text-cyan-300">
                {String(role).replaceAll("_", " ")}
              </p>
              <p className="max-w-[170px] truncate text-sm font-black text-white">
                {profile.name}
              </p>
              <p className="max-w-[170px] truncate text-[10px] font-medium text-slate-400">
                {profile.email}
              </p>
            </div>
            <Button tone="dark" onClick={logout}>
              <LogOut size={16} />
              <span className="hidden sm:inline">
                {HRMS_TEST_MODE ? "Reset Demo" : "Logout"}
              </span>
            </Button>
          </div>
        </div>
        <div className="border-t border-white/5 bg-slate-900/90">
          <nav className="mx-auto flex max-w-[1560px] gap-1.5 overflow-x-auto px-4 py-2.5 sm:px-6">
            <button
              type="button"
              onClick={() => {
                setActive("dashboard");
                setDashboardView("portal");
              }}
              className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-black transition ${
                active === "dashboard"
                  ? "bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg shadow-blue-950/30"
                  : "text-slate-300 hover:bg-white/10 hover:text-white"
              }`}
            >
              <Home size={15} /> Command Center
            </button>
            {NAV_SECTIONS.filter(
              (section) =>
                !canAccounts ||
                section.modules.some((moduleId) => ACCOUNTS_MODULES.has(moduleId))
            ).map((section) => {
              const SectionIcon = section.icon;
              const selected = section.modules.includes(active);
              const sectionTarget =
                canAccounts && section.id === "governance"
                  ? "reports"
                  : section.target;
              return (
                <button
                  key={section.id}
                  type="button"
                  onClick={() => setActive(sectionTarget)}
                  className={`flex shrink-0 items-center gap-2 rounded-xl px-3.5 py-2.5 text-xs font-black transition ${
                    selected
                      ? "bg-white text-slate-950 shadow-lg"
                      : "text-slate-300 hover:bg-white/10 hover:text-white"
                  }`}
                >
                  <SectionIcon size={15} /> {section.label}
                </button>
              );
            })}
          </nav>
        </div>
      </header>

      {HRMS_TEST_MODE && (
        <div className="border-b border-amber-200 bg-amber-50 px-4 py-2 text-center text-[11px] font-semibold text-amber-800">
          TEST MODE ACTIVE — password login bypassed through Firebase Anonymous
          Authentication. Production se pehle `HRMS_TEST_MODE = false` karein.
        </div>
      )}

      <main className="mx-auto max-w-[1560px] px-4 py-6 sm:px-6 lg:py-8">
        {loadWarnings.length > 0 && (
          <div className="mb-6 rounded-2xl border border-red-200 bg-red-50/95 p-5 text-red-800 shadow-lg shadow-red-900/5">
            <div className="flex items-start gap-3">
              <ShieldAlert className="mt-0.5 shrink-0" size={20} />
              <div>
                <p className="font-semibold">
                  {loadWarnings.length} HRMS collection(s) load nahi hui
                </p>
                <p className="mt-1 text-xs font-semibold">
                  Zero records ko real zero na samjhein. Firestore rules/index
                  verify karein: {loadWarnings.join(" • ")}
                </p>
                <p className="mt-2 text-[11px] font-semibold text-red-600/80">
                  Connected Firebase project: {db?.app?.options?.projectId || "unknown"}
                  {" • "}CLI deploy isi project par hona chahiye.
                </p>
              </div>
            </div>
          </div>
        )}
        {active !== "dashboard" && (
          <div className="relative mb-6 flex flex-wrap items-center justify-between gap-4 overflow-hidden rounded-2xl border border-slate-200 bg-white px-5 py-5 shadow-[0_16px_50px_rgba(15,23,42,.07)] sm:px-6">
            <div className="absolute inset-y-0 left-0 w-1.5 bg-gradient-to-b from-blue-600 via-cyan-500 to-emerald-500" />
            <div className="pointer-events-none absolute -right-16 -top-24 h-56 w-56 rounded-full bg-blue-50" />
            <div className="relative flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-gradient-to-br from-blue-50 to-cyan-50 text-blue-700 shadow-sm">
                <ActiveModuleIcon size={19} />
              </span>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="text-[10px] font-black uppercase tracking-[.16em] text-emerald-700">
                    Live HRMS Operational Module
                  </p>
                  <Badge value={role} />
                </div>
                <h1 className="mt-1 text-xl font-black tracking-tight text-slate-950 sm:text-2xl">
                  {activeModuleDefinition?.[1] || active}
                </h1>
                <p className="mt-1 max-w-3xl text-xs font-semibold leading-5 text-slate-500">
                  {MODULE_DESCRIPTIONS[active] ||
                    "Controlled HRMS workflow and official records."}
                </p>
              </div>
            </div>
            <Button
              tone="light"
              onClick={() => {
                setActive("dashboard");
                setDashboardView("portal");
              }}
            >
              <ChevronLeft size={17} /> Portal Home
            </Button>
          </div>
        )}

        {active === "dashboard" && (
          <Dashboard
            profile={profile}
            stats={stats}
            data={visibleData}
            setActive={setActive}
            dashboardView={dashboardView}
            setDashboardView={setDashboardView}
          />
        )}
        {active === "requisitions" && (
          <RequisitionModule
            form={reqForm}
            setForm={setReqForm}
            rows={visibleData.requisitions}
            create={createRequisition}
            approve={approveRequisition}
            publish={publishJob}
            canCEO={canCEO}
            canHR={canHR}
          />
        )}
        {active === "recruitment" && (
          <RecruitmentModule
            rows={visibleData.applications}
            candidateUsers={visibleData.candidateUsers}
            downloadCv={downloadApplicantCv}
            updateStage={updateCandidateStage}
            schedule={scheduleInterview}
            archive={archiveCandidateApplication}
            reopen={reopenCandidateApplication}
            reInterview={reInterviewCandidate}
            prepareOffer={openOfferPreparation}
            convertApplicantToEmployee={convertApplicantToEmployee}
            search={search}
            setSearch={setSearch}
            canHR={canHR}
            canPrepareOffer={isHRAdmin}
          />
        )}
        {active === "interviews" && (
          <InterviewModule
            rows={visibleData.interviews}
            result={interviewResult}
            join={(item) => setLiveInterview(item)}
            resend={resendInterviewInvite}
            reschedule={rescheduleInterview}
            cancel={cancelInterview}
            applications={visibleData.applications}
            archive={archiveCandidateApplication}
            reopen={reopenCandidateApplication}
            reInterview={reInterviewCandidate}
            canHR={canHR}
            isHRAdmin={isHRAdmin}
            canCEO={canCEO}
            openApprovals={() => setActive("approvals")}
          />
        )}
        {active === "onboarding" && (
          <OnboardingModule rows={visibleData.onboarding} toggle={toggleOnboarding} canHR={canHR} />
        )}
        {active === "employees" && (
          <EmployeeModule
            form={employeeForm}
            setForm={setEmployeeForm}
            rows={visibleData.employees}
            applications={visibleData.applications}
            candidateUsers={visibleData.candidateUsers}
            create={createEmployee}
            updateDetails={updateEmployeeDetails}
            updateBank={updateEmployeeBank}
            updateSalary={updateEmployeeSalary}
            updateAttendanceLocation={updateAttendanceLocation}
            resetAttendanceDevice={resetAttendanceDevice}
            setAttendancePin={setAttendancePin}
            canHR={canHR}
            canCreateDirect={canCEO}
          />
        )}
        {active === "attendance" && (
          <AttendanceModule
            rows={visibleData.attendance}
            decision={attendanceDecision}
            openCorrections={() => setActive("employeeRequests")}
            canHR={canHR}
          />
        )}
        {active === "employeeRequests" && (
          <EmployeeRequestsModule
            rows={visibleData.employeeRequests}
            decision={employeeRequestDecision}
            canHR={canHR}
          />
        )}
        {active === "leave" && (
          <LeaveModule
            form={leaveForm}
            setForm={setLeaveForm}
            rows={visibleData.leaves}
            employees={visibleData.employees}
            submit={submitLeave}
            decision={leaveDecision}
            canCEO={canCEO}
            isHRAdmin={isHRAdmin}
            holidays={visibleData.holidays}
            settings={visibleData.settings}
          />
        )}
        {active === "holidays" && (
          <HolidayCalendarModule
            form={holidayForm}
            setForm={setHolidayForm}
            rows={visibleData.holidays}
            settings={visibleData.settings}
            save={saveHoliday}
            importGujarat2026={importGujarat2026Holidays}
            saveSettings={savePayrollCalendarSettings}
            canHR={canHR}
          />
        )}
        {active === "payroll" && (
          <PayrollModule
            form={payForm}
            setForm={setPayForm}
            employees={visibleData.employees}
            rows={visibleData.payroll}
            batches={visibleData.payrollBatches}
            mailRows={visibleData.mail}
            calculation={payrollCalculation}
            selectEmployee={selectPayrollEmployee}
            prepare={preparePayroll}
            decision={payrollDecision}
            sendToAccount={sendPayrollToAccount}
            recordPayment={recordAccountsPayment}
            runAutomation={runPayrollAutomation}
            downloadPayslip={downloadPayslip}
            canHR={canHR}
            isHRAdmin={isHRAdmin}
            canCEO={canCEO}
            canSendToAccounts={canSendToAccounts}
            canRecordPayment={canRecordAccountsPayment}
          />
        )}
        {active === "statutory" && (
          <StatutoryComplianceModule
            form={statutoryForm}
            setForm={setStatutoryForm}
            rows={visibleData.statutory}
            create={createStatutoryEntry}
            recordPayment={recordStatutoryPayment}
            canManage={canRecordAccountsPayment}
          />
        )}
        {active === "reimbursements" && (
          <ReimbursementModule
            form={reimbursementForm}
            setForm={setReimbursementForm}
            employees={visibleData.employees}
            rows={visibleData.reimbursements}
            selectEmployee={selectReimbursementEmployee}
            submit={submitReimbursement}
            decision={reimbursementDecision}
            recordPayment={recordReimbursementPayment}
            canHR={canHR}
            isHRAdmin={isHRAdmin}
            canCEO={canCEO}
            canRecordPayment={canRecordAccountsPayment}
          />
        )}
        {active === "documents" && (
          <DocumentsModule
            rows={visibleData.documents}
            decision={documentDecision}
            canHR={canHR}
          />
        )}
        {active === "approvals" && (
          <ApprovalModule
            rows={visibleData.approvals}
            decision={handleApprovalDecision}
            canCEO={canCEO}
          />
        )}
        {active === "mail" && (
          <MailModule
            rows={visibleData.mail}
            retry={retryFailedMail}
            testMail={sendMailSystemTest}
            canHR={canManageMail}
          />
        )}
        {active === "reports" && <ReportsModule data={visibleData} />}
        {active === "settings" && <SettingsModule role={role} />}
      </main>
    </div>
    </CEODeleteContext.Provider>
  );
}

function ProfessionalPortalHome({
  profile,
  stats,
  data,
  setActive,
  openAnalytics,
}) {
  const dashboardRole = normalizedDashboardRole(profile);
  const isCEO = dashboardRole === ROLE.CEO_MASTER;
  const isAccounts = dashboardRole === ROLE.ACCOUNTS_ADMIN;
  const activeEmployees = data.employees.filter(
    (item) => item.status !== "INACTIVE"
  ).length;
  const publishedJobs = data.jobs.filter((item) =>
    ["PUBLISHED", "ACTIVE"].includes(item.status)
  ).length;
  const onboardingPending = data.onboarding.filter(
    (item) => item.status !== "COMPLETED"
  ).length;
  const leavePending = data.leaves.filter((item) =>
    ["PENDING", "PENDING_HR_APPROVAL", "PENDING_CEO_APPROVAL"].includes(
      clean(item.status).toUpperCase()
    )
  ).length;
  const documentRisk = data.documents.filter((item) =>
    ["PENDING", "EXPIRED", "REJECTED", "PENDING_HR_VERIFICATION"].includes(
      clean(item.status).toUpperCase()
    )
  ).length;
  const paidPayrollValue = data.payroll
    .filter((item) => item.paymentStatus === "PAID")
    .reduce(
      (total, item) => total + number(item.net || item.netPayable),
      0
    );
  const paidReimbursementValue = data.reimbursements
    .filter((item) => item.paymentStatus === "PAID")
    .reduce((total, item) => total + number(item.amount), 0);
  const statutoryOpen = data.statutory.filter((item) => !item.isClosed);
  const statutoryOverdue = statutoryOpen.filter(
    (item) => effectiveStatutoryStatus(item) === "OVERDUE"
  ).length;
  const paidPayrollCount = data.payroll.filter(
    (item) => clean(item.paymentStatus).toUpperCase() === "PAID"
  ).length;
  const payrollPaymentRate = data.payroll.length
    ? Math.round((paidPayrollCount / data.payroll.length) * 100)
    : 100;
  const closedStatutoryCount = data.statutory.filter(
    (item) => item.isClosed || clean(item.status).toUpperCase() === "PAID"
  ).length;
  const statutoryClearanceRate = data.statutory.length
    ? Math.round((closedStatutoryCount / data.statutory.length) * 100)
    : 100;
  const openActions =
    stats.approvalPending +
    stats.employeeRequestPending +
    leavePending +
    onboardingPending +
    documentRisk +
    stats.reimbursementPending +
    stats.reimbursementPaymentPending +
    stats.mailPending;
  const visibleOpenActions = isAccounts
    ? stats.salaryPending + statutoryOpen.length
    : openActions;
  const attendanceRate = activeEmployees
    ? Math.min(100, Math.round((stats.present / activeEmployees) * 100))
    : 0;
  const onboardingComplete = data.onboarding.filter(
    (item) => item.status === "COMPLETED"
  ).length;
  const onboardingRate = data.onboarding.length
    ? Math.round((onboardingComplete / data.onboarding.length) * 100)
    : 100;
  const selectedEmployees = data.applications.filter(
    (item) => item.status === "EMPLOYEE_CREATED"
  ).length;
  const hiringRate = data.applications.length
    ? Math.round((selectedEmployees / data.applications.length) * 100)
    : 0;
  const currentDate = new Intl.DateTimeFormat("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  }).format(new Date());

  const workspaceCards = [
    {
      id: "talent",
      title: "Talent & Recruitment",
      description:
        "Plan vacancies, screen applicants and complete structured interviews.",
      icon: UserRoundSearch,
      tone: "border-blue-200 bg-blue-50 text-blue-700",
      metric: `${publishedJobs} open position${publishedJobs === 1 ? "" : "s"}`,
      modules: [
        ["requisitions", "Job Requisition", data.requisitions.length],
        ["recruitment", "Recruitment ATS", data.applications.length],
        ["interviews", "Interview Process", data.interviews.length],
      ],
    },
    {
      id: "people",
      title: "People & Onboarding",
      description:
        "Convert selected candidates into verified, active employee records.",
      icon: Users,
      tone: "border-emerald-200 bg-emerald-50 text-emerald-700",
      metric: `${activeEmployees} active employee${
        activeEmployees === 1 ? "" : "s"
      }`,
      modules: [
        ["onboarding", "Onboarding", onboardingPending],
        ["employees", "Employee Master", activeEmployees],
        ["documents", "Document Control", data.documents.length],
      ],
    },
    {
      id: "service",
      title: "Time, Leave & Service",
      description:
        "Review attendance, employee requests, leave balance and LOP impact.",
      icon: CalendarDays,
      tone: "border-cyan-200 bg-cyan-50 text-cyan-700",
      metric: `${stats.present} present today`,
      modules: [
        ["attendance", "Attendance", data.attendance.length],
        [
          "employeeRequests",
          "Employee Requests",
          stats.employeeRequestPending,
        ],
        ["leave", "Leave Management", leavePending],
      ],
    },
    {
      id: "finance",
      title: "Payroll & Accounts",
      description:
        "Process payroll, statutory deductions, reimbursements and Accounts payments.",
      icon: Wallet,
      tone: "border-violet-200 bg-violet-50 text-violet-700",
      metric: `${
        stats.salaryPending + stats.reimbursementPaymentPending
      } payment pending`,
      modules: [
        ["payroll", "Payroll Processing", data.payroll.length],
        ["statutory", "TDS / PF Compliance", statutoryOpen.length],
        [
          "reimbursements",
          "Reimbursements",
          data.reimbursements.length,
        ],
        ["mail", "Accounts Advice", stats.mailPending],
      ],
    },
    {
      id: "governance",
      title: "Executive Governance",
      description:
        "Control management approvals, exceptions, reports and audit evidence.",
      icon: ShieldCheck,
      tone: "border-amber-200 bg-amber-50 text-amber-700",
      metric: `${stats.approvalPending} approval${
        stats.approvalPending === 1 ? "" : "s"
      } pending`,
      modules: [
        ["approvals", "Approval Center", stats.approvalPending],
        ["reports", "Reports & Audit", data.audit.length],
      ],
    },
    {
      id: "communication",
      title: "Communication & System",
      description:
        "Track official mail delivery, retry failures and review configuration.",
      icon: Mail,
      tone: "border-slate-200 bg-slate-100 text-slate-700",
      metric: `${stats.mailPending} mail item${
        stats.mailPending === 1 ? "" : "s"
      } queued`,
      modules: [
        ["mail", "Communication Queue", data.mail.length],
        ["settings", "System Settings", 0],
      ],
    },
  ];
  const visibleWorkspaceCards = isAccounts
    ? workspaceCards
        .filter((workspace) => workspace.id === "finance")
        .map((workspace) => ({
          ...workspace,
          description:
            "Process CEO-approved payroll payments and maintain Section 192/194 TDS and PF challan compliance.",
          metric: `${stats.salaryPending + statutoryOpen.length} finance action(s) pending`,
          modules: workspace.modules.filter(([target]) =>
            ACCOUNTS_MODULES.has(target)
          ),
        }))
    : workspaceCards;

  const actionQueue = [
    {
      label: "Statutory compliance",
      detail: "Section 192/194 TDS and PF challan tracking",
      value: statutoryOverdue || statutoryOpen.length,
      target: "statutory",
      icon: Landmark,
      tone: statutoryOverdue ? "bg-red-50 text-red-700" : "bg-blue-50 text-blue-700",
    },
    {
      label: "Salary payments",
      detail: "Approved payroll awaiting Accounts UTR",
      value: stats.salaryPending,
      target: "payroll",
      icon: ReceiptIndianRupee,
      tone: "bg-emerald-50 text-emerald-700",
    },
    {
      label: "Executive approvals",
      detail: "CEO decision required",
      value: stats.approvalPending,
      target: "approvals",
      icon: Crown,
      tone: "bg-amber-50 text-amber-700",
    },
    {
      label: "Employee service requests",
      detail: "Attendance, profile and helpdesk",
      value: stats.employeeRequestPending,
      target: "employeeRequests",
      icon: MessageSquareText,
      tone: "bg-blue-50 text-blue-700",
    },
    {
      label: "Leave requests",
      detail: "Balance and LOP review",
      value: leavePending,
      target: "leave",
      icon: CalendarClock,
      tone: "bg-orange-50 text-orange-700",
    },
    {
      label: "Onboarding files",
      detail: "Activation steps incomplete",
      value: onboardingPending,
      target: "onboarding",
      icon: UserCheck,
      tone: "bg-emerald-50 text-emerald-700",
    },
    {
      label: "Document exceptions",
      detail: "Verification or expiry attention",
      value: documentRisk,
      target: "documents",
      icon: FileCheck2,
      tone: "bg-red-50 text-red-700",
    },
    {
      label: "Reimbursements",
      detail: "HR/CEO review and Accounts payment",
      value:
        stats.reimbursementPending + stats.reimbursementPaymentPending,
      target: "reimbursements",
      icon: CircleDollarSign,
      tone: "bg-violet-50 text-violet-700",
    },
    {
      label: "Communication queue",
      detail: "Mail delivery pending",
      value: stats.mailPending,
      target: "mail",
      icon: Mail,
      tone: "bg-violet-50 text-violet-700",
    },
  ];
  const visibleActionQueue = isAccounts
    ? actionQueue.filter((action) =>
        ["payroll", "statutory"].includes(action.target)
      )
    : actionQueue;

  const keyMetrics = [
    {
      label: "Active workforce",
      value: activeEmployees,
      detail: `${data.employees.length} employee records`,
      icon: Users,
      tone: "bg-blue-50 text-blue-700",
    },
    {
      label: "Present today",
      value: stats.present,
      detail: `${attendanceRate}% attendance`,
      icon: CalendarCheck,
      tone: "bg-emerald-50 text-emerald-700",
    },
    {
      label: "Open positions",
      value: publishedJobs,
      detail: `${data.applications.length} applications`,
      icon: BriefcaseBusiness,
      tone: "bg-cyan-50 text-cyan-700",
    },
    {
      label: "Pending approvals",
      value: stats.approvalPending,
      detail: openActions ? `${openActions} total open actions` : "Queue clear",
      icon: CheckCheck,
      tone: "bg-amber-50 text-amber-700",
    },
    {
      label: "Payroll processed",
      value: money(paidPayrollValue),
      detail: `${stats.salaryPending} payments pending`,
      icon: Banknote,
      tone: "bg-violet-50 text-violet-700",
    },
    {
      label: "Statutory open",
      value: statutoryOpen.length,
      detail: `${statutoryOverdue} overdue TDS/PF item(s)`,
      icon: Landmark,
      tone: "bg-blue-50 text-blue-700",
    },
    {
      label: "Reimbursements paid",
      value: money(paidReimbursementValue),
      detail: `${stats.reimbursementPaymentPending} payments pending`,
      icon: CircleDollarSign,
      tone: "bg-cyan-50 text-cyan-700",
    },
  ];
  const visibleKeyMetrics = isAccounts
    ? keyMetrics.filter((metric) =>
        ["Payroll processed", "Statutory open"].includes(metric.label)
      )
    : keyMetrics;

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-[0_1px_2px_rgba(15,23,42,.04)]">
        <div className="h-1 bg-gradient-to-r from-[#12345b] via-blue-600 to-cyan-500" />
        <div className="flex flex-col gap-6 p-6 lg:flex-row lg:items-center lg:justify-between lg:p-8">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-emerald-700">
                <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                Live workspace
              </span>
              <Badge value={dashboardRole} />
            </div>
            <p className="mt-5 text-xs font-medium text-slate-500">
              {currentDate}
            </p>
            <h1 className="mt-2 text-3xl font-semibold tracking-tight text-slate-950">
              {isCEO
                ? "Executive HR overview"
                : isAccounts
                  ? "Finance operations overview"
                  : "People operations overview"}
            </h1>
            <p className="mt-2 max-w-3xl text-sm leading-6 text-slate-500">
              {isAccounts
                ? `Welcome, ${profile.name}. Only approved payroll payments and statutory TDS/PF compliance records are available in this restricted workspace.`
                : `Welcome, ${profile.name}. Current workforce, talent, employee service and payroll priorities are consolidated below from live HRMS records.`}
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {!isAccounts && (
              <Button tone="light" onClick={openAnalytics}>
                <BarChart3 size={16} /> Executive Analytics
              </Button>
            )}
            <Button
              onClick={() =>
                setActive(
                  isCEO ? "approvals" : isAccounts ? "payroll" : "requisitions"
                )
              }
            >
              {isCEO ? (
                <Crown size={16} />
              ) : isAccounts ? (
                <IndianRupee size={16} />
              ) : (
                <BriefcaseBusiness size={16} />
              )}
              {isCEO
                ? "Review approvals"
                : isAccounts
                  ? "Open payment desk"
                  : "Create requisition"}
            </Button>
          </div>
        </div>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-6">
        {visibleKeyMetrics.map((metric) => {
          const MetricIcon = metric.icon;
          return (
            <article
              key={metric.label}
              className="rounded-lg border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)]"
            >
              <div
                className={`flex h-10 w-10 items-center justify-center rounded-xl ${metric.tone}`}
              >
                <MetricIcon size={19} />
              </div>
              <p className="mt-4 break-words text-2xl font-semibold tracking-tight text-slate-950">
                {metric.value}
              </p>
              <p className="mt-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                {metric.label}
              </p>
              <p className="mt-2 text-xs text-slate-400">{metric.detail}</p>
            </article>
          );
        })}
      </section>

      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-blue-700">
              Enterprise Workspaces
            </p>
            <h2 className="mt-1 text-xl font-semibold text-slate-950">
              Choose where you want to work
            </h2>
          </div>
          <p className="text-xs text-slate-500">
            All modules use the same role-controlled live records.
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {visibleWorkspaceCards.map((workspace) => {
            const WorkspaceIcon = workspace.icon;
            return (
              <article
                key={workspace.id}
                className="group flex min-h-[270px] flex-col rounded-lg border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,.04)] transition hover:border-blue-200 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-4">
                  <div
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border ${workspace.tone}`}
                  >
                    <WorkspaceIcon size={22} />
                  </div>
                  <span className="rounded-full bg-slate-50 px-3 py-1 text-[10px] font-semibold text-slate-500">
                    {workspace.metric}
                  </span>
                </div>
                <h3 className="mt-5 text-lg font-semibold text-slate-950">
                  {workspace.title}
                </h3>
                <p className="mt-2 text-sm leading-6 text-slate-500">
                  {workspace.description}
                </p>
                <div className="mt-auto space-y-1 border-t border-slate-100 pt-4">
                  {workspace.modules.map(([target, label, count]) => (
                    <button
                      key={target}
                      type="button"
                      onClick={() => setActive(target)}
                      className="flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm font-medium text-slate-700 transition hover:bg-blue-50 hover:text-blue-700"
                    >
                      <span>{label}</span>
                      <span className="flex items-center gap-2 text-xs text-slate-400">
                        {count}
                        <ChevronRight size={14} />
                      </span>
                    </button>
                  ))}
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.3fr_.7fr]">
        <Card
          title={
            isCEO
              ? "Executive Action Inbox"
              : isAccounts
                ? "Accounts Action Inbox"
                : "HR Action Inbox"
          }
          icon={ClipboardCheck}
          action={
            <span className="rounded-full bg-slate-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-wide text-slate-600">
              {visibleOpenActions} active queue{visibleOpenActions === 1 ? "" : "s"}
            </span>
          }
        >
          <div className="divide-y divide-slate-100">
            {visibleActionQueue.map((action) => {
              const ActionIcon = action.icon;
              return (
                <button
                  key={action.label}
                  type="button"
                  onClick={() => setActive(action.target)}
                  className="flex w-full items-center gap-4 py-3 text-left transition first:pt-0 last:pb-0 hover:bg-slate-50"
                >
                  <span
                    className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${action.tone}`}
                  >
                    <ActionIcon size={18} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-semibold text-slate-800">
                      {action.label}
                    </span>
                    <span className="mt-0.5 block truncate text-xs text-slate-400">
                      {action.detail}
                    </span>
                  </span>
                  <span
                    className={`flex min-w-8 items-center justify-center rounded-full px-2 py-1 text-xs font-semibold ${
                      action.value
                        ? "bg-slate-950 text-white"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {action.value}
                  </span>
                  <ChevronRight size={16} className="text-slate-300" />
                </button>
              );
            })}
          </div>
        </Card>

        <Card title={isAccounts ? "Finance Control" : "Workforce Pulse"} icon={TrendingUp}>
          <div className="space-y-5">
            {(isAccounts
              ? [
                  ["Payroll payments", payrollPaymentRate, "bg-emerald-500"],
                  ["Statutory clearance", statutoryClearanceRate, "bg-blue-600"],
                ]
              : [
                  ["Attendance", attendanceRate, "bg-emerald-500"],
                  ["Onboarding", onboardingRate, "bg-blue-600"],
                  ["Hiring conversion", hiringRate, "bg-violet-600"],
                ]
            ).map(([label, value, color]) => (
              <div key={label}>
                <div className="mb-2 flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-600">{label}</span>
                  <span className="text-slate-900">{value}%</span>
                </div>
                <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${color}`}
                    style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              Official process
            </p>
            <div className="mt-3 space-y-3 text-xs">
              {[
                ["01", "HR prepares and verifies", OFFICIAL_EMAILS.HR],
                ["02", "CEO approves exceptions", OFFICIAL_EMAILS.CEO],
                ["03", "Accounts processes payment", OFFICIAL_EMAILS.ACCOUNT],
              ].map(([step, title, email]) => (
                <div key={step} className="flex items-start gap-3">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-[#12345b] text-[9px] font-semibold text-white">
                    {step}
                  </span>
                  <div className="min-w-0">
                    <p className="font-semibold text-slate-700">{title}</p>
                    <p className="truncate text-slate-400">{email}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </Card>
      </section>

      <section className="flex flex-col gap-3 rounded-xl border border-slate-200 bg-white px-5 py-4 text-xs text-slate-500 shadow-[0_1px_2px_rgba(15,23,42,.04)] sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <ShieldCheck size={16} className="text-emerald-600" />
          <span>
            Signed in as <strong className="text-slate-700">{profile.email}</strong>
          </span>
        </div>
        <span>
          Role-controlled access • Live Firestore records • Audit trail enabled
        </span>
      </section>
    </div>
  );
}

function Dashboard({
  profile,
  stats,
  data,
  setActive,
  dashboardView,
  setDashboardView,
}) {
  const [aiPrompt, setAiPrompt] = useState("");
  const [aiAnswer, setAiAnswer] = useState(
    "Good evening. I have analysed recruitment, workforce, compliance and payroll. Ask me for an executive summary or a recommended action."
  );
  const workspaceDate = new Intl.DateTimeFormat("en-IN", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
    timeZone: "Asia/Kolkata",
  }).format(new Date());

  const activeEmployees = data.employees.filter(
    (item) => item.status !== "INACTIVE"
  ).length;
  const publishedJobs = data.jobs.filter((item) =>
    ["PUBLISHED", "ACTIVE"].includes(item.status)
  ).length;
  const shortlisted = data.applications.filter((item) =>
    ["SHORTLISTED", "INTERVIEW_SCHEDULED", "PENDING_CEO_APPROVAL"].includes(
      item.status
    )
  ).length;
  const offers = data.applications.filter((item) =>
    ["OFFER_RELEASED", "CANDIDATE_ACCEPTED", "EMPLOYEE_CREATED"].includes(
      item.status
    )
  ).length;
  const hires = data.applications.filter(
    (item) => item.status === "EMPLOYEE_CREATED"
  ).length;
  const onboardingPending = data.onboarding.filter(
    (item) => item.status !== "COMPLETED"
  ).length;
  const onboardingDone = data.onboarding.filter(
    (item) => item.status === "COMPLETED"
  ).length;
  const todayAttendance = data.attendance.filter(
    (item) => String(item.date || "").slice(0, 10) === today()
  );
  const lateToday = todayAttendance.filter(
    (item) =>
      item.status === "LATE" ||
      item.attendanceStatus === "LATE" ||
      item.isLate === true
  ).length;
  const leavePending = data.leaves.filter(
    (item) => item.status === "PENDING_HR_APPROVAL"
  ).length;
  const lopDays = data.leaves.reduce(
    (total, item) => total + number(item.lopDays),
    0
  );
  const payrollValue = data.payroll.reduce(
    (total, item) => total + number(item.net || item.netPayable),
    0
  );
  const payrollPaid = data.payroll
    .filter((item) => item.paymentStatus === "PAID")
    .reduce((total, item) => total + number(item.net || item.netPayable), 0);
  const reimbursementValue = data.reimbursements.reduce(
    (total, item) => total + number(item.amount),
    0
  );
  const reimbursementPaid = data.reimbursements
    .filter((item) => item.paymentStatus === "PAID")
    .reduce((total, item) => total + number(item.amount), 0);
  const documentRisk = data.documents.filter((item) => {
    if (["PENDING", "EXPIRED", "REJECTED"].includes(item.status)) return true;
    if (!item.expiryDate) return false;
    const remaining =
      (new Date(item.expiryDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24);
    return remaining <= 30;
  }).length;
  const pendingActions =
    stats.approvalPending +
    leavePending +
    stats.employeeRequestPending +
    stats.salaryPending +
    stats.reimbursementPending +
    stats.reimbursementPaymentPending +
    stats.mailPending +
    onboardingPending;
  const conversionRate = data.applications.length
    ? Math.round((hires / data.applications.length) * 100)
    : 0;
  const attendanceRate = activeEmployees
    ? Math.min(100, Math.round((stats.present / activeEmployees) * 100))
    : 0;
  const onboardingRate = data.onboarding.length
    ? Math.round((onboardingDone / data.onboarding.length) * 100)
    : 100;
  const automationHealth = Math.max(
    72,
    Math.min(99, 98 - Math.min(26, pendingActions))
  );

  const funnel = [
    ["Applications", data.applications.length, "bg-blue-600"],
    ["AI Shortlisted", shortlisted, "bg-cyan-500"],
    ["Interviews", data.interviews.length, "bg-violet-600"],
    ["Offers", offers, "bg-amber-500"],
    ["Employees", hires, "bg-emerald-600"],
  ];
  const funnelMax = Math.max(1, ...funnel.map((item) => item[1]));

  const processCards = [
    {
      id: "requisitions",
      title: "Talent Acquisition",
      subtitle: "Smart JD, approvals & career publishing",
      icon: UserRoundSearch,
      tone: "from-blue-600 to-cyan-500",
      value: publishedJobs,
      metric: "LIVE JOBS",
      alert: stats.approvalPending,
    },
    {
      id: "onboarding",
      title: "Employee Lifecycle",
      subtitle: "ID, hierarchy, documents & activation",
      icon: Layers3,
      tone: "from-emerald-600 to-teal-500",
      value: onboardingRate,
      metric: "% COMPLETE",
      alert: onboardingPending,
    },
    {
      id: "leave",
      title: "Time & Leave",
      subtitle: "Attendance, balance, Comp-Off & LOP",
      icon: CalendarDays,
      tone: "from-orange-500 to-amber-500",
      value: leavePending,
      metric: "PENDING",
      alert: lateToday,
    },
    {
      id: "payroll",
      title: "Payroll & Compliance",
      subtitle: "Statutory payroll, approval & NEFT",
      icon: CircleDollarSign,
      tone: "from-violet-600 to-indigo-600",
      value: stats.salaryPending,
      metric: "NEFT PENDING",
      alert: stats.payrollPending,
    },
    {
      id: "reimbursements",
      title: "Reimbursements",
      subtitle: "HR review, CEO threshold & Accounts UTR",
      icon: Banknote,
      tone: "from-cyan-600 to-blue-600",
      value:
        stats.reimbursementPending + stats.reimbursementPaymentPending,
      metric: "ACTION PENDING",
      alert: stats.reimbursementPending,
    },
  ];

  const aiInsights = [
    stats.employeeRequestPending > 0
      ? {
          level: "warning",
          title: `${stats.employeeRequestPending} employee request${
            stats.employeeRequestPending > 1 ? "s" : ""
          } waiting`,
          text: "Attendance corrections, profile changes and employee helpdesk requests require HR action.",
          action: "employeeRequests",
        }
      : null,
    stats.approvalPending > 0
      ? {
          level: "critical",
          title: `${stats.approvalPending} management approval${stats.approvalPending > 1 ? "s" : ""} waiting`,
          text: "Clearing approvals now will reduce recruitment and payroll cycle time.",
          action: "approvals",
        }
      : null,
    data.applications.length > 0 && shortlisted === 0
      ? {
          level: "warning",
          title: "Candidate screening needs attention",
          text: `${data.applications.length} applications are available but no candidate has progressed.`,
          action: "recruitment",
        }
      : null,
    onboardingPending > 0
      ? {
          level: "warning",
          title: `${onboardingPending} onboarding file${onboardingPending > 1 ? "s" : ""} incomplete`,
          text: "Documents, hierarchy or attendance activation is still pending.",
          action: "onboarding",
        }
      : null,
    documentRisk > 0
      ? {
          level: "critical",
          title: `${documentRisk} document compliance alert${documentRisk > 1 ? "s" : ""}`,
          text: "Expired, rejected or expiring documents require verification.",
          action: "documents",
        }
      : null,
    leavePending > 0
      ? {
          level: "info",
          title: `${leavePending} leave request${leavePending > 1 ? "s" : ""} pending`,
          text: `Current approved LOP impact is ${lopDays} day(s).`,
          action: "leave",
        }
      : null,
    stats.reimbursementPending + stats.reimbursementPaymentPending > 0
      ? {
          level: "warning",
          title: `${
            stats.reimbursementPending +
            stats.reimbursementPaymentPending
          } reimbursement action${
            stats.reimbursementPending +
              stats.reimbursementPaymentPending >
            1
              ? "s"
              : ""
          } pending`,
          text: "Complete HR/CEO review or record the Accounts UTR to close the claim.",
          action: "reimbursements",
        }
      : null,
  ].filter(Boolean);
  if (!aiInsights.length) {
    aiInsights.push({
      level: "success",
      title: "All critical workflows are under control",
      text: "No immediate exception requires management action.",
      action: "reports",
    });
  }

  if (dashboardView === "portal") {
    return (
      <ProfessionalPortalHome
        profile={profile}
        stats={stats}
        data={data}
        setActive={setActive}
        openAnalytics={() => setDashboardView("analytics")}
      />
    );
  }

  const runAi = (prompt = aiPrompt) => {
    const value = clean(prompt).toLowerCase();
    if (!value) {
      setAiAnswer("Type a question or select one of the recommended prompts.");
      return;
    }
    if (/(hire|recruit|candidate|vacancy|ats)/.test(value)) {
      setAiAnswer(
        `Recruitment intelligence: ${publishedJobs} job(s) are live, ${data.applications.length} application(s) received, ${shortlisted} progressed, ${offers} offer(s) issued and ${hires} converted to employee. Current hiring conversion is ${conversionRate}%. ${
          shortlisted === 0 && data.applications.length
            ? "Recommended next action: open ATS Applications and shortlist high-score candidates."
            : "Recommended next action: prioritise pending interviews and management approvals."
        }`
      );
    } else if (/(pay|salary|neft|tax|pf|esi|tds)/.test(value)) {
      setAiAnswer(
        `Payroll intelligence: processed payroll value is ${money(
          payrollValue
        )}; ${money(payrollPaid)} is marked paid. ${
          stats.payrollPending
        } payroll approval(s) and ${
          stats.salaryPending
        } NEFT payment(s) remain. Verify attendance/LOP, deductions and bank references before closure.`
      );
    } else if (/(attendance|leave|late|lop)/.test(value)) {
      setAiAnswer(
        `Workforce time intelligence: ${stats.present}/${activeEmployees} employees are present today (${attendanceRate}%), ${lateToday} late event(s), ${leavePending} leave request(s) pending and ${lopDays} LOP day(s) recorded.`
      );
    } else if (/(compliance|document|risk|audit)/.test(value)) {
      setAiAnswer(
        `Compliance intelligence: ${documentRisk} document risk(s), ${
          data.audit.length
        } auditable activity record(s), and ${
          stats.approvalPending
        } governance approval(s) pending. ${
          documentRisk
            ? "Review the Document Vault first."
            : "No immediate document exception is detected."
        }`
      );
    } else {
      setAiAnswer(
        `Executive brief: ${activeEmployees} active employees, ${
          data.applications.length
        } candidates, ${attendanceRate}% attendance, ${onboardingRate}% onboarding completion, ${pendingActions} open actions and ${automationHealth}% automation health. My priority recommendation is: ${aiInsights[0].title}.`
      );
    }
    setAiPrompt("");
  };

  return (
    <div className="space-y-6">
      <section className="relative overflow-hidden rounded-[32px] border border-white/10 bg-[#06182d] text-white shadow-[0_30px_90px_rgba(6,24,45,.26)]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_78%_8%,rgba(37,99,235,.42),transparent_32%),radial-gradient(circle_at_25%_105%,rgba(13,148,136,.24),transparent_35%)]" />
        <div className="absolute right-[-80px] top-[-110px] h-80 w-80 rounded-full border-[50px] border-white/[.035]" />
        <div className="relative grid xl:grid-cols-[1fr_470px]">
          <div className="p-7 sm:p-10 xl:p-12">
            <div className="flex flex-wrap items-center gap-3">
              <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/25 bg-cyan-300/10 px-4 py-2 text-[10px] font-semibold uppercase tracking-[.22em] text-cyan-100">
                <BarChart3 size={14} /> Executive Analytics
              </div>
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-300/20 bg-emerald-300/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-wider text-emerald-200">
                <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-400" />
                Role-controlled workspace
              </div>
            </div>
            <p className="mt-7 text-xs font-semibold uppercase tracking-[.22em] text-blue-300">
              {workspaceDate} • Executive Workspace
            </p>
            <h1 className="mt-3 max-w-4xl text-4xl font-semibold leading-[1.04] tracking-[-.035em] sm:text-6xl">
              Workforce Analytics
              <span className="block bg-gradient-to-r from-cyan-300 via-blue-300 to-emerald-300 bg-clip-text text-transparent">
                & Management Control
              </span>
            </h1>
            <p className="mt-5 max-w-3xl text-sm font-medium leading-7 text-slate-300 sm:text-base">
              A consolidated operational view of talent, workforce, time,
              payroll, compliance and executive governance based on current
              HRMS records.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Button
                className="bg-white !text-slate-950 hover:bg-cyan-50"
                onClick={() => setActive("requisitions")}
              >
                <BriefcaseBusiness size={17} /> Create Requisition
              </Button>
              <button
                type="button"
                onClick={() => runAi("executive summary")}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                <Bot size={17} /> Generate Rules-based Brief
              </button>
              <button
                type="button"
                onClick={() => setDashboardView("portal")}
                className="inline-flex items-center gap-2 rounded-xl border border-white/15 bg-white/5 px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-white/10"
              >
                <Home size={17} /> Portal Home
              </button>
            </div>
          </div>
          <div className="border-t border-white/10 bg-white/[.035] p-6 backdrop-blur-xl xl:border-l xl:border-t-0 xl:p-8">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-slate-400">
                  Organization Pulse
                </p>
                <p className="mt-1 text-lg font-semibold">{profile.name}</p>
              </div>
              <Badge value={normalizedDashboardRole(profile)} />
            </div>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {[
                ["Workforce", activeEmployees, Users, "text-cyan-300"],
                ["Attendance", `${attendanceRate}%`, Gauge, "text-emerald-300"],
                ["Open Actions", pendingActions, Target, "text-amber-300"],
                ["Automation", `${automationHealth}%`, Zap, "text-violet-300"],
              ].map(([label, value, Icon, color]) => (
                <div
                  key={label}
                  className="rounded-lg border border-white/10 bg-[#0b2746]/85 p-4"
                >
                  <Icon size={18} className={color} />
                  <p className="mt-3 text-2xl font-semibold">{value}</p>
                  <p className="mt-1 text-[9px] font-semibold uppercase tracking-wider text-slate-400">
                    {label}
                  </p>
                </div>
              ))}
            </div>
            <div className="mt-3 rounded-lg border border-white/10 bg-[#0b2746]/85 p-4">
              <div className="flex items-center justify-between text-xs font-bold">
                <span className="text-slate-300">Automation health</span>
                <span className="text-emerald-300">{automationHealth}%</span>
              </div>
              <div className="mt-3 h-2 overflow-hidden rounded-full bg-white/10">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-blue-500 via-cyan-400 to-emerald-400"
                  style={{ width: `${automationHealth}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="grid overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm lg:grid-cols-[1fr_auto_1fr_auto_1fr]">
        {[
          [
            "01",
            "HR Operations",
            OFFICIAL_EMAILS.HR,
            "Prepare, verify and finalise",
            "bg-blue-600",
          ],
          [
            "02",
            "CEO Master",
            OFFICIAL_EMAILS.CEO,
            "Approve management and financial risk",
            "bg-slate-950",
          ],
          [
            "03",
            "Account Payment",
            OFFICIAL_EMAILS.ACCOUNT,
            "Receive advice, process NEFT and return UTR",
            "bg-emerald-600",
          ],
        ].map(([step, title, email, text, tone], index) => (
          <React.Fragment key={step}>
            {index > 0 && (
              <div className="hidden items-center justify-center text-slate-300 lg:flex">
                <ChevronRight size={22} />
              </div>
            )}
            <div className="flex items-start gap-4 border-b border-slate-100 p-5 last:border-b-0 lg:border-b-0">
              <span
                className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xs font-semibold text-white ${tone}`}
              >
                {step}
              </span>
              <div>
                <p className="font-semibold">{title}</p>
                <p className="mt-1 text-xs font-semibold text-blue-600">
                  {email}
                </p>
                <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                  {text}
                </p>
              </div>
            </div>
          </React.Fragment>
        ))}
      </section>

      <section className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-7">
        {[
          {
            label: "Active Workforce",
            value: activeEmployees,
            detail: `${data.employees.length} total records`,
            icon: Users,
            tone: "bg-blue-50 text-blue-700",
            trend: "+ Live",
          },
          {
            label: "Present Today",
            value: stats.present,
            detail: `${attendanceRate}% attendance`,
            icon: CalendarCheck,
            tone: "bg-emerald-50 text-emerald-700",
            trend: `${lateToday} late`,
          },
          {
            label: "Active Hiring",
            value: publishedJobs,
            detail: `${data.applications.length} applications`,
            icon: UserRoundSearch,
            tone: "bg-cyan-50 text-cyan-700",
            trend: `${conversionRate}% hire`,
          },
          {
            label: "Approvals",
            value: stats.approvalPending,
            detail: "Management queue",
            icon: CheckCheck,
            tone: "bg-amber-50 text-amber-700",
            trend: stats.approvalPending ? "Action due" : "Clear",
          },
          {
            label: "Payroll Value",
            value: money(payrollValue),
            detail: `${money(payrollPaid)} paid`,
            icon: Banknote,
            tone: "bg-violet-50 text-violet-700",
            trend: `${stats.salaryPending} pending`,
          },
          {
            label: "Compliance Risk",
            value: documentRisk,
            detail: `${data.audit.length} audit events`,
            icon: ShieldAlert,
            tone:
              documentRisk > 0
                ? "bg-red-50 text-red-700"
                : "bg-slate-100 text-slate-700",
            trend: documentRisk ? "Review" : "Healthy",
          },
          {
            label: "Reimbursements",
            value: money(reimbursementValue),
            detail: `${money(reimbursementPaid)} paid`,
            icon: CircleDollarSign,
            tone: "bg-cyan-50 text-cyan-700",
            trend: `${
              stats.reimbursementPending +
              stats.reimbursementPaymentPending
            } pending`,
          },
        ].map((item) => {
          const MetricIcon = item.icon;
          return (
            <article
              key={item.label}
              className="group rounded-[22px] border border-slate-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-lg sm:p-5"
            >
              <div className="flex items-start justify-between gap-2">
                <div
                  className={`flex h-11 w-11 items-center justify-center rounded-lg ${item.tone}`}
                >
                  <MetricIcon size={20} />
                </div>
                <span className="rounded-full bg-slate-100 px-2 py-1 text-[9px] font-semibold uppercase text-slate-500">
                  {item.trend}
                </span>
              </div>
              <p className="mt-4 truncate text-2xl font-semibold tracking-tight text-slate-950">
                {item.value}
              </p>
              <p className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                {item.label}
              </p>
              <p className="mt-2 truncate text-xs font-semibold text-slate-500">
                {item.detail}
              </p>
            </article>
          );
        })}
      </section>

      <section className="grid gap-5 xl:grid-cols-[1.15fr_.85fr]">
        <Card className="overflow-hidden !p-0">
          <div className="border-b border-slate-200 px-5 py-5 sm:px-6">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-blue-600">
                  Live Intelligence
                </p>
                <h2 className="mt-1 text-xl font-semibold">Talent Acquisition Funnel</h2>
              </div>
              <Button tone="light" onClick={() => setActive("recruitment")}>
                Open ATS <ArrowUpRight size={15} />
              </Button>
            </div>
          </div>
          <div className="grid lg:grid-cols-[1fr_220px]">
            <div className="space-y-4 p-5 sm:p-6">
              {funnel.map(([label, value, color], index) => (
                <div key={label}>
                  <div className="mb-2 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <span className="flex h-6 w-6 items-center justify-center rounded-lg bg-slate-100 text-[10px] font-semibold text-slate-500">
                        {index + 1}
                      </span>
                      <span className="text-sm font-semibold text-slate-700">{label}</span>
                    </div>
                    <span className="text-sm font-semibold text-slate-950">{value}</span>
                  </div>
                  <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                    <div
                      className={`h-full rounded-full ${color} transition-all`}
                      style={{
                        width: `${Math.max(
                          value ? 7 : 0,
                          Math.round((value / funnelMax) * 100)
                        )}%`,
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="border-t border-slate-200 bg-slate-50 p-5 lg:border-l lg:border-t-0">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                Hiring efficiency
              </p>
              <p className="mt-3 text-5xl font-semibold tracking-tight text-slate-950">
                {conversionRate}
                <span className="text-xl text-blue-600">%</span>
              </p>
              <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                Applicant-to-employee conversion across the active recruitment cycle.
              </p>
              <div className="mt-5 space-y-3">
                <div className="rounded-xl bg-white p-3">
                  <p className="text-[9px] font-semibold uppercase text-slate-400">
                    Time to action
                  </p>
                  <p className="mt-1 text-sm font-semibold">
                    {stats.approvalPending ? "Approval required" : "On track"}
                  </p>
                </div>
                <div className="rounded-xl bg-white p-3">
                  <p className="text-[9px] font-semibold uppercase text-slate-400">
                    AI screening
                  </p>
                  <p className="mt-1 text-sm font-semibold">Enabled</p>
                </div>
              </div>
            </div>
          </div>
        </Card>

        <section className="overflow-hidden rounded-lg border border-indigo-200 bg-gradient-to-br from-[#101b46] via-[#16255b] to-[#093b55] text-white shadow-xl">
          <div className="border-b border-white/10 p-5 sm:p-6">
            <div className="flex items-start justify-between gap-4">
              <div>
                <div className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-cyan-200">
                  <Bot size={14} /> SAAM HR AI
                </div>
                <h2 className="mt-3 text-2xl font-semibold">Ask your workforce</h2>
                <p className="mt-1 text-xs font-medium text-slate-300">
                  Instant answers from live HRMS records.
                </p>
              </div>
              <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-400 to-blue-600 shadow-lg">
                <Sparkles size={21} />
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <div className="min-h-[108px] rounded-lg border border-white/10 bg-black/15 p-4 text-sm font-medium leading-6 text-slate-200">
              {aiAnswer}
            </div>
            <div className="mt-4 flex gap-2">
              <input
                value={aiPrompt}
                onChange={(event) => setAiPrompt(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") runAi();
                }}
                placeholder="Ask about hiring, payroll, leave or compliance..."
                className="min-w-0 flex-1 rounded-xl border border-white/10 bg-white/10 px-4 py-3 text-sm font-semibold text-white outline-none placeholder:text-slate-400 focus:border-cyan-300"
              />
              <button
                type="button"
                onClick={() => runAi()}
                className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-cyan-400 text-slate-950 transition hover:bg-cyan-300"
              >
                <ArrowUpRight size={19} />
              </button>
            </div>
            <div className="mt-3 flex flex-wrap gap-2">
              {[
                ["Hiring summary", "hiring summary"],
                ["Payroll risk", "payroll risk"],
                ["Compliance", "compliance status"],
              ].map(([label, prompt]) => (
                <button
                  key={label}
                  type="button"
                  onClick={() => runAi(prompt)}
                  className="rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-[10px] font-semibold text-slate-300 transition hover:bg-white/10"
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
        </section>
      </section>

      <section className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
        {processCards.map((process) => {
          const Icon = process.icon;
          return (
            <button
              key={process.id}
              type="button"
              onClick={() => setActive(process.id)}
              className="group relative overflow-hidden rounded-[24px] border border-slate-200 bg-white p-5 text-left shadow-sm transition hover:-translate-y-1 hover:border-blue-300 hover:shadow-xl"
            >
              <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${process.tone}`} />
              <div className="flex items-start justify-between gap-3">
                <div
                  className={`flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br ${process.tone} text-white shadow-lg`}
                >
                  <Icon size={22} />
                </div>
                <div className="flex items-center gap-2">
                  {process.alert > 0 && (
                    <span className="rounded-full bg-red-500 px-2 py-1 text-[9px] font-semibold text-white">
                      {process.alert}
                    </span>
                  )}
                  <ChevronRight
                    size={18}
                    className="text-slate-300 transition group-hover:translate-x-1 group-hover:text-blue-600"
                  />
                </div>
              </div>
              <p className="mt-5 text-3xl font-semibold tracking-tight">{process.value}</p>
              <p className="text-[9px] font-semibold uppercase tracking-widest text-slate-400">
                {process.metric}
              </p>
              <h3 className="mt-4 text-lg font-semibold group-hover:text-blue-700">
                {process.title}
              </h3>
              <p className="mt-1 text-xs font-semibold leading-5 text-slate-500">
                {process.subtitle}
              </p>
            </button>
          );
        })}
      </section>

      <section className="grid gap-5 xl:grid-cols-[1fr_390px]">
        <Card
          title="AI Priority & Exception Centre"
          icon={Target}
          action={
            <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {pendingActions} open actions
            </span>
          }
        >
          <div className="grid gap-3 md:grid-cols-2">
            {aiInsights.slice(0, 6).map((insight, index) => {
              const styles = {
                critical: "border-red-200 bg-red-50 text-red-700",
                warning: "border-amber-200 bg-amber-50 text-amber-700",
                info: "border-blue-200 bg-blue-50 text-blue-700",
                success: "border-emerald-200 bg-emerald-50 text-emerald-700",
              };
              return (
                <button
                  key={`${insight.title}-${index}`}
                  type="button"
                  onClick={() => setActive(insight.action)}
                  className={`flex items-start gap-3 rounded-lg border p-4 text-left transition hover:-translate-y-0.5 hover:shadow-md ${styles[insight.level]}`}
                >
                  <div className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-white/80">
                    {insight.level === "success" ? (
                      <CheckCircle2 size={17} />
                    ) : (
                      <AlertTriangle size={17} />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-semibold">{insight.title}</p>
                    <p className="mt-1 text-xs font-semibold leading-5 opacity-80">
                      {insight.text}
                    </p>
                  </div>
                </button>
              );
            })}
          </div>
        </Card>

        <Card title="Workforce Health" icon={TrendingUp}>
          <div className="space-y-5">
            {[
              ["Attendance", attendanceRate, "bg-emerald-500"],
              ["Onboarding", onboardingRate, "bg-blue-600"],
              ["Hiring Conversion", conversionRate, "bg-violet-600"],
              ["Automation", automationHealth, "bg-cyan-500"],
            ].map(([label, value, color]) => (
              <div key={label}>
                <div className="mb-2 flex items-center justify-between text-xs font-semibold">
                  <span className="text-slate-600">{label}</span>
                  <span>{value}%</span>
                </div>
                <div className="h-2.5 overflow-hidden rounded-full bg-slate-100">
                  <div
                    className={`h-full rounded-full ${color}`}
                    style={{ width: `${Math.min(100, Math.max(0, value))}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={() => setActive("attendance")}
              className="rounded-lg bg-slate-950 p-4 text-left text-white"
            >
              <Clock size={17} className="text-cyan-300" />
              <p className="mt-3 text-xl font-semibold">{lateToday}</p>
              <p className="text-[9px] font-semibold uppercase text-slate-400">Late today</p>
            </button>
            <button
              type="button"
              onClick={() => setActive("leave")}
              className="rounded-lg bg-slate-100 p-4 text-left"
            >
              <CalendarDays size={17} className="text-orange-600" />
              <p className="mt-3 text-xl font-semibold">{lopDays}</p>
              <p className="text-[9px] font-semibold uppercase text-slate-400">LOP days</p>
            </button>
          </div>
        </Card>
      </section>

      <section>
        <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-blue-600">
              Enterprise Modules
            </p>
            <h2 className="mt-1 text-2xl font-semibold">Complete HR Operating System</h2>
          </div>
          <button
            type="button"
            onClick={() => setActive("reports")}
            className="text-xs font-semibold text-blue-700"
          >
            View management reports →
          </button>
        </div>
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 2xl:grid-cols-5">
          {NAV.map(([id, label, Icon]) => {
            const counts = {
              requisitions: data.requisitions.length,
              recruitment: data.applications.length,
              interviews: data.interviews.length,
              onboarding: onboardingPending,
              employees: activeEmployees,
              attendance: todayAttendance.length,
              employeeRequests: stats.employeeRequestPending,
              leave: leavePending,
              payroll: data.payroll.length,
              statutory: data.statutory.length,
              reimbursements: data.reimbursements.length,
              documents: data.documents.length,
              approvals: stats.approvalPending,
              mail: stats.mailPending,
              reports: data.audit.length,
              settings: 0,
            };
            return (
              <button
                key={id}
                type="button"
                onClick={() => setActive(id)}
                className="group flex min-h-[100px] items-center gap-4 rounded-lg border border-slate-200 bg-white p-4 text-left shadow-sm transition hover:-translate-y-0.5 hover:border-blue-400 hover:shadow-lg"
              >
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-700 transition group-hover:bg-blue-600 group-hover:text-white">
                  <Icon size={20} />
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-slate-900">{label}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-400">
                    {counts[id]} record{counts[id] === 1 ? "" : "s"}
                  </p>
                </div>
              </button>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function RequisitionModule({ form, setForm, rows, create, approve, publish, canCEO, canHR }) {
  const updateEmploymentType = (employmentType) => {
    setForm({
      ...form,
      employmentType,
      employmentTypeDetails: defaultEmploymentDetails(employmentType),
    });
  };
  const generateSmartJd = () => {
    if (!clean(form.title)) {
      window.alert("Pehle Job Title likhiye, jaise Accountant, Civil Engineer ya HR Executive.");
      return;
    }
    if (!clean(form.department)) {
      window.alert("Pehle Department select kijiye.");
      return;
    }
    setForm(buildSmartJdTemplate(form));
  };
  return (
    <div className="space-y-5">
      <Card>
        <div className="relative mb-5 overflow-hidden rounded-lg border border-[#c6d1dc] border-l-4 border-l-[#2f783d] bg-[#f8fafc] p-5 text-slate-900">
          <div className="absolute -right-16 -top-20 h-56 w-56 rounded-full border-[35px] border-[#17365d]/[.035]" />
          <div className="relative flex flex-wrap items-center justify-between gap-5">
            <div>
              <div className="inline-flex items-center gap-2 rounded-md border border-[#c9d8e7] bg-[#edf4fa] px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[.14em] text-[#17365d]">
                <Sparkles size={13} /> Smart JD Builder
              </div>
              <h2 className="mt-3 text-xl font-semibold text-[#17365d] sm:text-2xl">Create Professional Job Requisition</h2>
              <p className="mt-1.5 max-w-2xl text-sm font-medium leading-6 text-slate-600">
                Job title aur department enter karke smart template generate karein,
                review/edit karein aur management approval ke liye submit karein.
                Salary candidate select hone ke baad Employee Master me configure hogi.
              </p>
            </div>
            <div className="rounded-md border border-[#c6d1dc] bg-white px-4 py-3 text-right">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Workflow</p>
              <p className="mt-1 text-sm font-semibold text-[#17365d]">Draft → CEO Approval → Publish</p>
            </div>
          </div>
        </div>

        <section className="rounded-lg border border-slate-200 bg-slate-50/70 p-5">
          <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-blue-600">Step 01</p>
              <h3 className="mt-1 text-lg font-semibold">Position & Employment Details</h3>
            </div>
            <Button type="button" onClick={generateSmartJd}>
              <Sparkles size={16} /> Generate Smart JD
            </Button>
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field label="Job Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Accountant, Civil Engineer" />
            <Select label="Department *" value={form.department} onChange={(e) => setForm({ ...form, department: e.target.value })}>
              <option value="">Select Department</option>
              {DEPARTMENT_OPTIONS.map(([id, name]) => <option key={id} value={name}>{name}</option>)}
            </Select>
            <Field label="Number of Vacancies" type="number" value={form.vacancies} onChange={(e) => setForm({ ...form, vacancies: e.target.value })} />
            <Select label="Employment Type" value={form.employmentType} onChange={(e) => updateEmploymentType(e.target.value)}>
              {EMPLOYMENT_TYPE_OPTIONS.map((item) => (
                <option key={item.value} value={item.value}>{item.label}</option>
              ))}
            </Select>
            <Field label="Experience Required" value={form.experience} onChange={(e) => setForm({ ...form, experience: e.target.value })} placeholder="Minimum 3 Years" />
            <Field label="Location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="Head Office / Field-Based" />
            <TextArea label="Employment Terms / Details *" className="md:col-span-2 xl:col-span-4" rows={3} value={form.employmentTypeDetails || ""} onChange={(e) => setForm({ ...form, employmentTypeDetails: e.target.value })} placeholder="Contract duration, probation, notice period, renewal aur benefit eligibility..." />
            <TextArea label="Areas Covered (one per line)" className="md:col-span-2 xl:col-span-4" rows={3} value={form.areasCovered} onChange={(e) => setForm({ ...form, areasCovered: e.target.value })} placeholder={"Banaskantha\nMehsana\nSabarkantha"} />
          </div>

          <div className="mt-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-sm font-semibold leading-6 text-emerald-900">
            Vacancy create karte waqt salary, CTC ya TDS nahi liya jayega. Candidate
            final select aur employee me convert hone ke baad Employee Master ke
            <span className="font-bold"> Update Salary</span> action se complete salary
            structure aur TDS treatment set hoga.
          </div>
        </section>

        <section className="mt-5 rounded-lg border border-slate-200 bg-white p-5">
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-emerald-600">Step 02</p>
            <h3 className="mt-1 text-lg font-semibold">Public Job Description</h3>
            <p className="mt-1 text-xs font-semibold text-slate-500">Generated content ko HR final submit se pehle edit kar sakta hai.</p>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
            <TextArea label="Job Overview / Description" className="lg:col-span-2" rows={4} value={form.jobOverview} onChange={(e) => setForm({ ...form, jobOverview: e.target.value })} />
            <TextArea label="Key Responsibilities (one per line)" rows={6} value={form.responsibilities} onChange={(e) => setForm({ ...form, responsibilities: e.target.value })} />
            <TextArea label="Required Skills (one per line)" rows={6} value={form.skills} onChange={(e) => setForm({ ...form, skills: e.target.value })} />
            <TextArea label="Required Qualifications (one per line)" rows={5} value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} />
            <TextArea label="Preferred Experience" rows={5} value={form.preferredExperience} onChange={(e) => setForm({ ...form, preferredExperience: e.target.value })} />
          </div>
        </section>

        <section className="mt-5 rounded-lg border border-amber-200 bg-amber-50/60 p-5">
          <p className="text-[10px] font-semibold uppercase tracking-[.2em] text-amber-700">Step 03 • Internal Approval Note</p>
          <TextArea label="Business Reason (HR/CEO only — Career page par show nahi hoga)" className="mt-3" rows={3} value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })} />
        </section>

        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 pt-5">
          <Button tone="light" onClick={() => setForm(emptyReq)}>Clear Form</Button>
          <div className="flex items-center gap-3">
            <p className="hidden text-xs font-semibold text-slate-500 sm:block">Submit ke baad CEO approval required hoga.</p>
            <Button tone="green" className="px-6" onClick={create}>
              <CheckCircle2 size={17} /> Submit Requisition
            </Button>
          </div>
        </div>
      </Card>
      <Card title="Requisition & Posting Control" icon={ClipboardList}>
        <Table
          collectionName={COLLECTIONS.requisitions}
          recordLabel="job requisition"
          columns={["Job", "Department", "Vacancy", "Salary Stage", "Status", "Action"]}
          rows={rows}
          renderRow={(item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <TD className="font-semibold">{item.title}</TD><TD>{item.department}</TD>
              <TD>{item.vacancies}</TD>
              <TD><Badge value="AFTER_SELECTION" /></TD>
              <TD><Badge value={item.status} /></TD>
              <TD>
                <div className="flex flex-wrap gap-2">
                  {canCEO && isPendingCEOApproval(item.status) && (
                    <><Button tone="green" onClick={() => approve(item, "APPROVED")}><Check size={15}/>Approve</Button>
                    <Button tone="red" onClick={() => approve(item, "REJECTED")}><X size={15}/>Reject</Button></>
                  )}
                  {canHR && item.status === "APPROVED" && item.postingStatus !== "PUBLISHED" && (
                    <Button onClick={() => publish(item)}>Publish Job</Button>
                  )}
                  {item.postingStatus === "PUBLISHED" && <Badge value="PUBLISHED" />}
                </div>
              </TD>
            </tr>
          )}
        />
      </Card>
    </div>
  );
}

function ApplicantDetailModal({ application, candidate, onClose, onDownloadCv }) {
  if (!application) return null;
  const profile = employeeProfileFromApplication(application, candidate || {});
  const cv = applicationCvRecord(application, candidate || {});
  const identityRows = [
    ["Full Name", profile.name],
    ["Email", application.email || application.candidateEmail || candidate?.email],
    ["Mobile", profile.mobile],
    ["Date of Birth", profile.dateOfBirth],
    ["Gender", profile.gender],
    ["Blood Group", profile.bloodGroup],
  ];
  const careerRows = [
    ["Applied Position", application.jobTitle || application.position],
    ["Department", profile.department],
    ["Qualification", profile.highestQualification],
    ["Total Experience", profile.totalExperience],
    ["Current Employer", application.currentEmployer || candidate?.currentEmployer],
    ["Notice Period", application.noticePeriod || candidate?.noticePeriod],
    ["Current Location", application.currentLocation || profile.city],
    ["Expected Joining", profile.joiningDate],
  ];
  const addressRows = [
    ["Current Address", profile.currentAddress],
    ["Permanent Address", profile.permanentAddress],
    ["City / Village", profile.city],
    ["Taluka", profile.taluka],
    ["District", profile.district],
    ["State", profile.state],
    ["PIN Code", profile.pincode],
    ["Emergency Contact", profile.emergencyContactName],
    ["Emergency Mobile", profile.emergencyContactMobile],
  ];
  const hiddenKeys = new Set([
    "id",
    "resume",
    "cv",
    "documents",
    "createdAt",
    "updatedAt",
    "candidateUserId",
    "candidateUid",
  ]);
  const knownKeys = new Set(
    [...identityRows, ...careerRows, ...addressRows]
      .map(([label]) => label.toLowerCase().replace(/[^a-z0-9]/g, ""))
  );
  const prettyLabel = (key) =>
    key
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .replaceAll("_", " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  const renderSubmittedValue = (value) => {
    if (value === null || value === undefined || value === "") return "";
    if (value?.toDate) return formatDate(value);
    if (typeof value === "boolean") return value ? "Yes" : "No";
    if (Array.isArray(value)) return value.filter(Boolean).join(", ");
    if (typeof value === "object") {
      const output = Object.entries(value)
        .filter(([key]) => !/(password|secret|token|hash)/i.test(key))
        .map(([key, entry]) => `${prettyLabel(key)}: ${String(entry ?? "")}`)
        .join(" • ");
      return output.slice(0, 1000);
    }
    return String(value);
  };
  const additionalRows = Object.entries({ ...(candidate || {}), ...application })
    .filter(([key, value]) => {
      const normalized = key.toLowerCase().replace(/[^a-z0-9]/g, "");
      return (
        !hiddenKeys.has(key) &&
        !knownKeys.has(normalized) &&
        !/(password|secret|token|hash|storagepath|downloadurl|url)$/i.test(key) &&
        renderSubmittedValue(value)
      );
    })
    .slice(0, 40);
  const DetailSection = ({ title, rows }) => (
    <section className="rounded-2xl border border-slate-200 bg-slate-50/70 p-4">
      <h3 className="text-sm font-black text-slate-950">{title}</h3>
      <dl className="mt-3 grid gap-3 sm:grid-cols-2">
        {rows.map(([label, value]) => (
          <div key={label} className="rounded-xl border border-slate-200 bg-white p-3">
            <dt className="text-[10px] font-black uppercase tracking-wider text-slate-400">
              {label}
            </dt>
            <dd className="mt-1 break-words text-sm font-semibold text-slate-800">
              {renderSubmittedValue(value) || "-"}
            </dd>
          </div>
        ))}
      </dl>
    </section>
  );
  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Applicant full details"
    >
      <div className="flex max-h-[94vh] w-full max-w-6xl flex-col overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-5 py-5 text-white sm:px-7">
          <div className="flex min-w-0 items-start gap-3">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-cyan-400/15 text-cyan-200 ring-1 ring-cyan-300/20">
              <UserRoundSearch size={21} />
            </span>
            <div className="min-w-0">
              <p className="text-[10px] font-black uppercase tracking-[.18em] text-cyan-300">
                Complete Applicant Record
              </p>
              <h2 className="mt-1 truncate text-xl font-black">
                {profile.name || "Candidate"}
              </h2>
              <p className="mt-1 text-xs font-semibold text-slate-300">
                {application.jobTitle || application.position || "Position not specified"} • Application {application.id}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 text-white transition hover:bg-white/20"
            aria-label="Close applicant details"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-5 sm:p-7">
          <div className="mb-5 flex flex-col gap-4 rounded-2xl border border-blue-200 bg-blue-50 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p className="text-sm font-black text-blue-950">Candidate CV / Resume</p>
              <p className="mt-1 text-xs font-semibold text-blue-700">
                {cv.path ? cv.fileName : "Candidate ne CV attach nahi kiya hai."}
              </p>
            </div>
            {cv.path && (
              <div className="flex flex-wrap gap-2">
                <Button tone="light" onClick={() => onDownloadCv(application, "inline")}>
                  <Eye size={15} /> Preview CV
                </Button>
                <Button tone="dark" onClick={() => onDownloadCv(application)}>
                  <Download size={15} /> Download CV
                </Button>
              </div>
            )}
          </div>

          <div className="grid gap-5 lg:grid-cols-2">
            <DetailSection title="Identity & Contact" rows={identityRows} />
            <DetailSection title="Career & Application" rows={careerRows} />
            <div className="lg:col-span-2">
              <DetailSection title="Address & Emergency" rows={addressRows} />
            </div>
          </div>

          {additionalRows.length > 0 && (
            <section className="mt-5 rounded-2xl border border-slate-200 bg-white p-4">
              <h3 className="text-sm font-black text-slate-950">
                Other Submitted Application Details
              </h3>
              <dl className="mt-3 grid gap-x-6 gap-y-3 md:grid-cols-2">
                {additionalRows.map(([key, value]) => (
                  <div key={key} className="border-b border-slate-100 pb-3">
                    <dt className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                      {prettyLabel(key)}
                    </dt>
                    <dd className="mt-1 break-words text-sm font-semibold text-slate-700">
                      {renderSubmittedValue(value)}
                    </dd>
                  </div>
                ))}
              </dl>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}

function RecruitmentModule({
  rows,
  candidateUsers = [],
  downloadCv,
  updateStage,
  schedule,
  archive,
  reopen,
  reInterview,
  prepareOffer,
  convertApplicantToEmployee,
  search,
  setSearch,
  canHR,
  canPrepareOffer,
}) {
  const [showArchived, setShowArchived] = useState(false);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const linkedCandidateFor = (application) => {
    if (!application) return undefined;
    const linkedIds = [
      application.candidateUserId,
      application.candidateUid,
      application.candidateId,
      application.userId,
      application.uid,
    ]
      .map(clean)
      .filter(Boolean);
    const applicationEmail = clean(
      application.email || application.candidateEmail
    ).toLowerCase();
    return candidateUsers.find((candidate) => {
      const candidateIds = [
        candidate.id,
        candidate.uid,
        candidate.userId,
        candidate.candidateUserId,
      ]
        .map(clean)
        .filter(Boolean);
      return (
        linkedIds.some((id) => candidateIds.includes(id)) ||
        (applicationEmail &&
          applicationEmail ===
            clean(candidate.email || candidate.candidateEmail).toLowerCase())
      );
    });
  };
  const filtered = rows.filter(
    (item) =>
      (showArchived || item.status !== "ARCHIVED") &&
      `${item.candidateName} ${item.email} ${item.jobTitle}`
        .toLowerCase()
        .includes(search.toLowerCase())
  );
  return (
    <div className="space-y-5">
      <ApplicantDetailModal
        application={selectedApplication}
        candidate={linkedCandidateFor(selectedApplication)}
        onClose={() => setSelectedApplication(null)}
        onDownloadCv={downloadCv}
      />
      <Card>
        <div className="flex flex-col gap-4 rounded-lg border border-emerald-200 bg-emerald-50 p-5 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-white">
              <ShieldCheck size={21} />
            </div>
            <div>
              <p className="font-semibold text-emerald-950">
                Verified Career Portal Applications
              </p>
              <p className="mt-1 text-sm font-semibold leading-6 text-emerald-800">
                Candidate pehle account banakar login karega, vacancy select
                karega aur documents ke saath apply karega. Application yahan
                automatically ATS screening me aayegi.
              </p>
            </div>
          </div>
          <Badge value="ACTIVE" />
        </div>
      </Card>
      <Card
        title="Application Screening (ATS)"
        icon={Search}
        action={
          <div className="flex flex-wrap items-end gap-2">
            <Field
              label="Search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Name, email or job..."
            />
            <Button
              tone={showArchived ? "dark" : "light"}
              onClick={() => setShowArchived((current) => !current)}
            >
              {showArchived ? "Hide Archived" : "Show Archived"}
            </Button>
          </div>
        }
      >
        <Table
          collectionName={COLLECTIONS.applications}
          recordLabel="candidate application"
          columns={["Candidate", "Job", "Qualification", "ATS", "Status", "Workflow Action"]}
          rows={filtered}
          renderRow={(item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <TD><p className="font-semibold">{item.candidateName || item.name}</p><p className="text-xs text-slate-500">{item.email}<br/>{item.mobile}</p></TD>
              <TD>{item.jobTitle || item.position || "-"}</TD><TD>{item.qualification || "-"}</TD>
              <TD><span className="text-lg font-semibold text-blue-700">{item.atsScore || 0}%</span><p className="text-[10px] font-semibold text-slate-400">{String(item.atsDecision || "MANUAL_REVIEW").replaceAll("_", " ")}</p></TD>
              <TD>
                <Badge value={item.status} />
                {item.status === "ARCHIVED" && (
                  <p className="mt-2 max-w-[180px] text-[10px] font-semibold text-slate-500">
                    From {item.archivedFromStatus || "-"} •{" "}
                    {item.archivedReason || "No reason"}
                  </p>
                )}
              </TD>
              <TD><div className="flex flex-wrap gap-2">
                <Button tone="light" onClick={() => setSelectedApplication(item)}>
                  <Eye size={14} /> Full Details / CV
                </Button>
                {canHR && ["APPLIED", "NEW", "DOCUMENT_VERIFIED"].includes(item.status) && <Button tone="green" onClick={() => updateStage(item, "SHORTLISTED")}>Shortlist</Button>}
                {canHR && ["SHORTLISTED", "INTERVIEW_NEXT_ROUND"].includes(item.status) && <Button onClick={() => schedule(item)}>Schedule Interview</Button>}
                {canHR && ["REJECTED", "COMPLETED", "INTERVIEW_COMPLETED"].includes(item.status) && (
                  <Button onClick={() => reInterview(item)}>
                    <CalendarCheck size={14} /> Re-interview
                  </Button>
                )}
                {canHR && ["ARCHIVED", "REJECTED"].includes(item.status) && (
                  <Button tone="green" onClick={() => reopen(item)}>
                    <RefreshCw size={14} /> Reopen
                  </Button>
                )}
                {canPrepareOffer && item.status === "PENDING_CEO_APPROVAL" && (
                  <Button tone="amber" onClick={() => prepareOffer(item)}>
                    <FileText size={14} /> Prepare Offer + TDS/PF
                  </Button>
                )}
                {canHR && item.status === "CANDIDATE_ACCEPTED" && (
                  <Button tone="green" onClick={() => convertApplicantToEmployee(item)}>
                    <UserCheck size={14} /> Activate Employee
                  </Button>
                )}
                {canHR && ![
                  "REJECTED",
                  "ARCHIVED",
                  "OFFER_RELEASED",
                  "CANDIDATE_ACCEPTED",
                  "EMPLOYEE_CREATED",
                ].includes(item.status) && (
                  <Button
                    tone="red"
                    onClick={() => updateStage(item, "REJECTED")}
                  >
                    Reject
                  </Button>
                )}
                {canHR &&
                  !["ARCHIVED", "EMPLOYEE_CREATED"].includes(item.status) && (
                    <Button tone="light" onClick={() => archive(item)}>
                      Archive / Remove
                    </Button>
                  )}
              </div></TD>
            </tr>
          )}
        />
      </Card>
    </div>
  );
}

function InterviewModule({
  rows,
  result,
  join,
  resend,
  reschedule,
  cancel,
  applications,
  archive,
  reopen,
  reInterview,
  canHR,
  isHRAdmin,
  canCEO,
  openApprovals,
}) {
  const scheduled = rows.filter((item) =>
    ["SCHEDULED", "RESCHEDULED"].includes(item.status)
  ).length;
  const live = rows.filter(
    (item) => item.status === "LIVE" || item.roomStatus === "LIVE"
  ).length;
  const completed = rows.filter((item) =>
    ["COMPLETED", "SELECTED", "REJECTED"].includes(item.status)
  ).length;
  const linkedApplicationFor = (interview) => {
    const directId = clean(
      interview.applicationId ||
        interview.jobApplicationId ||
        interview.relatedApplicationId
    );
    if (directId) {
      return applications.find((item) => item.id === directId) || null;
    }
    const candidateUserId = clean(interview.candidateUserId);
    const candidateEmail = clean(interview.candidateEmail).toLowerCase();
    const jobTitle = clean(interview.jobTitle).toLowerCase();
    const matches = applications.filter((application) => {
      const identityMatches = candidateUserId
        ? application.candidateUserId === candidateUserId
        : candidateEmail &&
          clean(application.email).toLowerCase() === candidateEmail;
      const jobMatches =
        !jobTitle ||
        clean(application.jobTitle || application.position).toLowerCase() ===
          jobTitle;
      return identityMatches && jobMatches;
    });
    return matches.length === 1 ? matches[0] : null;
  };

  return (
    <div className="space-y-5">
      <section className="relative overflow-hidden rounded-2xl border border-slate-800 bg-gradient-to-br from-slate-950 via-[#071b33] to-blue-950 p-6 text-white shadow-[0_24px_70px_rgba(2,6,23,.22)]">
        <div className="absolute inset-y-0 right-0 w-1/2 bg-[radial-gradient(circle_at_80%_20%,rgba(34,211,238,.18),transparent_52%)]" />
        <div className="relative flex flex-wrap items-center justify-between gap-6">
          <div>
            <div className="inline-flex items-center gap-2 rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1.5 text-[10px] font-black uppercase tracking-[.16em] text-cyan-200">
              <Video size={14} /> Secure Video Interview Studio
            </div>
            <h1 className="mt-3 text-2xl font-black tracking-tight text-white">
              HR • Technical • Management • CEO
            </h1>
            <p className="mt-1.5 max-w-3xl text-sm font-semibold leading-6 text-slate-300">
              Private rooms, multi-member internal/external expert panel,
              individual calendar invitations, live presence, scorecards and
              complete audit trail.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-2">
            {[
              ["Scheduled", scheduled],
              ["Live", live],
              ["Completed", completed],
            ].map(([label, value]) => (
              <div
                key={label}
                className="min-w-[88px] rounded-xl border border-white/10 bg-white/5 p-3 text-center backdrop-blur"
              >
                <p className="text-2xl font-black text-white">{value}</p>
                <p className="mt-1 text-[9px] font-black uppercase text-slate-400">
                  {label}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Card
        title="Interview Control Centre"
        icon={CalendarCheck}
        action={
          <span className="rounded-full bg-emerald-50 px-3 py-1.5 text-[10px] font-semibold uppercase text-emerald-700">
            Secure room automation active
          </span>
        }
      >
        <Table
          collectionName={COLLECTIONS.interviews}
          recordLabel="interview record"
          columns={[
            "Candidate / Position",
            "Schedule",
            "Secure Room",
            "Presence",
            "Round / Status",
            "Evaluation",
            "Application Lifecycle",
          ]}
          rows={rows}
          empty="ATS me candidate shortlist karke Schedule Interview click kijiye."
          renderRow={(item) => {
            const secureRoom = Boolean(item.roomName || item.inviteId);
            const joinable = [
              "SCHEDULED",
              "RESCHEDULED",
              "WAITING",
              "LIVE",
            ].includes(item.status);
            const canEvaluate = ["COMPLETED", "NO_SHOW"].includes(item.status);
            const roundName = clean(item.round || "HR").toUpperCase();
            const isCeoRound = roundName.includes("CEO");
            const recommendation = clean(
              item.latestRecommendation || item.recommendation
            ).toUpperCase();
            const hasScorecard =
              number(item.scorecardCount) > 0 ||
              number(item.latestOverallScore) > 0 ||
              number(item.overallScore || item.score) > 0 ||
              ["SELECT", "REJECT", "NEXT_ROUND", "HOLD"].includes(
                recommendation
              );
            const canSubmitScorecard =
              canEvaluate &&
              !hasScorecard &&
              ((isCeoRound && canCEO) || (!isCeoRound && isHRAdmin));
            const finalCeoApprovalReady =
              canCEO && hasScorecard && recommendation === "SELECT";
            const linkedApplication = linkedApplicationFor(item);
            return (
              <tr key={item.id} className="hover:bg-slate-50">
                <TD>
                  <p className="font-semibold">{item.candidateName}</p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">
                    {item.jobTitle || "-"}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">
                    {item.candidateEmail || "-"}
                  </p>
                </TD>
                <TD>
                  <div className="flex gap-2">
                    <CalendarClock
                      size={17}
                      className="mt-0.5 shrink-0 text-blue-600"
                    />
                    <div>
                      <p className="text-xs font-semibold">
                        {formatDate(
                          item.scheduledStartAt ||
                            item.scheduledStart ||
                            item.interviewDateTime
                        )}
                      </p>
                      <p className="mt-1 text-[10px] font-bold text-slate-400">
                        {item.durationMinutes || 60} MIN • IST
                      </p>
                    </div>
                  </div>
                </TD>
                <TD>
                  {secureRoom ? (
                    <div className="space-y-2">
                      <Badge value="ACTIVE" />
                      {Array.isArray(item.panelMembers) && item.panelMembers.length > 0 && (
                        <div className="max-w-[220px] rounded-xl border border-blue-100 bg-blue-50/70 p-2.5">
                          <p className="text-[9px] font-black uppercase tracking-wider text-blue-700">
                            {item.panelMembers.length} Panel / Expert Member(s)
                          </p>
                          <p className="mt-1 line-clamp-2 text-[10px] font-semibold leading-4 text-slate-600">
                            {item.panelMembers.map((member) => member.name).join(", ")}
                          </p>
                          {item.panelDeliverySummary && (
                            <p className="mt-1 text-[9px] font-bold text-emerald-700">
                              Mail sent: {item.panelDeliverySummary.sent || 0}/{item.panelDeliverySummary.total || item.panelMembers.length}
                            </p>
                          )}
                          <div className="mt-2 space-y-1 border-t border-blue-100 pt-2">
                            {item.panelMembers.map((member) => {
                              const delivery = Array.isArray(item.panelDeliveryResults)
                                ? item.panelDeliveryResults.find(
                                    (result) => result.panelId === member.id
                                  )
                                : null;
                              return (
                                <div key={member.id || member.email} className="flex items-center justify-between gap-2 text-[9px] font-semibold">
                                  <span className="truncate text-slate-600">{member.name}</span>
                                  <span className={delivery?.status === "SENT" ? "text-emerald-700" : delivery?.status === "FAILED" ? "text-red-700" : "text-amber-700"}>
                                    {delivery?.status || "QUEUED"}
                                  </span>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      )}
                      <p className="max-w-[170px] truncate text-[10px] font-bold text-slate-400">
                        {item.roomName}
                      </p>
                      <div className="flex flex-wrap gap-2">
                        {canHR && joinable && (
                          <Button
                            tone="green"
                            className="!px-3 !py-2 text-xs"
                            onClick={() => join(item)}
                          >
                            <Video size={14} /> Join
                          </Button>
                        )}
                        {canHR &&
                          ["SCHEDULED", "RESCHEDULED", "WAITING", "LIVE"].includes(
                            item.status
                          ) && (
                          <Button
                            tone="light"
                            className="!px-3 !py-2 text-xs"
                            onClick={() => resend(item)}
                          >
                            <Mail size={14} /> Resend
                          </Button>
                        )}
                        {canHR &&
                          ["SCHEDULED", "RESCHEDULED", "WAITING"].includes(
                            item.status
                          ) && (
                            <>
                              <Button
                                tone="light"
                                className="!px-3 !py-2 text-xs"
                                onClick={() => reschedule(item)}
                              >
                                <CalendarClock size={14} /> Reschedule
                              </Button>
                              <Button
                                tone="red"
                                className="!px-3 !py-2 text-xs"
                                onClick={() => cancel(item)}
                              >
                                Cancel
                              </Button>
                            </>
                          )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      Legacy/offline interview
                    </span>
                  )}
                </TD>
                <TD>
                  <div className="space-y-1 text-xs">
                    <p className="font-semibold text-slate-700">
                      Candidate:{" "}
                      {item.candidateJoinedAt ? "Joined" : "Not joined"}
                    </p>
                    <p className="font-semibold text-slate-500">
                      Panel:{" "}
                      {item.hostJoinedAt || item.staffJoinedAt || item.panelJoinedAt
                        ? "Joined"
                        : "Not joined"}
                    </p>
                    {(item.roomStatus === "WAITING" ||
                      item.candidateWaitingAt) && (
                      <p className="font-semibold text-violet-600">
                        Candidate waiting
                      </p>
                    )}
                  </div>
                </TD>
                <TD>
                  <p className="mb-2 text-xs font-semibold">
                    {(item.round || "HR").replaceAll("_", " ")}
                  </p>
                  <Badge value={item.status} />
                </TD>
                <TD>
                  {canSubmitScorecard ? (
                    <div className="space-y-2">
                      <Button
                        tone="green"
                        className="w-full !px-3 !py-2 text-xs"
                        onClick={() => result(item, "SELECTED")}
                      >
                        <ClipboardCheck size={14} /> Submit Scorecard: Select
                      </Button>
                      <Button
                        tone="red"
                        className="w-full !px-3 !py-2 text-xs"
                        onClick={() => result(item, "REJECTED")}
                      >
                        Submit Scorecard: Reject
                      </Button>
                    </div>
                  ) : finalCeoApprovalReady ? (
                    <div className="space-y-2">
                      <p className="text-xs font-semibold text-emerald-700">
                        Panel selected • CEO decision pending
                      </p>
                      <Button
                        tone="amber"
                        className="w-full !px-3 !py-2 text-xs"
                        onClick={openApprovals}
                      >
                        <Crown size={14} /> Open CEO Approval
                      </Button>
                    </div>
                  ) : canCEO && canEvaluate && !hasScorecard ? (
                    <div className="max-w-[210px] rounded-xl bg-amber-50 p-3 text-xs font-semibold text-amber-800">
                      HR/panel scorecard pending. HR VIEW me scorecard submit
                      hone ke baad final approval yahan available hoga.
                    </div>
                  ) : (
                    <div className="max-w-[190px] text-xs">
                      <p className="font-semibold">
                        {item.overallScore || item.score
                          ? `${item.overallScore || item.score}/100`
                          : "-"}
                      </p>
                      <p className="mt-1 line-clamp-3 font-semibold text-slate-500">
                        {item.recommendation || item.remarks || ""}
                      </p>
                    </div>
                  )}
                </TD>
                <TD>
                  {linkedApplication ? (
                    <div className="space-y-2">
                      <Badge value={linkedApplication.status} />
                      <div className="flex max-w-[220px] flex-wrap gap-2">
                        {canHR &&
                          [
                            "REJECTED",
                            "COMPLETED",
                            "INTERVIEW_COMPLETED",
                          ].includes(linkedApplication.status) && (
                            <Button
                              className="!px-3 !py-2 text-xs"
                              onClick={() => reInterview(linkedApplication)}
                            >
                              Re-interview
                            </Button>
                          )}
                        {canHR &&
                          ["ARCHIVED", "REJECTED"].includes(
                            linkedApplication.status
                          ) && (
                            <Button
                              tone="green"
                              className="!px-3 !py-2 text-xs"
                              onClick={() => reopen(linkedApplication)}
                            >
                              Reopen
                            </Button>
                          )}
                        {canHR &&
                          !["ARCHIVED", "EMPLOYEE_CREATED"].includes(
                            linkedApplication.status
                          ) && (
                            <Button
                              tone="light"
                              className="!px-3 !py-2 text-xs"
                              onClick={() => archive(linkedApplication)}
                            >
                              Archive / Remove
                            </Button>
                          )}
                      </div>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      Application link unavailable
                    </span>
                  )}
                </TD>
              </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function OnboardingModule({ rows, toggle, canHR }) {
  const steps = [
    ["documentCollection", "Documents"],
    ["idCreated", "Employee ID"],
    ["salaryConfigured", "Salary Setup"],
    ["roleMapped", "Role Mapping"],
    ["hierarchySetup", "Hierarchy"],
    ["attendanceActivated", "Attendance"],
  ];
  return (
    <Card title="Employee Onboarding Checklist" icon={UserCheck}>
      <Table
        collectionName={COLLECTIONS.onboarding}
        recordLabel="onboarding record"
        columns={["Employee", ...steps.map((x) => x[1]), "Overall"]}
        rows={rows}
        renderRow={(item) => (
          <tr key={item.id} className="hover:bg-slate-50">
            <TD><p className="font-semibold">{item.employeeName}</p><p className="text-xs text-slate-500">{item.employeeCode}</p></TD>
            {steps.map(([key]) => <TD key={key}><button type="button" disabled={!canHR || key === "salaryConfigured"} onClick={() => toggle(item, key)} title={key === "salaryConfigured" ? "Employee Directory ke Update Salary action se complete hoga" : "Update onboarding step"} className={`flex h-9 w-9 items-center justify-center rounded-xl ${item[key] ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-400"}`}>{item[key] ? <Check size={18}/> : <Clock size={18}/>}</button></TD>)}
            <TD><Badge value={item.status} /></TD>
          </tr>
        )}
      />
    </Card>
  );
}

function EmployeeProfileEditor({ employee, onSave, onClose }) {
  const [form, setForm] = useState(() => employeeEditFormFromRecord(employee));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setForm(employeeEditFormFromRecord(employee));
  }, [employee]);

  if (!employee) return null;

  const saveProfile = async (event) => {
    event.preventDefault();
    setSaving(true);
    try {
      const saved = await onSave(employee, form);
      if (saved) onClose();
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center bg-slate-950/70 p-3 backdrop-blur-sm sm:p-6"
      role="dialog"
      aria-modal="true"
      aria-label="Edit employee details"
    >
      <form
        onSubmit={saveProfile}
        className="max-h-[94vh] w-full max-w-6xl overflow-y-auto rounded-2xl border border-white/10 bg-white shadow-[0_30px_100px_rgba(2,6,23,.45)]"
      >
        <div className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 bg-white/95 px-5 py-4 backdrop-blur sm:px-6">
          <div className="flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-blue-700">
              <Pencil size={19} />
            </span>
            <div>
              <p className="text-[10px] font-black uppercase tracking-[.14em] text-blue-600">
                HR Controlled Profile Update
              </p>
              <h2 className="text-lg font-black text-slate-950">
                Edit Employee Details
              </h2>
              <p className="text-xs font-semibold text-slate-500">
                {employee.employeeCode || "-"} • {employee.email || "-"}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={saving}
            className="flex h-10 w-10 items-center justify-center rounded-xl border border-slate-200 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900"
            aria-label="Close employee editor"
          >
            <X size={18} />
          </button>
        </div>

        <div className="space-y-6 p-5 sm:p-6">
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold leading-5 text-emerald-900">
            Candidate/application se mili hui aur pehle saved saari details yahan
            automatically pre-filled hain. HR jo field change karega wahi employee,
            onboarding aur linked employee-login profile me safely sync hogi. Official
            email identity protection ke liye read-only rahegi.
            {employee._autoFilledFields?.length > 0 && (
              <span className="mt-2 block font-black text-emerald-700">
                {employee._autoFilledFields.length} missing field(s) linked candidate
                record se abhi auto-filled hui hain.
              </span>
            )}
          </div>

          <section>
            <h3 className="mb-4 text-sm font-black text-slate-900">
              Official & Work Details
            </h3>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field
                label="Full Name *"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Field label="Official Email (Locked)" value={form.email} readOnly />
              <Field
                label="Mobile"
                value={form.mobile}
                onChange={(e) => setForm({ ...form, mobile: e.target.value })}
              />
              <Select
                label="Employee Status"
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
              >
                <option value="ACTIVE">Active</option>
                <option value="PROBATION">Probation</option>
                <option value="ON_LEAVE">On Leave</option>
                <option value="INACTIVE">Inactive</option>
              </Select>
              <Select
                label="Department"
                value={form.department}
                onChange={(e) => setForm({ ...form, department: e.target.value })}
              >
                <option value="">Select Department</option>
                {form.department &&
                  !DEPARTMENT_OPTIONS.some(([, name]) => name === form.department) && (
                    <option value={form.department}>{form.department}</option>
                  )}
                {DEPARTMENT_OPTIONS.map(([id, name]) => (
                  <option key={id} value={name}>
                    {name}
                  </option>
                ))}
              </Select>
              <Field
                label="Designation"
                value={form.designation}
                onChange={(e) => setForm({ ...form, designation: e.target.value })}
              />
              <Field
                label="Reporting Manager"
                value={form.managerName}
                onChange={(e) => setForm({ ...form, managerName: e.target.value })}
              />
              <Field
                label="Joining Date"
                type="date"
                value={form.joiningDate}
                onChange={(e) => setForm({ ...form, joiningDate: e.target.value })}
              />
              <Field
                label="Highest Qualification"
                value={form.highestQualification}
                onChange={(e) =>
                  setForm({ ...form, highestQualification: e.target.value })
                }
              />
              <Field
                label="Total Experience"
                value={form.totalExperience}
                onChange={(e) =>
                  setForm({ ...form, totalExperience: e.target.value })
                }
                placeholder="e.g. 5 Years"
              />
            </div>
          </section>

          <section className="border-t border-slate-100 pt-5">
            <h3 className="mb-4 text-sm font-black text-slate-900">
              Personal & Emergency Details
            </h3>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <Field
                label="Date of Birth"
                type="date"
                value={form.dateOfBirth}
                onChange={(e) => setForm({ ...form, dateOfBirth: e.target.value })}
              />
              <Select
                label="Gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
              >
                <option value="">Select</option>
                <option value="MALE">Male</option>
                <option value="FEMALE">Female</option>
                <option value="OTHER">Other</option>
                <option value="PREFER_NOT_TO_SAY">Prefer not to say</option>
              </Select>
              <Select
                label="Blood Group"
                value={form.bloodGroup}
                onChange={(e) => setForm({ ...form, bloodGroup: e.target.value })}
              >
                <option value="">Select</option>
                {["A+", "A-", "B+", "B-", "AB+", "AB-", "O+", "O-"].map(
                  (group) => (
                    <option key={group} value={group}>
                      {group}
                    </option>
                  )
                )}
              </Select>
              <Field
                label="Emergency Contact Name"
                value={form.emergencyContactName}
                onChange={(e) =>
                  setForm({ ...form, emergencyContactName: e.target.value })
                }
              />
              <Field
                label="Emergency Contact Mobile"
                value={form.emergencyContactMobile}
                onChange={(e) =>
                  setForm({ ...form, emergencyContactMobile: e.target.value })
                }
              />
            </div>
          </section>

          <section className="border-t border-slate-100 pt-5">
            <h3 className="mb-4 text-sm font-black text-slate-900">
              Address Details
            </h3>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <TextArea
                label="Current Address"
                className="md:col-span-2"
                rows={3}
                value={form.currentAddress}
                onChange={(e) =>
                  setForm({ ...form, currentAddress: e.target.value })
                }
              />
              <TextArea
                label="Permanent Address"
                className="md:col-span-2"
                rows={3}
                value={form.permanentAddress}
                onChange={(e) =>
                  setForm({ ...form, permanentAddress: e.target.value })
                }
              />
              <Field
                label="Village / City"
                value={form.city}
                onChange={(e) => setForm({ ...form, city: e.target.value })}
              />
              <Field
                label="Taluka"
                value={form.taluka}
                onChange={(e) => setForm({ ...form, taluka: e.target.value })}
              />
              <Field
                label="District"
                value={form.district}
                onChange={(e) => setForm({ ...form, district: e.target.value })}
              />
              <Field
                label="State"
                value={form.state}
                onChange={(e) => setForm({ ...form, state: e.target.value })}
              />
              <Field
                label="Pincode"
                inputMode="numeric"
                maxLength={6}
                value={form.pincode}
                onChange={(e) => setForm({ ...form, pincode: e.target.value })}
              />
            </div>
          </section>

          <section className="border-t border-slate-100 pt-5">
            <h3 className="mb-1 text-sm font-black text-slate-900">
              Annual Leave Entitlements
            </h3>
            <p className="mb-4 text-xs font-semibold text-slate-500">
              Entitlement change karne par already used leave preserve rahegi; current
              balance automatically adjust hoga.
            </p>
            <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
              {[
                ["leaveCL", "Annual CL"],
                ["leaveEL", "Annual EL"],
                ["leaveOL", "Annual OL"],
                ["leaveCompOff", "Opening Comp-Off"],
              ].map(([key, label]) => (
                <Field
                  key={key}
                  label={label}
                  type="number"
                  min="0"
                  value={form[key]}
                  onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                />
              ))}
            </div>
          </section>
        </div>

        <div className="sticky bottom-0 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 bg-slate-50/95 px-5 py-4 backdrop-blur sm:px-6">
          <p className="text-xs font-semibold text-slate-500">
            Salary aur bank details unke separate secured actions se update hongi.
          </p>
          <div className="flex gap-3">
            <Button tone="light" onClick={onClose} disabled={saving}>
              Cancel
            </Button>
            <Button type="submit" tone="green" disabled={saving}>
              <Save size={16} /> {saving ? "Saving…" : "Save Employee Details"}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function EmployeeModule({
  form,
  setForm,
  rows,
  applications = [],
  candidateUsers = [],
  create,
  updateDetails,
  updateBank,
  updateSalary,
  updateAttendanceLocation,
  resetAttendanceDevice,
  setAttendancePin,
  canHR,
  canCreateDirect,
}) {
  const [editingEmployee, setEditingEmployee] = useState(null);
  const openEmployeeEditor = (item) => {
    const sourceApplication = applications.find(
      (application) =>
        application.id === item.sourceApplicationId ||
        application.employeeId === item.id ||
        (item.candidateUserId &&
          application.candidateUserId === item.candidateUserId)
    );
    const candidateProfile = candidateUsers.find(
      (candidate) => candidate.id === item.candidateUserId
    );
    const sourceProfile = employeeProfileFromApplication(
      sourceApplication || {},
      candidateProfile || {}
    );
    const hydratedEmployee = { ...item };
    const autoFilledFields = [];
    Object.entries(sourceProfile).forEach(([key, value]) => {
      if (!clean(hydratedEmployee[key]) && clean(value)) {
        hydratedEmployee[key] = value;
        autoFilledFields.push(key);
      }
    });
    hydratedEmployee._autoFilledFields = autoFilledFields;
    setEditingEmployee(hydratedEmployee);
  };
  return (
    <div className="space-y-5">
      <EmployeeProfileEditor
        employee={editingEmployee}
        onSave={updateDetails}
        onClose={() => setEditingEmployee(null)}
      />
      {canCreateDirect ? (
        <Card title="Direct / Manual Employee Creation" icon={Users}>
          <div className="mb-5 rounded-lg border border-emerald-200 bg-emerald-50 p-4 text-xs font-semibold leading-5 text-emerald-900">
            Selected candidate ko Recruitment se convert karte hi available name,
            contact, job, address, qualification aur joining details automatically
            Employee Master me aa jayengi. Yeh form sirf direct/manual employee ke
            liye hai. Salary vacancy me nahi li jati.
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            {[
              ["name", "Full Name"], ["email", "Official Email"], ["mobile", "Mobile"],
              ["department", "Department"], ["designation", "Designation"], ["managerName", "Reporting Manager"],
              ["joiningDate", "Joining Date", "date"], ["basic", "Basic Salary", "number"],
              ["hra", "HRA", "number"], ["da", "DA", "number"], ["allowances", "Allowances", "number"],
              ["pf", "Employee PF Deduction", "number"],
              ["employerPf", "Employer PF Contribution", "number"],
              ["esi", "Employee ESI Deduction", "number"],
              ["professionalTax", "Professional Tax", "number"],
              ["otherDeduction", "Other Monthly Deduction", "number"],
              ["accountHolderName", "Bank Account Holder"],
              ["bankName", "Bank Name"],
              ["accountNumber", "Account Number"],
              ["ifscCode", "IFSC Code"],
              ["bankBranch", "Bank Branch"],
              ["leaveCL", "Annual CL", "number"],
              ["leaveEL", "Annual EL", "number"],
              ["leaveOL", "Annual OL", "number"],
              ["leaveCompOff", "Opening Comp-Off", "number"],
            ].map(([key, label, type]) => <Field key={key} label={label} type={type || "text"} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />)}
            <Select
              label="TDS Treatment"
              value={form.tdsMode}
              onChange={(e) =>
                setForm({
                  ...form,
                  tdsMode: e.target.value,
                  ...(e.target.value === TDS_MODE.NOT_APPLICABLE ? { tds: "" } : {}),
                })
              }
            >
              <option value={TDS_MODE.SALARY_DEDUCTION}>Deduct from salary</option>
              <option value={TDS_MODE.COMPANY_BORNE}>Company pays separately</option>
              <option value={TDS_MODE.NOT_APPLICABLE}>Not applicable</option>
            </Select>
            <Field
              label={
                form.tdsMode === TDS_MODE.COMPANY_BORNE
                  ? "Monthly TDS (Company Paid)"
                  : "Monthly TDS (Salary Deduction)"
              }
              type="number"
              min="0"
              value={form.tds}
              disabled={form.tdsMode === TDS_MODE.NOT_APPLICABLE}
              onChange={(e) => setForm({ ...form, tds: e.target.value })}
            />
          </div>
          <Button className="mt-5" onClick={create}>Create Employee & Start Onboarding</Button>
        </Card>
      ) : (
        <Card title="Controlled Employee Activation" icon={ShieldCheck}>
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm font-semibold leading-6 text-blue-950">
            HR ke liye direct employee creation locked hai. Recruitment me
            selected candidate ka signed offer PDF, salary, Section 192 TDS aur
            PF package submit karein; CEO approval aur candidate acceptance ke
            baad <strong>Activate Employee</strong> use karein. Candidate ki
            available details automatically yahan aa jayengi.
          </div>
        </Card>
      )}
      <Card title="Employee Directory" icon={Users}>
        <Table collectionName={COLLECTIONS.employees} recordLabel="employee record" columns={["Employee", "Department / Role", "Manager", "Joining", "Salary", "Bank", "Attendance", "Status", "Action"]} rows={rows} renderRow={(item) => (
          <tr key={item.id} className="hover:bg-slate-50">
            <TD>
              <p className="font-semibold">{item.name}</p>
              <p className="text-xs text-slate-500">{item.employeeCode}<br/>{item.email}</p>
              {(item.profileSource === "SELECTED_CANDIDATE" || item.sourceApplicationId) && (
                <span className="mt-2 inline-flex rounded-full bg-blue-50 px-2 py-1 text-[9px] font-black uppercase tracking-wide text-blue-700">
                  Candidate Data Synced
                </span>
              )}
            </TD>
            <TD>{item.department}<br/><b>{item.designation}</b></TD><TD>{item.managerName || "-"}</TD>
            <TD>{item.joiningDate || "-"}</TD>
            <TD>
              {number(item.basic) + number(item.hra) + number(item.da) + number(item.allowances) > 0 ? (
                <>
                  <p className="font-semibold">{money(number(item.basic)+number(item.hra)+number(item.da)+number(item.allowances))}</p>
                  <p className="mt-1 text-[10px] font-semibold text-slate-500">TDS: {tdsModeLabel(item.tdsMode || item.salaryStructure?.tdsMode)}</p>
                </>
              ) : (
                <Badge value="SALARY_PENDING" />
              )}
            </TD>
            <TD><p className="text-xs font-semibold">{item.bankName || "Not added"}</p><p className="mt-1 text-xs text-slate-500">{maskAccount(item.accountNumber)}{item.ifscCode ? ` • ${item.ifscCode}` : ""}</p></TD>
            <TD>
              <Badge value={item.attendanceActive ? "ACTIVE" : "PENDING"} />
              <p className="mt-2 max-w-[180px] text-[10px] font-semibold leading-4 text-slate-500">
                {item.attendanceLocation?.label ||
                  item.officeLocationName ||
                  "Office geofence not configured"}
                {item.attendanceLocation?.radiusMeters ||
                item.attendanceRadiusMeters
                  ? ` • ${
                      item.attendanceLocation?.radiusMeters ||
                      item.attendanceRadiusMeters
                    }m`
                  : ""}
              </p>
            </TD>
            <TD><Badge value={item.status} /></TD>
            <TD>
              {canHR && (
                <div className="flex min-w-[210px] flex-wrap gap-2">
                  <Button tone="light" onClick={() => openEmployeeEditor(item)}>
                    <Pencil size={14} /> Edit Details
                  </Button>
                  <Button tone="light" onClick={() => updateBank(item)}>
                    Update Bank
                  </Button>
                  <Button tone="light" onClick={() => updateSalary(item)}>
                    <IndianRupee size={14} /> Update Salary
                  </Button>
                  <Button
                    tone="light"
                    onClick={() => updateAttendanceLocation(item)}
                  >
                    <MapPin size={14} /> Office Geofence
                  </Button>
                  <Button
                    tone="light"
                    onClick={() => setAttendancePin(item)}
                  >
                    <Lock size={14} /> Set Attendance PIN
                  </Button>
                  {(item.attendanceDeviceFingerprintHash ||
                    item.allowedAttendanceDeviceHash) && (
                    <Button
                      tone="amber"
                      onClick={() => resetAttendanceDevice(item)}
                    >
                      <RefreshCw size={14} /> Reset Device
                    </Button>
                  )}
                </div>
              )}
            </TD>
          </tr>
        )}/>
      </Card>
    </div>
  );
}

function AttendanceModule({ rows, decision, openCorrections, canHR }) {
  return (
    <Card
      title="Photo • Device/IP • GPS Attendance Records"
      icon={MapPin}
      action={
        <Button tone="light" onClick={openCorrections}>
          <MessageSquareText size={15} /> Correction Requests
        </Button>
      }
    >
      <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs font-semibold leading-5 text-blue-800">
        Employee ke direct attendance rows yahan HR approve/reject karega.
        Employee-requested check-in/check-out corrections existing Correction
        Requests workflow me reviewed rahengi.
      </div>
      <Table
        collectionName={COLLECTIONS.attendance}
        recordLabel="attendance record"
        columns={[
          "Employee",
          "Date",
          "Check In",
          "Check Out",
          "Photo",
          "Device / IP",
          "Location",
          "Status",
          "HR Action",
        ]}
        rows={rows}
        renderRow={(item) => {
          const reviewStatus = clean(
            item.hrReviewStatus || "PENDING_REVIEW"
          ).toUpperCase();
          const pendingReview = [
            "PENDING",
            "PENDING_REVIEW",
            "PENDING_HR_REVIEW",
          ].includes(reviewStatus);
          return (
            <tr key={item.id} className="hover:bg-slate-50">
              <TD><p className="font-semibold">{item.employeeName}</p><p className="text-xs">{item.employeeCode}</p></TD>
              <TD>{item.date || formatDate(item.createdAt)}</TD><TD>{item.checkInTime || "-"}</TD><TD>{item.checkOutTime || "-"}</TD>
              <TD>
                <div className="flex min-w-[105px] flex-col gap-1 text-xs">
                  <SecureStorageLink
                    path={
                      item.checkInPhotoPath ||
                      item.checkInPhotoStoragePath ||
                      item.photoStoragePath ||
                      item.photoUrl
                    }
                    label="Check-in photo"
                  />
                  {(item.checkOutPhotoPath ||
                    item.checkOutPhotoStoragePath) && (
                    <SecureStorageLink
                      path={
                        item.checkOutPhotoPath ||
                        item.checkOutPhotoStoragePath
                      }
                      label="Check-out photo"
                    />
                  )}
                </div>
              </TD>
              <TD>
                <span className="text-xs">
                  {item.deviceFingerprintHash
                    ? `Verified hash …${String(
                        item.deviceFingerprintHash
                      ).slice(-10)}`
                    : item.deviceId || "-"}
                  <br />
                  {item.ipAddress || "Browser verified"}
                </span>
              </TD>
              <TD>{item.latitude && item.longitude ? <a className="font-semibold text-blue-600" href={`https://maps.google.com/?q=${item.latitude},${item.longitude}`} target="_blank" rel="noreferrer">Open Map</a> : item.attendanceFlag || "-"}</TD>
              <TD>
                <Badge value={reviewStatus} />
                <p className="mt-1 text-[10px] font-semibold text-slate-400">
                  {item.attendanceStatus || item.status || item.actionType || "-"}
                </p>
                {item.reviewReason && (
                  <p className="mt-1 max-w-[160px] text-[10px] text-red-600">
                    {item.reviewReason}
                  </p>
                )}
              </TD>
              <TD>
                {canHR && pendingReview ? (
                  <div className="flex flex-wrap gap-2">
                    <Button tone="green" onClick={() => decision(item, "APPROVED")}>
                      <Check size={14} /> Approve
                    </Button>
                    <Button tone="red" onClick={() => decision(item, "REJECTED")}>
                      <X size={14} /> Reject
                    </Button>
                  </div>
                ) : (
                  <span className="text-xs font-semibold text-slate-400">
                    {item.reviewedByName || "Reviewed"}
                  </span>
                )}
              </TD>
            </tr>
          );
        }}
      />
    </Card>
  );
}

function EmployeeRequestStat({ icon: Icon, label, value, tone = "blue" }) {
  const tones = {
    blue: "bg-blue-50 text-blue-700",
    amber: "bg-amber-50 text-amber-700",
    green: "bg-emerald-50 text-emerald-700",
    red: "bg-red-50 text-red-700",
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm">
      <div
        className={`flex h-10 w-10 items-center justify-center rounded-xl ${
          tones[tone] || tones.blue
        }`}
      >
        <Icon size={19} />
      </div>
      <p className="mt-4 text-2xl font-semibold">{value}</p>
      <p className="mt-1 text-xs font-semibold uppercase tracking-wide text-slate-400">
        {label}
      </p>
    </div>
  );
}

function EmployeeRequestsModule({ rows, decision, canHR }) {
  const [statusFilter, setStatusFilter] = useState("OPEN");
  const [requestSearch, setRequestSearch] = useState("");
  const [busyRequestId, setBusyRequestId] = useState("");
  const openStatuses = [
    "OPEN",
    "PENDING",
    "PENDING_HR_REVIEW",
    "IN_PROGRESS",
  ];
  const openCount = rows.filter((item) =>
    openStatuses.includes(clean(item.status).toUpperCase())
  ).length;
  const resolvedCount = rows.filter((item) =>
    ["APPROVED", "RESOLVED"].includes(clean(item.status).toUpperCase())
  ).length;
  const rejectedCount = rows.filter(
    (item) => clean(item.status).toUpperCase() === "REJECTED"
  ).length;
  const visibleRows = rows.filter((item) => {
    const status = clean(item.status || "PENDING_HR_REVIEW").toUpperCase();
    const matchesStatus =
      statusFilter === "ALL" ||
      (statusFilter === "OPEN" && openStatuses.includes(status)) ||
      status === statusFilter;
    const searchText = [
      item.employeeName,
      item.employeeCode,
      item.employeeEmail,
      item.requestType,
      item.type,
      item.subject,
      item.description,
    ]
      .map(clean)
      .join(" ")
      .toLowerCase();
    return (
      matchesStatus &&
      (!clean(requestSearch) ||
        searchText.includes(clean(requestSearch).toLowerCase()))
    );
  });

  const performDecision = async (item, nextStatus) => {
    let response = "";
    if (nextStatus !== "IN_PROGRESS") {
      const answer = window.prompt(
        nextStatus === "REJECTED"
          ? "Rejection reason likhein (required):"
          : "Employee ke liye HR response/note likhein (optional):",
        item.resolutionNote || ""
      );
      if (answer === null) return;
      response = clean(answer);
      if (nextStatus === "REJECTED" && !response) {
        window.alert("Reject karne ke liye reason required hai.");
        return;
      }
    }
    setBusyRequestId(item.id);
    try {
      await decision(item, nextStatus, response);
    } finally {
      setBusyRequestId("");
    }
  };

  const detailLines = (item) => {
    const payload =
      item.payload && typeof item.payload === "object" ? item.payload : {};
    const requestType = clean(item.requestType || item.type).toUpperCase();
    const lines = [];
    if (requestType === "ATTENDANCE_CORRECTION") {
      const date = clean(
        payload.date || item.attendanceDate || item.referenceDate
      );
      const checkIn = clean(
        payload.requestedCheckIn ||
          payload.requestedCheckInTime ||
          item.requestedCheckIn
      );
      const checkOut = clean(
        payload.requestedCheckOut ||
          payload.requestedCheckOutTime ||
          item.requestedCheckOut
      );
      if (date) lines.push(`Date: ${date}`);
      if (checkIn) lines.push(`Requested in: ${checkIn}`);
      if (checkOut) lines.push(`Requested out: ${checkOut}`);
    }
    if (requestType === "PROFILE_CHANGE") {
      const field = clean(
        payload.field || item.profileField || item.requestedField
      );
      const value = clean(
        payload.requestedValue ?? item.requestedValue ?? item.newValue
      );
      if (field) lines.push(`Field: ${field}`);
      if (value) lines.push(`Requested: ${value}`);
    }
    return lines;
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <EmployeeRequestStat
          icon={MessageSquareText}
          label="Total Requests"
          value={rows.length}
          tone="blue"
        />
        <EmployeeRequestStat
          icon={Clock}
          label="Open Queue"
          value={openCount}
          tone="amber"
        />
        <EmployeeRequestStat
          icon={CheckCheck}
          label="Approved / Resolved"
          value={resolvedCount}
          tone="green"
        />
        <EmployeeRequestStat
          icon={X}
          label="Rejected"
          value={rejectedCount}
          tone="red"
        />
      </div>

      <Card
        title="Employee Request & Helpdesk Control"
        icon={MessageSquareText}
        action={
          <div className="flex flex-wrap items-end gap-2">
            <Select
              label="Status"
              value={statusFilter}
              onChange={(event) => setStatusFilter(event.target.value)}
            >
              <option value="OPEN">Open Queue</option>
              <option value="ALL">All Requests</option>
              <option value="IN_PROGRESS">In Progress</option>
              <option value="APPROVED">Approved</option>
              <option value="RESOLVED">Resolved</option>
              <option value="REJECTED">Rejected</option>
            </Select>
            <Field
              label="Search"
              placeholder="Employee, type or subject"
              value={requestSearch}
              onChange={(event) => setRequestSearch(event.target.value)}
            />
          </div>
        }
      >
        <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-4">
          <p className="font-semibold text-blue-950">
            ESS → HR controlled workflow
          </p>
          <p className="mt-1 text-xs font-semibold leading-5 text-blue-700">
            Attendance correction aur allowlisted profile change Approve karne
            par linked record safely update hoga. Baaki helpdesk requests HR
            response aur complete audit trail ke saath close hongi.
          </p>
        </div>
        <Table
          collectionName={COLLECTIONS.employeeRequests}
          recordLabel="employee service request"
          columns={[
            "Employee",
            "Request",
            "Details",
            "Priority",
            "Submitted",
            "Status / HR Response",
            "Action",
          ]}
          rows={visibleRows}
          empty="Selected filter me koi employee request nahi mili"
          renderRow={(item) => {
            const requestType = clean(
              item.requestType || item.type || "HR_SUPPORT"
            ).toUpperCase();
            const status = clean(
              item.status || "PENDING_HR_REVIEW"
            ).toUpperCase();
            const actionable = openStatuses.includes(status);
            const details = detailLines(item);
            const priority = clean(item.priority || "NORMAL").toUpperCase();
            const priorityClass =
              priority === "URGENT"
                ? "bg-red-100 text-red-700"
                : priority === "HIGH"
                ? "bg-orange-100 text-orange-700"
                : "bg-slate-100 text-slate-700";
            return (
              <tr key={item.id} className="hover:bg-slate-50">
                <TD>
                  <p className="font-semibold">
                    {item.employeeName || "Employee"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.employeeCode || "-"}
                    {item.employeeEmail || item.email
                      ? ` • ${item.employeeEmail || item.email}`
                      : ""}
                  </p>
                  {item.department && (
                    <p className="mt-1 text-[10px] font-bold text-slate-400">
                      {item.department}
                    </p>
                  )}
                </TD>
                <TD>
                  <p className="font-semibold">
                    {requestType.replaceAll("_", " ")}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-600">
                    {item.subject || "-"}
                  </p>
                </TD>
                <TD>
                  <p className="max-w-sm whitespace-pre-line text-xs font-semibold leading-5 text-slate-600">
                    {item.description || item.reason || "-"}
                  </p>
                  {details.map((line) => (
                    <p
                      key={line}
                      className="mt-1 text-[10px] font-semibold text-blue-700"
                    >
                      {line}
                    </p>
                  ))}
                  {(item.attachmentUrl || item.fileUrl) && (
                    <a
                      href={item.attachmentUrl || item.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 inline-block text-xs font-semibold text-blue-600"
                    >
                      View attachment
                    </a>
                  )}
                </TD>
                <TD>
                  <span
                    className={`inline-flex rounded-full px-2.5 py-1 text-[10px] font-semibold ${priorityClass}`}
                  >
                    {priority}
                  </span>
                </TD>
                <TD>{formatDate(item.createdAt || item.submittedAt)}</TD>
                <TD>
                  <Badge value={status} />
                  {item.resolutionNote && (
                    <p className="mt-2 max-w-xs text-xs font-semibold leading-5 text-slate-600">
                      {item.resolutionNote}
                    </p>
                  )}
                  {item.actionByName && (
                    <p className="mt-1 text-[10px] font-bold text-slate-400">
                      By {item.actionByName} • {formatDate(item.actionAt)}
                    </p>
                  )}
                </TD>
                <TD>
                  {canHR && actionable ? (
                    <div className="flex min-w-[240px] flex-wrap gap-2">
                      {status !== "IN_PROGRESS" && (
                        <Button
                          tone="amber"
                          className="px-3 py-2 text-xs"
                          disabled={busyRequestId === item.id}
                          onClick={() =>
                            performDecision(item, "IN_PROGRESS")
                          }
                        >
                          Start
                        </Button>
                      )}
                      <Button
                        tone="green"
                        className="px-3 py-2 text-xs"
                        disabled={busyRequestId === item.id}
                        onClick={() => performDecision(item, "APPROVED")}
                      >
                        Approve
                      </Button>
                      <Button
                        tone="blue"
                        className="px-3 py-2 text-xs"
                        disabled={busyRequestId === item.id}
                        onClick={() => performDecision(item, "RESOLVED")}
                      >
                        Resolve
                      </Button>
                      <Button
                        tone="red"
                        className="px-3 py-2 text-xs"
                        disabled={busyRequestId === item.id}
                        onClick={() => performDecision(item, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      {actionable ? "HR access required" : "Closed"}
                    </span>
                  )}
                </TD>
              </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function LeaveModule({
  form,
  setForm,
  rows,
  employees,
  submit,
  decision,
  canCEO,
  isHRAdmin,
  holidays = [],
  settings = [],
}) {
  const calendarSpanDays = enumerateDateRange(
    form.fromDate,
    form.toDate
  ).length;
  const automaticDays = chargeableLeaveDates(
    form.fromDate,
    form.toDate,
    holidays,
    settings
  ).length;
  return (
    <div className="space-y-5">
      <Card title="Leave Request" icon={Clock}>
        <div className="grid gap-4 md:grid-cols-4">
          <Select label="Employee" value={form.employeeId} onChange={(e) => { const emp=employees.find(x=>x.id===e.target.value); setForm({...form,employeeId:e.target.value,employeeName:emp?.name||"",candidateUserId:emp?.candidateUserId||""}); }}>
            <option value="">Select employee</option>{employees.map((e)=><option key={e.id} value={e.id}>{e.name} ({e.employeeCode})</option>)}
          </Select>
          <Select label="Leave Type" value={form.type} onChange={(e)=>setForm({...form,type:e.target.value})}><option>CL</option><option>EL</option><option>OL</option><option>COMP_OFF</option><option>LOP</option></Select>
          <Field label="From Date" type="date" value={form.fromDate} onChange={(e)=>setForm({...form,fromDate:e.target.value})}/>
          <Field label="To Date" type="date" value={form.toDate} onChange={(e)=>setForm({...form,toDate:e.target.value})}/>
          <Field label="Leave Days (Working Days Auto)" type="number" value={automaticDays || ""} readOnly/>
          <Field label="Excluded Weekly Off / Holiday" type="number" value={Math.max(0, calendarSpanDays - automaticDays) || ""} readOnly/>
          <Field label="Reason" className="md:col-span-2" value={form.reason} onChange={(e)=>setForm({...form,reason:e.target.value})}/>
        </div><Button className="mt-5" onClick={submit}>Submit Leave Request</Button>
      </Card>
      <Card title="HR Approval → CEO Exception Workflow" icon={CheckCircle2}>
        <Table collectionName={COLLECTIONS.leaves} recordLabel="leave request" columns={["Employee", "Leave", "Period", "Reason", "LOP", "Status", "Action"]} rows={rows} renderRow={(item)=>(
          <tr key={item.id} className="hover:bg-slate-50"><TD className="font-semibold">{item.employeeName}</TD><TD>{item.type} • {item.days} day(s)</TD><TD>{item.fromDate} to {item.toDate}</TD><TD>{item.reason}</TD><TD>{item.lopDays||0}</TD><TD><Badge value={item.status}/></TD>
          <TD><div className="flex flex-wrap gap-2">
            {isHRAdmin&&item.status==="PENDING_HR_APPROVAL"&&<><Button tone="green" onClick={()=>decision(item,"HR","APPROVED")}>HR Approve</Button><Button tone="red" onClick={()=>decision(item,"HR","REJECTED")}>Reject</Button></>}
            {canCEO&&item.status==="PENDING_HR_APPROVAL"&&<><Button tone="green" onClick={()=>decision(item,"CEO_OVERRIDE","APPROVED")}>CEO Override</Button><Button tone="red" onClick={()=>decision(item,"CEO_OVERRIDE","REJECTED")}>CEO Reject</Button></>}
            {canCEO&&item.status==="PENDING_CEO_APPROVAL"&&<><Button tone="green" onClick={()=>decision(item,"CEO","APPROVED")}>CEO Approve</Button><Button tone="red" onClick={()=>decision(item,"CEO","REJECTED")}>Reject</Button></>}
          </div></TD></tr>
        )}/>
      </Card>
    </div>
  );
}

function HolidayCalendarModule({
  form,
  setForm,
  rows,
  settings,
  save,
  importGujarat2026,
  saveSettings,
  canHR,
}) {
  const [selectedMonth, setSelectedMonth] = useState(today().slice(0, 7));
  const savedSettings = useMemo(
    () => payrollCalendarSettings(settings),
    [settings]
  );
  const [weeklyOffDays, setWeeklyOffDays] = useState(
    savedSettings.weeklyOffDays
  );
  const [saturdayPolicy, setSaturdayPolicy] = useState(
    savedSettings.saturdayPolicy
  );
  useEffect(() => {
    setWeeklyOffDays(savedSettings.weeklyOffDays);
    setSaturdayPolicy(savedSettings.saturdayPolicy);
  }, [savedSettings.saturdayPolicy, settings]);

  const selectedYear = Number(selectedMonth.slice(0, 4)) || new Date().getFullYear();
  const monthSummaries = useMemo(
    () =>
      Array.from({ length: 12 }, (_, index) => {
        const month =
          String(selectedYear) + "-" + String(index + 1).padStart(2, "0");
        return buildMonthCalendar(month, rows, settings);
      }),
    [rows, selectedYear, settings]
  );
  const selectedSummary =
    monthSummaries.find((item) => item.month === selectedMonth) ||
    buildMonthCalendar(selectedMonth, rows, settings);
  const weekdayLabels = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  const visibleRows = [...rows]
    .filter(
      (item) =>
        !item.isDeleted &&
        clean(item.date).startsWith(String(selectedYear) + "-")
    )
    .sort((a, b) => clean(a.date).localeCompare(clean(b.date)));

  const toggleWeeklyOff = (day) => {
    setWeeklyOffDays((current) =>
      current.includes(day)
        ? current.filter((value) => value !== day)
        : [...current, day].sort()
    );
  };

  return (
    <div className="space-y-5">
      <Card
        title="Enterprise Working Calendar"
        icon={CalendarDays}
        action={
          canHR ? (
            <Button tone="dark" onClick={importGujarat2026}>
              <Download size={15} /> Import Gujarat GAD 2026
            </Button>
          ) : null
        }
      >
        <div className="grid gap-4 lg:grid-cols-[1.1fr_1fr]">
          <div className="rounded-xl border border-blue-200 bg-blue-50 p-5">
            <p className="text-sm font-black text-blue-950">
              One calendar for Attendance, Leave and Payroll
            </p>
            <p className="mt-2 text-xs font-semibold leading-5 text-blue-700">
              Actual 28/29/30/31 month days, weekly offs, joining/exit date and
              approved festival/company holidays are calculated automatically.
              Manual Working Days entry is disabled.
            </p>
          </div>
          <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-5">
            <p className="text-sm font-black text-emerald-950">
              Official calendar control
            </p>
            <p className="mt-2 text-xs font-semibold leading-5 text-emerald-700">
              2026 template follows Gujarat GAD notification
              GS/22/2025/JSR/2025/509/GH, including corrected Bakri Eid date:
              28 May 2026.
            </p>
            <a
              href="https://gad.gujarat.gov.in/personnel/holidays.htm"
              target="_blank"
              rel="noreferrer"
              className="mt-2 inline-flex text-xs font-black text-emerald-800 underline"
            >
              Open Gujarat GAD holiday source
            </a>
          </div>
        </div>
      </Card>

      {canHR && (
        <Card title="Weekly Off Policy" icon={Settings}>
          <div className="grid gap-5 lg:grid-cols-[1fr_320px_auto] lg:items-end">
            <div>
              <p className="mb-2 text-[10px] font-black uppercase tracking-[.1em] text-slate-500">
                Recurring weekly off
              </p>
              <div className="flex flex-wrap gap-2">
                {weekdayLabels.map((label, day) => (
                  <button
                    key={label}
                    type="button"
                    onClick={() => toggleWeeklyOff(day)}
                    className={
                      weeklyOffDays.includes(day)
                        ? "rounded-xl border border-blue-600 bg-blue-600 px-4 py-2.5 text-xs font-black text-white shadow-sm"
                        : "rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black text-slate-600 hover:border-blue-400"
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            <Select
              label="Saturday Policy"
              value={saturdayPolicy}
              onChange={(event) => setSaturdayPolicy(event.target.value)}
            >
              <option value="NONE">No extra Saturday off</option>
              <option value="ALL">Every Saturday off</option>
              <option value="SECOND_FOURTH">2nd & 4th Saturday off</option>
            </Select>
            <Button
              onClick={() =>
                saveSettings({ weeklyOffDays, saturdayPolicy })
              }
            >
              <Save size={15} /> Save Calendar Policy
            </Button>
          </div>
        </Card>
      )}

      <Card
        title={String(selectedYear) + " Month-wise Day Calculation"}
        icon={CalendarClock}
        action={
          <Field
            label="View Month"
            type="month"
            value={selectedMonth}
            onChange={(event) => setSelectedMonth(event.target.value)}
          />
        }
      >
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {monthSummaries.map((summary) => {
            const monthLabel = new Intl.DateTimeFormat("en-IN", {
              month: "long",
              year: "numeric",
              timeZone: "UTC",
            }).format(new Date(summary.month + "-01T00:00:00.000Z"));
            return (
              <button
                type="button"
                key={summary.month}
                onClick={() => setSelectedMonth(summary.month)}
                className={
                  summary.month === selectedMonth
                    ? "rounded-xl border border-blue-500 bg-blue-50 p-4 text-left shadow-md ring-2 ring-blue-100"
                    : "rounded-xl border border-slate-200 bg-white p-4 text-left hover:border-blue-300 hover:bg-slate-50"
                }
              >
                <p className="font-black text-slate-900">{monthLabel}</p>
                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <span className="font-semibold text-slate-500">Calendar</span>
                  <span className="text-right font-black">
                    {summary.calendarDays}
                  </span>
                  <span className="font-semibold text-slate-500">Weekly off</span>
                  <span className="text-right font-black">
                    {summary.weeklyOffDays}
                  </span>
                  <span className="font-semibold text-slate-500">Holidays</span>
                  <span className="text-right font-black">
                    {summary.holidayDays}
                  </span>
                  <span className="font-semibold text-emerald-700">Working</span>
                  <span className="text-right font-black text-emerald-700">
                    {summary.workingDays}
                  </span>
                </div>
              </button>
            );
          })}
        </div>
        <div className="mt-5 rounded-xl border border-slate-200 bg-slate-50 p-4">
          <p className="font-black text-slate-900">
            Selected month: {selectedSummary.calendarDays} calendar days −{" "}
            {selectedSummary.weeklyOffDays} weekly offs −{" "}
            {selectedSummary.holidayDays} weekday holidays ={" "}
            <span className="text-emerald-700">
              {selectedSummary.workingDays} working days
            </span>
          </p>
        </div>
      </Card>

      {canHR && (
        <Card title="Add / Update Festival or Company Holiday" icon={CalendarCheck}>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-5">
            <Field
              label="Holiday Date"
              type="date"
              value={form.date}
              onChange={(event) =>
                setForm({ ...form, date: event.target.value })
              }
            />
            <Field
              label="Holiday Name"
              value={form.name}
              placeholder="Festival / company holiday"
              onChange={(event) =>
                setForm({ ...form, name: event.target.value })
              }
            />
            <Select
              label="Holiday Type"
              value={form.type}
              onChange={(event) =>
                setForm({ ...form, type: event.target.value })
              }
            >
              <option value="FESTIVAL">Festival Holiday</option>
              <option value="NATIONAL">National Holiday</option>
              <option value="PUBLIC_HOLIDAY">Public Holiday</option>
              <option value="COMPANY">Company Holiday</option>
              <option value="LOCAL">Local Holiday</option>
            </Select>
            <Field
              label="Remarks"
              value={form.notes}
              onChange={(event) =>
                setForm({ ...form, notes: event.target.value })
              }
            />
            <Button onClick={save}>
              <Save size={15} /> {form.id ? "Update Holiday" : "Add Holiday"}
            </Button>
          </div>
        </Card>
      )}

      <Card title={String(selectedYear) + " Approved Holiday Register"} icon={ClipboardCheck}>
        <Table
          collectionName={COLLECTIONS.holidays}
          recordLabel="holiday"
          columns={["Date / Day", "Holiday", "Type", "Payroll Effect", "Source", "Action"]}
          rows={visibleRows}
          empty="Is year ke liye holiday record nahi mila"
          renderRow={(item) => (
            <tr key={item.id} className="hover:bg-slate-50">
              <TD>
                <p className="font-black">{item.date}</p>
                <p className="text-xs text-slate-500">
                  {isValidIsoDate(item.date)
                    ? new Intl.DateTimeFormat("en-IN", {
                        weekday: "long",
                        timeZone: "UTC",
                      }).format(new Date(item.date + "T00:00:00.000Z"))
                    : "-"}
                </p>
              </TD>
              <TD className="font-semibold">{item.name}</TD>
              <TD><Badge value={item.type || "FESTIVAL"} /></TD>
              <TD>
                <Badge value={item.isWorkingDay === true ? "WORKING" : "PAID"} />
                <p className="mt-1 text-xs text-slate-500">
                  {item.isWorkingDay === true
                    ? "Working-day override"
                    : "Excluded from working days"}
                </p>
              </TD>
              <TD>
                <p className="text-xs font-semibold">
                  {String(item.source || "COMPANY_CALENDAR").replaceAll("_", " ")}
                </p>
                {item.sourceNotification && (
                  <p className="mt-1 text-[10px] text-slate-500">
                    {item.sourceNotification}
                  </p>
                )}
              </TD>
              <TD>
                {canHR && (
                  <Button
                    tone="light"
                    onClick={() =>
                      setForm({
                        id: item.id,
                        date: item.date || "",
                        name: item.name || "",
                        type: item.type || "FESTIVAL",
                        paid: item.isPaidHoliday !== false,
                        notes: item.notes || "",
                        source: item.source || "COMPANY_CALENDAR",
                      })
                    }
                  >
                    <Pencil size={14} /> Edit
                  </Button>
                )}
              </TD>
            </tr>
          )}
        />
      </Card>
    </div>
  );
}

function PayrollModule({
  form,
  setForm,
  employees,
  rows,
  batches = [],
  mailRows = [],
  calculation,
  selectEmployee,
  prepare,
  decision,
  sendToAccount,
  recordPayment,
  runAutomation,
  downloadPayslip,
  canHR,
  isHRAdmin,
  canCEO,
  canSendToAccounts,
  canRecordPayment,
}) {
  const numeric = [
    ["basic", "Basic"],
    ["hra", "HRA"],
    ["da", "DA"],
    ["allowances", "Allowances"],
    ["pf", "Employee PF"],
    ["employerPf", "Employer PF"],
    ["esi", "ESI"],
    ["professionalTax", "Professional Tax"],
    ["otherDeduction", "Other Deduction"],
  ];
  const latestBatch =
    batches.find((item) => item.month === form.month) || batches[0] || null;
  return (
    <div className="space-y-5">
      {canHR && (
        <Card title="Monthly Payroll Automation Control" icon={Bot}>
          <div className="grid gap-4 lg:grid-cols-[1fr_auto] lg:items-center">
            <div>
              <p className="font-semibold text-[#17365d]">
                1st day 07:00 IST: attendance close → salary calculation → CEO
                approval queue → Accounts processing register
              </p>
              <p className="mt-2 text-xs font-semibold leading-5 text-slate-500">
                CEO approval ke baad final NEFT mail automatically jayega. UTR
                save hote hi branded PDF payslip generate hokar employee ko mail hogi.
              </p>
            </div>
            <Button
              tone="dark"
              onClick={() =>
                runAutomation(form.month || previousClosedMonthKey())
              }
            >
              <Zap size={15} /> Run Safe Automation
            </Button>
          </div>
          {latestBatch && (
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
              {[
                ["Month", latestBatch.month || "-"],
                ["Employees", latestBatch.eligibleEmployees || 0],
                ["Exceptions", latestBatch.exceptionCount || 0],
                ["Net Payroll", money(latestBatch.totalNet)],
                ["Company-paid TDS", money(latestBatch.totalCompanyBorneTds)],
                ["Status", String(latestBatch.status || "-").replaceAll("_", " ")],
              ].map(([label, value]) => (
                <div key={label} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[10px] font-semibold uppercase text-slate-400">{label}</p>
                  <p className="mt-1 text-sm font-semibold text-slate-900">{value}</p>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}
      {canHR && (
        <Card title="HR Payroll Finalisation" icon={Wallet}>
          <div className="mb-5 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <p className="font-semibold text-blue-950">
              HR Finalise → CEO Approve → HR Mail to Account → UTR Confirmation
            </p>
            <p className="mt-1 text-xs font-semibold text-blue-700">
              Final approved payment advice {OFFICIAL_EMAILS.ACCOUNT} ke
              Accounts workspace me jayega. Payment UTR save hone ke baad
              payslip automatically release hogi.
            </p>
          </div>
          <div className="grid gap-4 md:grid-cols-4">
            <Select
              label="Employee"
              value={form.employeeId}
              onChange={(e) => selectEmployee(e.target.value)}
            >
              <option value="">Select employee</option>
              {employees.map((employee) => (
                <option key={employee.id} value={employee.id}>
                  {employee.name} ({employee.employeeCode})
                </option>
              ))}
            </Select>
            <Field
              label="Payroll Month"
              type="month"
              value={form.month}
              onChange={(e) => setForm({ ...form, month: e.target.value })}
            />
            {numeric.map(([key, label]) => (
              <Field
                key={key}
                label={label}
                type="number"
                value={form[key]}
                onChange={(e) =>
                  setForm({ ...form, [key]: e.target.value })
                }
              />
            ))}
            <Select
              label="TDS Treatment"
              value={form.tdsMode}
              onChange={(e) =>
                setForm({
                  ...form,
                  tdsMode: e.target.value,
                  ...(e.target.value === TDS_MODE.NOT_APPLICABLE ? { tds: "" } : {}),
                })
              }
            >
              <option value={TDS_MODE.SALARY_DEDUCTION}>Deduct from salary</option>
              <option value={TDS_MODE.COMPANY_BORNE}>Company pays separately</option>
              <option value={TDS_MODE.NOT_APPLICABLE}>Not applicable</option>
            </Select>
            <Field
              label={
                form.tdsMode === TDS_MODE.COMPANY_BORNE
                  ? "TDS Amount (Company Paid)"
                  : "TDS Amount (Salary Deduction)"
              }
              type="number"
              min="0"
              value={form.tds}
              disabled={form.tdsMode === TDS_MODE.NOT_APPLICABLE}
              onChange={(e) => setForm({ ...form, tds: e.target.value })}
            />
          </div>
          {form.employeeId && (
            <div className="mt-5 grid gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-5">
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Bank Account
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {form.bankName || "Bank details missing"}
                </p>
                <p className="text-xs text-slate-500">
                  {maskAccount(form.accountNumber)} • {form.ifscCode || "-"}
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Month Calendar
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {calculation.calendarDays || 0} calendar days
                </p>
                <p className="text-xs text-slate-500">
                  {calculation.weeklyOffDays || 0} weekly off •{" "}
                  {calculation.holidayDays || 0} holiday
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Auto Working Days
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {calculation.workingDays || 0} working days
                </p>
                <p className="text-xs text-slate-500">
                  Manual day entry disabled
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Attendance
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {calculation.paidDays}/{calculation.workingDays} paid days
                </p>
                <p className="text-xs text-slate-500">
                  {calculation.attendanceAbsentDays} absent •{" "}
                  {calculation.approvedLopDays} LOP
                </p>
              </div>
              <div>
                <p className="text-[10px] font-semibold uppercase text-slate-400">
                  Employee
                </p>
                <p className="mt-1 text-sm font-semibold">
                  {form.employeeCode || "-"}
                </p>
                <p className="text-xs text-slate-500">
                  {form.department || "-"} • {form.designation || "-"}
                </p>
              </div>
            </div>
          )}
          <div className="mt-5 grid grid-cols-2 gap-3 rounded-md border border-[#c6d1dc] bg-[#f8fafc] p-4 text-slate-900 md:grid-cols-3 xl:grid-cols-6">
            <div>
              <p className="text-xs text-slate-500">Gross</p>
              <p className="text-xl font-semibold text-[#17365d]">{money(calculation.gross)}</p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Paid Days</p>
              <p className="text-xl font-semibold text-[#17365d]">
                {calculation.paidDays}/{calculation.workingDays}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Attendance/LOP</p>
              <p className="text-xl font-semibold text-[#17365d]">
                {money(calculation.attendanceDeduction)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Total Deduction</p>
              <p className="text-xl font-semibold text-[#17365d]">
                {money(calculation.totalDeduction)}
              </p>
            </div>
            <div>
              <p className="text-xs text-slate-500">Company-paid TDS</p>
              <p className="text-xl font-semibold text-violet-700">
                {money(calculation.companyBorneTds)}
              </p>
              <p className="text-[10px] font-semibold text-slate-500">
                {tdsModeLabel(form.tdsMode)}
              </p>
            </div>
            <div>
              <p className="text-xs text-emerald-700">Net Salary</p>
              <p className="text-2xl font-semibold text-emerald-700">
                {money(calculation.net)}
              </p>
            </div>
          </div>
          <Button className="mt-5" onClick={prepare}>
            HR Finalise & Send to CEO
          </Button>
        </Card>
      )}
      <Card title="CEO Approval & Account Payment Control" icon={IndianRupee}>
        <Table
          collectionName={COLLECTIONS.payroll}
          recordLabel="payroll record"
          columns={[
            "Employee / Month",
            "Attendance",
            "Gross / Deduction",
            "Net Pay",
            "CEO Approval",
            "Account Payment",
            "Action",
          ]}
          rows={rows}
          renderRow={(item) => {
            const linkedAccountMail = mailRows.find(
              (mailItem) => mailItem.id === item.accountsMailId
            );
            const accountMailStatus =
              linkedAccountMail?.status ||
              item.accountsMailStatus ||
              "NOT_SENT";
            return (
              <tr key={item.id} className="hover:bg-slate-50">
              <TD>
                <p className="font-semibold">{item.employeeName}</p>
                <p className="text-xs text-slate-500">
                  {item.employeeCode || "-"} • {item.month}
                </p>
              </TD>
              <TD>
                <p className="font-semibold">
                  {item.paidDays}/{item.workingDays} paid
                </p>
                <p className="text-xs text-slate-500">
                  {item.attendanceAbsentDays || 0} absent •{" "}
                  {item.approvedLopDays || 0} LOP
                </p>
              </TD>
              <TD>
                <p className="font-semibold">{money(item.gross)}</p>
                <p className="text-xs text-red-600">
                  − {money(item.totalDeduction)}
                </p>
                <p className="mt-1 text-[10px] font-semibold text-violet-700">
                  TDS: {tdsModeLabel(item.tdsMode)} • {money(item.tds)}
                </p>
              </TD>
              <TD className="font-semibold">{money(item.net)}</TD>
              <TD>
                <Badge value={item.status} />
                {item.ceoActionBy && (
                  <p className="mt-1 text-xs text-slate-500">
                    {item.ceoActionBy}
                  </p>
                )}
              </TD>
              <TD>
                <Badge value={item.paymentStatus} />
                <p className="mt-1 text-xs text-slate-500">
                  Mail: {String(accountMailStatus).replaceAll("_", " ")}
                </p>
                {item.utrNumber && (
                  <p className="mt-1 text-xs font-semibold">
                    UTR: {item.utrNumber}
                  </p>
                )}
                {item.payslipStatus && (
                  <p className="mt-1 text-[10px] font-semibold text-blue-600">
                    Payslip: {String(item.payslipStatus).replaceAll("_", " ")}
                  </p>
                )}
              </TD>
              <TD>
                <div className="flex flex-wrap gap-2">
                  {canCEO && isPendingCEOApproval(item.status) && (
                    <>
                      <Button
                        tone="green"
                        onClick={() => decision(item, "APPROVED_BY_CEO")}
                      >
                        CEO Approve
                      </Button>
                      <Button
                        tone="red"
                        onClick={() => decision(item, "REJECTED")}
                      >
                        Reject
                      </Button>
                    </>
                  )}
                  {canSendToAccounts &&
                    item.status === "APPROVED_BY_CEO" &&
                    item.paymentStatus === "READY_FOR_ACCOUNTS" && (
                      <Button onClick={() => sendToAccount(item)}>
                        <Mail size={14} /> Send Final to Account
                      </Button>
                    )}
                  {canRecordPayment &&
                    item.paymentStatus === "PENDING_ACCOUNT_PAYMENT" && (
                      <Button
                        tone="amber"
                        onClick={() => recordPayment(item)}
                      >
                        Record Account UTR
                      </Button>
                    )}
                  {item.payslipPath && item.payslipStatus === "READY" && (
                    <Button tone="light" onClick={() => downloadPayslip(item)}>
                      <FileText size={14} /> Open Payslip PDF
                    </Button>
                  )}
                </div>
              </TD>
            </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function StatutoryComplianceModule({
  form,
  setForm,
  rows = [],
  create,
  recordPayment,
  canManage,
}) {
  const [sectionFilter, setSectionFilter] = useState("ALL");
  const [periodFilter, setPeriodFilter] = useState("");
  const [paymentItem, setPaymentItem] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    paidAmount: "",
    paymentDate: today(),
    challanReference: "",
    bsrCode: "",
    bankReference: "",
    challanProofUrl: "",
  });
  const filteredRows = rows.filter((item) => {
    const section = clean(item.section || item.complianceType).toUpperCase();
    return (
      (sectionFilter === "ALL" || section === sectionFilter) &&
      (!periodFilter || clean(item.period) === periodFilter)
    );
  });
  const openRows = rows.filter((item) => !item.isClosed);
  const overdueRows = openRows.filter(
    (item) => effectiveStatutoryStatus(item) === "OVERDUE"
  );
  const dueSoonRows = openRows.filter(
    (item) => effectiveStatutoryStatus(item) === "DUE_SOON"
  );
  const totalOpen = openRows.reduce(
    (sum, item) => sum + Math.max(0, number(item.amount) - number(item.paidAmount)),
    0
  );
  const computed194Amount = roundMoney(
    number(form.grossAmount) * number(form.tdsRate) / 100
  );
  const openPayment = (item) => {
    setPaymentItem(item);
    setPaymentForm({
      paidAmount: String(Math.max(0, number(item.amount) - number(item.paidAmount))),
      paymentDate: today(),
      challanReference: item.challanReference || "",
      bsrCode: item.bsrCode || "",
      bankReference: item.bankReference || "",
      challanProofUrl: item.challanProofUrl || "",
    });
  };
  const savePayment = async (event) => {
    event.preventDefault();
    const saved = await recordPayment(paymentItem, paymentForm);
    if (saved) setPaymentItem(null);
  };
  const exportRegister = () => {
    if (!filteredRows.length) return;
    const headers = [
      "Period",
      "Section",
      "Payee/Employee",
      "PAN/Employee Code",
      "Gross Amount",
      "Employee PF",
      "Employer PF",
      "Liability",
      "Due Date",
      "Status",
      "Payment Date",
      "Challan/CIN",
      "BSR",
    ];
    const csvCell = (value) => `"${String(value ?? "").replaceAll('"', '""')}"`;
    const csv = [
      headers,
      ...filteredRows.map((item) => [
        item.period,
        item.section || item.complianceType,
        item.payeeName || item.employeeName,
        item.payeePan || item.employeeCode,
        item.grossAmount || item.gross,
        item.employeeContribution || item.pf,
        item.employerContribution || item.employerPf,
        item.amount,
        item.dueDate,
        effectiveStatutoryStatus(item),
        item.paymentDate,
        item.challanReference,
        item.bsrCode,
      ]),
    ]
      .map((line) => line.map(csvCell).join(","))
      .join("\n");
    const url = URL.createObjectURL(
      new Blob([csv], { type: "text/csv;charset=utf-8" })
    );
    const link = document.createElement("a");
    link.href = url;
    link.download = `statutory-register-${periodFilter || today()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {paymentItem && (
        <div
          className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm"
          role="dialog"
          aria-modal="true"
          aria-label="Record statutory payment"
        >
          <form
            onSubmit={savePayment}
            className="w-full max-w-2xl overflow-hidden rounded-3xl bg-white shadow-2xl"
          >
            <div className="flex items-start justify-between gap-4 bg-slate-950 px-6 py-5 text-white">
              <div>
                <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300">
                  Accounts Payment Evidence
                </p>
                <h2 className="mt-1 text-xl font-black">
                  {paymentItem.section || paymentItem.complianceType} • {paymentItem.period}
                </h2>
                <p className="mt-1 text-xs font-semibold text-slate-300">
                  Liability {money(paymentItem.amount)} • Due {paymentItem.dueDate || "-"}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setPaymentItem(null)}
                className="flex h-9 w-9 items-center justify-center rounded-xl border border-white/15 bg-white/10"
                aria-label="Close payment form"
              >
                <X size={17} />
              </button>
            </div>
            <div className="grid gap-4 p-6 sm:grid-cols-2">
              <Field
                label="Paid Amount *"
                type="number"
                min="0.01"
                step="0.01"
                value={paymentForm.paidAmount}
                onChange={(event) => setPaymentForm({ ...paymentForm, paidAmount: event.target.value })}
              />
              <Field
                label="Payment Date *"
                type="date"
                value={paymentForm.paymentDate}
                onChange={(event) => setPaymentForm({ ...paymentForm, paymentDate: event.target.value })}
              />
              <Field
                label="Challan / CIN *"
                value={paymentForm.challanReference}
                onChange={(event) => setPaymentForm({ ...paymentForm, challanReference: event.target.value })}
              />
              <Field
                label="BSR Code"
                value={paymentForm.bsrCode}
                onChange={(event) => setPaymentForm({ ...paymentForm, bsrCode: event.target.value })}
              />
              <Field
                label="Bank Reference / UTR"
                value={paymentForm.bankReference}
                onChange={(event) => setPaymentForm({ ...paymentForm, bankReference: event.target.value })}
              />
              <Field
                label="Challan Proof URL"
                value={paymentForm.challanProofUrl}
                onChange={(event) => setPaymentForm({ ...paymentForm, challanProofUrl: event.target.value })}
              />
            </div>
            <div className="flex justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
              <Button tone="light" onClick={() => setPaymentItem(null)}>Cancel</Button>
              <Button type="submit" tone="green"><Save size={15} /> Save Challan Payment</Button>
            </div>
          </form>
        </div>
      )}

      <section className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <EmployeeRequestStat icon={Landmark} label="Open Liability" value={money(totalOpen)} tone="blue" />
        <EmployeeRequestStat icon={AlertTriangle} label="Overdue Items" value={overdueRows.length} tone="red" />
        <EmployeeRequestStat icon={CalendarClock} label="Due in 7 Days" value={dueSoonRows.length} tone="amber" />
        <EmployeeRequestStat icon={CheckCheck} label="Paid / Closed" value={rows.filter((item) => item.isClosed).length} tone="green" />
      </section>

      {canManage && (
        <Card title="Add Section 194 Non-Salary TDS" icon={FileSpreadsheet}>
          <div className="mb-5 rounded-xl border border-blue-200 bg-blue-50 p-4 text-sm font-semibold leading-6 text-blue-900">
            Contractor/professional/vendor payment ka Section, PAN, gross amount,
            deduction date aur TDS record karein. Rate editable hai; applicable
            section/rate CA se verify karke enter karein. Due date automatically calculate hogi.
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Select label="TDS Section *" value={form.section} onChange={(event) => setForm({ ...form, section: event.target.value })}>
              <option value="194C">194C — Contractor / Work</option>
              <option value="194J">194J — Professional / Technical</option>
              <option value="194H">194H — Commission / Brokerage</option>
              <option value="194I">194I — Rent</option>
              <option value="194Q">194Q — Purchase of Goods</option>
              <option value="194A">194A — Interest other than securities</option>
              <option value="194OTHER">Other Section 194</option>
            </Select>
            <Field label="Nature of Payment" value={form.nature} onChange={(event) => setForm({ ...form, nature: event.target.value })} />
            <Field label="Payee / Vendor Name *" value={form.payeeName} onChange={(event) => setForm({ ...form, payeeName: event.target.value })} />
            <Field label="Payee PAN *" maxLength={10} value={form.payeePan} onChange={(event) => setForm({ ...form, payeePan: event.target.value.toUpperCase() })} />
            <Field label="Invoice / Payment Reference" value={form.invoiceReference} onChange={(event) => setForm({ ...form, invoiceReference: event.target.value })} />
            <Field label="Payment / Deduction Date *" type="date" value={form.paymentDate} onChange={(event) => setForm({ ...form, paymentDate: event.target.value, period: event.target.value.slice(0, 7) })} />
            <Field label="Deduction Month *" type="month" value={form.period} onChange={(event) => setForm({ ...form, period: event.target.value })} />
            <Field label="Gross Amount *" type="number" min="0" step="0.01" value={form.grossAmount} onChange={(event) => setForm({ ...form, grossAmount: event.target.value })} />
            <Field label="TDS Rate % *" type="number" min="0" max="100" step="0.01" value={form.tdsRate} onChange={(event) => setForm({ ...form, tdsRate: event.target.value })} />
            <Field label={`TDS Amount (Auto ${money(computed194Amount)})`} type="number" min="0" step="0.01" value={form.tdsAmount} onChange={(event) => setForm({ ...form, tdsAmount: event.target.value })} placeholder="Blank = auto calculation" />
            <Field label="Calculated Due Date" type="date" value={statutoryDueDate(form.section, form.period)} readOnly />
            <Field label="Remarks" value={form.remarks} onChange={(event) => setForm({ ...form, remarks: event.target.value })} />
          </div>
          <Button className="mt-5" tone="green" onClick={create}>
            <CheckCircle2 size={16} /> Create Liability & Notify Accounts
          </Button>
        </Card>
      )}

      <Card
        title="TDS 192 / TDS 194 / PF Statutory Register"
        icon={Landmark}
        action={
          <div className="flex flex-wrap items-end gap-2">
            <Select label="Section" value={sectionFilter} onChange={(event) => setSectionFilter(event.target.value)}>
              <option value="ALL">All</option>
              <option value="192">Section 192</option>
              <option value="EPF">EPF</option>
              <option value="194C">194C</option>
              <option value="194J">194J</option>
              <option value="194H">194H</option>
              <option value="194I">194I</option>
              <option value="194Q">194Q</option>
              <option value="194A">194A</option>
            </Select>
            <Field label="Period" type="month" value={periodFilter} onChange={(event) => setPeriodFilter(event.target.value)} />
            <Button tone="light" onClick={exportRegister}><Download size={14} /> Export CSV</Button>
          </div>
        }
      >
        <Table
          collectionName={COLLECTIONS.statutory}
          recordLabel="statutory liability"
          columns={["Person / Source", "Section / Period", "Deduction Breakup", "Due Date", "Status", "Challan / Payment", "Action"]}
          rows={filteredRows}
          empty="Payroll run ya Section 194 entry ke baad statutory liability yahan dikhegi."
          renderRow={(item) => {
            const status = effectiveStatutoryStatus(item);
            return (
              <tr key={item.id} className="hover:bg-slate-50">
                <TD>
                  <p className="font-semibold">{item.payeeName || item.employeeName || "Consolidated"}</p>
                  <p className="mt-1 text-xs text-slate-500">{item.payeePan || item.employeeCode || "-"}</p>
                  <p className="mt-1 text-[10px] font-bold text-slate-400">{String(item.sourceType || "MANUAL").replaceAll("_", " ")}</p>
                </TD>
                <TD>
                  <p className="font-semibold">{item.sectionLabel || item.section || item.complianceType}</p>
                  <p className="mt-1 text-xs text-slate-500">{item.period || "-"}</p>
                </TD>
                <TD>
                  {clean(item.section).toUpperCase() === "EPF" ? (
                    <>
                      <p className="text-xs font-semibold">Employee: {money(item.employeeContribution || item.pf)}</p>
                      <p className="mt-1 text-xs font-semibold">Employer: {money(item.employerContribution || item.employerPf)}</p>
                    </>
                  ) : (
                    <>
                      <p className="text-xs font-semibold">Gross: {money(item.grossAmount || item.gross)}</p>
                      {item.tdsRate !== undefined && <p className="mt-1 text-xs text-slate-500">Rate: {item.tdsRate}%</p>}
                    </>
                  )}
                  <p className="mt-2 font-black text-slate-950">Payable: {money(item.amount)}</p>
                </TD>
                <TD>
                  <p className={`font-semibold ${status === "OVERDUE" ? "text-red-700" : "text-slate-800"}`}>{item.dueDate || "-"}</p>
                  <p className="mt-1 text-[10px] font-semibold text-slate-400">Deducted {item.deductionDate || item.paymentDate || "-"}</p>
                </TD>
                <TD><Badge value={status} /></TD>
                <TD>
                  <p className="font-semibold">{item.challanReference || "Not recorded"}</p>
                  {item.paymentDate && <p className="mt-1 text-xs text-slate-500">Paid {item.paymentDate}</p>}
                  {item.bsrCode && <p className="mt-1 text-[10px] text-slate-400">BSR {item.bsrCode}</p>}
                  {item.challanProofUrl && <a href={item.challanProofUrl} target="_blank" rel="noreferrer" className="mt-2 inline-block text-xs font-semibold text-blue-600">Open proof</a>}
                </TD>
                <TD>
                  {canManage && !item.isClosed ? (
                    <Button tone={status === "OVERDUE" ? "red" : "green"} onClick={() => openPayment(item)}>
                      <ReceiptIndianRupee size={14} /> Record Challan
                    </Button>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">{item.isClosed ? "Completed" : "Read only"}</span>
                  )}
                </TD>
              </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function ReimbursementModule({
  form,
  setForm,
  employees,
  rows,
  selectEmployee,
  submit,
  decision,
  recordPayment,
  canHR,
  isHRAdmin,
  canCEO,
  canRecordPayment,
}) {
  const pendingHr = rows.filter((item) =>
    ["PENDING_HR_REVIEW", "PENDING_HR_APPROVAL"].includes(item.status)
  ).length;
  const pendingCeo = rows.filter((item) =>
    isPendingCEOApproval(item.status)
  ).length;
  const accountsPending = rows.filter(
    (item) => item.paymentStatus === "PENDING_ACCOUNT_PAYMENT"
  ).length;
  const paidValue = rows
    .filter((item) => item.paymentStatus === "PAID")
    .reduce((total, item) => total + number(item.amount), 0);

  return (
    <div className="space-y-5">
      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          ["HR Review", pendingHr, ClipboardCheck, "bg-blue-50 text-blue-700"],
          ["CEO Approval", pendingCeo, Crown, "bg-amber-50 text-amber-700"],
          [
            "Accounts Pending",
            accountsPending,
            Banknote,
            "bg-orange-50 text-orange-700",
          ],
          ["Paid Value", money(paidValue), CheckCheck, "bg-emerald-50 text-emerald-700"],
        ].map(([label, value, Icon, tone]) => (
          <article
            key={label}
            className="rounded-lg border border-slate-200 bg-white p-5 shadow-sm"
          >
            <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${tone}`}>
              <Icon size={19} />
            </div>
            <p className="mt-4 text-2xl font-semibold">{value}</p>
            <p className="mt-1 text-[10px] font-semibold uppercase tracking-wide text-slate-400">
              {label}
            </p>
          </article>
        ))}
      </section>

      {isHRAdmin && (
        <Card title="Create / Register Reimbursement Claim" icon={CircleDollarSign}>
          <div className="mb-5 rounded-lg border border-violet-200 bg-violet-50 p-4 text-xs font-semibold leading-5 text-violet-800">
            Employee ESS submissions automatically appear below. HR can also
            register an offline claim here. Claims above{" "}
            <strong>{money(REIMBURSEMENT_CEO_THRESHOLD)}</strong> route to CEO;
            approved claims route to {OFFICIAL_EMAILS.ACCOUNT}.
          </div>
          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Select
              label="Employee"
              value={form.employeeId}
              onChange={(event) => selectEmployee(event.target.value)}
            >
              <option value="">Select employee</option>
              {employees
                .filter((employee) => employee.status !== "INACTIVE")
                .map((employee) => (
                  <option key={employee.id} value={employee.id}>
                    {employee.name} • {employee.employeeCode || employee.id}
                  </option>
                ))}
            </Select>
            <Field
              label="Expense Date"
              type="date"
              max={today()}
              value={form.claimDate}
              onChange={(event) =>
                setForm({ ...form, claimDate: event.target.value })
              }
            />
            <Select
              label="Category"
              value={form.category}
              onChange={(event) =>
                setForm({ ...form, category: event.target.value })
              }
            >
              <option value="TRAVEL">Travel</option>
              <option value="LOCAL_CONVEYANCE">Local Conveyance</option>
              <option value="LODGING">Lodging</option>
              <option value="MEALS">Meals</option>
              <option value="OFFICE_EXPENSE">Office Expense</option>
              <option value="MEDICAL">Medical</option>
              <option value="OTHER">Other</option>
            </Select>
            <Field
              label="Amount"
              type="number"
              min="0.01"
              step="0.01"
              value={form.amount}
              onChange={(event) =>
                setForm({ ...form, amount: event.target.value })
              }
            />
            <TextArea
              label="Purpose / Description"
              className="md:col-span-2"
              rows={3}
              value={form.description}
              onChange={(event) =>
                setForm({ ...form, description: event.target.value })
              }
            />
            <Field
              label="Receipt URL (optional)"
              className="md:col-span-2"
              type="url"
              value={form.receiptUrl}
              onChange={(event) =>
                setForm({ ...form, receiptUrl: event.target.value })
              }
              placeholder="https://..."
            />
          </div>
          <div className="mt-5 flex justify-end">
            <Button tone="green" onClick={submit}>
              <CheckCircle2 size={16} /> Submit for HR Review
            </Button>
          </div>
        </Card>
      )}

      <Card
        title="HR → CEO Threshold → Accounts Payment Control"
        icon={Wallet}
        action={
          <span className="rounded-full bg-slate-100 px-3 py-1.5 text-[10px] font-semibold uppercase text-slate-600">
            CEO threshold {money(REIMBURSEMENT_CEO_THRESHOLD)}
          </span>
        }
      >
        <Table
          collectionName={COLLECTIONS.reimbursements}
          recordLabel="reimbursement claim"
          columns={[
            "Claim / Employee",
            "Expense",
            "Purpose / Evidence",
            "Approval",
            "Accounts Payment",
            "Action",
          ]}
          rows={rows}
          empty="No reimbursement claims found"
          renderRow={(item) => {
            const amount = number(item.amount);
            const pendingHrReview = [
              "PENDING_HR_REVIEW",
              "PENDING_HR_APPROVAL",
            ].includes(item.status);
            const receiptLabel =
              item.receiptFileName ||
              item.receiptStoragePath ||
              "Receipt attached";
            return (
              <tr key={item.id} className="hover:bg-slate-50">
                <TD>
                  <p className="font-semibold">
                    {item.claimNumber || item.claimId || item.id}
                  </p>
                  <p className="mt-1 text-xs font-semibold text-slate-600">
                    {item.employeeName || "Employee"} •{" "}
                    {item.employeeCode || "-"}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">
                    {item.source || "HRMS"}
                  </p>
                </TD>
                <TD>
                  <p className="text-lg font-semibold text-blue-700">
                    {money(amount)}
                  </p>
                  <p className="mt-1 text-xs font-semibold">
                    {String(item.category || "OTHER").replaceAll("_", " ")}
                  </p>
                  <p className="mt-1 text-[10px] text-slate-400">
                    {item.claimDate || item.expenseDate || "-"}
                  </p>
                  {amount > REIMBURSEMENT_CEO_THRESHOLD && (
                    <p className="mt-2 text-[10px] font-semibold text-amber-700">
                      CEO CONTROL LIMIT
                    </p>
                  )}
                </TD>
                <TD>
                  <p className="max-w-[240px] text-xs font-semibold leading-5 text-slate-700">
                    {item.description || item.purpose || "-"}
                  </p>
                  {item.projectSite && (
                    <p className="mt-1 text-[10px] text-slate-500">
                      Site: {item.projectSite}
                    </p>
                  )}
                  {(item.fromLocation || item.toLocation) && (
                    <p className="mt-1 text-[10px] text-slate-500">
                      {item.fromLocation || "-"} → {item.toLocation || "-"}
                    </p>
                  )}
                  {item.receiptUrl ? (
                    <SecureStorageLink
                      className="mt-2 inline-block text-xs"
                      path={item.receiptUrl}
                      label="Open Receipt"
                    />
                  ) : item.receiptFileName || item.receiptStoragePath ? (
                    <div
                      className="mt-2 max-w-[220px] text-xs"
                      title={item.receiptStoragePath || receiptLabel}
                    >
                      <SecureStorageLink
                        path={item.receiptStoragePath}
                        label={`Open secure receipt: ${receiptLabel}`}
                      />
                    </div>
                  ) : (
                    <p className="mt-2 text-[10px] font-semibold text-slate-400">
                      No receipt
                    </p>
                  )}
                </TD>
                <TD>
                  <Badge value={item.status} />
                  {item.rejectionReason && (
                    <p className="mt-2 max-w-[180px] text-[10px] text-red-600">
                      {item.rejectionReason}
                    </p>
                  )}
                  <p className="mt-2 text-[10px] font-semibold text-slate-400">
                    {item.ceoActionBy || item.hrActionBy || item.submittedByName || ""}
                  </p>
                </TD>
                <TD>
                  <Badge value={item.paymentStatus || "NOT_REQUESTED"} />
                  {item.paymentReference || item.utrNumber ? (
                    <p className="mt-2 text-xs font-semibold text-emerald-700">
                      {item.paymentReference || item.utrNumber}
                    </p>
                  ) : null}
                  {item.paymentDate && (
                    <p className="mt-1 text-[10px] text-slate-400">
                      {item.paymentDate}
                    </p>
                  )}
                </TD>
                <TD>
                  <div className="flex max-w-[230px] flex-wrap gap-2">
                    {isHRAdmin && pendingHrReview && (
                      <>
                        <Button
                          tone="green"
                          onClick={() => decision(item, "HR", "APPROVED")}
                        >
                          <Check size={14} />{" "}
                          {amount > REIMBURSEMENT_CEO_THRESHOLD
                            ? "Approve → CEO"
                            : "Approve → Accounts"}
                        </Button>
                        <Button
                          tone="red"
                          onClick={() => decision(item, "HR", "REJECTED")}
                        >
                          <X size={14} /> Reject
                        </Button>
                      </>
                    )}
                    {canCEO && isPendingCEOApproval(item.status) && (
                      <>
                        <Button
                          tone="green"
                          onClick={() => decision(item, "CEO", "APPROVED")}
                        >
                          <Crown size={14} /> CEO Approve
                        </Button>
                        <Button
                          tone="red"
                          onClick={() => decision(item, "CEO", "REJECTED")}
                        >
                          CEO Reject
                        </Button>
                      </>
                    )}
                    {canRecordPayment &&
                      item.paymentStatus === "PENDING_ACCOUNT_PAYMENT" && (
                        <Button onClick={() => recordPayment(item)}>
                          Record UTR / Payment
                        </Button>
                      )}
                    {!isHRAdmin &&
                      !canCEO &&
                      canHR && (
                        <span className="text-xs text-slate-400">
                          Read only
                        </span>
                      )}
                  </div>
                </TD>
              </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function DocumentsModule({ rows, decision, canHR }) {
  const [busyDocumentId, setBusyDocumentId] = useState("");
  const pendingStatuses = [
    "PENDING",
    "PENDING_HR_VERIFICATION",
    "PENDING_VERIFICATION",
  ];
  const pendingCount = rows.filter((item) =>
    pendingStatuses.includes(clean(item.status || "PENDING").toUpperCase())
  ).length;

  const performDecision = async (item, nextStatus) => {
    const answer = window.prompt(
      nextStatus === "REJECTED"
        ? "Document rejection reason likhein (required):"
        : "Verification remarks likhein (optional):",
      item.verificationNote || ""
    );
    if (answer === null) return;
    const note = clean(answer);
    if (nextStatus === "REJECTED" && !note) {
      window.alert("Document reject karne ke liye reason required hai.");
      return;
    }
    setBusyDocumentId(item.id);
    try {
      await decision(item, nextStatus, note);
    } finally {
      setBusyDocumentId("");
    }
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-3">
        <EmployeeRequestStat
          icon={FileText}
          label="Total Documents"
          value={rows.length}
          tone="blue"
        />
        <EmployeeRequestStat
          icon={Clock}
          label="Pending Verification"
          value={pendingCount}
          tone="amber"
        />
        <EmployeeRequestStat
          icon={FileCheck2}
          label="Verified"
          value={
            rows.filter(
              (item) => clean(item.status).toUpperCase() === "VERIFIED"
            ).length
          }
          tone="green"
        />
      </div>
      <Card title="Employee Document Vault & Expiry Control" icon={FileCheck2}>
        <Table
          collectionName={COLLECTIONS.documents}
          recordLabel="employee document record"
          columns={[
            "Employee",
            "Document",
            "Number",
            "Expiry",
            "Verification",
            "File",
            "HR Action",
          ]}
          rows={rows}
          renderRow={(item) => {
            const status = clean(item.status || "PENDING").toUpperCase();
            const pending = pendingStatuses.includes(status);
            const fileUrl =
              item.fileUrl || item.downloadUrl || item.attachmentUrl;
            return (
              <tr key={item.id} className="hover:bg-slate-50">
                <TD>
                  <p className="font-semibold">
                    {item.employeeName || "Employee"}
                  </p>
                  <p className="mt-1 text-xs text-slate-500">
                    {item.employeeCode || "-"}
                    {item.employeeEmail || item.email
                      ? ` • ${item.employeeEmail || item.email}`
                      : ""}
                  </p>
                </TD>
                <TD className="font-semibold">
                  {item.documentType || "Employee Document"}
                </TD>
                <TD>{item.documentNumber || "-"}</TD>
                <TD>{item.expiryDate || "-"}</TD>
                <TD>
                  <Badge value={status} />
                  {item.verificationNote && (
                    <p className="mt-2 max-w-xs text-xs font-semibold leading-5 text-slate-600">
                      {item.verificationNote}
                    </p>
                  )}
                  {item.verifiedByName && (
                    <p className="mt-1 text-[10px] font-bold text-slate-400">
                      By {item.verifiedByName} •{" "}
                      {formatDate(item.verifiedAt)}
                    </p>
                  )}
                </TD>
                <TD>
                  {fileUrl ? (
                    <a
                      href={fileUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="font-semibold text-blue-600"
                    >
                      View File
                    </a>
                  ) : (
                    "-"
                  )}
                </TD>
                <TD>
                  {canHR && pending ? (
                    <div className="flex min-w-[170px] flex-wrap gap-2">
                      <Button
                        tone="green"
                        className="px-3 py-2 text-xs"
                        disabled={busyDocumentId === item.id}
                        onClick={() => performDecision(item, "VERIFIED")}
                      >
                        <Check size={14} /> Verify
                      </Button>
                      <Button
                        tone="red"
                        className="px-3 py-2 text-xs"
                        disabled={busyDocumentId === item.id}
                        onClick={() => performDecision(item, "REJECTED")}
                      >
                        <X size={14} /> Reject
                      </Button>
                    </div>
                  ) : (
                    <span className="text-xs font-semibold text-slate-400">
                      {pending ? "HR access required" : "Completed"}
                    </span>
                  )}
                </TD>
              </tr>
            );
          }}
        />
      </Card>
    </div>
  );
}

function OfferPreparationModal({
  application,
  form,
  setForm,
  file,
  setFile,
  busy,
  onSubmit,
  onClose,
}) {
  const gross = roundMoney(
    number(form.basic) +
      number(form.hra) +
      number(form.da) +
      number(form.allowances)
  );
  const existingPackage = application.offerPackage || {};
  return (
    <div
      className="fixed inset-0 z-[85] flex items-center justify-center bg-slate-950/75 p-3 backdrop-blur-sm sm:p-5"
      role="dialog"
      aria-modal="true"
      aria-label="Prepare final offer and payroll compliance"
    >
      <section className="flex max-h-[94vh] w-full max-w-5xl flex-col overflow-hidden rounded-3xl border border-white/20 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-4 bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 px-6 py-5 text-white">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[.2em] text-cyan-300">
              HR Final Offer Package
            </p>
            <h2 className="mt-1 text-2xl font-black">
              Salary, Section 192 TDS & PF Setup
            </h2>
            <p className="mt-2 text-sm font-semibold text-slate-300">
              {application.candidateName || application.name || "Candidate"} • {application.jobTitle || application.position || "Position"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10"
            aria-label="Close offer preparation"
          >
            <X size={18} />
          </button>
        </div>

        <div className="overflow-y-auto p-6">
          <div className="mb-5 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-semibold leading-6 text-amber-950">
            HR signed offer/appointment PDF upload karke salary, TDS treatment aur
            PF compliance submit karega. CEO approval se pehle employee activate
            ya payroll me include nahi hoga.
          </div>

          <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <Field
              label="Proposed Joining Date *"
              type="date"
              value={form.joiningDate}
              onChange={(event) => setForm({ ...form, joiningDate: event.target.value })}
            />
            <Field
              label="Basic Salary *"
              type="number"
              min="0"
              step="0.01"
              value={form.basic}
              onChange={(event) => setForm({ ...form, basic: event.target.value })}
            />
            <Field
              label="HRA"
              type="number"
              min="0"
              step="0.01"
              value={form.hra}
              onChange={(event) => setForm({ ...form, hra: event.target.value })}
            />
            <Field
              label="DA"
              type="number"
              min="0"
              step="0.01"
              value={form.da}
              onChange={(event) => setForm({ ...form, da: event.target.value })}
            />
            <Field
              label="Other Allowances"
              type="number"
              min="0"
              step="0.01"
              value={form.allowances}
              onChange={(event) => setForm({ ...form, allowances: event.target.value })}
            />
            <Select
              label="Section 192 TDS Treatment *"
              value={form.tdsMode}
              onChange={(event) => setForm({ ...form, tdsMode: event.target.value })}
            >
              <option value={TDS_MODE.SALARY_DEDUCTION}>Deduct from salary</option>
              <option value={TDS_MODE.COMPANY_BORNE}>Company pays separately</option>
              <option value={TDS_MODE.NOT_APPLICABLE}>Not applicable</option>
            </Select>
            <Field
              label="Monthly TDS Amount"
              type="number"
              min="0"
              step="0.01"
              disabled={normalizeTdsMode(form.tdsMode) === TDS_MODE.NOT_APPLICABLE}
              value={form.tds}
              onChange={(event) => setForm({ ...form, tds: event.target.value })}
            />
            <Select
              label="PF Compliance *"
              value={form.pfApplicable}
              onChange={(event) =>
                setForm({
                  ...form,
                  pfApplicable: event.target.value,
                  ...(event.target.value === "NO" ? { pf: "0", employerPf: "0" } : {}),
                })
              }
            >
              <option value="YES">PF Applicable</option>
              <option value="NO">PF Not Applicable</option>
            </Select>
            <Field
              label="Employee PF Deduction"
              type="number"
              min="0"
              step="0.01"
              disabled={form.pfApplicable !== "YES"}
              value={form.pf}
              onChange={(event) => setForm({ ...form, pf: event.target.value })}
            />
            <Field
              label="Employer PF Contribution"
              type="number"
              min="0"
              step="0.01"
              disabled={form.pfApplicable !== "YES"}
              value={form.employerPf}
              onChange={(event) => setForm({ ...form, employerPf: event.target.value })}
            />
            <Field
              label="Employee ESI Deduction"
              type="number"
              min="0"
              step="0.01"
              value={form.esi}
              onChange={(event) => setForm({ ...form, esi: event.target.value })}
            />
            <Field
              label="Professional Tax"
              type="number"
              min="0"
              step="0.01"
              value={form.professionalTax}
              onChange={(event) => setForm({ ...form, professionalTax: event.target.value })}
            />
            <Field
              label="Other Monthly Deduction"
              type="number"
              min="0"
              step="0.01"
              value={form.otherDeduction}
              onChange={(event) => setForm({ ...form, otherDeduction: event.target.value })}
            />
          </div>

          <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_1.2fr]">
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
              <p className="text-xs font-black uppercase tracking-wider text-emerald-700">
                Payroll Summary
              </p>
              <p className="mt-2 text-3xl font-black text-emerald-950">{money(gross)}</p>
              <p className="mt-1 text-sm font-semibold text-emerald-800">Gross monthly salary</p>
              <p className="mt-3 text-xs font-semibold leading-5 text-emerald-900">
                TDS: {tdsModeLabel(form.tdsMode)} • PF: {form.pfApplicable === "YES" ? "Applicable" : "Not applicable"}
              </p>
            </div>
            <div className="rounded-2xl border border-blue-200 bg-blue-50 p-5">
              <label className="text-xs font-black uppercase tracking-wider text-blue-900">
                Signed Offer / Appointment Letter PDF *
              </label>
              <input
                type="file"
                accept="application/pdf,.pdf"
                disabled={busy}
                onChange={(event) => setFile(event.target.files?.[0] || null)}
                className="mt-3 block w-full rounded-xl border border-blue-200 bg-white px-3 py-3 text-sm font-semibold text-slate-700 file:mr-3 file:rounded-lg file:border-0 file:bg-blue-700 file:px-3 file:py-2 file:font-bold file:text-white"
              />
              <p className="mt-2 text-xs font-semibold text-blue-700">
                {file
                  ? `${file.name} • ${(file.size / 1024 / 1024).toFixed(2)} MB`
                  : existingPackage.offerLetterFileName || "PDF up to 10 MB"}
              </p>
              {existingPackage.offerLetterStoragePath && !file && (
                <div className="mt-3">
                  <SecureStorageLink
                    path={existingPackage.offerLetterStoragePath}
                    label="Open Existing Offer PDF"
                  />
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 bg-slate-50 px-6 py-4">
          <Button tone="light" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button tone="green" onClick={onSubmit} disabled={busy}>
            <Save size={16} /> {busy ? "Uploading & Submitting..." : "Submit Package to CEO"}
          </Button>
        </div>
      </section>
    </div>
  );
}

function OfferLeavePolicyModal({
  approval,
  form,
  setForm,
  busy,
  onConfirm,
  onClose,
}) {
  const application = approval.application || {};
  const offerPackage = application.offerPackage || {};
  const salary = offerPackage.salaryStructure || {};
  const pfCompliance = offerPackage.pfCompliance || {};
  const fields = [
    ["CL", "Casual Leave (CL)"],
    ["EL", "Earned Leave (EL)"],
    ["OL", "Other Leave (OL)"],
    ["COMP_OFF", "Compensatory Off"],
  ];
  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/70 p-4 backdrop-blur-sm">
      <section className="w-full max-w-2xl overflow-hidden rounded-lg border border-white/20 bg-white shadow-2xl">
        <div className="bg-[#10233f] px-6 py-6 text-white">
          <p className="text-[10px] font-semibold uppercase tracking-[.22em] text-cyan-300">
            CEO Controlled Offer Release
          </p>
          <h2 className="mt-2 text-2xl font-semibold">
            Review Offer, TDS, PF & Leave
          </h2>
          <p className="mt-2 text-sm leading-6 text-slate-300">
            Approval ke baad uploaded letter employee, HR, CEO aur Accounts ko
            mail hoga aur accepted candidate ka same package payroll me jayega.
          </p>
        </div>
        <div className="p-6">
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="font-semibold text-slate-950">
              {application.candidateName ||
                approval.candidateName ||
                "Selected Candidate"}
            </p>
            <p className="mt-1 text-xs font-medium text-slate-500">
              {application.jobTitle || approval.title || "-"} •{" "}
              {application.deptName ||
                application.department ||
                approval.department ||
                "-"}
            </p>
          </div>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-3">
              <p className="text-[10px] font-black uppercase text-emerald-700">Gross / Month</p>
              <p className="mt-1 font-black text-emerald-950">{money(salary.gross)}</p>
            </div>
            <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
              <p className="text-[10px] font-black uppercase text-blue-700">Section 192 TDS</p>
              <p className="mt-1 text-sm font-black text-blue-950">{tdsModeLabel(salary.tdsMode)}</p>
              <p className="text-xs font-semibold text-blue-800">{money(salary.tds)}</p>
            </div>
            <div className="rounded-xl border border-violet-200 bg-violet-50 p-3">
              <p className="text-[10px] font-black uppercase text-violet-700">PF Compliance</p>
              <p className="mt-1 text-sm font-black text-violet-950">{pfCompliance.applicable ? "Applicable" : "Not applicable"}</p>
              <p className="text-xs font-semibold text-violet-800">Emp {money(pfCompliance.employeeContribution)} • Co {money(pfCompliance.employerContribution)}</p>
            </div>
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
              <p className="text-[10px] font-black uppercase text-slate-500">Offer PDF</p>
              <div className="mt-2">
                <SecureStorageLink
                  path={offerPackage.offerLetterStoragePath}
                  label="Open & Verify PDF"
                />
              </div>
            </div>
          </div>
          <div className="mt-5 grid gap-4 sm:grid-cols-2">
            {fields.map(([key, label]) => (
              <Field
                key={key}
                label={`${label} / Year`}
                type="number"
                min="0"
                max="365"
                step="1"
                value={form[key]}
                onChange={(event) =>
                  setForm((current) => ({
                    ...current,
                    [key]: event.target.value,
                  }))
                }
              />
            ))}
          </div>
          <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 p-4 text-xs font-medium leading-5 text-amber-900">
            Approved entitlement se zyada sanctioned paid leave automatically
            LOP me convert hogi. Payroll employee ke gross monthly salary ko
            selected month ke working days se divide karke per-day deduction
            calculate karega.
          </div>
          <div className="mt-6 flex flex-wrap justify-end gap-3">
            <Button tone="light" onClick={onClose} disabled={busy}>
              Cancel
            </Button>
            <Button tone="green" onClick={onConfirm} disabled={busy}>
              <CheckCircle2 size={16} />
              {busy ? "Approving..." : "Approve & Release Offer"}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}

function ApprovalModule({ rows, decision, canCEO }) {
  const approvalTime = (item) => {
    if (typeof item?.createdAt?.toMillis === "function") {
      return item.createdAt.toMillis();
    }
    if (Number.isFinite(Number(item?.createdAt?.seconds))) {
      return Number(item.createdAt.seconds) * 1000;
    }
    const parsed = new Date(item?.createdAt || 0).getTime();
    return Number.isFinite(parsed) ? parsed : 0;
  };
  const deduplicatedRows = new Map();
  rows.forEach((item) => {
    const normalizedItem = {
      ...item,
      type: normalizeApprovalType(item.type),
    };
    if (!CEO_APPROVAL_TYPES.has(normalizedItem.type)) return;
    const uniqueKey = `${normalizedItem.type}:${
      normalizedItem.relatedId || normalizedItem.id
    }`;
    const existing = deduplicatedRows.get(uniqueKey);
    if (
      !existing ||
      (isPendingCEOApproval(normalizedItem.status) &&
        !isPendingCEOApproval(existing.status)) ||
      (isPendingCEOApproval(normalizedItem.status) ===
        isPendingCEOApproval(existing.status) &&
        approvalTime(normalizedItem) > approvalTime(existing))
    ) {
      deduplicatedRows.set(uniqueKey, normalizedItem);
    }
  });
  const visibleRows = [...deduplicatedRows.values()].sort((left, right) => {
    const pendingDifference =
      Number(isPendingCEOApproval(right.status)) -
      Number(isPendingCEOApproval(left.status));
    return pendingDifference || approvalTime(right) - approvalTime(left);
  });
  return (
    <Card title="CEO Master Approval Center" icon={Crown}>
      <div className="mb-5 rounded-lg border border-amber-200 bg-amber-50 p-4">
        <p className="font-semibold text-amber-950">
          Job Budget • Final Selection • Leave Exception • Payroll • Reimbursement
        </p>
        <p className="mt-1 text-xs font-semibold text-amber-700">
          HR operational work yahan nahi rukega. Sirf management-risk aur
          financial-control decisions CEO Master ko bheje jayenge.
        </p>
      </div>
      <Table
        collectionName={COLLECTIONS.approvals}
        recordLabel="approval request"
        columns={[
          "Approval Type",
          "Person / Subject",
          "Business Detail",
          "Requested By",
          "Status",
          "CEO Action",
        ]}
        rows={visibleRows}
        renderRow={(item) => (
          <tr key={item.id}>
            <TD className="font-semibold">
              {String(item.type || "").replaceAll("_", " ")}
            </TD>
            <TD>
              {item.candidateName ||
                item.employeeName ||
                item.title ||
                "-"}
            </TD>
            <TD>
              {item.title || "-"}
              {item.department ? ` • ${item.department}` : ""}
              {item.amount ? ` • ${money(item.amount)}` : ""}
              {item.type === "FINAL_CANDIDATE_SELECTION" && (
                <p className={`mt-2 text-[10px] font-black uppercase ${
                  item.offerPackageStatus === "READY_FOR_CEO"
                    ? "text-emerald-600"
                    : "text-amber-600"
                }`}>
                  {item.offerPackageStatus === "READY_FOR_CEO"
                    ? "Offer + TDS/PF ready"
                    : "Awaiting HR offer package"}
                </p>
              )}
            </TD>
            <TD>
              <p className="font-semibold">{item.requestedBy || "HR System"}</p>
              <p className="text-xs text-slate-500">
                {item.requestedByEmail || ""}
              </p>
            </TD>
            <TD>
              <Badge
                value={
                  isPendingCEOApproval(item.status)
                    ? "PENDING_CEO_APPROVAL"
                    : normalizeWorkflowToken(item.status)
                }
              />
            </TD>
            <TD>
              {canCEO && isPendingCEOApproval(item.status) ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    tone="green"
                    disabled={
                      item.type === "FINAL_CANDIDATE_SELECTION" &&
                      item.offerPackageStatus !== "READY_FOR_CEO"
                    }
                    onClick={() => decision(item, "APPROVED_BY_CEO")}
                  >
                    <Check size={14} /> Approve
                  </Button>
                  <Button
                    tone="red"
                    onClick={() => decision(item, "REJECTED")}
                  >
                    <X size={14} /> Reject
                  </Button>
                </div>
              ) : (
                <span className="text-xs font-semibold text-slate-400">
                  {item.actionBy || "Read only"}
                </span>
              )}
            </TD>
          </tr>
        )}
      />
    </Card>
  );
}

function MailModule({ rows, retry, testMail, canHR }) {
  const sent = rows.filter((item) => clean(item.status).toUpperCase() === "SENT").length;
  const failed = rows.filter((item) => ["FAILED", "ERROR"].includes(clean(item.status).toUpperCase())).length;
  const pending = rows.filter((item) => ["PENDING_SEND", "PROCESSING", "QUEUED"].includes(clean(item.status).toUpperCase())).length;
  return (
    <div className="space-y-5">
      <Card>
        <div className="flex flex-col gap-4 rounded-2xl bg-gradient-to-r from-slate-950 via-blue-950 to-slate-900 p-5 text-white sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-[10px] font-black uppercase tracking-[.18em] text-cyan-300">Live Mail Delivery Control</p>
            <h2 className="mt-1 text-xl font-black">SMTP + Retry Automation</h2>
            <p className="mt-1 text-xs font-semibold text-slate-300">Pending {pending} • Sent {sent} • Failed {failed}. Failed mail 15 minute retry cycle me jayegi.</p>
          </div>
          {canHR && <Button tone="green" onClick={testMail}><Mail size={15} /> Send Test Email</Button>}
        </div>
      </Card>
      <Card title="Automated Official Communication Queue" icon={Mail}>
      <Table
        collectionName={COLLECTIONS.mail}
        recordLabel="mail queue record"
        columns={[
          "Type",
          "From → Recipient",
          "Subject",
          "Status",
          "Created",
          "Action",
        ]}
        rows={rows}
        renderRow={(item) => {
          const retryable = ["FAILED", "ERROR"].includes(
            clean(item.status).toUpperCase()
          );
          return (
            <tr key={item.id}>
              <TD className="font-semibold">
                {String(item.type || "").replaceAll("_", " ")}
              </TD>
              <TD>
                <p className="text-xs font-semibold">
                  {item.from || OFFICIAL_EMAILS.HR}
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  → {item.to || "-"}
                </p>
                {Array.isArray(item.cc) && item.cc.length > 0 && (
                  <p className="mt-1 text-[10px] text-slate-400">
                    CC: {item.cc.join(", ")}
                  </p>
                )}
              </TD>
              <TD>{item.subject || "-"}</TD>
              <TD>
                <Badge value={item.status} />
                {(item.lastError || item.errorMessage) && (
                  <p className="mt-1 max-w-xs text-[10px] font-semibold text-rose-600">
                    {item.lastError || item.errorMessage}
                  </p>
                )}
              </TD>
              <TD>{formatDate(item.createdAt)}</TD>
              <TD>
                {canHR && retryable ? (
                  <Button tone="amber" onClick={() => retry(item)}>
                    Retry
                  </Button>
                ) : (
                  <span className="text-xs font-semibold text-slate-400">—</span>
                )}
              </TD>
            </tr>
          );
        }}
      />
      </Card>
    </div>
  );
}

function ReportsModule({ data }) {
  const exportCsv = (name, rows) => {
    if (!rows.length) return;
    const keys = Object.keys(rows[0]).filter((key)=>!["createdAt","updatedAt"].includes(key));
    const csv = [keys.join(","),...rows.map(row=>keys.map(key=>`"${String(row[key]??"").replaceAll('"','""')}"`).join(","))].join("\n");
    const url=URL.createObjectURL(new Blob([csv],{type:"text/csv"}));
    const a=document.createElement("a");a.href=url;a.download=`${name}-${today()}.csv`;a.click();URL.revokeObjectURL(url);
  };
  return <div className="space-y-5"><Card title="Management Reports" icon={Database}><div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">{[["Employees",data.employees],["Attendance",data.attendance],["Employee Requests",data.employeeRequests],["Leave",data.leaves],["Payroll",data.payroll],["Reimbursements",data.reimbursements],["Applications",data.applications],["Audit Log",data.audit]].map(([label,rows])=><button type="button" key={label} onClick={()=>exportCsv(label.toLowerCase().replace(" ","-"),rows)} className="rounded-lg border border-slate-200 p-5 text-left hover:border-blue-500 hover:bg-blue-50"><p className="text-2xl font-semibold">{rows.length}</p><p className="mt-1 font-semibold">{label} Report</p><p className="mt-2 text-xs font-bold text-blue-600">Download CSV</p></button>)}</div></Card><Card title="Complete HR Activity Audit Trail" icon={ShieldCheck}><Table columns={["Action","Target","Performed By","Role","Date"]} rows={data.audit} renderRow={(item)=><tr key={item.id}><TD className="font-semibold">{item.action}</TD><TD>{item.targetType}</TD><TD>{item.byName}</TD><TD><Badge value={item.byRole}/></TD><TD>{formatDate(item.createdAt)}</TD></tr>}/></Card></div>;
}

function SettingsModule({ role }) {
  const accessModel = [
    [
      ROLE.CEO_MASTER,
      OFFICIAL_EMAILS.CEO,
      "Complete master access, final candidate selection, financial approvals, payroll approval, policy and audit control.",
    ],
    [
      ROLE.HR_ADMIN,
      OFFICIAL_EMAILS.HR,
      "Recruitment, interview, onboarding, employee master, attendance, leave, documents, payroll finalisation and Accounts communication.",
    ],
    [
      ROLE.ACCOUNTS_ADMIN,
      OFFICIAL_EMAILS.ACCOUNT,
      "Restricted Finance workspace for CEO-approved salary and reimbursement payment processing, UTR entry, mail queue and audit reports.",
    ],
  ];
  return (
    <div className="grid gap-5 lg:grid-cols-2">
      <Card title="Attendance Security Controls" icon={ShieldCheck}>
        <div className="space-y-3">
          {[
            "Live webcam photo mandatory",
            "Approved office IP whitelist",
            "Registered device/browser lock",
            "GPS radius and office location lock",
            "Duplicate check-in prevention",
            "Manual correction requires HR approval",
          ].map((item) => (
            <div
              key={item}
              className="flex items-center gap-3 rounded-xl bg-slate-50 p-4 font-bold"
            >
              <CheckCircle2
                className="text-emerald-600"
                size={19}
              />
              {item}
            </div>
          ))}
        </div>
      </Card>
      <Card title="Final Three-Login Access Model" icon={Lock}>
        <div className="space-y-3">
          {accessModel.map(([accessRole, email, description]) => (
            <div
              key={accessRole}
              className={`rounded-xl border p-4 ${
                role === accessRole
                  ? "border-blue-500 bg-blue-50"
                  : "border-slate-200"
              }`}
            >
              <div className="flex flex-wrap items-center justify-between gap-2">
                <p className="font-semibold">{accessRole}</p>
                <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-600">
                  {email}
                </span>
              </div>
              <p className="mt-2 text-sm font-semibold leading-6 text-slate-500">
                {description}
              </p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}
